import React, { Fragment, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { createPortal } from 'react-dom';
import Drawflow from 'drawflow';

import 'drawflow/dist/drawflow.min.css';
import './flow-builder.css';

const { groupAttributeOptions } = require('./flowAttributeOptionGroups');
const {
  conditionArrayDescendantOptions,
  conditionArrayPathsForVariable,
  conditionVariableMetadata,
  conditionVariablePathMetadata,
  conditionVariableSelectionLevels,
  normalizeConditionVariablePath
} = require('./flowConditionVariables');
const { normalizeExecutionStartVariableOptions } = require('./flowExecutionStartVariables');
const {
  mergeResponseSchemas,
  schemaFromInspectionInput,
  schemaFromRuntimeOutput,
  schemaFromTaskPayload,
  schemasEqual
} = require('./flowLearnedVariables');

const commandCatalog = {
  start: {
    label: 'Início',
    icon: 'play_circle',
    tone: 'green',
    description: 'Define a demanda, os filtros das tasks e a periodicidade de execução do roteamento.',
    defaults: {
      startMode: 'execution',
      startModeVersion: 2,
      demandId: '',
      demandName: '',
      filterLogic: 'AND',
      filters: [
        { id: 'filter-status-pending', source: 'status', attribute: 'status', operator: 'equals', value: 'Pendente' }
      ],
      executionType: 'periodic',
      taskUpdateTriggerMode: 'enter_filter',
      scheduleEvery: 10,
      scheduleUnit: 'minute',
      executionIntegrationId: '',
      executionIntegrationName: '',
      executionResponseSchema: {},
      executionRecordPath: '',
      executionTemplateVariables: {},
      executionVariableBindings: {},
      executionEveryMinutes: 5,
      executionTaskIdKey: '',
      taskArrayPath: '',
      executionUpsertEnabled: false,
      executionUpsertKey: ''
    }
  },
  api: {
    label: 'API',
    icon: 'api',
    tone: 'purple',
    description: 'Executa uma Integração, com retry, timeout e tratamento de falha pela política do roteamento.',
    defaults: {
      integrationId: '',
      integrationName: '',
      requestVariables: [],
      variableBindings: {},
      timeoutMs: 15000,
      retryCount: 3,
      retryBackoff: 'exponential',
      idempotencyKey: '{{demand.id}}',
      responsePath: '$.data'
    }
  },
  chat: {
    label: 'Chat',
    icon: 'chat',
    tone: 'blue',
    description: 'Envia template de chat usando plataforma, template e parâmetros mapeados com atributos da Task.',
    defaults: {
      platform: '',
      templateId: '',
      templateName: '',
      templateOrigin: '',
      templateContentTypeName: '',
      chatbotId: '',
      chatUrl: '',
      headerPreview: '',
      preview: '',
      footer: '',
      paramsCount: 0,
      paramBindings: {},
      infoCustomMappings: [],
      paramBindingDetails: {},
      attachmentBinding: null,
      attachmentAttribute: '',
      toBinding: null,
      toAttribute: '',
      timeoutMs: 15000
    }
  },
  voice: {
    label: 'Voz',
    icon: 'call',
    tone: 'teal',
    description: 'Envia campanha de voz com número de destino e parâmetros mapeados com atributos da Task.',
    defaults: {
      campaignId: '',
      campaignName: '',
      voiceUrl: '',
      paramsCount: 0,
      paramBindings: {},
      paramBindingDetails: {},
      toBinding: null,
      toAttribute: '',
      dialAtAttribute: '',
      dialAtBinding: null,
      timeoutMs: 15000
    }
  },
  cortex: {
    label: 'Cortex',
    icon: 'neurology',
    tone: 'purple',
    description: 'Executa recursos Cortex (Tagent, Trag, TTS, OCR e Transcription) com token APP por tenant.',
    defaults: {
      resource: 'tagent',
      resourceCod: '',
      resourceResponseType: '',
      followUpAttribute: 'task_key',
      voiceAttribute: '',
      fileAttribute: '',
      tagentOptions: [],
      payloadBindings: {
        message: ''
      },
      payloadTemplates: {
        message: ''
      },
      genericPrompt: '',
      genericOutputFormat: '',
      ocrOptions: [],
      timeoutMs: 20000,
      errorPolicy: 'Falhar execução'
    }
  },
  condition: {
    label: 'Condições',
    icon: 'rule',
    tone: 'amber',
    description: 'Roteia a execução por expressões, operadores, branches e fallback controlado.',
    defaults: {
      decisionMode: 'boolean',
      decisionPaths: [],
      decisionSource: {
        sourceNodeId: 'task',
        sourceType: 'task',
        integrationId: '',
        variable: '',
        variableType: '',
        arrayMatchByPath: {},
        arrayNavigationByPath: {}
      },
      conditionLogic: 'AND',
      conditions: [],
      errorPolicy: 'Assumir como falso',
      strictMode: true,
      auditTag: 'route-condition'
    }
  },
  delay: {
    label: 'Delay',
    icon: 'timer',
    tone: 'blue',
    description: 'Pausa controlada com estratégia fixa ou exponencial.',
    defaults: {
      duration: 90,
      unit: 'seconds',
      strategy: 'fixed',
      jitterPercent: 15,
      maxWait: 900
    }
  },
  saveInfo: {
    label: 'Salvar info',
    icon: 'save',
    tone: 'purple',
    description: 'Atualiza atributos existentes ou adiciona novas Informações Agregadas à Task.',
    defaults: {
      targetScope: 'Task',
      mappings: [],
      nextStatus: ''
    }
  },
  insertDemand: {
    label: 'Inserir em Demanda',
    icon: 'playlist_add',
    tone: 'green',
    description: 'Cria ou move uma Task preservando seus atributos e adicionando ou atualizando somente as chaves configuradas.',
    defaults: {
      operation: 'copy',
      targetDemandId: '',
      payloadMode: 'clone_task',
      apiResponseValueAttribute: '',
      payloadJson: '{\n  "source_task_key": "{{task.task_key}}"\n}',
      status: ''
    }
  }
};

const legacyCommandCatalog = {
  json: {
    label: 'Comando removido',
    icon: 'data_object',
    tone: 'green',
    description: 'Este comando não  está mais disponível para novos roteamentos.'
  }
};

const commandForType = (type) => commandCatalog[type] || legacyCommandCatalog[type] || commandCatalog.api;

const START_NODE_ID = 'start-1';
const START_MODE_SEARCH = 'search';
const START_MODE_EXECUTION = 'execution';
const START_EXECUTION_TYPE_PERIODIC = 'periodic';
const START_EXECUTION_TYPE_TASK_UPDATE = 'task_update';
const TASK_UPDATE_TRIGGER_MODE_ENTER_FILTER = 'enter_filter';
const TASK_UPDATE_TRIGGER_MODE_ANY_MATCH = 'any_change_matching_filter';
const COMPACT_PALETTE_MEDIA_QUERY = '(max-width: 67.5rem)';
const SELECTED_ROUTE_KEY = 't-demand-routing-selected-route-id';
const SESSION_EXPIRED_EVENT = 'tdemand:session-expired';
const AUTH_API_PATH_RE = /^\/api\/(?:login\/|logout(?:\/|$))/i;

const apiPathFromUrl = (url) => {
  try {
    return new URL(String(url || ''), window.location.origin).pathname;
  } catch (error) {
    return String(url || '').split('?')[0];
  }
};

const shouldDispatchSessionExpired = (status, url) => {
  if (Number(status) !== 401) return false;
  const path = apiPathFromUrl(url);
  return !AUTH_API_PATH_RE.test(path);
};

const dispatchSessionExpiredEvent = (url, status) => {
  if (!window?.dispatchEvent) return;
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, {
    detail: {
      status: Number(status) || 0,
      url: String(url || '')
    }
  }));
};

const clampDrawflowZoom = (value) => {
  const zoom = Number(value);
  if (!Number.isFinite(zoom)) return null;
  return Math.max(0.35, Math.min(1.6, zoom));
};

const readStoredSelectedRouteId = () => {
  try {
    const value = String(localStorage.getItem(SELECTED_ROUTE_KEY) || '').trim();
    return value || null;
  } catch {
    return null;
  }
};

const persistSelectedRouteId = (routeId) => {
  const value = routeId == null ? '' : String(routeId).trim();
  try {
    if (!value) {
      localStorage.removeItem(SELECTED_ROUTE_KEY);
      return;
    }
    localStorage.setItem(SELECTED_ROUTE_KEY, value);
  } catch {
    // noop
  }
};

const isCompactPaletteViewport = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(COMPACT_PALETTE_MEDIA_QUERY).matches;
};

const isStartNode = (node = {}) => node.id === START_NODE_ID || node.data?.commandType === 'start';

const normalizeStartMode = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (['execution', 'execucao', 'execução'].includes(normalized)) return START_MODE_EXECUTION;
  if (['search', 'busca', 'buscar'].includes(normalized)) return START_MODE_SEARCH;
  return START_MODE_EXECUTION;
};

// Roteamentos antigos nao possuem executionType: a ausencia do atributo equivale a 'periodic'.
const normalizeStartExecutionType = (value) => {
  const normalized = String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if ([
    'task_update',
    'taskupdate',
    'task_updated',
    'on_task_update',
    'atualizacao_task',
    'atualizacao_da_task'
  ].includes(normalized)) {
    return START_EXECUTION_TYPE_TASK_UPDATE;
  }
  return START_EXECUTION_TYPE_PERIODIC;
};

const isTaskUpdateStartConfig = (config = {}) => (
  normalizeStartMode(config.startMode || config.start_type || config.startType) === START_MODE_EXECUTION
  && normalizeStartExecutionType(
    config.executionType ?? config.execution_type ?? config.executionTriggerType
  ) === START_EXECUTION_TYPE_TASK_UPDATE
);

const normalizeTaskUpdateTriggerMode = (value) => {
  const normalized = String(value || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if ([
    'any_change_matching_filter',
    'any_change_with_filter',
    'any_matching_change',
    'qualquer_alteracao_com_filtro',
    'qualquer_alteração_com_filtro',
    'qualquer_alteracao',
    'qualquer_alteração',
    'legado'
  ].includes(normalized)) {
    return TASK_UPDATE_TRIGGER_MODE_ANY_MATCH;
  }
  return TASK_UPDATE_TRIGGER_MODE_ENTER_FILTER;
};

const normalizeStartExecutionInterval = (value, fallback = 5) => {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed <= 0) return fallback;
  return Math.max(1, Math.min(60, parsed));
};

const normalizeStartExecutionPathKey = (value) => String(value || '').trim();

const normalizeResponseSchema = (value) => (
  value && typeof value === 'object' && !Array.isArray(value) ? value : {}
);

const learnedTaskPayloadSchemaFromConfig = (config = {}) => normalizeResponseSchema(
  config.learnedTaskPayloadSchema || config.learned_task_payload_schema
);

const learnedResponseSchemaFromConfig = (config = {}) => normalizeResponseSchema(
  config.learnedResponseSchema || config.learned_response_schema
);

const learnedInputSchemaFromConfig = (config = {}) => normalizeResponseSchema(
  config.learnedInputSchema || config.learned_input_schema
);

const INSPECTION_CONTRACT_VERSION = 2;
const INSPECTION_CONTEXT_VERSION = 2;
const hasCurrentInspectionContract = (config = {}) => (
  Number(config.inspectionContractVersion || 0) >= INSPECTION_CONTRACT_VERSION
);

const normalizePayloadPath = (value) => {
  const raw = String(value || '').trim();
  if (!raw || raw === '$') return '';
  return raw
    .replace(/^\$\./, '')
    .replace(/^\$/, '')
    .replace(/\[\]/g, '.[]')
    .replace(/\[(\d+)\]/g, '.$1')
    .replace(/^\./, '')
    .replace(/\.\./g, '.')
    .replace(/\s+/g, '');
};

const readPayloadPath = (value, path) => {
  const normalizedPath = normalizePayloadPath(path);
  if (!normalizedPath) return value;
  return normalizedPath
    .split('.')
    .filter(Boolean)
    .reduce((acc, part) => {
      if (acc == null) return undefined;
      if (part === '[]') return Array.isArray(acc) ? acc : undefined;
      if (part.endsWith('[]')) {
        const key = part.slice(0, -2);
        const nextValue = key ? acc[key] : acc;
        return Array.isArray(nextValue) ? nextValue : undefined;
      }
      return acc[part];
    }, value);
};

const parseJsonLikeValue = (value) => {
  if (value == null) return null;
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const isPlainObjectValue = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const assessResponseSchemaPayloadShape = (schema = {}, path = '$') => {
  const keys = Object.keys(normalizeResponseSchema(schema));
  const schemaObject = normalizeResponseSchema(schema);
  const normalizedPath = normalizePayloadPath(path);
  if (!keys.length) {
    return {
      known: false,
      mayBeNonObject: true
    };
  }
  if (normalizedPath) {
    const directType = String(schemaObject[normalizedPath] || '').trim().toLowerCase();
    if (directType) {
      const typeTokens = directType.split('|').map((token) => token.trim()).filter(Boolean);
      if (typeTokens.some((token) => token === 'array')) {
        return {
          known: true,
          mayBeNonObject: true
        };
      }
      if (typeTokens.some((token) => token === 'object')) {
        return {
          known: true,
          mayBeNonObject: false
        };
      }
      return {
        known: true,
        mayBeNonObject: true
      };
    }
    const arrayPathPrefix = `${normalizedPath}[]`;
    if (keys.some((key) => String(key).startsWith(arrayPathPrefix))) {
      return {
        known: true,
        mayBeNonObject: true
      };
    }
    if (keys.some((key) => String(key).startsWith(`${normalizedPath}.`))) {
      return {
        known: true,
        mayBeNonObject: false
      };
    }
    return {
      known: false,
      mayBeNonObject: true
    };
  }
  const hasArrayRoot = keys.some((key) => String(key || '').startsWith('[]'));
  if (hasArrayRoot) {
    return {
      known: true,
      mayBeNonObject: true
    };
  }
  return {
    known: true,
    mayBeNonObject: false
  };
};

const runtimeOutputPayloadDetails = (output) => {
  if (!output || typeof output !== 'object') return { known: false, payload: null };
  if (Object.prototype.hasOwnProperty.call(output, 'body_json')) {
    const bodyJson = output.body_json;
    if (bodyJson !== null && bodyJson !== undefined) return { known: true, payload: bodyJson };
    if (Object.prototype.hasOwnProperty.call(output, 'body')) {
      return { known: true, payload: parseJsonLikeValue(output.body) };
    }
    return { known: true, payload: bodyJson };
  }
  if (Object.prototype.hasOwnProperty.call(output, 'body')) {
    return { known: true, payload: parseJsonLikeValue(output.body) };
  }
  return { known: true, payload: output };
};

const isSearchStartConfig = (config = {}) => normalizeStartMode(
  config.startMode || config.start_type || config.startType
) === START_MODE_SEARCH;

const normalizeStartConfig = (config = {}, route = {}) => {
  const defaults = commandCatalog.start.defaults;
  return {
    ...defaults,
    ...config,
    startMode: normalizeStartMode(config.startMode || config.start_type || config.startType || defaults.startMode),
    startModeVersion: Number.parseInt(config.startModeVersion || config.start_mode_version || defaults.startModeVersion || 2, 10) || 2,
    executionType: normalizeStartExecutionType(
      config.executionType
      ?? config.execution_type
      ?? config.executionTriggerType
      ?? config.execution_trigger_type
      ?? defaults.executionType
    ),
    taskUpdateTriggerMode: normalizeTaskUpdateTriggerMode(
      config.taskUpdateTriggerMode
      ?? config.task_update_trigger_mode
      ?? config.updateTriggerMode
      ?? config.update_trigger_mode
      ?? defaults.taskUpdateTriggerMode
    ),
    scheduleEvery: Number(config.scheduleEvery || defaults.scheduleEvery),
    scheduleUnit: config.scheduleUnit === 'hour' ? 'hour' : 'minute',
    executionIntegrationId: String(
      config.executionIntegrationId
      || config.execution_integration_id
      || config.executionIntegration
      || config.execution_integration
      || defaults.executionIntegrationId
      || ''
    ),
    executionIntegrationName: String(
      config.executionIntegrationName
      || config.execution_integration_name
      || defaults.executionIntegrationName
      || ''
    ),
    executionResponseSchema: normalizeResponseSchema(
      config.executionResponseSchema
      || config.execution_response_schema
      || defaults.executionResponseSchema
    ),
    executionRecordPath: normalizeStartExecutionPathKey(
      config.executionRecordPath
      || config.execution_record_path
      || config.executionArrayPath
      || config.execution_array_path
      || defaults.executionRecordPath
      || ''
    ),
    executionTemplateVariables: normalizeResponseSchema(
      config.executionTemplateVariables
      || config.execution_template_variables
      || defaults.executionTemplateVariables
    ),
    executionVariableBindings: normalizeResponseSchema(
      config.executionVariableBindings
      || config.execution_variable_bindings
      || defaults.executionVariableBindings
    ),
    executionEveryMinutes: normalizeStartExecutionInterval(
      config.executionEveryMinutes
      ?? config.execution_interval_minutes
      ?? config.executionIntervalMinutes
      ?? defaults.executionEveryMinutes,
      defaults.executionEveryMinutes
    ),
    executionTaskIdKey: normalizeStartExecutionPathKey(
      config.executionTaskIdKey
      || config.execution_task_id_key
      || config.taskIdKey
      || config.task_id_key
      || defaults.executionTaskIdKey
      || ''
    ),
    taskArrayPath: normalizeStartExecutionPathKey(
      config.taskArrayPath
      || config.task_array_path
      || config.startTaskArrayPath
      || config.start_task_array_path
      || config.arrayPath
      || config.array_path
      || defaults.taskArrayPath
      || ''
    ),
    executionUpsertEnabled: normalizeBooleanFlag(
      config.executionUpsertEnabled
      ?? config.execution_upsert_enabled
      ?? config.executionUpsert
      ?? config.execution_upsert
      ?? defaults.executionUpsertEnabled
    ),
    executionUpsertKey: normalizeStartExecutionPathKey(
      config.executionUpsertKey
      || config.execution_upsert_key
      || config.upsertKey
      || config.upsert_key
      || defaults.executionUpsertKey
      || ''
    ),
    demandId: route?.demand_id ? String(route.demand_id) : String(config.demandId || ''),
    demandName: route?.demand_id ? routeDemandName(route) : String(config.demandName || '')
  };
};

const startSubtitleForConfig = (config = {}) => {
  const normalized = normalizeStartConfig(config);
  if (isSearchStartConfig(normalized)) {
    const integrationName = String(normalized.executionIntegrationName || normalized.executionIntegrationId || '').trim() || 'Integração não definida';
    return `${normalizeStartExecutionInterval(normalized.executionEveryMinutes, 5)} minuto(s) - ${integrationName}`;
  }
  if (isTaskUpdateStartConfig(normalized)) return 'a cada atualização da task';
  const unitLabel = normalized.scheduleUnit === 'hour' ? 'hora(s)' : 'minuto(s)';
  return `${normalized.scheduleEvery || 10} ${unitLabel}`;
};

const normalizeBooleanFlag = (value) => {
  if (value === true || value === false) return value;
  if (typeof value === 'number') return value !== 0;
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return false;
  return ['1', 'true', 't', 'yes', 'y', 'on'].includes(normalized);
};

const startNodeForRoute = (route = {}) => {
  const config = normalizeStartConfig({}, route);
  return {
    id: START_NODE_ID,
    type: 'command',
    position: { x: 60, y: 120 },
    data: {
      commandType: 'start',
      title: 'Início',
      subtitle: startSubtitleForConfig(config),
      config
    }
  };
};

const initialNodes = [
  startNodeForRoute(),
  {
    id: 'trigger-1',
    type: 'command',
    position: { x: 340, y: 118 },
    data: {
      commandType: 'api',
      title: 'Executar integração',
      subtitle: 'Selecione uma Integração',
      config: { ...commandCatalog.api.defaults, retryCount: 2 }
    }
  },
  {
    id: 'condition-1',
    type: 'command',
    position: { x: 650, y: 118 },
    data: {
      commandType: 'condition',
      title: 'Validar status',
      subtitle: '$.status == Pendente',
      config: { ...commandCatalog.condition.defaults }
    }
  },
  {
    id: 'delay-1',
    type: 'command',
    position: { x: 930, y: 276 },
    data: {
      commandType: 'delay',
      title: 'Aguardar retry',
      subtitle: '90s com jitter',
      config: { ...commandCatalog.delay.defaults }
    }
  }
];

const initialEdges = [
  { id: 'e-start-trigger', source: START_NODE_ID, target: 'trigger-1', type: 'smoothstep', animated: true, label: 'tasks' },
  { id: 'e-trigger-condition', source: 'trigger-1', target: 'condition-1', type: 'smoothstep', animated: true, label: 'payload' },
  { id: 'e-condition-delay', source: 'condition-1', target: 'delay-1', sourceHandle: 'output_2', type: 'smoothstep', animated: true, label: 'Insucesso' }
];

const cloneFlow = (items) => JSON.parse(JSON.stringify(items || []));

const normalizeStartNode = (node, route = {}) => {
  const base = startNodeForRoute(route);
  const config = normalizeStartConfig(node?.data?.config || {}, route);
  return {
    ...base,
    ...node,
    id: START_NODE_ID,
    data: {
      ...base.data,
      ...(node?.data || {}),
      commandType: 'start',
      title: 'Início',
      subtitle: startSubtitleForConfig(config),
      config
    }
  };
};

const normalizeFlowForRoute = (route = {}) => {
  if (!route?.id) {
    return { nodes: [], edges: [] };
  }

  const rawNodes = Array.isArray(route?.flow_nodes) && route.flow_nodes.length
    ? cloneFlow(route.flow_nodes)
    : cloneFlow(initialNodes);
  const rawEdges = Array.isArray(route?.flow_edges) && route.flow_edges.length
    ? cloneFlow(route.flow_edges)
    : cloneFlow(initialEdges);
  const existingStart = rawNodes.find(isStartNode);
  const existingStartId = existingStart?.id ? String(existingStart.id) : START_NODE_ID;
  const otherNodes = rawNodes.filter((node) => !isStartNode(node));
  const startNode = normalizeStartNode(existingStart, route);
  const nodes = [startNode, ...otherNodes];
  const nodeIds = new Set(nodes.map((node) => String(node.id)));
  const nodeById = new Map(nodes.map((node) => [String(node.id), node]));
  const edges = rawEdges
    .map((edge) => {
      const source = String(edge.source) === existingStartId ? START_NODE_ID : edge.source;
      const target = String(edge.target) === existingStartId ? START_NODE_ID : edge.target;
      const sourceNode = nodeById.get(String(source));
      const outputHandle = edgeOutputHandle(edge, sourceNode || {});
      if (sourceNode?.data?.commandType === 'condition') {
        const mode = normalizeConditionDecisionMode(sourceNode?.data?.config?.decisionMode);
        if (mode === 'multiple') {
          const allowedHandles = new Set(conditionOutputDefinitions(sourceNode).map((item) => item.handle));
          if (!allowedHandles.has(outputHandle)) {
            return null;
          }
        }
      }
      return {
        ...edge,
        source,
        target,
        sourceHandle: outputHandle,
        label: edgeLabelForOutput(sourceNode || {}, outputHandle, edge.label || '')
      };
    })
    .filter((edge) => (
      edge
      && edge.source
      && edge.target
      && nodeIds.has(String(edge.source))
      && nodeIds.has(String(edge.target))
      && String(edge.target) !== START_NODE_ID
    ));

  if (otherNodes.length && !edges.some((edge) => String(edge.source) === START_NODE_ID)) {
    const firstTarget = [...otherNodes].sort((left, right) => (
      (left.position?.x || 0) - (right.position?.x || 0)
    ))[0];
    edges.unshift({
      id: `e-${START_NODE_ID}-${firstTarget.id}`,
      source: START_NODE_ID,
      target: firstTarget.id,
      type: 'smoothstep',
      animated: true,
      label: 'tasks'
    });
  }

  return { nodes, edges };
};

const routeNodes = (route) => normalizeFlowForRoute(route).nodes;

const routeEdges = (route) => normalizeFlowForRoute(route).edges;

const api = async (url, options = {}) => {
  const method = String(options.method || 'GET').toUpperCase();
  const headers = {
    'content-type': 'application/json',
    ...(options.headers || {})
  };
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method) && window.TDEMAND_CSRF_TOKEN && !headers['X-TDemand-CSRF']) {
    headers['X-TDemand-CSRF'] = window.TDEMAND_CSRF_TOKEN;
  }
  const response = await fetch(url, {
    headers,
    ...options
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    if (shouldDispatchSessionExpired(response.status, url)) {
      dispatchSessionExpiredEvent(url, response.status);
    }
    throw new Error(data.message || 'Falha na requisicao');
  }
  return data;
};

const demandDisplayName = (demand = {}) => demand.name || demand.process || demand.protocol || `Demanda #${demand.id}`;

const routeDemandName = (route = {}) => route.demand_name || route.demand_process || route.demand_protocol || 'Sem demanda vinculada';

const routeDemandKey = (route = {}) => (route.demand_id == null ? 'unlinked' : String(route.demand_id));

const groupRoutesByDemand = (routes = [], demands = []) => {
  const demandById = new Map(demands.map((demand) => [String(demand.id), demand]));
  const groups = new Map();

  demands.forEach((demand) => {
    groups.set(String(demand.id), {
      id: String(demand.id),
      name: demandDisplayName(demand),
      subtitle: demand.protocol || `${(demand.task_schema && Object.keys(demand.task_schema).length) || 0} atributos`,
      routes: []
    });
  });

  routes.forEach((route) => {
    const key = routeDemandKey(route);
    if (!groups.has(key)) {
      const demand = demandById.get(key);
      groups.set(key, {
        id: key,
        name: demand ? demandDisplayName(demand) : routeDemandName(route),
        subtitle: demand?.protocol || (key === 'unlinked' ? 'Roteamentos legados sem demanda' : 'Demanda vinculada'),
        routes: []
      });
    }
    groups.get(key).routes.push(route);
  });

  return [...groups.values()].filter((group) => group.routes.length || group.id !== 'unlinked');
};

const makeNode = (type, position, index) => {
  const command = commandForType(type);
  return {
    id: `${type}-${Date.now()}-${index}`,
    type: 'command',
    position,
    data: {
      commandType: type,
      title: command.label,
      subtitle: command.description,
      config: { ...command.defaults }
    }
  };
};

const optionLabel = (options, value, fallback = value) => (
  options.find((option) => String(option.value) === String(value))?.label || fallback || ''
);

const isArrayLikeSchemaType = (type) => String(type || '')
  .toLowerCase()
  .split(/[^a-z0-9_]+/)
  .filter(Boolean)
  .includes('array');

const schemaTypeLabel = (type) => {
  const normalized = String(type || '').toLowerCase();
  if (isArrayLikeSchemaType(type)) return 'lista';
  if (['int', 'integer', 'number', 'float', 'double'].includes(normalized)) return 'numero';
  if (['bool', 'boolean'].includes(normalized)) return 'booleano';
  if (['object', 'json'].includes(normalized)) return 'objeto';
  return normalized || 'valor';
};

const schemaTypeTokens = (type) => String(type || '')
  .trim()
  .toLowerCase()
  .split(/[^a-z0-9_]+/)
  .filter(Boolean);

const isFileSchemaDefinition = (definition) => {
  if (typeof definition === 'string') return schemaTypeTokens(definition).includes('file');
  if (!definition || typeof definition !== 'object' || Array.isArray(definition)) return false;
  return schemaTypeTokens(definition.type).includes('file');
};

const demandTaskFileFields = (demand = {}) => Object.entries(demand?.task_schema || {})
  .filter(([, definition]) => isFileSchemaDefinition(definition))
  .map(([field]) => String(field || '').trim())
  .filter(Boolean);

const normalizeFileInputContractSchema = (schema = {}, demand = {}) => {
  const fileFields = demandTaskFileFields(demand);
  const normalizedSchema = mergeResponseSchemas(schema);
  if (!fileFields.length) return normalizedSchema;

  const nextSchema = { ...normalizedSchema };
  fileFields.forEach((field) => {
    const basePath = `task.payload.${field}`;
    Object.keys(nextSchema).forEach((path) => {
      if (path === basePath || path.startsWith(`${basePath}.`)) {
        delete nextSchema[path];
      }
    });
    nextSchema[basePath] = 'object';
    nextSchema[`${basePath}.name`] = 'string';
    nextSchema[`${basePath}.url`] = 'url';
    nextSchema[`${basePath}.base64`] = 'string';
    nextSchema[`${basePath}.binario`] = 'file';
  });

  return nextSchema;
};

const isTaskKeyCompatibleVariableType = (type) => {
  const normalized = String(type || '').trim().toLowerCase();
  if (!normalized) return false;
  if (isArrayLikeSchemaType(normalized)) return false;
  const tokens = normalized.split(/[^a-z0-9_]+/).filter(Boolean);
  if (tokens.some((token) => ['object', 'json', 'bool', 'boolean'].includes(token))) return false;
  if (tokens.some((token) => ['int', 'integer', 'number', 'float', 'double', 'decimal'].includes(token))) return true;
  if (tokens.some((token) => ['string', 'text', 'char', 'varchar', 'email', 'uuid', 'guid', 'date', 'datetime'].includes(token))) return true;
  return false;
};

const isIntegerSchemaType = (type) => {
  const tokens = String(type || '').trim().toLowerCase().split(/[^a-z0-9_]+/).filter(Boolean);
  return tokens.some((token) => ['int', 'integer', 'number', 'bigint', 'smallint'].includes(token));
};

const isDateLikeSchemaType = (type) => schemaTypeTokens(type)
  .some((token) => ['date', 'datetime', 'timestamp', 'timestamptz'].includes(token));

const SAVE_INFO_LITERAL_SOURCE = '__literal__';
const SAVE_INFO_SUM_SOURCE = '__sum__';
const SAVE_INFO_TASK_SOURCE = '__task__';
const SAVE_INFO_NEW_ATTRIBUTE = '__new_attribute__';
const mappingSourceType = (mapping = {}) => String(mapping.sourceType || mapping.source_type || '').trim().toLowerCase();
const mappingHasLiteralValue = (mapping = {}) => (
  Object.prototype.hasOwnProperty.call(mapping, 'literalValue')
  || Object.prototype.hasOwnProperty.call(mapping, 'literal_value')
);
const validMappingSumValue = (mapping = {}) => {
  const increment = mapping.sumValue ?? mapping.sum_value ?? 1;
  return Number.isInteger(Number(increment));
};
const isCompleteSaveInfoMapping = (mapping = {}) => {
  const sourceType = mappingSourceType(mapping);
  const saveAs = String(mapping.saveAs || mapping.targetKey || mapping.targetPath || '').trim();
  if (!saveAs) return false;
  if (sourceType === 'literal') return mappingHasLiteralValue(mapping);
  if (sourceType === 'sum') return Boolean(String(mapping.key || mapping.path || '').trim()) && validMappingSumValue(mapping);
  if (sourceType === 'task') return Boolean(String(mapping.key || mapping.path || '').trim());
  return Boolean(String(mapping.sourceNodeId || '').trim() && String(mapping.key || mapping.path || '').trim());
};

const unitText = (unit, amount = 1) => {
  const singular = unit === 'hour' || unit === 'hours' ? 'hora' : unit === 'milliseconds' ? 'milissegundo' : unit === 'seconds' ? 'segundo' : 'minuto';
  return `${singular}${Number(amount) === 1 ? '' : 's'}`;
};

const deriveCommandIdentity = (commandType, config = {}, context = {}) => {
  const demandName = context.demandName || config.demandName || 'demanda vinculada';
  if (commandType === 'start') {
    const normalizedStart = normalizeStartConfig(config);
    if (isSearchStartConfig(normalizedStart)) {
      const integrationLabel = String(normalizedStart.executionIntegrationName || normalizedStart.executionIntegrationId || '').trim() || 'integração não definida';
      const intervalMinutes = normalizeStartExecutionInterval(normalizedStart.executionEveryMinutes, 5);
      return {
        title: 'Início - Buscar',
        subtitle: `${demandName} - API ${integrationLabel}, a cada ${intervalMinutes} minuto(s)`
      };
    }
    const logic = normalizedStart.filterLogic === 'OR' ? 'qualquer regra' : 'todas as regras';
    const filterCount = Array.isArray(normalizedStart.filters) ? normalizedStart.filters.length : 1;
    if (isTaskUpdateStartConfig(normalizedStart)) {
      return {
        title: 'Início',
        subtitle: `${demandName} - ${filterCount} filtro(s), ${logic}, a cada atualização da task`
      };
    }
    const every = Number(normalizedStart.scheduleEvery || 10);
    const unit = unitText(normalizedStart.scheduleUnit || 'minute', every);
    return {
      title: 'Início',
      subtitle: `${demandName} - ${filterCount} filtro(s), ${logic}, a cada ${every} ${unit}`
    };
  }
  if (commandType === 'api') {
    const name = config.integrationName || 'integração';
    return {
      title: `API - ${name}`,
      subtitle: `${config.retryCount || 0} tentativa(s), timeout ${config.timeoutMs || 15000}ms, resposta ${config.responsePath || '$'}`
    };
  }
  if (commandType === 'chat') {
    const platform = String(config.platform || '').trim() || 'plataforma não definida';
    const template = String(config.templateName || config.templateId || '').trim() || 'template não definido';
    const paramsCount = Math.max(0, Number.parseInt(config.paramsCount || 0, 10) || 0);
    const contextCount = Array.isArray(config.infoCustomMappings)
      ? config.infoCustomMappings.length
      : Array.isArray(config.info_custom_mappings)
        ? config.info_custom_mappings.length
        : 0;
    return {
      title: `Chat - ${template}`,
      subtitle: `${platform}, ${paramsCount} parâmetro(s), contexto ${contextCount}, timeout ${config.timeoutMs || 15000}ms`
    };
  }
  if (commandType === 'voice') {
    const campaign = String(config.campaignName || config.campaignId || '').trim() || 'campanha não definida';
    const paramsCount = Math.max(0, Number.parseInt(config.paramsCount || 0, 10) || 0);
    return {
      title: `Voz - ${campaign}`,
      subtitle: `${paramsCount} parâmetro(s), timeout ${config.timeoutMs || 15000}ms`
    };
  }
  if (commandType === 'cortex') {
    const resource = String(config.resource || 'tagent').toLowerCase();
    const resourceLabel = optionLabel(cortexResourceOptions, resource, resource.toUpperCase());
    const mode = cortexRequestModeFromConfig(config);
    const timeout = Number(config.timeoutMs || cortexDefaultTimeoutMsByResource(resource));
    return {
      title: resource === 'tagent'
        ? 'Cortex - Agente IA'
        : `Cortex - ${String(resourceLabel || '').replace(/^Cortex\s*-\s*/i, '')}`,
      subtitle: `execucao ${mode}, timeout ${timeout}ms`
    };
  }
  if (commandType === 'condition') {
    const normalized = normalizeConditionConfig(config);
    const rules = Array.isArray(normalized.conditions) && normalized.conditions.length ? normalized.conditions : [];
    const logic = normalized.conditionLogic === 'OR' ? 'OR' : 'AND';
    const mode = normalizeConditionDecisionMode(normalized.decisionMode);
    if (mode === 'multiple') {
      const paths = Array.isArray(normalized.decisionPaths) ? normalized.decisionPaths : [];
      const rulesCount = paths.reduce((acc, path) => acc + (Array.isArray(path?.conditions) ? path.conditions.length : 0), 0);
      return {
        title: 'Decisões',
        subtitle: `${paths.length} decisão(ões), ${rulesCount} condição(ões), erro: ${normalizeConditionErrorPolicy(normalized.errorPolicy)}`
      };
    }
    return {
      title: 'Condição inteligente',
      subtitle: `${rules.length || 1} regra(s), combinacao ${logic}, erro: ${normalizeConditionErrorPolicy(config.errorPolicy)}`
    };
  }
  if (commandType === 'delay') {
    const amount = Number(config.duration || 0);
    const unit = unitText(config.unit || 'seconds', amount);
    const strategy = optionLabel(delayStrategyOptions, config.strategy, config.strategy || 'Fixa');
    return {
      title: 'Aguardar',
      subtitle: `${amount} ${unit}, estratégia ${strategy.toLowerCase()}`
    };
  }
  if (commandType === 'saveInfo') {
    const mappings = Array.isArray(config.mappings) ? config.mappings.filter(isCompleteSaveInfoMapping) : [];
    const status = config.nextStatus ? `, status -> ${config.nextStatus}` : '';
    return {
      title: 'Salvar na Task',
      subtitle: `${mappings.length || 0} campo(s) mapeado(s)${status}`
    };
  }
  if (commandType === 'insertDemand') {
    const target = config.targetDemandName || config.targetDemandId || 'demanda atual';
    const operation = String(config.operation || config.transferMode || '').trim().toLowerCase() === 'move'
      ? 'move'
      : 'copy';
    const payloadMode = String(config.payloadMode || config.payload_mode || '').trim();
    const mode = payloadMode === 'template'
      ? 'adiciona/atualiza por template'
      : payloadMode === 'merge_api'
        ? 'adiciona/atualiza pela API'
        : payloadMode === 'api_response'
          ? 'adiciona/atualiza pela API'
        : 'mantém atributos';
    return {
      title: operation === 'move' ? 'Mover para Demanda' : 'Inserir em Demanda',
      subtitle: `${operation === 'move' ? 'move' : 'copia'} para ${target} | ${mode}`
    };
  }
  const command = commandForType(commandType);
  return {
    title: command.label,
    subtitle: command.description
  };
};

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const renderCommandNodeHtml = (node, options = {}) => {
  const command = commandForType(node?.data?.commandType);
  const configCount = Object.keys(node?.data?.config || {}).length;
  const customName = String(node?.data?.customName || '').trim();
  const cortexIdentity = node?.data?.commandType === 'cortex'
    ? deriveCommandIdentity('cortex', node?.data?.config || {})
    : null;
  const displayTitle = customName || cortexIdentity?.title || node?.data?.title || command.label;
  const displaySubtitle = cortexIdentity?.subtitle || node?.data?.subtitle || command.description;
  const inspecting = options.inspectionMode === true;
  const inspected = options.inspectedNodeId && String(options.inspectedNodeId) === String(node?.id);
  const nodeState = inspecting
    ? (options.nodeStates?.[String(node?.id)] || 'waiting')
    : 'idle';
  const executed = ['success', 'error'].includes(nodeState) || options.executedNodeIds?.has?.(String(node?.id));
  const branchBlocked = nodeState === 'blocked';
  const playIcon = branchBlocked ? 'block' : executed ? 'replay' : 'play_arrow';
  const playText = branchBlocked ? 'Fora do ramo' : executed ? 'Reexecutar' : 'Play';

  return `
    <div class="flow-command-node tone-${escapeHtml(command.tone)} ${isStartNode(node) ? 'is-start-node' : ''} ${inspecting ? 'is-inspectable' : ''} ${inspected ? 'is-inspected' : ''} ${executed ? 'is-executed' : ''} inspection-state-${escapeHtml(nodeState)}" data-rf-id="${escapeHtml(node.id)}" data-command-type="${escapeHtml(node?.data?.commandType || 'api')}">
      <div class="command-node-head">
        <span class="material-symbols-rounded" aria-hidden="true">${escapeHtml(command.icon)}</span>
        <div>
          <strong>${escapeHtml(displayTitle)}</strong>
          <p>${escapeHtml(displaySubtitle)}</p>
        </div>
      </div>
      <div class="command-node-meta">
        <span>${escapeHtml(command.label)}</span>
        <span>${configCount} confs</span>
      </div>
      ${inspecting ? `
        <div class="command-node-inspection-footer">
          <button class="command-inspection-play" type="button" data-inspect-node-id="${escapeHtml(node.id)}" title="${branchBlocked ? 'A condição direcionou a execução para o outro ramo' : 'Executar este passo'}" ${branchBlocked ? 'disabled aria-disabled="true"' : ''}>
            <span class="material-symbols-rounded" aria-hidden="true">${playIcon}</span>
            ${playText}
          </button>
        </div>
      ` : ''}
    </div>
  `;
};

const conditionBooleanOutputs = () => ([
  { handle: 'output_1', outputClass: 'output_1', label: 'Sucesso' },
  { handle: 'output_2', outputClass: 'output_2', label: 'Insucesso' }
]);

const conditionOutputDefinitions = (node = {}) => {
  if (node.data?.commandType !== 'condition') return [];
  const config = normalizeConditionConfig(node.data?.config || {});
  if (normalizeConditionDecisionMode(config.decisionMode) !== 'multiple') {
    return conditionBooleanOutputs();
  }
  const paths = Array.isArray(config.decisionPaths) ? config.decisionPaths : [];
  return paths.map((path, index) => ({
    handle: `decision:${String(path.id || '').trim()}`,
    outputClass: `output_${index + 1}`,
    label: String(path.label || '').trim() || `Decisão ${index + 1}`,
    color: normalizeConditionDecisionColor(path.color, index)
  }));
};

const conditionOutputHandleByClass = (node = {}, outputClass = 'output_1') => {
  const outputs = conditionOutputDefinitions(node);
  const direct = outputs.find((item) => item.outputClass === outputClass);
  if (direct?.handle) return direct.handle;
  if (outputClass === 'output_2') return 'output_2';
  return 'output_1';
};

const conditionOutputClassByHandle = (node = {}, handle = 'output_1') => {
  const outputs = conditionOutputDefinitions(node);
  const normalizedHandle = String(handle || '').trim();
  const direct = outputs.find((item) => item.handle === normalizedHandle);
  if (direct?.outputClass) return direct.outputClass;
  if (/^output_\d+$/.test(normalizedHandle)) return normalizedHandle;
  if (normalizedHandle === 'decision:success') return 'output_1';
  if (normalizedHandle === 'decision:failure') return 'output_2';
  return 'output_1';
};

const conditionOutputLabelForHandle = (node = {}, handle = 'output_1') => {
  const outputs = conditionOutputDefinitions(node);
  const normalizedHandle = String(handle || '').trim();
  const direct = outputs.find((item) => item.handle === normalizedHandle);
  if (direct?.label) return direct.label;
  if (normalizedHandle === 'output_2') return 'Insucesso';
  return 'Sucesso';
};

const nodeOutputCount = (node = {}) => {
  if (node.data?.commandType !== 'condition') return 1;
  const outputs = conditionOutputDefinitions(node);
  return Math.max(1, outputs.length || 2);
};

const nodeClassName = (node = {}) => [
  'drawflow-command-node',
  `drawflow-command-${node.data?.commandType || 'api'}`,
  isStartNode(node) ? 'drawflow-command-start' : ''
].filter(Boolean).join(' ');

const inferConditionOutputHandle = (edge = {}, sourceNode = {}) => {
  const rawHandle = String(edge.sourceHandle || edge.outputClass || '').trim();
  if (rawHandle.startsWith('decision:')) return rawHandle;
  if (/^output_\d+$/.test(rawHandle)) {
    return conditionOutputHandleByClass(sourceNode, rawHandle);
  }
  const label = String(edge.label || '').trim().toLocaleLowerCase('pt-BR');
  const outputs = conditionOutputDefinitions(sourceNode);
  const fromLabel = outputs.find((item) => String(item.label || '').trim().toLocaleLowerCase('pt-BR') === label);
  if (fromLabel?.handle) return fromLabel.handle;
  if (['false', 'falso', 'insucesso', 'falha', 'erro'].some((token) => label.includes(token))) return 'output_2';
  return outputs[0]?.handle || 'output_1';
};

const edgeOutputHandle = (edge = {}, sourceNode = {}) => (
  sourceNode.data?.commandType === 'condition'
    ? inferConditionOutputHandle(edge, sourceNode)
    : edge.sourceHandle || edge.outputClass || 'output_1'
);

const edgeDrawflowOutputClass = (edge = {}, sourceNode = {}) => {
  if (sourceNode.data?.commandType !== 'condition') return 'output_1';
  const handle = edgeOutputHandle(edge, sourceNode);
  return conditionOutputClassByHandle(sourceNode, handle);
};

const edgeLabelForOutput = (sourceNode = {}, outputHandle = 'output_1', fallback = '') => (
  sourceNode.data?.commandType === 'condition'
    ? conditionOutputLabelForHandle(sourceNode, outputHandle)
    : fallback
);

const readOutputHandleClass = (element) => {
  if (!(element instanceof Element)) return 'output_1';
  const handleClass = [...element.classList].find((className) => /^output_\d+$/.test(className));
  return handleClass || 'output_1';
};

const makeEdge = (source, target, label = '', sourceHandle = 'output_1') => ({
  id: `e-${source}-${target}-${Date.now()}`,
  source,
  target,
  sourceHandle,
  type: 'smoothstep',
  animated: true,
  label
});

const syncConditionNodeOutputs = (editor, drawId, node = {}) => {
  const nodeElement = editor?.precanvas?.querySelector?.(`#node-${drawId}`);
  if (!nodeElement) return;
  const outputs = [...nodeElement.querySelectorAll('.outputs .output')];
  const contentNode = nodeElement.querySelector('.flow-command-node');
  if (node.data?.commandType !== 'condition') {
    outputs.forEach((output) => {
      output.style.removeProperty('top');
      output.style.removeProperty('--condition-color');
      delete output.dataset.conditionLabel;
      delete output.dataset.outputClass;
      delete output.dataset.conditionColor;
    });
    contentNode?.style?.removeProperty('min-height');
    return;
  }

  const definitions = conditionOutputDefinitions(node);
  const decisionCount = Math.max(definitions.length, 1);
  if (contentNode) {
    const extraDecisions = Math.max(0, decisionCount - 4);
    const minHeightPx = 104 + (extraDecisions * 24);
    if (minHeightPx > 0) {
      contentNode.style.minHeight = `${minHeightPx}px`;
    } else {
      contentNode.style.removeProperty('min-height');
    }
  }
  const total = Math.max(outputs.length, 1);
  outputs.forEach((output, index) => {
    const definition = definitions[index] || {
      label: `Decisão ${index + 1}`,
      outputClass: `output_${index + 1}`
    };
    let top = 50;
    if (total === 2) {
      top = index === 0 ? 36 : 64;
    } else if (total > 2) {
      const minTop = total <= 4 ? 20 : 14;
      const maxTop = total <= 4 ? 80 : 86;
      top = minTop + (((maxTop - minTop) / (total - 1)) * index);
    }
    output.style.top = `${Math.max(12, Math.min(88, top))}%`;
    output.dataset.conditionLabel = definition.label;
    output.dataset.outputClass = definition.outputClass;
    const colorValue = String(definition.color || '').trim();
    if (colorValue) {
      output.dataset.conditionColor = colorValue;
      output.style.setProperty('--condition-color', conditionDecisionColorHex(colorValue, index));
    } else {
      delete output.dataset.conditionColor;
      output.style.removeProperty('--condition-color');
    }
  });
};

const reconcileConditionEdgesForNode = ({ nodeId, nodeData, edges = [] }) => {
  const sourceNode = { id: nodeId, data: nodeData || {} };
  if (sourceNode.data?.commandType !== 'condition') {
    return { edges: cloneFlow(edges), removedCount: 0 };
  }

  const mode = normalizeConditionDecisionMode(sourceNode.data?.config?.decisionMode);
  const definitions = conditionOutputDefinitions(sourceNode);
  const allowedHandles = new Set(definitions.map((item) => item.handle));
  let removedCount = 0;
  const nextEdges = (Array.isArray(edges) ? edges : []).reduce((acc, edge) => {
    if (String(edge?.source) !== String(nodeId)) {
      acc.push(edge);
      return acc;
    }

    const handle = edgeOutputHandle(edge, sourceNode);
    if (mode === 'multiple' && !allowedHandles.has(handle)) {
      removedCount += 1;
      return acc;
    }

    acc.push({
      ...edge,
      sourceHandle: handle,
      label: edgeLabelForOutput(sourceNode, handle, edge.label || '')
    });
    return acc;
  }, []);

  return { edges: nextEdges, removedCount };
};

const routingFieldHelp = {
  'Nome do comando (opcional)': {
    description: 'Substitui somente o nome visível deste bloco no canvas. A identidade operacional e o tipo do comando continuam sendo calculados pelo sistema.',
    usage: 'Use um nome curto que explique a responsabilidade do passo, como “Consultar cadastro” ou “Aguardar retentativa”. Deixe vazio para usar o nome automático.'
  },
  Demanda: {
    description: 'É a Demanda vinculada ao roteamento. Ela determina quais atributos, status e Tasks estarão disponíveis para os comandos do fluxo.',
    usage: 'Este valor vem do roteamento selecionado e não pode ser alterado dentro do comando Início. Para usar outra Demanda, crie ou duplique o roteamento nela.'
  },
  'Tipo de início': {
    description: 'Define de onde vêm os itens processados pelo roteamento. Execução trabalha com Tasks da Demanda; Buscar consulta uma Integração e transforma cada registro retornado em contexto temporário.',
    usage: 'Escolha Execução para processar Tasks já cadastradas. Escolha Buscar para iniciar o fluxo a partir de registros obtidos em uma API.'
  },
  'Integração(API)': {
    description: 'Seleciona a Integração que será consultada pelo Início - Buscar. Cada registro encontrado poderá percorrer o restante do fluxo.',
    usage: 'Prepare e teste a API em Integrações. Uma tentativa com falha não bloqueia seu uso; a política do roteamento tratará novas falhas.'
  },
  'Lista do retorno': {
    description: 'Indica qual lista dentro do JSON retornado representa os registros que devem ser processados individualmente.',
    usage: 'Selecione, por exemplo, “data.items” quando a API responder com um objeto contendo essa lista. Use o retorno inteiro quando a própria resposta já for um array.'
  },
  'Executar a cada': {
    description: 'Controla o intervalo entre consultas da Integração usada no Início - Buscar.',
    usage: 'Escolha um intervalo compatível com a atualização da fonte e com os limites da API. Intervalos menores consultam a origem com maior frequência.'
  },
  'Tipo de execução': {
    description: 'Define quando o roteamento é executado. Execução periódica procura Tasks elegíveis em um intervalo fixo; Execução por atualização da task reage a cada criação/atualização de Task da Demanda.',
    usage: 'Use Execução periódica para varreduras regulares. Use Execução por atualização da task quando o fluxo precisa responder imediatamente à mudança de uma Task; nesse modo não existe intervalo nem varredura periódica.'
  },
  'Regra do gatilho': {
    description: 'Controla como uma atualização de Task dispara o roteamento quando o Tipo de execução é Execução por atualização da task.',
    usage: 'Sempre que a task for criada ou atualizada, verifica se ela atende à busca configurada neste roteamento e, caso atenda, executa o fluxo.\n\nEscolha a regra conforme o comportamento esperado:',
    options: [
      {
        title: 'Novo valor igual ao filtro',
        icon: 'filter_alt',
        description: 'Executa quando antes a Task não atendia ao filtro e depois passou a atender. Exemplo: Estado muda de Prospecção para Contratado com filtro Estado = Contratado.'
      },
      {
        title: 'Qualquer alteração com o filtro',
        icon: 'change_circle',
        description: 'Executa em qualquer alteração relevante no payload ou status, desde que a Task continue atendendo ao filtro. Exemplo: e-mail muda e o status permanece Pendente.'
      }
    ]
  },
  Periodicidade: {
    description: 'Define a frequência com que o worker procura novas Tasks elegíveis para este roteamento.',
    usage: 'Combine número e unidade. Por exemplo, 10 minutos executa a seleção de Tasks a cada dez minutos.'
  },
  Filtro: {
    description: 'Determina quais Tasks da Demanda podem entrar neste roteamento usando uma ou várias regras sobre status, atributos e datas.',
    usage: 'Adicione regras específicas e escolha AND para exigir todas ou OR para aceitar qualquer uma. Filtros mais seletivos evitam processamento desnecessário.'
  },
  'Operador entre regras': {
    description: 'Controla como as regras do filtro inicial são combinadas antes de uma Task ser carregada pelo worker.',
    usage: 'AND exige que todas as regras sejam verdadeiras. OR aceita a Task quando pelo menos uma regra for verdadeira.'
  },
  Integração: {
    description: 'Referencia uma request já criada na área de Integrações. URL, autenticação, headers e body permanecem centralizados nessa Integração.',
    usage: 'Selecione a API pelo nome. Integrações da pasta Geral ou da Demanda continuam disponíveis mesmo quando a última tentativa falhou.'
  },
  'Timeout (ms)': {
    description: 'Tempo máximo, em milissegundos, que o worker aguarda a operação externa responder antes de considerá-la sem resposta.',
    usage: 'Use 15000 para 15 segundos. Ajuste conforme a latência esperada, evitando valores muito altos que prendam workers por longos períodos.'
  },
  Tentativas: {
    description: 'Quantidade máxima de execuções do mesmo request quando ocorrer uma falha recuperável, como timeout ou indisponibilidade temporária.',
    usage: 'Use poucas tentativas e combine com um intervalo progressivo. Erros funcionais ou respostas inválidas podem seguir diretamente a política de erro.'
  },
  'Intervalo entre tentativas': {
    description: 'Determina como aumenta o tempo de espera antes de repetir uma chamada que falhou.',
    usage: 'Exponencial é indicada para serviços instáveis ou com rate limit. Linear aumenta em passos constantes; sem intervalo repete imediatamente.'
  },
  'Chave única': {
    description: 'Valor de idempotência enviado para identificar a mesma operação e reduzir duplicidades em retentativas.',
    usage: 'Use uma variável estável do contexto. A API de destino precisa reconhecer a chave para garantir idempotência.',
    example: '{{task.task_key}}'
  },
  'Caminho da resposta': {
    description: 'Seleciona a parte útil do JSON de resposta que será exposta aos comandos seguintes.',
    usage: 'Informe um caminho JSON. Deixe a raiz quando os campos necessários já estiverem no primeiro nível.',
    example: '$.data.items'
  },
  'Política de erro': {
    description: 'Define o comportamento do fluxo quando este comando não consegue concluir sua operação.',
    usage: 'Escolha falhar para interromper, ignorar para seguir sem saída, fila de falha para tratamento operacional ou a alternativa de contingência quando disponível.'
  },
  'Combinação das regras': {
    description: 'Define como as condições deste comando produzem o ramo de sucesso ou de insucesso.',
    usage: 'Use AND quando todas as condições precisam ser verdadeiras. Use OR quando uma única condição verdadeira já deve seguir pelo ramo de sucesso.'
  },
  'Variáveis da request': {
    description: 'Relaciona cada placeholder {{VAR}} da Integração a um valor disponível no contexto do roteamento.',
    usage: 'Para cada variável, escolha a origem e o atributo correspondente. O worker resolve os valores no momento da chamada sem alterar a Integração original.'
  },
  Recurso: {
    description: 'Seleciona a capacidade Cortex que será executada, como agente de IA, base de conhecimento, voz, OCR ou transcrição.',
    usage: 'Escolha o recurso de acordo com o formato de entrada e saída esperado. Os demais campos do modal se adaptam à opção selecionada.'
  },
  'Recurso listado': {
    description: 'Identifica a configuração específica disponível no recurso Cortex, como agente, base ou voz cadastrada.',
    usage: 'Selecione o item que deve receber a execução. A lista é carregada automaticamente usando as credenciais do tenant.'
  },
  'Follow-up ID': {
    description: 'É o identificador estável usado pelo Cortex para correlacionar mensagens e manter continuidade entre chamadas relacionadas.',
    usage: 'Escolha um atributo único da Task, normalmente task_key ou um protocolo externo. Evite valores que mudem durante o fluxo.'
  },
  'Arquivo da Task': {
    description: 'Seleciona o atributo que contém o arquivo ou a URL utilizada pelo recurso Cortex.',
    usage: 'Escolha um campo do tipo File para OCR ou transcrição. Para recursos em que o arquivo é opcional, deixe vazio quando não houver mídia.'
  },
  Duração: {
    description: 'É o valor base do tempo de espera aplicado antes de liberar a execução para o próximo comando.',
    usage: 'Informe um número positivo e combine com a unidade. O tempo efetivo também pode considerar a estratégia e a variação configuradas.'
  },
  Unidade: {
    description: 'Define a escala usada para interpretar o valor numérico deste campo de tempo.',
    usage: 'Selecione segundos, minutos, horas ou dias conforme o contexto. Confira o resumo operacional antes de salvar.'
  },
  Estratégia: {
    description: 'Controla como o tempo de espera é calculado. Fixa usa uma duração base; exponencial amplia a espera em novas tentativas.',
    usage: 'Use fixa para pausas previsíveis e exponencial para aliviar serviços externos durante falhas repetidas.'
  },
  'Variação aleatória (%)': {
    description: 'Adiciona uma pequena variação aleatória ao delay fixo para evitar que muitas Tasks sejam retomadas exatamente ao mesmo tempo.',
    usage: 'Um valor como 15 distribui as retomadas em torno da duração configurada. Use 0 para uma espera totalmente determinística.'
  },
  'Espera máxima (s)': {
    description: 'Limita, em segundos, o crescimento do delay exponencial.',
    usage: 'Defina o maior tempo aceitável entre tentativas. Mesmo que o cálculo exponencial ultrapasse esse valor, o worker respeitará o teto.'
  },
  Operação: {
    description: 'Define se a Task será copiada ou movida para outra Demanda.',
    usage: 'Copiar preserva a Task original. Mover remove a origem somente depois que a gravação no destino for concluída com sucesso.'
  },
  'Demanda de destino': {
    description: 'É a Demanda que receberá a nova Task e cujo schema de atributos e status será aplicado.',
    usage: 'Escolha um destino compatível com o payload produzido. Para mover, o destino precisa ser diferente da Demanda atual.'
  },
  'Task key': {
    description: 'Define o identificador funcional da Task criada no destino. Ele também é usado para localizar registros existentes durante um upsert.',
    usage: 'Reaproveite uma chave estável do contexto ou escolha um template personalizado quando precisar compor o identificador.'
  },
  'Task key personalizado': {
    description: 'Monta a chave da Task com texto e variáveis do contexto de execução.',
    usage: 'Combine texto fixo e variáveis. Garanta que o resultado seja estável para evitar duplicidades.',
    example: '{{task.task_key}}-financeiro'
  },
  'Modo de payload': {
    wide: true,
    description: 'Define de onde vêm os atributos que serão adicionados ou atualizados na Task de destino. O T-Demand sempre preserva todas as chaves que já existem na Task atual e no registro de destino.',
    usage: 'Escolha a origem do enriquecimento. Chaves configuradas podem receber novos valores; chaves não mencionadas nunca são removidas. Objetos são mesclados profundamente e arrays enviados atualizam apenas o valor daquele atributo.',
    options: [
      {
        title: 'Manter atributos da Task atual',
        icon: 'content_copy',
        description: 'Mantém todos os atributos disponíveis na Task que está percorrendo o fluxo, sem aplicar novos campos.'
      },
      {
        title: 'Adicionar ou atualizar pela API',
        icon: 'merge',
        description: 'Acrescenta as chaves retornadas pela API. Quando uma chave já existe, atualiza seu valor; todo o restante da Task permanece intacto.'
      },
      {
        title: 'Adicionar ou atualizar por template',
        icon: 'data_object',
        description: 'Aplica somente os atributos mapeados no builder, usando Task, APIs antecessoras, valores fixos ou soma. Campos fora do template são preservados.'
      }
    ],
    sections: [
      {
        title: 'Quando a task_key já existe',
        description: 'Com upsert ativo, o T-Demand faz uma mesclagem profunda com o payload salvo. Campos enviados atualizam o valor existente; campos não enviados permanecem; objetos aninhados são mesclados; arrays enviados substituem o array anterior. Com upsert desativado, uma task_key repetida gera erro e nada é alterado.'
      },
      {
        title: 'Proteção contra perda de dados',
        description: 'Todos os modos seguem a mesma política: adicionar chaves novas ou atualizar valores existentes. Nenhum comando de roteamento remove atributos ausentes do seu mapeamento.'
      }
    ],
    example: 'Destino atual: { nome: "Ana", status_externo: "antigo", tags: ["A"] }\nPayload enviado: { status_externo: "novo", tags: ["B"] }\nResultado do upsert: { nome: "Ana", status_externo: "novo", tags: ["B"] }'
  },
  'API de origem': {
    description: 'Seleciona qual comando API antecessor fornece o retorno usado para montar o payload da nova Task.',
    usage: 'Escolha um passo conectado e executado antes deste comando. O retorno aprendido da Integração orienta os campos disponíveis.'
  },
  'Nome do atributo': {
    description: 'Define em qual atributo o valor simples retornado pela API será armazenado quando a resposta não for um objeto JSON.',
    usage: 'Informe um nome válido, como score ou protocolo_externo. Para objetos, os campos podem ser gravados diretamente.'
  },
  'Payload template': {
    description: 'Monta os atributos que serão gravados na Task de destino usando dados da Task, da Demanda ou de comandos antecessores.',
    usage: 'Adicione uma linha por atributo, escolha a fonte e selecione a key disponível. O valor mantém seu tipo original; todos os atributos não mapeados continuam na Task.',
    example: 'cliente_id | API - Buscar cliente | data.id'
  },
  'Status da task criada': {
    description: 'Define o status recebido pela Task no destino imediatamente após a criação ou atualização.',
    usage: 'Escolha um status da Demanda de destino ou mantenha o status inicial configurado nela.'
  },
  'Alterar status da Task': {
    description: 'Opcionalmente atualiza o status da Task depois que todas as informações configuradas neste comando forem salvas.',
    usage: 'Escolha um status da Demanda para refletir o avanço operacional. Mantenha “Não alterar status” quando este passo deve apenas enriquecer dados.'
  },
  'Upsert por task_key': {
    description: 'Controla o que acontece quando já existe uma Task com a mesma task_key na Demanda de destino.',
    usage: 'Ative para atualizar o registro existente sem duplicar. Desative quando uma chave repetida deve ser tratada como erro.'
  },
  Quantidade: {
    description: 'Número de unidades que será somado ou subtraído da data no momento da execução.',
    usage: 'Use 0 para a data atual ou um número positivo combinado com Unidade e Direção.'
  },
  Direção: {
    description: 'Indica se o deslocamento de data será aplicado antes ou depois do momento atual.',
    usage: 'Use passado para consultar janelas anteriores e futuro para prazos, vencimentos ou agendamentos.'
  },
  'Nome do roteamento': {
    description: 'É o nome utilizado para localizar e selecionar este fluxo dentro da pasta da Demanda.',
    usage: 'Escolha um nome orientado ao objetivo, como “Triagem de contratos” ou “Enriquecimento cadastral”.'
  }
};

const normalizeRoutingHelpKey = (value) => String(value || '')
  .replace(/\s*\*\s*$/, '')
  .trim();

const resolveRoutingFieldHelp = (label, helpKey = '') => {
  const normalizedKey = normalizeRoutingHelpKey(helpKey || label);
  if (routingFieldHelp[normalizedKey]) {
    return {
      title: normalizedKey,
      ...routingFieldHelp[normalizedKey]
    };
  }
  if (/^API (com retorno|para usar)/i.test(normalizedKey)) {
    return { title: normalizedKey, ...routingFieldHelp['API de origem'] };
  }
  return {
    title: normalizedKey || 'Ajuda do campo',
    description: `Este campo configura “${normalizedKey || 'esta opção'}” para a execução do comando.`,
    usage: 'Preencha de acordo com o dado que este passo deve receber ou produzir. O valor será aplicado quando o worker executar este ponto do fluxo.'
  };
};

const FieldHelp = ({ label, helpKey = '', help = null }) => {
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const popoverId = `routing-field-help-${useId().replace(/:/g, '')}`;
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ left: 12, top: 12, above: false, width: 320, maxHeight: 432 });
  const content = help && typeof help === 'object'
    ? { title: normalizeRoutingHelpKey(label), ...help }
    : resolveRoutingFieldHelp(label, helpKey);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger || typeof window === 'undefined') return;
    const rect = trigger.getBoundingClientRect();
    const viewportPadding = 12;
    const preferredWidth = content.wide === true ? 540 : 360;
    const width = Math.min(preferredWidth, Math.max(260, window.innerWidth - (viewportPadding * 2)));
    const left = Math.min(
      Math.max(viewportPadding, rect.left + (rect.width / 2) - (width / 2)),
      window.innerWidth - width - viewportPadding
    );
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
    const spaceAbove = rect.top - viewportPadding;
    const above = spaceBelow < 240 && spaceAbove > spaceBelow;
    setPosition({
      left,
      top: above ? rect.top - 10 : rect.bottom + 10,
      above,
      width,
      maxHeight: Math.max(180, Math.min(content.wide === true ? 560 : 432, above ? spaceAbove : spaceBelow))
    });
  }, [content.wide]);

  useEffect(() => {
    if (!open) return undefined;
    updatePosition();
    const closeOnOutside = (event) => {
      if (triggerRef.current?.contains(event.target) || popoverRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', closeOnOutside);
    document.addEventListener('keydown', closeOnEscape);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutside);
      document.removeEventListener('keydown', closeOnEscape);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, updatePosition]);

  return (
    <span className="routing-field-help-wrap">
      <button
        ref={triggerRef}
        className={`routing-field-help ${open ? 'is-open' : ''}`}
        type="button"
        aria-label={`Ajuda sobre ${normalizeRoutingHelpKey(label)}`}
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen((current) => !current);
        }}
      >
        ?
      </button>
      {open && typeof document !== 'undefined' && createPortal(
        <aside
          ref={popoverRef}
          id={popoverId}
          className={`routing-field-help-popover ${position.above ? 'is-above' : ''}`}
          style={{ left: position.left, top: position.top, width: position.width, maxHeight: position.maxHeight }}
          role="dialog"
          aria-label={`Ajuda: ${content.title}`}
          onClick={(event) => event.stopPropagation()}
        >
          <header>
            <span className="material-symbols-rounded" aria-hidden="true">help</span>
            <div>
              <small>Ajuda do campo</small>
              <strong>{content.title}</strong>
            </div>
            <button type="button" onClick={() => setOpen(false)} aria-label="Fechar ajuda">
              <span className="material-symbols-rounded" aria-hidden="true">close</span>
            </button>
          </header>
          <div className="routing-field-help-content">
            <section>
              <span>Como funciona</span>
              <p>{content.description}</p>
            </section>
            <section>
              <span>Como usar</span>
              <p>{content.usage}</p>
            </section>
            {Array.isArray(content.options) && content.options.length > 0 && (
              <section>
                <span>Opções disponíveis</span>
                <div className="routing-field-help-options">
                  {content.options.map((option) => (
                    <article key={option.title}>
                      <span className="material-symbols-rounded" aria-hidden="true">{option.icon || 'check_circle'}</span>
                      <div>
                        <strong>{option.title}</strong>
                        <p>{option.description}</p>
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            )}
            {Array.isArray(content.sections) && content.sections.map((section) => (
              <section className="routing-field-help-detail" key={section.title}>
                <span>{section.title}</span>
                <p>{section.description}</p>
              </section>
            ))}
            {content.example && (
              <section className="routing-field-help-example">
                <span>Exemplo</span>
                <code>{content.example}</code>
              </section>
            )}
          </div>
        </aside>,
        document.body
      )}
    </span>
  );
};

const FieldLabel = ({ children, helpKey = '', help = null }) => (
  <span className="modal-field-label">
    <span className="modal-field-label-text">{children}</span>
    <FieldHelp label={children} helpKey={helpKey} help={help} />
  </span>
);

const Field = ({ label, helpKey = '', help = null, value, onChange, type = 'text', textarea = false, selectOptions, disabled = false, multiple = false }) => (
  <label className={textarea ? 'modal-field modal-field-wide' : 'modal-field'}>
    <FieldLabel helpKey={helpKey} help={help}>{label}</FieldLabel>
    {textarea ? (
      <textarea value={value ?? ''} onChange={(event) => onChange(event.target.value)} rows={7} disabled={disabled} />
    ) : selectOptions ? (
      <select
        value={multiple ? (Array.isArray(value) ? value : []) : (value ?? '')}
        onChange={(event) => {
          if (multiple) {
            const selectedValues = Array.from(event.target.selectedOptions || []).map((option) => option.value);
            onChange(selectedValues);
            return;
          }
          onChange(event.target.value);
        }}
        disabled={disabled}
        multiple={multiple}
      >
        {selectOptions.map((option) => {
          const normalized = typeof option === 'string' ? { value: option, label: option } : option;
          return (
            <option key={normalized.value} value={normalized.value} disabled={Boolean(normalized.disabled)}>
              {normalized.label}
            </option>
          );
        })}
      </select>
    ) : (
      <input
        type={type}
        value={value ?? ''}
        onChange={(event) => onChange(type === 'number' ? Number(event.target.value) : event.target.value)}
        disabled={disabled}
      />
    )}
  </label>
);

const integrationLastAttemptFailed = (integration = {}) => (
  String(integration.status || '').trim().toLowerCase() === 'falha'
  || Boolean(integration.last_error)
  || (integration.last_tested_at && (
    !Number.isFinite(Number(integration.last_status))
    || Number(integration.last_status) < 200
    || Number(integration.last_status) >= 400
  ))
);

const testedApiOptions = (apis) => [
  { value: '', label: apis.length ? 'Selecione uma integração' : 'Nenhuma integração testada disponível' },
  ...apis.map((item) => ({
    value: String(item.id),
    label: integrationLastAttemptFailed(item)
      ? `${item.name} - falha no último teste (disponível)`
      : item.name
  }))
];

const isGeneralApiFolder = (api = {}) => api.demand_id == null || String(api.demand_id).trim() === '';

const apiMatchesRouteDemand = (api = {}, demandId = '') => (
  isGeneralApiFolder(api)
  || (String(demandId || '').trim() !== '' && String(api.demand_id) === String(demandId))
);

const testedApisForDemand = (apis = [], demandId = '') => apis.filter((apiItem) => (
  apiMatchesRouteDemand(apiItem, demandId)
));

const retryBackoffOptions = [
  { value: 'none', label: 'Sem intervalo progressivo' },
  { value: 'linear', label: 'Linear' },
  { value: 'exponential', label: 'Exponencial' }
];

const cortexResourceOptions = [
  { value: 'tagent', label: 'Cortex - Agente IA' },
  { value: 'trag', label: 'Bases de Conhecimento' },
  { value: 'tts', label: 'Texto para Voz' },
  { value: 'ocr', label: 'OCR' },
  { value: 'transcription', label: 'Transcrição' }
];

const cortexResourceDescription = {
  tagent: 'Agente conversacional para atendimento, com ferramentas e contexto de conversa.',
  trag: 'Busca semântica em documentos para respostas com grounding.',
  tts: 'Converte texto em áudio com seleção de voz.',
  ocr: 'Extrai texto estruturado de imagens e documentos via arquivo.',
  transcription: 'Converte áudio em texto para análise e automação.'
};

const cortexResourceSupportsList = (resource) => !['ocr', 'transcription'].includes(String(resource || '').toLowerCase());
const cortexResourceRequiresFile = (resource) => ['ocr', 'transcription'].includes(String(resource || '').toLowerCase());
const cortexResourceSupportsOptionalFile = (resource) => String(resource || '').toLowerCase() === 'tagent';
const CHAT_MEDIA_TEMPLATE_TYPES = new Set(['image', 'video', 'document', 'file']);

const isChatMediaTemplateType = (value) => CHAT_MEDIA_TEMPLATE_TYPES.has(String(value || '').trim().toLowerCase());

const cortexPayloadFieldCatalog = {
  tagent: [
    { key: 'message', label: 'Mensagem', required: true },
    { key: 'options', label: 'Opções', required: false },
    { key: 'variables', label: 'Variáveis', required: false }
  ],
  trag: [
    { key: 'query', label: 'Pergunta', required: true }
  ],
  tts: [
    { key: 'text', label: 'Texto', required: true }
  ],
  ocr: [
    { key: 'options', label: 'Opções OCR', required: false }
  ],
  transcription: [
    { key: 'language', label: 'Idioma', required: false }
  ]
};

const cortexPayloadFieldsForResource = (resource) => {
  const normalized = String(resource || '').toLowerCase();
  return cortexPayloadFieldCatalog[normalized] || [];
};

const cortexResourceExecuteModes = (resource) => {
  const normalized = String(resource || '').toLowerCase();
  if (normalized === 'tagent') return ['json', 'multipart'];
  if (['ocr', 'transcription'].includes(normalized)) return ['multipart'];
  return ['json'];
};

const CORTEX_DEFAULT_TIMEOUT_MS = 20000;
const CORTEX_OCR_DEFAULT_TIMEOUT_MS = 5 * 60 * 1000;

const cortexDefaultTimeoutMsByResource = (resource = '') => {
  const normalized = String(resource || '').toLowerCase();
  if (normalized === 'ocr') return CORTEX_OCR_DEFAULT_TIMEOUT_MS;
  return CORTEX_DEFAULT_TIMEOUT_MS;
};

const cortexRequestModeFromConfig = (config = {}) => {
  const resource = String(config.resource || 'tagent').toLowerCase();
  if (cortexResourceRequiresFile(resource)) return 'multipart';
  if (resource === 'tagent' && String(config.fileAttribute || config.file_attribute || '').trim()) {
    return 'multipart';
  }
  return 'json';
};

const normalizeTaskAttributePathFromTemplate = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const taskTemplateMatch = raw.match(/^\{\{\s*task\.([a-zA-Z0-9_.-]+)\s*\}\}$/i);
  if (taskTemplateMatch?.[1]) return taskTemplateMatch[1];
  const demandTemplateMatch = raw.match(/^\{\{\s*demand\.([a-zA-Z0-9_.-]+)\s*\}\}$/i);
  if (demandTemplateMatch?.[1]) return `demand.${demandTemplateMatch[1]}`;
  return raw;
};

const normalizeTaskSelectorBindingPath = (value, fallbackPath = '') => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const candidate = value.path
      ?? value.selector
      ?? value.attribute
      ?? value.value
      ?? value.key
      ?? fallbackPath;
    return normalizeTaskAttributePathFromTemplate(candidate);
  }
  return normalizeTaskAttributePathFromTemplate(value || fallbackPath);
};

const normalizeTaskSelectorBinding = (value, fallbackPath = '') => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const arrayMatchByPath = normalizeConditionArrayMatches(value.arrayMatchByPath || value.array_match_by_path || {});
    return {
      path: normalizeTaskSelectorBindingPath(value, fallbackPath),
      arrayMatchByPath,
      arrayNavigationByPath: normalizeConditionArrayNavigations(
        value.arrayNavigationByPath || value.array_navigation_by_path || {},
        arrayMatchByPath
      )
    };
  }

  return {
    path: normalizeTaskSelectorBindingPath(value, fallbackPath),
    arrayMatchByPath: {},
    arrayNavigationByPath: {}
  };
};

const taskSelectorBindingHasValue = (value) => {
  if (Array.isArray(value)) {
    return value.some((item) => String(normalizeTaskSelectorBindingPath(item)).trim());
  }
  return String(normalizeTaskSelectorBindingPath(value)).trim() !== '';
};

const parseLegacyCortexPayloadBindings = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return Object.entries(parsed).reduce((acc, [key, item]) => {
      const path = normalizeTaskAttributePathFromTemplate(item);
      if (path) acc[key] = path;
      return acc;
    }, {});
  } catch {
    return {};
  }
};

const taskAttributeSelectOptions = (
  demand = {},
  {
    includeTaskMeta = true,
    includeDemandMeta = true,
    extraVariables = [],
    fileOnly = false,
    placeholder = 'Selecione o atributo da Task ou da Demanda'
  } = {}
) => {
  const payloadOptions = taskVariableOptions(demand, { extraVariables })
    .filter((item) => {
      if (!fileOnly) return true;
      const type = String(item.type || '').toLowerCase();
      return type === 'file' || type === 'url';
    })
    .map((item) => ({
      value: `payload.${item.value}`,
      label: `Payload: ${item.label}`
    }));

  const baseOptions = includeTaskMeta
    ? [
      { value: 'task_key', label: 'Task: task_key' },
      { value: 'id', label: 'Task: id' },
      { value: 'status', label: 'Task: status' },
      { value: 'comments', label: 'Task: comentários' },
      { value: 'last_comment', label: 'Task: último comentário' }
    ]
    : [];

  const demandOptions = includeDemandMeta
    ? [
      { value: 'demand.id', label: 'Demanda: id' },
      { value: 'demand.name', label: 'Demanda: nome' },
      { value: 'demand.protocol', label: 'Demanda: protocolo' },
      { value: 'demand.process', label: 'Demanda: processo' }
    ]
    : [];

  const unique = new Map();
  [...baseOptions, ...demandOptions, ...payloadOptions].forEach((option) => {
    if (!option.value || unique.has(option.value)) return;
    unique.set(option.value, option);
  });

  return [
    { value: '', label: placeholder },
    ...unique.values()
  ];
};

const normalizeCortexPayloadBindings = (resource, config = {}) => {
  const fields = cortexPayloadFieldsForResource(resource);
  const rawBindings = config.payloadBindings && typeof config.payloadBindings === 'object' && !Array.isArray(config.payloadBindings)
    ? config.payloadBindings
    : {};
  const legacyBindings = parseLegacyCortexPayloadBindings(config.payloadJson || config.payload_json || '');
  return fields.reduce((acc, field) => {
    const rawValue = rawBindings[field.key] ?? rawBindings[field.value] ?? legacyBindings[field.key] ?? '';
    if (field.key === 'variables') {
      const source = Array.isArray(rawValue)
        ? rawValue
        : typeof rawValue === 'string' && rawValue.trim().startsWith('[')
          ? (() => {
              try {
                const parsed = JSON.parse(rawValue);
                return Array.isArray(parsed) ? parsed : [rawValue];
              } catch {
                return [rawValue];
              }
            })()
          : [rawValue];
      acc[field.key] = source
        .map((item) => normalizeTaskSelectorBinding(item))
        .filter((item) => item.path);
      return acc;
    }
    acc[field.key] = normalizeTaskSelectorBinding(rawValue);
    return acc;
  }, {});
};

const CORTEX_TEMPLATE_TEXT_FIELDS = new Set(['message', 'query', 'text']);

const normalizeCortexPayloadTemplates = (resource, config = {}) => {
  const fields = cortexPayloadFieldsForResource(resource);
  const rawTemplates = config.payloadTemplates && typeof config.payloadTemplates === 'object' && !Array.isArray(config.payloadTemplates)
    ? config.payloadTemplates
    : {};
  const rawBindings = config.payloadBindings && typeof config.payloadBindings === 'object' && !Array.isArray(config.payloadBindings)
    ? config.payloadBindings
    : {};

  return fields.reduce((acc, field) => {
    const templateValue = rawTemplates[field.key];
    if (typeof templateValue === 'string') {
      acc[field.key] = templateValue;
      return acc;
    }

    if (CORTEX_TEMPLATE_TEXT_FIELDS.has(field.key)) {
      const boundSelector = normalizeTaskSelectorBindingPath(rawBindings[field.key] || '');
      acc[field.key] = boundSelector ? `{{task.${boundSelector}}}` : '';
    }
    return acc;
  }, {});
};

const normalizeCortexOcrOptions = (config = {}) => {
  const rawOptions = Array.isArray(config.ocrOptions)
    ? config.ocrOptions
    : Array.isArray(config.ocr_options)
      ? config.ocr_options
      : [];

  return rawOptions.map((item) => ({
    documentName: String(item?.documentName || item?.document_name || item?.name || ''),
    extractionData: String(item?.extractionData || item?.extraction_data || item?.context || '')
  }));
};

const normalizeCortexTagentOptions = (config = {}) => {
  const readLegacyBindingOptions = () => {
    const rawBindings = config.payloadBindings && typeof config.payloadBindings === 'object' && !Array.isArray(config.payloadBindings)
      ? config.payloadBindings
      : {};
    const rawLegacy = rawBindings.options;
    if (Array.isArray(rawLegacy)) return rawLegacy;
    if (typeof rawLegacy === 'string' && rawLegacy.trim().startsWith('[')) {
      try {
        const parsed = JSON.parse(rawLegacy);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const rawOptions = Array.isArray(config.tagentOptions)
    ? config.tagentOptions
    : Array.isArray(config.tagent_options)
      ? config.tagent_options
      : Array.isArray(config.options)
        ? config.options
        : typeof config.options === 'string' && config.options.trim().startsWith('[')
          ? (() => {
              try {
                const parsed = JSON.parse(config.options);
                return Array.isArray(parsed) ? parsed : [];
              } catch {
                return [];
              }
            })()
        : readLegacyBindingOptions();

  return rawOptions.map((item) => String(item ?? ''));
};

const stripLegacyCortexResponseTypeFields = (config = {}) => {
  if (!config || typeof config !== 'object' || Array.isArray(config)) return {};
  const nextConfig = { ...config };
  delete nextConfig.responseType;
  delete nextConfig.response_type;
  return nextConfig;
};

const CORTEX_FILE_MODE_SUFFIXES = new Set(['name', 'url', 'base64', 'binario']);

const normalizeCortexOcrFileBindingPath = (value = '') => {
  const normalizedPath = normalizeTaskAttributePathFromTemplate(value);
  if (!normalizedPath || !normalizedPath.includes('.')) return normalizedPath;
  const pathSegments = normalizedPath.split('.').map((part) => String(part || '').trim()).filter(Boolean);
  if (pathSegments.length < 2) return normalizedPath;
  const suffix = String(pathSegments[pathSegments.length - 1] || '').toLowerCase();
  if (!CORTEX_FILE_MODE_SUFFIXES.has(suffix)) return normalizedPath;
  return pathSegments.slice(0, -1).join('.');
};

const normalizeCortexConfig = (config = {}) => {
  const sanitizedConfig = stripLegacyCortexResponseTypeFields(config);
  const defaultConfig = commandCatalog.cortex.defaults;
  const rawResource = String(sanitizedConfig.resource || defaultConfig.resource).toLowerCase();
  const resource = cortexResourceOptions.some((item) => item.value === rawResource)
    ? rawResource
    : defaultConfig.resource;
  const resourceResponseType = String(
    sanitizedConfig.resourceResponseType
    || sanitizedConfig.resource_response_type
    || ''
  ).trim().toUpperCase();
  const genericPromptSource = sanitizedConfig.genericPrompt
    ?? sanitizedConfig.generic_prompt
    ?? sanitizedConfig.prompt;
  const legacyGenericPrompt = genericPromptSource == null
    && resource === 'tagent'
    && resourceResponseType === 'GENERIC'
    ? String(sanitizedConfig?.payloadTemplates?.message ?? '')
    : '';
  const followUpBinding = normalizeTaskSelectorBinding(
    sanitizedConfig.followUpBinding
    || sanitizedConfig.follow_up_binding
    || {
      path: sanitizedConfig.followUpAttribute
        || sanitizedConfig.follow_up_attribute
        || sanitizedConfig.followUpId
        || sanitizedConfig.follow_up_id
        || defaultConfig.followUpAttribute,
      arrayMatchByPath: sanitizedConfig.followUpArrayMatchByPath || sanitizedConfig.follow_up_array_match_by_path || {}
    }
  );
  const fileBinding = normalizeTaskSelectorBinding(
    sanitizedConfig.fileBinding
    || sanitizedConfig.file_binding
    || {
      path: sanitizedConfig.fileAttribute || sanitizedConfig.file_attribute || '',
      arrayMatchByPath: sanitizedConfig.fileArrayMatchByPath || sanitizedConfig.file_array_match_by_path || {}
    }
  );
  const normalizedFileBinding = resource === 'ocr'
    ? {
        ...fileBinding,
        path: normalizeCortexOcrFileBindingPath(fileBinding.path)
      }
    : fileBinding;
  const rawTimeout = sanitizedConfig.timeoutMs ?? sanitizedConfig.timeout_ms;
  const parsedTimeout = Number(rawTimeout);
  const hasUsableTimeout = Number.isFinite(parsedTimeout) && parsedTimeout > 0;
  const legacyCortexDefaultTimeoutMs = Number(commandCatalog.cortex.defaults?.timeoutMs || CORTEX_DEFAULT_TIMEOUT_MS);
  const isLegacyOcrTimeout = resource === 'ocr' && hasUsableTimeout && parsedTimeout === legacyCortexDefaultTimeoutMs;
  const resolvedTimeout = hasUsableTimeout
    ? (isLegacyOcrTimeout ? cortexDefaultTimeoutMsByResource(resource) : parsedTimeout)
    : cortexDefaultTimeoutMsByResource(resource);

  return {
    ...defaultConfig,
    ...sanitizedConfig,
    resource,
    resourceCod: cortexResourceSupportsList(resource)
      ? (sanitizedConfig.resourceCod || sanitizedConfig.resource_cod || sanitizedConfig.resourceCode || sanitizedConfig.cod || '')
      : '',
    resourceResponseType: cortexResourceSupportsList(resource) ? resourceResponseType : '',
    followUpAttribute: followUpBinding.path,
    followUpBinding,
    voiceAttribute: '',
    fileBinding: (cortexResourceRequiresFile(resource) || cortexResourceSupportsOptionalFile(resource))
      ? normalizedFileBinding
      : normalizeTaskSelectorBinding(''),
    fileAttribute: (cortexResourceRequiresFile(resource) || cortexResourceSupportsOptionalFile(resource))
      ? normalizedFileBinding.path
      : '',
    tagentOptions: resource === 'tagent' ? normalizeCortexTagentOptions(sanitizedConfig) : [],
    payloadBindings: normalizeCortexPayloadBindings(resource, sanitizedConfig),
    payloadTemplates: normalizeCortexPayloadTemplates(resource, sanitizedConfig),
    genericPrompt: String(genericPromptSource ?? legacyGenericPrompt),
    genericOutputFormat: String(sanitizedConfig.genericOutputFormat ?? sanitizedConfig.generic_response_format ?? sanitizedConfig.outputFormat ?? sanitizedConfig.response_format ?? ''),
    ocrOptions: resource === 'ocr' ? normalizeCortexOcrOptions(sanitizedConfig) : [],
    timeoutMs: resolvedTimeout,
    errorPolicy: sanitizedConfig.errorPolicy || defaultConfig.errorPolicy
  };
};

const hasNonEmptyValue = (value) => String(value || '').trim() !== '';

const validateCortexRequiredFields = (config = {}) => {
  const normalized = normalizeCortexConfig(config);
  const missingFields = [];

  if (!hasNonEmptyValue(normalized.followUpAttribute)) {
    missingFields.push('Follow-up ID');
  }

  if (cortexResourceRequiresFile(normalized.resource) && !hasNonEmptyValue(normalized.fileAttribute)) {
    missingFields.push('Arquivo da Task');
  }

  cortexPayloadFieldsForResource(normalized.resource)
    .filter((field) => field.required)
    .forEach((field) => {
      const fieldValue = normalized.payloadBindings?.[field.key];
      const fieldTemplate = normalized.payloadTemplates?.[field.key];
      if (!taskSelectorBindingHasValue(fieldValue) && !hasNonEmptyValue(fieldTemplate)) {
        missingFields.push(field.label);
      }
    });

  if (!missingFields.length) return '';
  return `Preencha os campos obrigatórios: ${missingFields.join(', ')}.`;
};

const validateSaveInfoRequiredFields = (config = {}) => {
  const mappings = Array.isArray(config.mappings) ? config.mappings : [];

  for (let index = 0; index < mappings.length; index += 1) {
    const mapping = mappings[index] || {};
    const sourceNodeId = String(mapping.sourceNodeId || '').trim();
    const key = String(mapping.key || '').trim();
    const saveAs = String(mapping.saveAs || '').trim();
    const sourceType = mappingSourceType(mapping);
    const missingFields = [];

    if (!['literal', 'sum', 'task'].includes(sourceType) && !sourceNodeId) missingFields.push('Fonte antecessora');
    if (sourceType === 'literal' && !mappingHasLiteralValue(mapping)) missingFields.push('Texto personalizado');
    if (sourceType !== 'literal' && !key) {
      if (sourceType === 'sum') missingFields.push('Atributo inteiro da Task');
      else if (sourceType === 'task') missingFields.push('Atributo da Task');
      else missingFields.push('Chave da resposta');
    }
    if (sourceType === 'sum' && !validMappingSumValue(mapping)) missingFields.push('Incremento inteiro');
    if (!saveAs) missingFields.push('Salvar como');

    if (missingFields.length) {
      return `Mapeamento ${index + 1}: preencha os campos obrigatórios: ${missingFields.join(', ')}.`;
    }
  }

  return '';
};

const validateCommandRequiredFields = ({ commandType, config = {} }) => {
  if (commandType === 'start') {
    const normalizedStart = normalizeStartConfig(config);
    if (isSearchStartConfig(normalizedStart) && !String(normalizedStart.executionIntegrationId || '').trim()) {
      return 'Selecione a Integração(API) para o Início em modo Buscar.';
    }
    if (isSearchStartConfig(normalizedStart) && !Number.isInteger(Number(normalizedStart.executionEveryMinutes || 0))) {
      return 'Defina o intervalo de execução em minutos.';
    }
    return '';
  }

  if (commandType === 'cortex') {
    return validateCortexRequiredFields(config);
  }

  if (commandType === 'api') {
    if (!String(config.integrationId || '').trim()) {
      return 'Selecione a Integração.';
    }
    return '';
  }

  if (commandType === 'chat') {
    if (!String(config.platform || '').trim()) return 'Selecione a plataforma de Chat.';
    if (!String(config.templateId || '').trim()) return 'Selecione o template de Chat.';
    const toBinding = normalizeChatVoiceBinding(
      config.toBinding
      || config.to_binding
      || config.toAttribute
      || config.to_attribute,
      [],
      []
    );
    if (!chatVoiceBindingHasValue(toBinding)) return 'Selecione a origem e o valor de destino (to) do Chat.';
    const paramsCount = Math.max(0, Number.parseInt(config.paramsCount || 0, 10) || 0);
    const paramBindings = config.paramBindings && typeof config.paramBindings === 'object' && !Array.isArray(config.paramBindings)
      ? config.paramBindings
      : {};
    const paramBindingDetails = config.paramBindingDetails && typeof config.paramBindingDetails === 'object' && !Array.isArray(config.paramBindingDetails)
      ? config.paramBindingDetails
      : (config.param_binding_details && typeof config.param_binding_details === 'object' && !Array.isArray(config.param_binding_details)
        ? config.param_binding_details
        : {});
    for (let index = 1; index <= paramsCount; index += 1) {
      const binding = normalizeChatVoiceBinding(paramBindingDetails[String(index)] ?? paramBindings[String(index)], [], []);
      if (!chatVoiceBindingHasValue(binding)) {
        return `Mapeie o parâmetro {{${index}}} do template de Chat.`;
      }
    }
    return '';
  }

  if (commandType === 'voice') {
    if (!String(config.campaignId || '').trim()) return 'Selecione a campanha de Voz.';
    const toBinding = normalizeChatVoiceBinding(
      config.toBinding
      || config.to_binding
      || config.toAttribute
      || config.to_attribute,
      [],
      []
    );
    if (!chatVoiceBindingHasValue(toBinding)) return 'Selecione a origem e o valor de destino (to) da Voz.';
    const paramsCount = Math.max(0, Number.parseInt(config.paramsCount || 0, 10) || 0);
    const editableParamsCount = Math.max(paramsCount - 1, 0);
    const paramBindings = config.paramBindings && typeof config.paramBindings === 'object' && !Array.isArray(config.paramBindings)
      ? config.paramBindings
      : {};
    const paramBindingDetails = config.paramBindingDetails && typeof config.paramBindingDetails === 'object' && !Array.isArray(config.paramBindingDetails)
      ? config.paramBindingDetails
      : (config.param_binding_details && typeof config.param_binding_details === 'object' && !Array.isArray(config.param_binding_details)
        ? config.param_binding_details
        : {});
    for (let index = 1; index <= editableParamsCount; index += 1) {
      const campaignParamIndex = index + 1;
      const binding = normalizeChatVoiceBinding(paramBindingDetails[String(index)] ?? paramBindings[String(index)], [], []);
      if (!chatVoiceBindingHasValue(binding)) {
        return `Mapeie o parâmetro {{${campaignParamIndex}}} da campanha de Voz.`;
      }
    }
    return '';
  }

  if (commandType === 'condition') {
    const normalized = normalizeConditionConfig(config);
    if (normalizeConditionDecisionMode(normalized.decisionMode) !== 'multiple') return '';
    const paths = Array.isArray(normalized.decisionPaths) ? normalized.decisionPaths : [];
    if (!paths.length) return 'Adicione pelo menos uma decisão.';
    const missingLabel = paths.some((path) => !String(path?.label || '').trim());
    if (missingLabel) return 'Preencha o nome de todos os caminhos.';
    const duplicateLabels = new Set();
    const hasDuplicateLabel = paths.some((path) => {
      const key = String(path?.label || '').trim().toLocaleLowerCase('pt-BR');
      if (!key) return false;
      if (duplicateLabels.has(key)) return true;
      duplicateLabels.add(key);
      return false;
    });
    if (hasDuplicateLabel) return 'Cada caminho deve ter um nome único.';
    if (!String(normalized.decisionSource?.sourceNodeId || '').trim() || !String(normalized.decisionSource?.variable || '').trim()) {
      return 'Selecione a origem e o atributo que definem qual caminho deve ser seguido.';
    }
    return '';
  }

  if (commandType === 'saveInfo') {
    return validateSaveInfoRequiredFields(config);
  }

  if (commandType === 'insertDemand') {
    const payloadMode = String(config.payloadMode || config.payload_mode || '').trim();
    if (!String(config.targetDemandId || config.demandId || '').trim()) {
      return 'Selecione a Demanda de destino.';
    }
    if (
      ['merge_api', 'api_response'].includes(payloadMode)
      && !String(config.mergeSourceNodeId || config.merge_source_node_id || '').trim()
    ) {
      return 'Selecione a API para obter o payload de inserção na demanda.';
    }
    const payloadMappings = config.payloadMappings ?? config.payload_mappings;
    if (payloadMode === 'template' && Array.isArray(payloadMappings)) {
      if (!payloadMappings.length) {
        return 'Adicione pelo menos um atributo ao Payload template.';
      }
      const incomplete = payloadMappings.find((mapping) => (
        !String(mapping?.targetKey || mapping?.target_key || '').trim()
        || (
          String(mapping?.sourceType || mapping?.source_type || '') !== 'literal'
          && !String(mapping?.path || mapping?.valueKey || mapping?.value_key || '').trim()
        )
        || (
          String(mapping?.sourceType || mapping?.source_type || '') === 'api'
          && !String(mapping?.sourceNodeId || mapping?.source_node_id || '').trim()
        )
      ));
      if (incomplete) {
        return 'Preencha o nome do atributo, a fonte e a Value key em todas as linhas do Payload template.';
      }
      const invalidLiteral = payloadMappings.find((mapping) => (
        mappingSourceType(mapping) === 'literal' && !mappingHasLiteralValue(mapping)
      ));
      if (invalidLiteral) {
        return 'Preencha o Texto personalizado em todas as linhas configuradas com essa fonte.';
      }
      const invalidSum = payloadMappings.find((mapping) => (
        mappingSourceType(mapping) === 'sum' && !validMappingSumValue(mapping)
      ));
      if (invalidSum) {
        return 'O incremento da operação Somar deve ser um número inteiro.';
      }
      const targetKeys = payloadMappings.map((mapping) => String(mapping?.targetKey || mapping?.target_key || '').trim());
      if (new Set(targetKeys).size !== targetKeys.length) {
        return 'Cada atributo do Payload template deve ter um nome único.';
      }
    }
    return '';
  }

  return '';
};

const operatorOptions = [
  { value: 'equals', label: 'Igual' },
  { value: 'not_equals', label: 'Diferente' },
  { value: 'contains', label: 'Contem' },
  { value: 'greater_than', label: 'Maior que' },
  { value: 'less_than', label: 'Menor que' },
  { value: 'exists', label: 'Existe' },
  { value: 'matches_regex', label: 'Corresponde a regex' }
];

const delayUnitOptions = [
  { value: 'milliseconds', label: 'Milissegundos' },
  { value: 'seconds', label: 'Segundos' },
  { value: 'minutes', label: 'Minutos' },
  { value: 'hours', label: 'Horas' }
];

const delayStrategyOptions = [
  { value: 'fixed', label: 'Fixa' },
  { value: 'exponential', label: 'Exponencial' }
];

const normalizeDelayConfig = (config = {}) => {
  const strategy = config.strategy === 'exponential' ? 'exponential' : 'fixed';
  const unit = delayUnitOptions.some((option) => option.value === config.unit) ? config.unit : commandCatalog.delay.defaults.unit;
  const base = {
    learnedInputSchema: learnedInputSchemaFromConfig(config),
    learnedResponseSchema: learnedResponseSchemaFromConfig(config),
    duration: config.duration ?? commandCatalog.delay.defaults.duration,
    unit,
    strategy
  };

  if (strategy === 'exponential') {
    return {
      ...base,
      maxWait: config.maxWait ?? commandCatalog.delay.defaults.maxWait
    };
  }

  return {
    ...base,
    jitterPercent: config.jitterPercent ?? commandCatalog.delay.defaults.jitterPercent
  };
};

const filterSourceOptions = [
  { value: 'status', label: 'Status da task' },
  { value: 'attribute', label: 'Atributo da task' },
  { value: 'task_key', label: 'Identificador da task' },
  { value: 'created_at', label: 'Data de criação' },
  { value: 'updated_at', label: 'Data de atualização' }
];

const filterOperatorOptions = [
  { value: 'equals', label: 'Igual' },
  { value: 'not_equals', label: 'Diferente' },
  { value: 'contains', label: 'Contém' },
  { value: 'not_contains', label: 'Não contém' },
  { value: 'starts_with', label: 'Começa com' },
  { value: 'ends_with', label: 'Termina com' },
  { value: 'greater_than', label: 'Maior que' },
  { value: 'less_than', label: 'Menor que' },
  { value: 'exists', label: 'Existe' },
  { value: 'empty', label: 'Vazio' }
];

const conditionOperatorOptions = [
  { value: 'equals', label: 'Igual' },
  { value: 'not_equals', label: 'Diferente' },
  { value: 'contains', label: 'Contém' },
  { value: 'not_contains', label: 'Não contém' },
  { value: 'greater_than', label: 'Maior que' },
  { value: 'greater_or_equal', label: 'Maior ou igual' },
  { value: 'less_than', label: 'Menor que' },
  { value: 'less_or_equal', label: 'Menor ou igual' },
  { value: 'exists', label: 'Existe' },
  { value: 'not_exists', label: 'Não existe' },
  { value: 'empty', label: 'Vazio' },
  { value: 'not_empty', label: 'Não vazio' },
  { value: 'matches_regex', label: 'Corresponde a regex' }
];

const CONDITION_ARRAY_MATCH_OPTIONS = [
  { value: 'any_equals', label: 'Qualquer item', icon: 'format_list_bulleted' },
  { value: 'all_equals', label: 'Todos os itens', icon: 'done_all' },
  { value: 'first_equals', label: 'Pelo primeiro item', icon: 'vertical_align_top' },
  { value: 'last_equals', label: 'Pelo último item', icon: 'vertical_align_bottom' },
  { value: 'index_equals', label: 'Posição específica', icon: 'pin' },
  { value: 'filter_equals', label: 'Localizar por regras', icon: 'filter_alt' }
];

const normalizeConditionArrayMatch = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (['filter_equals', 'filter', 'where', 'find'].includes(normalized)) return 'filter_equals';
  if (['index_equals', 'index', 'position'].includes(normalized)) return 'index_equals';
  if (['all_equals', 'all', 'every'].includes(normalized)) return 'all_equals';
  if (['last_equals', 'last', 'last_item'].includes(normalized)) return 'last_equals';
  if (['first_equals', 'first', 'first_item'].includes(normalized)) return 'first_equals';
  return 'any_equals';
};

const nextConditionArrayMatch = (value) => {
  const current = normalizeConditionArrayMatch(value);
  if (current === 'any_equals') return 'first_equals';
  if (current === 'first_equals') return 'last_equals';
  return 'any_equals';
};

const conditionArrayMatchLabel = (value) => {
  const normalized = normalizeConditionArrayMatch(value);
  return CONDITION_ARRAY_MATCH_OPTIONS.find((option) => option.value === normalized)?.label || 'Qualquer item';
};

const conditionArrayMatchIcon = (value) => {
  const normalized = normalizeConditionArrayMatch(value);
  return CONDITION_ARRAY_MATCH_OPTIONS.find((option) => option.value === normalized)?.icon || 'format_list_bulleted';
};

const normalizeConditionArrayMatches = (value) => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? Object.entries(value).reduce((acc, [path, match]) => {
      acc[normalizeConditionVariablePath(path)] = normalizeConditionArrayMatch(match);
      return acc;
    }, {})
    : {}
);

const makeArrayNavigationRule = (path = '') => ({
  id: `array-rule-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  path,
  type: '',
  operator: 'equals',
  value: '',
  arrayPaths: []
});

const normalizeArrayNavigationRule = (rule = {}) => {
  const rawPath = rule.path || rule.field || rule.attribute || '';
  const type = String(rule.type || rule.variableType || '');
  const metadata = conditionVariablePathMetadata(rawPath, type);
  const configuredArrayPaths = Array.isArray(rule.arrayPaths || rule.array_paths)
    ? (rule.arrayPaths || rule.array_paths).map((path) => normalizeConditionVariablePath(path)).filter(Boolean)
    : [];
  return {
    ...makeArrayNavigationRule(),
    ...rule,
    id: String(rule.id || makeArrayNavigationRule().id),
    path: metadata.path,
    type,
    operator: conditionOperatorOptions.some((option) => option.value === rule.operator) ? rule.operator : 'equals',
    value: rule.value ?? '',
    dateConfig: isDateLikeSchemaType(type)
      ? normalizeApiDateBindingConfig(rule.dateConfig || rule.date_config || {})
      : undefined,
    arrayPaths: [...new Set([...metadata.arrayPaths, ...configuredArrayPaths])]
  };
};

const normalizeArrayNavigation = (value, fallbackMode = 'any_equals') => {
  const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const mode = normalizeConditionArrayMatch(source.mode || (typeof value === 'string' ? value : fallbackMode));
  const rules = Array.isArray(source.rules || source.conditions)
    ? (source.rules || source.conditions).map(normalizeArrayNavigationRule)
    : [];
  return {
    mode,
    index: Math.max(0, Number.parseInt(source.index, 10) || 0),
    logic: String(source.logic || source.filterLogic || 'AND').toUpperCase() === 'OR' ? 'OR' : 'AND',
    rules
  };
};

const normalizeConditionArrayNavigations = (value, legacyMatches = {}) => {
  const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const legacy = normalizeConditionArrayMatches(legacyMatches);
  const paths = new Set([...Object.keys(legacy), ...Object.keys(source)]);
  return [...paths].reduce((acc, path) => {
    const normalizedPath = normalizeConditionVariablePath(path);
    acc[normalizedPath] = normalizeArrayNavigation(source[path], legacy[path] || 'any_equals');
    return acc;
  }, {});
};

const conditionErrorPolicyOptions = [
  { value: 'Assumir como verdadeiro', label: 'Assumir como verdadeiro' },
  { value: 'Assumir como falso', label: 'Assumir como false' }
];

const normalizeConditionErrorPolicy = (policy) => {
  const normalized = String(policy || '').trim().toLowerCase();
  if (normalized.includes('verdadeiro') || normalized.includes('true')) return 'Assumir como verdadeiro';
  if (normalized.includes('falso') || normalized.includes('false')) return 'Assumir como falso';
  return 'Assumir como falso';
};

const scheduleNumberOptions = Array.from({ length: 60 }, (_, index) => {
  const value = String(index + 1);
  return { value, label: value };
});

const startExecutionIntervalOptions = [1, 5, 10, 30, 60].map((value) => ({
  value: String(value),
  label: String(value)
}));

const scheduleUnitOptions = [
  { value: 'minute', label: 'Minuto' },
  { value: 'hour', label: 'Hora' }
];

const startExecutionTypeOptions = [
  { value: START_EXECUTION_TYPE_PERIODIC, label: 'Execução periódica' },
  { value: START_EXECUTION_TYPE_TASK_UPDATE, label: 'Execução por atualização da task' }
];

const taskUpdateTriggerModeOptions = [
  { value: TASK_UPDATE_TRIGGER_MODE_ENTER_FILTER, label: 'Novo valor igual ao filtro' },
  { value: TASK_UPDATE_TRIGGER_MODE_ANY_MATCH, label: 'Qualquer alteração com o filtro' }
];

const demandStatusOptions = (demand = {}) => {
  const source = demand && typeof demand === 'object' && !Array.isArray(demand) ? demand : {};
  const statuses = Array.isArray(source.task_statuses) ? source.task_statuses : [];
  const names = statuses.map((status) => status?.name).filter(Boolean);
  return [...new Set(names.length ? names : ['Pendente', 'Aguardando', 'Concluida', 'Falha'])]
    .map((name) => ({ value: name, label: name }));
};

const makeFilterRule = (source = 'status') => ({
  id: `filter-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  source,
  attribute: source === 'status' ? 'status' : '',
  operator: 'equals',
  value: source === 'status' ? 'Pendente' : '',
  arrayMatchByPath: {},
  arrayNavigationByPath: {}
});

const schemaEntries = (schema) => Object.entries(schema || {})
  .sort(([left], [right]) => left.localeCompare(right));

const normalizeResponseVariablePath = (value = '') => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const noRoot = raw.replace(/^\$\./, '').replace(/^\$/, '');
  if (!noRoot || noRoot === '[]' || noRoot === '[0]' || noRoot === '0') return '$';
  return noRoot
    .replace(/\[\]/g, '.0')
    .replace(/\[(\d+)\]/g, '.$1')
    .replace(/^\./, '')
    .replace(/\.\./g, '.');
};

const runtimeOutputVariableOptions = (output) => {
  const schema = schemaFromRuntimeOutput(output);
  if (!Object.keys(schema).length) return [];
  return schemaEntries(schema).map(([key, type]) => ({
    value: normalizeResponseVariablePath(key) || key,
    label: `${normalizeResponseVariablePath(key) || key} (${schemaTypeLabel(type)})`,
    type
  }));
};

const taskPayloadExtraVariableOptions = (task = {}) => {
  const schema = schemaFromTaskPayload(task);
  if (!Object.keys(schema).length) return [];
  return schemaEntries(schema).map(([key, type]) => ({
    value: normalizeResponseVariablePath(key) || key,
    label: `${normalizeResponseVariablePath(key) || key} (${schemaTypeLabel(type)})`,
    type
  }));
};

const mergeVariableOptions = (...sets) => {
  const merged = new Map();
  sets.forEach((set) => {
    (Array.isArray(set) ? set : []).forEach((variable) => {
      const value = String(variable?.value || '').trim();
      if (!value) return;
      const current = merged.get(value);
      if (!current) {
        merged.set(value, variable);
        return;
      }
      const currentType = String(current?.type || '').trim();
      const nextType = String(variable?.type || '').trim();
      if (!currentType && nextType) {
        merged.set(value, variable);
      }
    });
  });
  return [...merged.values()];
};

const FILE_VARIABLE_HINT_LEVEL_BY_TOKEN = Object.freeze({
  content_base64: 'strong',
  content_url: 'strong',
  storage: 'strong',
  storage_path: 'strong',
  s3_key: 'strong',
  base64: 'strong',
  data: 'strong',
  mime_type: 'weak',
  mimetype: 'weak',
  sha256: 'weak',
  size_bytes: 'weak',
  stored_at: 'weak',
  file_name: 'weak',
  filename: 'weak'
});

const normalizeFileLikeVariableOptions = (variables = [], { knownFileFields = [] } = {}) => {
  const optionsByValue = new Map();
  (Array.isArray(variables) ? variables : []).forEach((item) => {
    const value = String(item?.value || '').trim();
    if (!value || optionsByValue.has(value)) return;
    optionsByValue.set(value, item);
  });

  const fileFieldHintScores = [...optionsByValue.values()].reduce((acc, item) => {
    const value = String(item?.value || '').trim();
    if (!value || !value.includes('.')) return acc;
    const parts = value.split('.').map((part) => String(part || '').trim()).filter(Boolean);
    if (parts.length < 2) return acc;
    for (let index = 1; index < parts.length; index += 1) {
      const token = parts[index].toLowerCase();
      const hintLevel = FILE_VARIABLE_HINT_LEVEL_BY_TOKEN[token];
      if (!hintLevel) continue;
      const basePath = parts.slice(0, index).join('.');
      if (!basePath) break;
      const currentScore = acc.get(basePath) || { strong: 0, weak: 0 };
      currentScore[hintLevel] += 1;
      acc.set(basePath, currentScore);
      break;
    }
    return acc;
  }, new Map());

  const fileFieldsFromHints = [...fileFieldHintScores.entries()]
    .filter(([, score]) => score.strong > 0 || score.weak >= 2)
    .map(([basePath]) => basePath);

  const fileFields = [...new Set([
    ...(Array.isArray(knownFileFields) ? knownFileFields : []),
    ...fileFieldsFromHints
  ])];
  if (!fileFields.length) return [...optionsByValue.values()];

  fileFields.forEach((field) => {
    const baseValue = String(field || '').trim();
    if (!baseValue) return;
    [...optionsByValue.keys()].forEach((value) => {
      if (value.startsWith(`${baseValue}.`)) {
        optionsByValue.delete(value);
      }
    });

    const baseOption = optionsByValue.get(baseValue) || {
      value: baseValue,
      label: `${baseValue} (objeto)`,
      type: 'object',
      group: 'configured'
    };
    optionsByValue.set(baseValue, {
      ...baseOption,
      type: 'object',
      label: `${baseValue} (${schemaTypeLabel('object')})`
    });

    const addPreset = (key, type) => {
      const value = `${baseValue}.${key}`;
      if (optionsByValue.has(value)) return;
      optionsByValue.set(value, {
        value,
        label: `${key} (${schemaTypeLabel(type)})`,
        displayName: key,
        type,
        group: baseOption.group || 'configured'
      });
    };
    addPreset('name', 'string');
    addPreset('url', 'url');
    addPreset('base64', 'string');
    addPreset('binario', 'file');
  });

  return [...optionsByValue.values()];
};

const normalizeTaskFileBindingPathForRouting = (value = '') => {
  const normalizedPath = normalizeTaskAttributePathFromTemplate(value);
  if (!normalizedPath || !normalizedPath.includes('.')) return normalizedPath;
  const parts = normalizedPath.split('.').map((part) => String(part || '').trim()).filter(Boolean);
  if (parts.length < 2) return normalizedPath;

  const hintTokenToMode = {
    name: 'name',
    url: 'url',
    content_url: 'url',
    base64: 'base64',
    content_base64: 'base64',
    data: 'base64',
    binario: 'binario',
    mime_type: 'url',
    mimetype: 'url',
    sha256: 'url',
    size_bytes: 'url',
    stored_at: 'url',
    storage: 'url',
    storage_path: 'url',
    s3_key: 'url',
    file_name: 'url',
    filename: 'url'
  };

  for (let index = parts.length - 1; index >= 1; index -= 1) {
    const token = parts[index].toLowerCase();
    const targetMode = hintTokenToMode[token];
    if (!targetMode) continue;
    return `${parts.slice(0, index).join('.')}.${targetMode}`;
  }

  return normalizedPath;
};

const normalizeChatInfoCustomTaskPath = (value = '') => {
  const normalizedPath = normalizeTaskFileBindingPathForRouting(value);
  if (!normalizedPath || !normalizedPath.includes('.')) return normalizedPath;
  const parts = normalizedPath.split('.').map((part) => String(part || '').trim()).filter(Boolean);
  if (parts.length < 2) return normalizedPath;
  const suffix = String(parts[parts.length - 1] || '').toLowerCase();
  if (suffix !== 'binario') return normalizedPath;
  return `${parts.slice(0, -1).join('.')}.url`;
};

const variableOptionsFromSchema = (schema = {}, group = 'configured') => schemaEntries(schema).map(([key, definition]) => {
  const type = typeof definition === 'string' ? definition : definition?.type;
  const normalizedKey = normalizeResponseVariablePath(key) || key;
  return {
    value: normalizedKey,
    label: `${normalizedKey} (${schemaTypeLabel(type)})`,
    type,
    group
  };
});

const mergeSourceVariableOptions = ({ schema = {}, runtimeOutput = null, preserveValue = '' } = {}) => {
  const configured = variableOptionsFromSchema(schema, 'configured');
  const configuredKeys = new Set(configured.map((item) => item.value));
  const extra = runtimeOutputVariableOptions(runtimeOutput)
    .filter((item) => !configuredKeys.has(item.value))
    .map((item) => ({ ...item, group: 'extra' }));
  const values = [...configured, ...extra];
  const selected = String(preserveValue || '').trim();
  if (selected && !values.some((item) => item.value === selected)) {
    values.push({ value: selected, label: `${selected} (preservado)`, type: '', group: 'extra' });
  }
  return values;
};

const conditionSourceVariableOptions = (source = {}) => {
  const configured = variableOptionsFromSchema(source.schema || {}, 'configured');
  const sourceVariables = Array.isArray(source.variables) ? source.variables : [];
  const unique = new Map();

  [...sourceVariables, ...configured].forEach((variable) => {
    const value = String(variable?.value || '').trim();
    if (!value || unique.has(value)) return;
    unique.set(value, variable);
  });

  return normalizeFileLikeVariableOptions([...unique.values()]);
};

const executionStartArrayOptions = (schema = {}) => normalizeExecutionStartVariableOptions(
  variableOptionsFromSchema(schema)
).filter((variable) => (
  isArrayLikeSchemaType(variable.type)
  && !String(variable.value || '').includes('[]')
))
  .map((variable) => ({
    value: variable.value,
    label: `${variable.value} (lista)`
  }));

const variableGroupLabel = (group) => ({
  configured: 'Atributos configurados',
  extra: 'Atributos dinâmicos'
}[group] || 'Atributos');

const groupedVariableOptions = (variables = [], preserveValue = '') => {
  return groupAttributeOptions(variables, preserveValue);
};

const VariableOptions = ({ variables = [], preserveValue = '' }) => groupedVariableOptions(variables, preserveValue).map(([group, options]) => (
  <optgroup key={group} label={variableGroupLabel(group)}>
    {options.map((variable) => <option key={variable.value} value={variable.value}>{variable.label}</option>)}
  </optgroup>
));

const CORTEX_OUTPUT_VARIABLE_CATALOG_BY_RESOURCE = {
  default: [
    ['data', 'object'],
    ['id', 'string'],
    ['error', 'string'],
    ['message', 'string']
  ],
  tagent: [
    ['data.success', 'boolean'],
    ['data.content_type', 'string'],
    ['data.content', 'string'],
    ['data.variables', 'object'],
    ['data.history_id', 'string'],
    ['data.attachment_url', 'string'],
    ['data.attachment_name', 'string'],
    ['data.attachment_kind', 'string'],
    ['cost', 'number'],
    ['error', 'string']
  ],
  trag: [
    ['data.success', 'boolean'],
    ['data.documents', 'array'],
    ['data.documents.0.content', 'string'],
    ['data.documents.0.file_id', 'string'],
    ['data.documents.0.cod', 'string'],
    ['data.documents.0.score', 'string'],
    ['data.documents.0.rerank_score', 'string'],
    ['data.documents.0.rerank_strategy', 'string'],
    ['data.history_id', 'string'],
    ['cost', 'number'],
    ['error', 'string']
  ],
  tts: [
    ['data.audio_url', 'string'],
    ['data.file_name', 'string'],
    ['data.mime_type', 'string'],
    ['cost', 'number'],
    ['error', 'string']
  ],
  ocr: [
    ['data.result', 'object'],
    ['data.result.text', 'string'],
    ['data.result.fields', 'object'],
    ['error', 'string']
  ],
  transcription: [
    ['data.text', 'string'],
    ['data.segments', 'array'],
    ['data.language', 'string'],
    ['error', 'string']
  ]
};

const cortexOutputVariableCatalog = (resource = '') => {
  const normalized = String(resource || '').toLowerCase();
  const specific = CORTEX_OUTPUT_VARIABLE_CATALOG_BY_RESOURCE[normalized] || [];
  const fallback = CORTEX_OUTPUT_VARIABLE_CATALOG_BY_RESOURCE.default || [];
  const unique = new Map();
  [...specific, ...fallback].forEach(([key, type]) => {
    const normalizedKey = String(key || '').trim();
    if (!normalizedKey || unique.has(normalizedKey)) return;
    unique.set(normalizedKey, [normalizedKey, type]);
  });
  return [...unique.values()];
};

const cortexOutputVariableOptions = (resource = '') => cortexOutputVariableCatalog(resource).map(([key, type]) => ({
  value: key,
  label: `${key} (${schemaTypeLabel(type)})`,
  type,
  group: 'configured'
}));

const extractTemplateVariables = (value) => {
  const source = typeof value === 'string' ? value : JSON.stringify(value ?? '');
  const variables = new Set();
  source.replace(/\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}/g, (_, name) => {
    variables.add(name);
    return '';
  });
  return [...variables].sort((left, right) => left.localeCompare(right));
};

const integrationTemplateVariables = (integration = {}) => {
  const source = integration && typeof integration === 'object' ? integration : {};
  const fallback = extractTemplateVariables([
    source.base_url,
    source.request_path,
    source.request_body
  ].map((item) => String(item ?? '').trim()).filter(Boolean).join('\n'));
  const declared = Array.isArray(source.request_variables)
    ? source.request_variables
    : [];
  return [...new Set([...declared, ...fallback])].filter(Boolean);
};

const taskVariableOptions = (demand = {}, options = {}) => {
  const includeTaskCommentSources = options.includeTaskCommentSources === true;
  const configured = schemaEntries(demand?.task_schema || {}).map(([key, definition]) => {
    const type = typeof definition === 'string' ? definition : definition?.type;
    return {
      value: key,
      label: `${key} (${schemaTypeLabel(type)})`,
      type,
      group: 'configured'
    };
  });

  const configuredKeys = new Set(configured.map((item) => item.value));
  const extraCandidates = Array.isArray(options.extraVariables) ? options.extraVariables : [];
  const extras = extraCandidates.reduce((acc, variable) => {
    const value = String(variable?.value || '').trim();
    if (!value || configuredKeys.has(value)) return acc;
    configuredKeys.add(value);
    const type = String(variable?.type || '').trim();
    acc.push({
      value,
      label: `${value} (${schemaTypeLabel(type)}) [extra]`,
      type,
      group: 'extra'
    });
    return acc;
  }, []);

  if (includeTaskCommentSources) {
    [
      { value: 'created_at', label: 'Criada em', displayName: 'Criada em', type: 'datetime' },
      { value: 'comments', label: 'Comentários da task (texto)', displayName: 'Comentários da task', type: 'string' },
      { value: 'last_comment', label: 'Último comentário da task (texto)', displayName: 'Último comentário da task', type: 'string' }
    ].forEach((option) => {
      if (configuredKeys.has(option.value)) return;
      configuredKeys.add(option.value);
      configured.push({ ...option, group: 'configured' });
    });
  }

  return normalizeFileLikeVariableOptions(
    configured.concat(extras),
    { knownFileFields: demandTaskFileFields(demand) }
  );
};

const appendTaskCommentVariableOptions = (variables = []) => {
  const source = Array.isArray(variables) ? variables : [];
  const labelsByValue = {
    comments: { label: 'Comentários da task (texto)', displayName: 'Comentários da task', type: 'string' },
    last_comment: { label: 'Último comentário da task (texto)', displayName: 'Último comentário da task', type: 'string' },
    created_at: { label: 'Criada em', displayName: 'Criada em', type: 'datetime' }
  };
  const normalized = source.map((item) => {
    const value = String(item?.value || '').trim();
    const override = labelsByValue[value];
    if (!override) return item;
    return {
      ...item,
      ...override,
      group: 'configured'
    };
  });
  const seen = new Set(normalized.map((item) => String(item?.value || '').trim()));
  const extras = Object.entries(labelsByValue)
    .filter(([value]) => !seen.has(value))
    .map(([value, meta]) => ({ value, ...meta, group: 'configured' }));
  return normalized.concat(extras);
};

const taskSelectorVariableOptions = (
  taskOptions = [],
  {
    includeTaskMeta = true,
    includeDemandMeta = true,
    fileOnly = false
  } = {}
) => {
  const payloadOptions = (Array.isArray(taskOptions) ? taskOptions : [])
    .reduce((acc, item) => {
      const value = String(item?.value || '').trim();
      if (!value) return acc;
      const type = String(item?.type || '').trim();
      if (fileOnly) {
        const normalizedType = type.toLowerCase();
        if (normalizedType !== 'file' && normalizedType !== 'url') return acc;
      }
      acc.push({
        value,
        label: `Task: ${String(item?.label || value)}`,
        type,
        group: item?.group || 'configured'
      });
      return acc;
    }, []);

  const taskMetaOptions = includeTaskMeta
    ? [
      { value: 'task.task_key', label: 'Task: chave técnica (string)', type: 'string', group: 'configured' },
      { value: 'task.task_uuid', label: 'Task: UUID (string)', type: 'string', group: 'configured' },
      { value: 'task.id', label: 'Task: id (runtime) (string)', type: 'string', group: 'configured' },
      { value: 'task.status', label: 'Task: status atual (string)', type: 'string', group: 'configured' },
      { value: 'task.created_at', label: 'Criada em', displayName: 'Criada em', type: 'datetime', group: 'configured' },
      { value: 'task.comments', label: 'Task: comentários (texto)', displayName: 'Comentários da task', type: 'string', group: 'configured' },
      { value: 'task.last_comment', label: 'Task: último comentário (string)', type: 'string', group: 'configured' }
    ]
    : [];

  const demandMetaOptions = includeDemandMeta
    ? [
      { value: 'demand.id', label: 'Demanda: id (string)', type: 'string', group: 'configured' },
      { value: 'demand.name', label: 'Demanda: nome (string)', type: 'string', group: 'configured' },
      { value: 'demand.protocol', label: 'Demanda: protocolo (string)', type: 'string', group: 'configured' },
      { value: 'demand.process', label: 'Demanda: processo (string)', type: 'string', group: 'configured' }
    ]
    : [];

  const unique = new Map();
  [...taskMetaOptions, ...demandMetaOptions, ...payloadOptions].forEach((option) => {
    if (!option.value || unique.has(option.value)) return;
    unique.set(option.value, option);
  });

  return [...unique.values()];
};

const commandOutputSelectorVariableOptions = (sources = []) => {
  const sourceList = Array.isArray(sources) ? sources : [];
  return sourceList.reduce((acc, source) => {
    const sourceNodeId = String(source?.nodeId || '').trim();
    if (!sourceNodeId) return acc;
    (Array.isArray(source?.variables) ? source.variables : []).forEach((variable) => {
      const variablePath = String(variable?.value || '').trim();
      if (!variablePath) return;
      acc.push({
        ...variable,
        value: `outputs.${sourceNodeId}.${variablePath}`,
        label: String(variable?.displayName || variable?.label || variablePath).trim() || variablePath,
        group: 'extra'
      });
    });
    return acc;
  }, []);
};

const mergeSelectorVariableOptions = (...collections) => {
  const unique = new Map();
  collections.forEach((collection) => {
    (Array.isArray(collection) ? collection : []).forEach((option) => {
      const value = String(option?.value || '').trim();
      if (!value || unique.has(value)) return;
      unique.set(value, option);
    });
  });
  return [...unique.values()];
};

const CORTEX_FILE_SELECTOR_PRESETS = [
  { key: 'name', label: 'name', type: 'string' },
  { key: 'url', label: 'url', type: 'url' },
  { key: 'base64', label: 'base64', type: 'string' },
  { key: 'binario', label: 'binario', type: 'file' }
];

const cortexTaskFileSelectorOptions = (taskOptions = [], { resource = '' } = {}) => {
  const normalizedResource = String(resource || '').trim().toLowerCase();
  const onlyBinarySelection = normalizedResource === 'ocr';
  const singleFileAttributeSelection = ['ocr', 'tagent', 'transcription'].includes(normalizedResource);
  const unique = new Map();
  const pushOption = (option = {}) => {
    const value = String(option?.value || '').trim();
    if (!value || unique.has(value)) return;
    unique.set(value, option);
  };

  const ocrLabelFromPath = (path = '') => {
    const parts = String(path || '').split('.').map((part) => String(part || '').trim()).filter(Boolean);
    if (!parts.length) return String(path || '').trim();
    return parts[parts.length - 1];
  };

  (Array.isArray(taskOptions) ? taskOptions : []).forEach((item) => {
    const value = String(item?.value || '').trim();
    if (!value) return;
    const normalizedType = String(item?.type || '').trim().toLowerCase();
    const optionLabelText = String(item?.displayName || item?.label || value).replace(/^Task:\s*/i, '');

    if (singleFileAttributeSelection) {
      if (normalizedType !== 'file' && normalizedType !== 'url') return;
      const normalizedOcrValue = normalizeCortexOcrFileBindingPath(value) || value;
      const ocrLabel = ocrLabelFromPath(normalizedOcrValue);
      pushOption({
        value: normalizedOcrValue,
        label: `${ocrLabel} (${schemaTypeLabel('file')})`,
        displayName: ocrLabel,
        type: 'file',
        group: item?.group || 'configured'
      });
      return;
    }

    if (normalizedType === 'file') {
      if (!onlyBinarySelection) {
        pushOption({
          value,
          label: `Task: ${optionLabelText} (objeto)`,
          displayName: optionLabelText,
          type: 'object',
          group: item?.group || 'configured'
        });
      }
      CORTEX_FILE_SELECTOR_PRESETS.forEach((preset) => {
        if (onlyBinarySelection && preset.key !== 'binario') return;
        pushOption({
          value: `${value}.${preset.key}`,
          label: `${preset.label} (${schemaTypeLabel(preset.type)})`,
          displayName: preset.label,
          type: preset.type,
          group: item?.group || 'configured'
        });
      });
      return;
    }

    if (normalizedType === 'url' && !onlyBinarySelection) {
      pushOption({
        ...item,
        value,
        type: 'url',
        displayName: optionLabelText
      });
    }
  });

  return [...unique.values()];
};

const schemaWithPrefix = (schema = {}, prefix = '') => {
  const normalizedPrefix = String(prefix || '').trim().replace(/\.+$/, '');
  if (!normalizedPrefix) return mergeResponseSchemas(schema);
  return schemaEntries(mergeResponseSchemas(schema)).reduce((acc, [path, type]) => {
    const normalizedPath = String(path || '').trim();
    acc[normalizedPath && normalizedPath !== '$' ? `${normalizedPrefix}.${normalizedPath}` : normalizedPrefix] = type;
    return acc;
  }, { [normalizedPrefix]: 'object' });
};

const taskContextContractSchema = (demand = {}, extraVariables = []) => {
  const payloadSchema = taskVariableOptions(demand, { extraVariables }).reduce((acc, variable) => {
    const path = String(variable?.value || '').trim();
    if (path) acc[path] = variable?.type || 'string';
    return acc;
  }, {});
  return mergeResponseSchemas({
    demand: 'object',
    'demand.id': 'string',
    'demand.name': 'string',
    'demand.protocol': 'string',
    'demand.process': 'string',
    task: 'object',
    'task.id': 'string',
    'task.task_key': 'string',
    'task.task_uuid': 'string',
    'task.status': 'string',
    'task.comments': 'string',
    'task.last_comment': 'string',
    'task.payload': 'object'
  }, schemaWithPrefix(payloadSchema, 'task.payload'));
};

const commandRequestContractSchema = ({ commandType, config = {}, integration = null } = {}) => {
  const requestVariables = commandType === 'api'
    ? integrationTemplateVariables(integration)
    : commandType === 'start' && isSearchStartConfig(config)
      ? integrationTemplateVariables(integration)
      : [];
  const variableSchema = requestVariables.reduce((acc, name) => {
    acc[`request.variables.${name}`] = 'string';
    return acc;
  }, {});

  if (commandType === 'chat') {
    const paramsCount = Math.max(0, Number.parseInt(config.paramsCount || 0, 10) || 0);
    const templateContentTypeName = String(
      config.templateContentTypeName
      || config.template_content_type_name
      || config.contentTypeName
      || config.content_type_name
      || ''
    ).trim().toLowerCase();
    const schema = {
      'request.to': 'string',
      'request.template_id': 'number',
      'request.info_custom': 'object'
    };
    if (isChatMediaTemplateType(templateContentTypeName)) {
      schema['request.attachment_url'] = 'string';
    }
    for (let index = 1; index <= paramsCount; index += 1) {
      schema[`request.template_data.${index - 1}`] = 'string';
    }
    return schema;
  }

  if (commandType === 'voice') {
    const paramsCount = Math.max(0, Number.parseInt(config.paramsCount || 0, 10) || 0);
    const schema = {
      'request.campaign_id': 'number',
      'request.customers.0.phone_numbers.0.ddd': 'string',
      'request.customers.0.phone_numbers.0.phone_number': 'string',
      'request.customers.0.phone_numbers.0.dial_at': 'string'
    };
    for (let index = 1; index <= paramsCount; index += 1) {
      schema[`request.customers.0.parameters.${index - 1}`] = 'string';
    }
    return schema;
  }

  if (commandType !== 'cortex') return variableSchema;
  const fields = cortexPayloadFieldsForResource(config.resource || 'tagent');
  const cortexSchema = fields.reduce((acc, field) => {
    acc[`request.${field.key}`] = field.key === 'variables' || field.key === 'options' ? 'array' : 'string';
    return acc;
  }, variableSchema);
  const resourceResponseType = String(
    config.resourceResponseType
    || config.resource_response_type
    || config.responseType
    || config.response_type
    || ''
  ).trim().toUpperCase();
  if (String(config.resource || '').trim().toLowerCase() === 'tagent' && resourceResponseType === 'GENERIC') {
    const reorderedSchema = {};
    Object.entries(cortexSchema).forEach(([key, value]) => {
      reorderedSchema[key] = value;
      if (key === 'request.message') {
        reorderedSchema['request.payload.prompt'] = 'string';
        reorderedSchema['request.payload.response_format'] = 'string';
      }
    });
    if (!Object.prototype.hasOwnProperty.call(reorderedSchema, 'request.payload.prompt')) {
      reorderedSchema['request.payload.prompt'] = 'string';
      reorderedSchema['request.payload.response_format'] = 'string';
    }
    return reorderedSchema;
  }
  return cortexSchema;
};

const saveInfoOutputContractSchema = (config = {}) => {
  const schema = { saved: 'object', status: 'string', mappings: 'number' };
  (Array.isArray(config.mappings) ? config.mappings : []).forEach((mapping) => {
    const path = String(mapping?.saveAs || mapping?.targetKey || mapping?.targetPath || '').trim();
    if (path) {
      const sourceType = mappingSourceType(mapping);
      schema[`saved.${path}`] = sourceType === 'sum'
        ? 'integer'
        : sourceType === 'literal'
          ? typeof (mapping.literalValue ?? mapping.literal_value)
          : mapping?.keyType || 'string';
    }
  });
  return schema;
};

const knownCommandOutputSchema = ({ commandType, config = {}, demand = {}, integration = null, runtimeOutput = null } = {}) => {
  const learnedSchema = hasCurrentInspectionContract(config)
    ? learnedResponseSchemaFromConfig(config)
    : {};
  const runtimeSchema = schemaFromRuntimeOutput(runtimeOutput, { includeDeclaredSchema: false });
  let configuredSchema = {};

  if (commandType === 'start') {
    configuredSchema = isSearchStartConfig(config)
      ? mergeResponseSchemas(
        normalizeResponseSchema(integration?.response_schema),
        normalizeResponseSchema(config.executionResponseSchema || config.execution_response_schema)
      )
      : taskContextContractSchema(demand);
  } else if (commandType === 'api') {
    configuredSchema = normalizeResponseSchema(integration?.response_schema);
  } else if (commandType === 'chat') {
    configuredSchema = {
      ok: 'boolean',
      status: 'number',
      latency_ms: 'number',
      'body_json.data': 'array'
    };
  } else if (commandType === 'voice') {
    configuredSchema = {
      ok: 'boolean',
      status: 'number',
      latency_ms: 'number',
      'body_json.campaign_id': 'number',
      'body_json.totalIntegratedCustomers': 'number',
      'body_json.error': 'string',
      'body_json.errorCustomers': 'array'
    };
  } else if (commandType === 'cortex') {
    configuredSchema = Object.fromEntries(cortexOutputVariableCatalog(config.resource || config.resourceType || ''));
  } else if (commandType === 'condition') {
    configuredSchema = {
      passed: 'boolean',
      assumed: 'boolean',
      error: 'string',
      mode: 'string',
      selected_handle: 'string',
      selected_path_id: 'string',
      selected_label: 'string',
      source_value: 'string|number|boolean'
    };
  } else if (commandType === 'delay') {
    configuredSchema = { delayed_ms: 'number' };
  } else if (commandType === 'saveInfo') {
    configuredSchema = saveInfoOutputContractSchema(config);
  } else if (commandType === 'insertDemand') {
    configuredSchema = {
      operation: 'string',
      source_demand_id: 'string',
      source_task_id: 'string',
      source_removed: 'boolean',
      target_demand_id: 'string',
      task_id: 'string',
      task_uuid: 'string',
      task_key: 'string',
      action: 'string'
    };
  }

  const observedSchema = mergeResponseSchemas(learnedSchema, runtimeSchema);
  const prefersObservedBody = commandType === 'api'
    || commandType === 'chat'
    || commandType === 'voice'
    || commandType === 'cortex'
    || (commandType === 'start' && isSearchStartConfig(config));
  if (prefersObservedBody && Object.keys(observedSchema).length) return observedSchema;
  return mergeResponseSchemas(configuredSchema, observedSchema);
};

const commandSchemaTypeToken = (type = '') => {
  const tokens = String(type || '').toLowerCase().split('|').filter(Boolean);
  if (tokens.includes('array')) return { className: 'array', symbol: '[]' };
  if (tokens.includes('object') || tokens.includes('json')) return { className: 'object', symbol: '{}' };
  if (tokens.some((token) => ['number', 'int', 'integer', 'float', 'decimal'].includes(token))) return { className: 'number', symbol: '#' };
  if (tokens.some((token) => ['boolean', 'bool'].includes(token))) return { className: 'boolean', symbol: 'tf' };
  if (tokens.includes('null')) return { className: 'null', symbol: '-' };
  return { className: 'string', symbol: 'A' };
};

const buildCommandSchemaTree = (schema = {}, keyLabels = {}) => {
  const root = { key: '', path: '', type: 'object', children: new Map() };
  schemaEntries(mergeResponseSchemas(schema)).forEach(([rawPath, type]) => {
    const normalizedPath = String(rawPath || '').trim().replace(/^\$\.?/, '');
    const parts = (normalizedPath || 'raiz').split('.').filter(Boolean);
    let current = root;
    parts.forEach((rawPart, index) => {
      const key = /^\d+$/.test(rawPart) ? 'item[]' : rawPart;
      const path = current.path ? `${current.path}.${key}` : key;
      if (!current.children.has(key)) {
        current.children.set(key, {
          key,
          displayKey: String(keyLabels[path] || '').trim() || key,
          path,
          type: '',
          children: new Map()
        });
      }
      current = current.children.get(key);
      if (index === parts.length - 1) current.type = type;
    });
  });

  const finalize = (node) => {
    const children = [...node.children.values()]
      .map(finalize)
      .sort((left, right) => left.key.localeCompare(right.key, 'pt-BR'));
    const inferredType = node.type || (node.key === 'item[]' ? 'object' : children.length ? 'object' : 'string');
    return { ...node, type: inferredType, children };
  };
  return finalize(root).children;
};

const CommandSchemaNode = ({ node, depth = 0 }) => {
  const hasChildren = Boolean(node.children?.length);
  const typeToken = commandSchemaTypeToken(node.type);
  const typeLabel = schemaTypeLabel(node.type);
  const displayKey = node.displayKey || node.key;
  const keyTitle = displayKey === node.key
    ? `${node.path} - ${typeLabel}`
    : `${displayKey} (${node.path}) - ${typeLabel}`;
  if (!hasChildren) {
    return (
      <div className="task-detail-structured-tree-row command-schema-tree-row" title={keyTitle}>
        <span className="task-detail-structured-tree-spacer" aria-hidden="true"></span>
        <span className={`task-detail-structured-tree-type is-${typeToken.className}`} aria-hidden="true">{typeToken.symbol}</span>
        <span className="task-detail-structured-tree-key">{displayKey}</span>
        <span className="task-detail-structured-tree-value">{typeLabel}</span>
      </div>
    );
  }

  return (
    <details className="task-detail-structured-tree-node command-schema-tree-node" open={depth === 0}>
      <summary title={keyTitle}>
        <span className="task-detail-structured-tree-chevron material-symbols-rounded" aria-hidden="true">chevron_right</span>
        <span className={`task-detail-structured-tree-type is-${typeToken.className}`} aria-hidden="true">{typeToken.symbol}</span>
        <span className="task-detail-structured-tree-key">{displayKey}</span>
        <small>{typeLabel}</small>
      </summary>
      <div className="task-detail-structured-tree-children">
        {node.children.map((child) => <CommandSchemaNode key={child.path} node={child} depth={depth + 1} />)}
      </div>
    </details>
  );
};

const CommandContractPanel = ({ side, title, subtitle, schema = {}, learnedCount = 0, keyLabels = {} }) => {
  const nodes = buildCommandSchemaTree(schema, keyLabels);
  const total = Object.keys(mergeResponseSchemas(schema)).length;
  const isInput = side === 'input';
  return (
    <aside className={`modal-side modal-side-${side} command-contract-panel`}>
      <header className="command-contract-head">
        <span className="material-symbols-rounded" aria-hidden="true">{isInput ? 'input' : 'output'}</span>
        <div>
          <small>{isInput ? 'Entrada conhecida' : 'Saída conhecida'}</small>
          <strong>{title}</strong>
        </div>
        <b>{total}</b>
      </header>
      <p>{subtitle}</p>
      <div className="task-detail-structured-content command-contract-content">
        {nodes.length ? (
          <div className="task-detail-structured-tree">
            {nodes.map((schemaNode) => <CommandSchemaNode key={schemaNode.path} node={schemaNode} />)}
          </div>
        ) : (
          <div className="command-contract-empty">
            <span className="material-symbols-rounded" aria-hidden="true">manage_search</span>
            <strong>Nenhuma key conhecida</strong>
            <small>Execute uma inspeção para este comando aprender o contrato.</small>
          </div>
        )}
      </div>
      <footer className={learnedCount ? 'is-learned' : ''}>
        <span></span>
        {learnedCount
          ? `${learnedCount} key(s) aprendida(s) em inspeções`
          : 'O aprendizado será ampliado a cada inspeção'}
      </footer>
    </aside>
  );
};

const INSERT_DEMAND_TASK_KEY_CUSTOM = '__custom__';
const INSERT_DEMAND_TASK_KEY_MODE_CUSTOM = 'custom';
const INSERT_DEMAND_TASK_KEY_MODE_PRESET = 'preset';

const normalizeInsertDemandTaskKeyPath = (value = '') => {
  const normalized = normalizeResponseVariablePath(value);
  if (!normalized || normalized === '$') return '';
  return normalized
    .split('.')
    .map((part) => String(part || '').trim())
    .filter((part) => part && !/^\d+$/.test(part))
    .join('.');
};

const insertDemandTaskKeyOptions = (demand = {}, options = {}) => {
  const extraVariables = Array.isArray(options.extraVariables) ? options.extraVariables : [];
  const base = [
    { value: '{{task.task_key}}', label: 'Task atual (task_key)' },
    { value: '{{task.id}}', label: 'Task atual (id)' },
    { value: '{{task.status}}', label: 'Task atual (status)' }
  ];
  const payloadFields = taskVariableOptions(demand, { extraVariables })
    .reduce((acc, item) => {
      const normalizedPath = normalizeInsertDemandTaskKeyPath(item?.value || '');
      if (!normalizedPath || !isTaskKeyCompatibleVariableType(item?.type)) return acc;
      const originalPath = normalizeResponseVariablePath(item?.value || '');
      const isArrayItemPath = normalizedPath !== originalPath;
      acc.push({
        value: `{{task.payload.${normalizedPath}}}`,
        label: isArrayItemPath
          ? `Payload: ${normalizedPath} (item da lista)`
          : `Payload: ${item.label}`
      });
      return acc;
    }, []);

  const unique = new Map();
  [...base, ...payloadFields].forEach((option) => {
    if (!unique.has(option.value)) unique.set(option.value, option);
  });
  return [...unique.values()];
};

const insertDemandTaskKeySelectValue = (taskKey = '', options = []) => {
  const current = String(taskKey || '').trim();
  if (!current) return '{{task.task_key}}';
  return options.some((option) => option.value === current) ? current : INSERT_DEMAND_TASK_KEY_CUSTOM;
};

const normalizeInsertDemandTaskKeyMode = (value) => (
  String(value || '').trim().toLowerCase() === INSERT_DEMAND_TASK_KEY_MODE_CUSTOM
    ? INSERT_DEMAND_TASK_KEY_MODE_CUSTOM
    : INSERT_DEMAND_TASK_KEY_MODE_PRESET
);

const normalizeInsertDemandOperation = (value) => (
  String(value || '').trim().toLowerCase() === 'move' ? 'move' : 'copy'
);

const API_DATE_DIRECTION_OPTIONS = [
  { value: 'past', label: 'Atrás' },
  { value: 'future', label: 'À frente' }
];

const API_DATE_UNIT_OPTIONS = [
  { value: 'minutes', label: 'Minutos' },
  { value: 'hours', label: 'Horas' },
  { value: 'days', label: 'Dias' }
];

const API_DATE_UNIT_MS = Object.freeze({
  minutes: 60 * 1000,
  hours: 60 * 60 * 1000,
  days: 24 * 60 * 60 * 1000
});

const normalizeApiDateBindingDirection = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (['future', 'ahead', 'forward', 'frente', 'futuro'].includes(normalized)) return 'future';
  return 'past';
};

const normalizeApiDateBindingUnit = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (['day', 'days', 'dia', 'dias'].includes(normalized)) return 'days';
  if (['hour', 'hours', 'hora', 'horas'].includes(normalized)) return 'hours';
  return 'minutes';
};

const normalizeApiDateBindingAmount = (value, fallback = 10) => {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed < 0) return fallback;
  return Math.min(parsed, 525600);
};

const defaultApiDateBindingConfig = () => ({
  amount: 10,
  unit: 'minutes',
  direction: 'past'
});

const normalizeApiDateBindingConfig = (value = {}) => {
  const source = value && typeof value === 'object' && !Array.isArray(value)
    ? value
    : {};
  const defaults = defaultApiDateBindingConfig();
  return {
    amount: normalizeApiDateBindingAmount(
      source.amount ?? source.value ?? source.offset,
      defaults.amount
    ),
    unit: normalizeApiDateBindingUnit(source.unit || defaults.unit),
    direction: normalizeApiDateBindingDirection(source.direction || defaults.direction)
  };
};

const apiDateUnitLabel = (unit, amount = 0) => {
  const normalizedUnit = normalizeApiDateBindingUnit(unit);
  const normalizedAmount = normalizeApiDateBindingAmount(amount, 0);
  if (normalizedUnit === 'days') return normalizedAmount === 1 ? 'dia' : 'dias';
  if (normalizedUnit === 'hours') return normalizedAmount === 1 ? 'hora' : 'horas';
  return normalizedAmount === 1 ? 'minuto' : 'minutos';
};

const apiDateDirectionLabel = (direction) => (
  normalizeApiDateBindingDirection(direction) === 'future' ? 'à frente' : 'atrás'
);

const apiDateBindingSummary = (config = {}) => {
  const normalized = normalizeApiDateBindingConfig(config);
  return `${normalized.amount} ${apiDateUnitLabel(normalized.unit, normalized.amount)} ${apiDateDirectionLabel(normalized.direction)}`;
};

const resolveApiDateBindingValue = (config = {}, now = new Date()) => {
  const normalized = normalizeApiDateBindingConfig(config);
  const multiplier = normalized.direction === 'future' ? 1 : -1;
  const offsetMs = normalized.amount * (API_DATE_UNIT_MS[normalized.unit] || API_DATE_UNIT_MS.minutes) * multiplier;
  const date = new Date(now.getTime() + offsetMs);
  return {
    normalized,
    date,
    iso: date.toISOString()
  };
};

const formatApiDatePreview = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '--';
  return date.toLocaleString('pt-BR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
};

const RelativeDateTrigger = ({ config = {}, onConfigure, disabled = false }) => {
  const summary = apiDateBindingSummary(config);
  return (
    <button
      type="button"
      className="relative-date-trigger"
      onClick={onConfigure}
      disabled={disabled}
      aria-label={`Configurar data relativa (${summary})`}
    >
      <span className="relative-date-trigger-icon material-symbols-rounded" aria-hidden="true">event_repeat</span>
      <span className="relative-date-trigger-value">{summary}</span>
      <span className="relative-date-trigger-edit material-symbols-rounded" aria-hidden="true">tune</span>
    </button>
  );
};

const RelativeDateModal = ({
  open = false,
  title = 'Configurar data relativa',
  description = 'Defina o deslocamento em minutos, horas ou dias para calcular a data em tempo de execução.',
  draft = {},
  onDraftChange,
  onCancel,
  onApply
}) => {
  if (!open || typeof document === 'undefined') return null;
  const activeBinding = normalizeApiDateBindingConfig(draft);
  const now = new Date();
  const preview = resolveApiDateBindingValue(activeBinding, now);
  const patch = (partial) => {
    if (typeof onDraftChange === 'function') onDraftChange({ ...draft, ...partial });
  };
  return createPortal(
    <div className="flow-modal-backdrop" role="presentation" onClick={onCancel}>
      <section
        className="flow-modal api-date-binding-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="flow-modal-head">
          <div className="flow-modal-icon tone-blue">
            <span className="material-symbols-rounded" aria-hidden="true">schedule</span>
          </div>
          <div>
            <span>Data relativa</span>
            <h3>{title}</h3>
            <p>{description}</p>
          </div>
          <button className="icon-button" type="button" onClick={onCancel} aria-label="Fechar modal">
            <span className="material-symbols-rounded" aria-hidden="true">close</span>
          </button>
        </header>

        <div className="flow-modal-body api-date-binding-body">
          <div className="api-date-binding-grid">
            <label className="modal-field">
              <FieldLabel>Quantidade</FieldLabel>
              <input
                type="number"
                min={0}
                max={525600}
                step={1}
                value={String(draft.amount ?? '')}
                onChange={(event) => patch({ amount: event.target.value })}
              />
            </label>
            <label className="modal-field">
              <FieldLabel>Unidade</FieldLabel>
              <select value={activeBinding.unit} onChange={(event) => patch({ unit: event.target.value })}>
                {API_DATE_UNIT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label className="modal-field">
              <FieldLabel>Direção</FieldLabel>
              <select value={activeBinding.direction} onChange={(event) => patch({ direction: event.target.value })}>
                {API_DATE_DIRECTION_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
          </div>

          <section className="api-date-binding-preview">
            <span className="api-date-binding-preview-head">
              <span className="material-symbols-rounded" aria-hidden="true">event_available</span>
              {apiDateBindingSummary(activeBinding)}
            </span>
            <div>
              <p>{`Agora: ${formatApiDatePreview(now)}`}</p>
              <p>{`Resultado: ${formatApiDatePreview(preview.date)}`}</p>
            </div>
          </section>
        </div>

        <footer className="flow-modal-footer">
          <button className="secondary-button" type="button" onClick={onCancel}>Cancelar</button>
          <button
            className="primary-button"
            type="button"
            onClick={() => onApply(normalizeApiDateBindingConfig(draft))}
          >
            Aplicar data
          </button>
        </footer>
      </section>
    </div>,
    document.body
  );
};

const bindingSourceOptions = (sources = [], taskSourceLabel = 'Atributos da Task') => [
  { value: 'task', label: taskSourceLabel },
  { value: 'literal', label: 'Texto personalizado' },
  { value: 'date', label: 'Data relativa' },
  ...sources.map((source) => ({
    value: `api:${source.nodeId}`,
    label: source.title || source.integrationName || 'Comando antecessor'
  }))
];

const chatVoiceBindingSourceOptions = (sources = [], taskSourceLabel = 'Atributos da Task') => [
  { value: 'task', label: taskSourceLabel },
  { value: 'literal', label: 'Texto personalizado' },
  ...sources.map((source) => ({
    value: `api:${source.nodeId}`,
    label: source.title || source.integrationName || 'Comando antecessor'
  }))
];

const parseCommandOutputBindingPath = (value = '') => {
  const normalized = String(value || '').trim();
  const match = normalized.match(/^outputs\.([^.]+)\.(.+)$/);
  if (!match?.[1] || !match?.[2]) return null;
  return {
    sourceNodeId: String(match[1]).trim(),
    path: String(match[2]).trim()
  };
};

const defaultChatVoiceBinding = (taskOptions = [], sources = []) => {
  const task = taskOptions[0];
  if (task) {
    return {
      sourceType: 'task',
      path: String(task.value || '').trim()
    };
  }
  const apiBinding = firstApiBinding(sources);
  if (apiBinding) return apiBinding;
  return {
    sourceType: 'task',
    path: ''
  };
};

const normalizeChatVoiceBinding = (binding, taskOptions = [], sources = []) => {
  if (!binding || (typeof binding !== 'object' || Array.isArray(binding))) {
    const legacyPath = normalizeTaskSelectorBindingPath(binding || '');
    if (legacyPath) {
      const outputBinding = parseCommandOutputBindingPath(legacyPath);
      if (outputBinding) {
        const source = sources.find((item) => String(item.nodeId) === outputBinding.sourceNodeId) || {};
        return {
          sourceType: 'api',
          sourceNodeId: outputBinding.sourceNodeId,
          integrationId: String(source.integrationId || ''),
          path: outputBinding.path,
          arrayMatchByPath: {}
        };
      }
      return {
        sourceType: 'task',
        path: legacyPath,
        arrayMatchByPath: {}
      };
    }
    return defaultChatVoiceBinding(taskOptions, sources);
  }

  const sourceType = String(binding.sourceType || binding.source_type || '').trim().toLowerCase();
  if (sourceType === 'literal') {
    return {
      sourceType: 'literal',
      literalValue: Object.prototype.hasOwnProperty.call(binding, 'literalValue')
        ? binding.literalValue
        : (binding.literal_value ?? '')
    };
  }

  if (sourceType === 'api') {
    const sourceNodeId = String(binding.sourceNodeId || binding.source_node_id || '').trim();
    const source = sources.find((item) => String(item.nodeId) === sourceNodeId) || null;
    const fallbackSource = sources.find((item) => Array.isArray(item.variables) && item.variables.length) || source;
    const defaultPath = source?.variables?.[0]?.value || fallbackSource?.variables?.[0]?.value || '';
    return {
      sourceType: 'api',
      sourceNodeId: sourceNodeId || fallbackSource?.nodeId || '',
      integrationId: String(binding.integrationId || binding.integration_id || source?.integrationId || fallbackSource?.integrationId || ''),
      path: String(binding.path || binding.variable || defaultPath || '').trim(),
      arrayMatchByPath: normalizeConditionArrayMatches(binding.arrayMatchByPath)
    };
  }

  const taskPath = normalizeTaskSelectorBindingPath(binding.path || binding.attribute || binding.value || '');
  if (taskPath) {
    const outputBinding = parseCommandOutputBindingPath(taskPath);
    if (outputBinding) {
      const source = sources.find((item) => String(item.nodeId) === outputBinding.sourceNodeId) || {};
      return {
        sourceType: 'api',
        sourceNodeId: outputBinding.sourceNodeId,
        integrationId: String(source.integrationId || ''),
        path: outputBinding.path,
        arrayMatchByPath: normalizeConditionArrayMatches(binding.arrayMatchByPath)
      };
    }
  }

  return {
    sourceType: 'task',
    path: taskPath,
    arrayMatchByPath: normalizeConditionArrayMatches(binding.arrayMatchByPath)
  };
};

const chatVoiceBindingHasValue = (binding = {}) => {
  const sourceType = String(binding?.sourceType || '').trim().toLowerCase();
  if (sourceType === 'literal') {
    return mappingHasLiteralValue(binding);
  }
  return Boolean(String(binding?.path || '').trim());
};

const chatVoiceBindingSourceValue = (binding = {}) => {
  const sourceType = String(binding?.sourceType || '').trim().toLowerCase();
  if (sourceType === 'api') return `api:${String(binding?.sourceNodeId || '').trim()}`;
  if (sourceType === 'literal') return 'literal';
  return 'task';
};

const nextChatVoiceBindingForSource = ({ sourceValue = '', previousBinding = {}, taskOptions = [], sources = [] } = {}) => {
  if (sourceValue === 'literal') {
    return {
      sourceType: 'literal',
      literalValue: Object.prototype.hasOwnProperty.call(previousBinding || {}, 'literalValue')
        ? previousBinding.literalValue
        : (previousBinding.literal_value ?? '')
    };
  }

  if (sourceValue === 'task') {
    return {
      sourceType: 'task',
      path: previousBinding.sourceType === 'task'
        ? String(previousBinding.path || '')
        : String(taskOptions[0]?.value || '')
    };
  }

  const sourceNodeId = String(sourceValue || '').replace(/^api:/, '').trim();
  const source = sources.find((item) => String(item.nodeId) === sourceNodeId);
  const firstVariable = source?.variables?.[0];
  return {
    sourceType: 'api',
    sourceNodeId,
    integrationId: source?.integrationId || '',
    path: (
      previousBinding.sourceType === 'api' && String(previousBinding.sourceNodeId || '') === sourceNodeId
        ? String(previousBinding.path || '')
        : (firstVariable?.value || '')
    )
  };
};

const demandTemplateVariableOptions = () => [
  { value: 'demand.id', label: 'Demanda: id (string)', type: 'string', group: 'configured' },
  { value: 'demand.name', label: 'Demanda: nome (string)', type: 'string', group: 'configured' },
  { value: 'demand.protocol', label: 'Demanda: protocolo (string)', type: 'string', group: 'configured' },
  { value: 'demand.process', label: 'Demanda: processo (string)', type: 'string', group: 'configured' }
];

const templateSuggestSourceGroups = ({
  taskLabel = 'Task',
  taskVariables = [],
  includeDemand = false,
  apiSources = []
} = {}) => {
  const groups = [{
    key: 'task',
    label: taskLabel,
    variables: Array.isArray(taskVariables) ? taskVariables : []
  }];
  if (includeDemand) {
    groups.push({
      key: 'demand',
      label: 'Dados da Demanda',
      variables: demandTemplateVariableOptions()
    });
  }
  (Array.isArray(apiSources) ? apiSources : []).forEach((source) => {
    const sourceNodeId = String(source?.nodeId || '').trim();
    if (!sourceNodeId) return;
    const mappedVariables = (Array.isArray(source?.variables) ? source.variables : []).map((variable) => {
      const variablePath = String(variable?.value || '').trim();
      if (!variablePath) return null;
      return {
        ...variable,
        value: `outputs.${sourceNodeId}.${variablePath}`,
        label: String(variable?.displayName || variable?.label || variablePath).trim() || variablePath,
        displayName: String(variable?.displayName || variablePath).trim() || variablePath
      };
    }).filter(Boolean);
    groups.push({
      key: `api:${sourceNodeId}`,
      label: source.title || source.integrationName || `Comando ${sourceNodeId}`,
      variables: mappedVariables
    });
  });
  return groups;
};

const VOICE_DIAL_AT_SOURCE_OPTIONS = [
  { value: '', label: 'Sem agendamento' },
  { value: 'task', label: 'Atributo da Task' },
  { value: 'date', label: 'Data relativa' }
];

const firstApiBinding = (sources = []) => {
  const source = sources.find((item) => item.variables?.length);
  const variable = source?.variables?.[0];
  if (!source || !variable) return null;
  return {
    sourceType: 'api',
    sourceNodeId: source.nodeId,
    integrationId: source.integrationId || '',
    path: variable.value
  };
};

const defaultApiVariableBinding = (taskOptions = [], sources = []) => {
  const task = taskOptions[0];
  if (task) {
    return {
      sourceType: 'task',
      path: task.value
    };
  }
  return firstApiBinding(sources) || {
    sourceType: 'task',
    path: ''
  };
};

const normalizeApiVariableBinding = (binding, taskOptions = [], sources = []) => {
  if (!binding || typeof binding !== 'object' || Array.isArray(binding)) {
    return defaultApiVariableBinding(taskOptions, sources);
  }

  const sourceType = String(binding.sourceType || '').trim().toLowerCase();
  if (sourceType === 'date') {
    return {
      sourceType: 'date',
      dateConfig: normalizeApiDateBindingConfig(binding.dateConfig || binding.date_config || {})
    };
  }

  if (sourceType === 'literal') {
    return {
      sourceType: 'literal',
      literalValue: Object.prototype.hasOwnProperty.call(binding, 'literalValue')
        ? binding.literalValue
        : (binding.literal_value ?? '')
    };
  }

  if (sourceType === 'api') {
    const sourceNodeId = String(binding.sourceNodeId || '').trim();
    const source = sources.find((item) => String(item.nodeId) === sourceNodeId) || null;
    const fallbackSource = sources.find((item) => Array.isArray(item.variables) && item.variables.length) || source;
    const defaultPath = source?.variables?.[0]?.value || fallbackSource?.variables?.[0]?.value || '';
    return {
      sourceType: 'api',
      sourceNodeId: sourceNodeId || fallbackSource?.nodeId || '',
      integrationId: String(binding.integrationId || source?.integrationId || fallbackSource?.integrationId || ''),
      path: String(binding.path || binding.variable || defaultPath || '').trim(),
      arrayMatchByPath: normalizeConditionArrayMatches(binding.arrayMatchByPath)
    };
  }

  if (sourceType === 'aggregate') {
    const legacyPath = String(binding.path || binding.variable || '').trim();
    const [sourceNodeId, ...segments] = legacyPath.split('.');
    const source = sources.find((item) => String(item.nodeId) === String(sourceNodeId));
    const legacyApiPath = segments.join('.');
    if (source && legacyApiPath) {
      return {
        sourceType: 'api',
        sourceNodeId: source.nodeId,
        integrationId: source.integrationId || '',
        path: legacyApiPath
      };
    }
    return defaultApiVariableBinding(taskOptions, sources);
  }

  return {
    sourceType: 'task',
    path: String(binding.path || binding.attribute || '').trim(),
    arrayMatchByPath: normalizeConditionArrayMatches(binding.arrayMatchByPath)
  };
};

const normalizeApiVariableBindings = (config = {}, integration = null, demand = null, sources = [], taskOptionsOverride = null) => {
  const variables = integrationTemplateVariables(integration);
  const taskOptions = Array.isArray(taskOptionsOverride)
    ? taskOptionsOverride
    : taskVariableOptions(demand, { includeTaskCommentSources: true });
  const current = config.variableBindings && typeof config.variableBindings === 'object' && !Array.isArray(config.variableBindings)
    ? config.variableBindings
    : {};
  const variableBindings = variables.reduce((acc, name) => {
    acc[name] = normalizeApiVariableBinding(current[name], taskOptions, sources);
    return acc;
  }, {});
  return {
    ...config,
    requestVariables: variables,
    variableBindings
  };
};

const StartFilterBuilder = ({ config, demand, taskExtraVariables = [], canEdit, onChange }) => {
  const filters = Array.isArray(config.filters) && config.filters.length ? config.filters : [makeFilterRule()];
  const taskVariables = taskVariableOptions(demand, {
    extraVariables: taskExtraVariables,
    includeTaskCommentSources: true
  });
  const [dateFilterModal, setDateFilterModal] = useState(null);
  const [dateFilterDraft, setDateFilterDraft] = useState(defaultApiDateBindingConfig());
  const openDateFilterModal = (filter) => {
    setDateFilterDraft(normalizeApiDateBindingConfig(filter?.dateConfig || filter?.date_config || {}));
    setDateFilterModal({ id: filter.id });
  };
  const updateConfig = (patch) => onChange({ ...config, ...patch });
  const updateFilter = (id, patch) => {
    updateConfig({
      filters: filters.map((filter) => {
        if (filter.id !== id) return filter;
        const nextSource = patch.source ?? filter.source ?? 'status';
        const sourceChanged = nextSource !== filter.source;
        const nextAttribute = nextSource === 'status'
          ? 'status'
          : sourceChanged
            ? ''
            : normalizeConditionVariablePath(patch.attribute ?? filter.attribute);
        const currentArrayMatches = normalizeConditionArrayMatches(
          patch.arrayMatchByPath ?? filter.arrayMatchByPath
        );
        const currentArrayNavigations = normalizeConditionArrayNavigations(
          patch.arrayNavigationByPath ?? filter.arrayNavigationByPath,
          currentArrayMatches
        );
        const validArrayPaths = nextSource === 'attribute'
          ? conditionArrayPathsForVariable(taskVariables, nextAttribute)
          : [];
        const arrayMatchByPath = validArrayPaths.reduce((acc, path) => {
          acc[path] = currentArrayNavigations[path]?.mode || currentArrayMatches[path] || 'any_equals';
          return acc;
        }, {});
        const arrayNavigationByPath = validArrayPaths.reduce((acc, path) => {
          acc[path] = normalizeArrayNavigation(currentArrayNavigations[path], arrayMatchByPath[path]);
          return acc;
        }, {});

        return {
          ...filter,
          ...patch,
          source: nextSource,
          attribute: nextAttribute,
          value: nextSource === 'status'
            ? (sourceChanged ? 'Pendente' : patch.value ?? filter.value ?? 'Pendente')
            : sourceChanged
              ? ''
              : patch.value ?? filter.value,
          arrayMatchByPath,
          arrayNavigationByPath
        };
      })
    });
  };
  const removeFilter = (id) => {
    updateConfig({ filters: filters.length > 1 ? filters.filter((filter) => filter.id !== id) : filters });
  };
  const addFilter = () => updateConfig({ filters: filters.concat(makeFilterRule()) });

  return (
    <section className="start-filter-builder modal-field-wide">
      <header className="start-filter-head">
        <div>
          <FieldLabel>Filtro</FieldLabel>
          <strong>Seleção composta de tasks</strong>
        </div>
        <label>
          <FieldLabel>Operador entre regras</FieldLabel>
          <select value={config.filterLogic || 'AND'} onChange={(event) => updateConfig({ filterLogic: event.target.value })} disabled={!canEdit}>
            <option value="AND">Todas as regras (AND)</option>
            <option value="OR">Qualquer regra (OR)</option>
          </select>
        </label>
      </header>
      <div className="start-filter-table" role="table" aria-label="Filtros de tasks">
        <div className="start-filter-row is-head" role="row">
          <span>Origem</span>
          <span>Atributo</span>
          <span>Operador</span>
          <span>Valor</span>
          <span></span>
        </div>
        {filters.map((filter) => {
          const source = filter.source || 'status';
          const attributeType = source === 'attribute'
            ? (taskVariables.find((option) => String(option.value) === String(filter.attribute || ''))?.type || '')
            : '';
          const isDateField = source === 'created_at'
            || source === 'updated_at'
            || isDateLikeSchemaType(attributeType);
          return (
            <div className="start-filter-row" role="row" key={filter.id}>
              <select value={source} onChange={(event) => updateFilter(filter.id, { source: event.target.value })} disabled={!canEdit}>
                {filterSourceOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
              {source === 'status' ? (
                <input value="status" disabled />
              ) : source === 'attribute' ? (
                <ContextVariablePathSelector
                  variables={taskVariables}
                  binding={{
                    path: filter.attribute || '',
                    arrayMatchByPath: filter.arrayMatchByPath || {},
                    arrayNavigationByPath: filter.arrayNavigationByPath || {}
                  }}
                  advancedArrayNavigation
                  canEdit={canEdit}
                  emptyLabel="Sem atributos disponíveis"
                  containerClassName="condition-variable-selectors start-filter-variable-selectors"
                  controlClassName="condition-variable-selector-control start-filter-variable-selector-control"
                  arrayPathFallbackLabel="atributo da task"
                  onChange={(binding) => updateFilter(filter.id, {
                    attribute: binding.path,
                    arrayMatchByPath: binding.arrayMatchByPath,
                    arrayNavigationByPath: binding.arrayNavigationByPath
                  })}
                />
              ) : (
                <input value={source === 'status' ? 'status' : filter.attribute || source} disabled />
              )}
              <select value={filter.operator || 'equals'} onChange={(event) => updateFilter(filter.id, { operator: event.target.value })} disabled={!canEdit}>
                {filterOperatorOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
              {source === 'status' ? (
                <select value={filter.value || 'Pendente'} onChange={(event) => updateFilter(filter.id, { value: event.target.value })} disabled={!canEdit || ['exists', 'empty'].includes(filter.operator)}>
                  {demandStatusOptions(demand).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
              ) : isDateField ? (
                <div className="api-variable-date-cell">
                  <RelativeDateTrigger
                    config={filter.dateConfig}
                    onConfigure={() => openDateFilterModal(filter)}
                    disabled={!canEdit || ['exists', 'empty'].includes(filter.operator)}
                  />
                </div>
              ) : (
                <input
                  value={filter.value || ''}
                  onChange={(event) => updateFilter(filter.id, { value: event.target.value })}
                  disabled={!canEdit || ['exists', 'empty'].includes(filter.operator)}
                  placeholder="Valor"
                />
              )}
              <button className="icon-button" type="button" onClick={() => removeFilter(filter.id)} disabled={!canEdit || filters.length <= 1} aria-label="Remover filtro">
                <span className="material-symbols-rounded" aria-hidden="true">delete</span>
              </button>
            </div>
          );
        })}
      </div>
      <button className="secondary-button compact-action" type="button" onClick={addFilter} disabled={!canEdit}>
        <span className="material-symbols-rounded" aria-hidden="true">add</span>
        Adicionar filtro
      </button>
      <RelativeDateModal
        open={Boolean(dateFilterModal)}
        title="Configurar data do filtro"
        description="A data é calculada em tempo de execução, relativa ao momento de cada rodada do roteamento."
        draft={dateFilterDraft}
        onDraftChange={setDateFilterDraft}
        onCancel={() => setDateFilterModal(null)}
        onApply={(nextConfig) => {
          if (dateFilterModal) {
            updateFilter(dateFilterModal.id, { dateConfig: nextConfig, value: '' });
          }
          setDateFilterModal(null);
        }}
      />
    </section>
  );
};

const upstreamNodeIds = (nodeId, edges = []) => {
  const parentsByTarget = new Map();
  edges.forEach((edge) => {
    const target = String(edge.target);
    const source = String(edge.source);
    if (!parentsByTarget.has(target)) parentsByTarget.set(target, new Set());
    parentsByTarget.get(target).add(source);
  });

  const visited = new Set();
  const queue = [...(parentsByTarget.get(String(nodeId)) || [])];
  while (queue.length) {
    const current = queue.shift();
    if (visited.has(current)) continue;
    visited.add(current);
    (parentsByTarget.get(current) || []).forEach((parent) => queue.push(parent));
  }
  return visited;
};

const downstreamNodeIdsIncludingSelf = (nodeId, edges = []) => {
  const childrenBySource = new Map();
  edges.forEach((edge) => {
    const source = String(edge.source);
    const target = String(edge.target);
    if (!childrenBySource.has(source)) childrenBySource.set(source, new Set());
    childrenBySource.get(source).add(target);
  });

  const visited = new Set();
  const queue = [String(nodeId)];
  while (queue.length) {
    const current = queue.shift();
    if (visited.has(current)) continue;
    visited.add(current);
    (childrenBySource.get(current) || []).forEach((child) => queue.push(child));
  }
  return visited;
};

const conditionSelectedOutputHandle = (node = {}, output = {}) => {
  const selectedHandle = String(
    output?.selected_handle
    || output?.selectedHandle
    || output?.selected_path_handle
    || output?.selectedPathHandle
    || ''
  ).trim();
  if (selectedHandle) return selectedHandle;
  if (typeof output?.passed === 'boolean') return output.passed ? 'output_1' : 'output_2';
  const definitions = conditionOutputDefinitions(node);
  return definitions[0]?.handle || 'output_1';
};

const inspectionConditionBranches = (node = {}, output = {}, edges = []) => {
  const nodeId = node?.id;
  const expectedOutput = conditionSelectedOutputHandle(node, output);
  const outgoing = (Array.isArray(edges) ? edges : [])
    .filter((edge) => String(edge?.source) === String(nodeId));
  const selectedRoots = outgoing
    .filter((edge) => edgeOutputHandle(edge, node) === expectedOutput)
    .map((edge) => String(edge.target));
  const rejectedRoots = outgoing
    .filter((edge) => edgeOutputHandle(edge, node) !== expectedOutput)
    .map((edge) => String(edge.target));
  const selectedNodeIds = selectedRoots.reduce((acc, targetId) => {
    downstreamNodeIdsIncludingSelf(targetId, edges).forEach((id) => acc.add(String(id)));
    return acc;
  }, new Set());
  const blockedNodeIds = rejectedRoots.reduce((acc, targetId) => {
    downstreamNodeIdsIncludingSelf(targetId, edges).forEach((id) => {
      if (!selectedNodeIds.has(String(id))) acc.add(String(id));
    });
    return acc;
  }, new Set());

  return { expectedOutput, selectedNodeIds, blockedNodeIds };
};

const commandOutputSourceVariables = ({ schema = {}, runtimeOutput = null } = {}) => (
  mergeSourceVariableOptions({ schema, runtimeOutput })
);

const commandAncestorOutputSources = ({ nodeId, nodes = [], edges = [], apis = [], runtimeOutputs = {} }) => {
  const ancestorIds = upstreamNodeIds(nodeId, edges);
  const startNode = nodes.find((candidate) => isStartNode(candidate)) || null;
  const executionStartSource = (() => {
    if (!startNode || String(startNode.id) === String(nodeId)) return null;
    const normalizedStartConfig = normalizeStartConfig(startNode.data?.config || {});
    if (!isSearchStartConfig(normalizedStartConfig)) return null;
    const integrationId = normalizedStartConfig.executionIntegrationId;
    const integration = apis.find((item) => String(item.id) === String(integrationId));
    const runtimeOutput = runtimeOutputs?.[String(startNode.id)] || null;
    const integrationSchema = normalizeResponseSchema(integration?.response_schema);
    const savedStartSchema = normalizeResponseSchema(normalizedStartConfig.executionResponseSchema);
    const learnedStartSchema = hasCurrentInspectionContract(normalizedStartConfig)
      ? learnedResponseSchemaFromConfig(normalizedStartConfig)
      : {};
    const runtimeSchema = schemaFromRuntimeOutput(runtimeOutput, { includeDeclaredSchema: false });
    const observedSchema = mergeResponseSchemas(learnedStartSchema, runtimeSchema);
    const configuredSchema = mergeResponseSchemas(
      integrationSchema,
      savedStartSchema
    );
    const schema = Object.keys(observedSchema).length ? observedSchema : configuredSchema;
    return {
      nodeId: startNode.id,
      title: 'Início - Buscar',
      subtitle: startNode.data?.subtitle || integration?.name || 'Sem integração selecionada',
      integrationId: integrationId || '',
      integrationName: normalizedStartConfig.executionIntegrationName || integration?.name || '',
      responsePath: normalizedStartConfig.executionRecordPath || '$',
      schema,
      sourceType: 'api',
      commandType: 'start',
      variables: normalizeExecutionStartVariableOptions(
        commandOutputSourceVariables({ schema, runtimeOutput }),
        normalizedStartConfig.executionRecordPath
      )
    };
  })();

  const ancestorSources = nodes
    .filter((candidate) => {
      if (isStartNode(candidate)) return false;
      if (!ancestorIds.has(String(candidate.id))) return false;
      const commandType = String(candidate.data?.commandType || '').toLowerCase();
      return [
        'api',
        'chat',
        'voice',
        'cortex',
        'condition',
        'saveinfo',
        'insertdemand',
        'delay'
      ].includes(commandType);
    })
    .map((candidate) => {
      const commandType = String(candidate.data?.commandType || '').toLowerCase();
      const runtimeOutput = runtimeOutputs?.[String(candidate.id)] || null;
      const identity = deriveCommandIdentity(commandType, candidate.data?.config || {});
      const integrationId = candidate.data?.config?.integrationId;
      const integration = apis.find((item) => String(item.id) === String(integrationId));
      const schema = knownCommandOutputSchema({
        commandType,
        config: candidate.data?.config || {},
        demand: {},
        integration,
        runtimeOutput
      });
      return {
        nodeId: candidate.id,
        title: candidate.data?.customName || identity?.title || candidate.data?.title || commandForType(commandType).label,
        subtitle: candidate.data?.subtitle || identity?.subtitle || commandForType(commandType).description,
        integrationId: integrationId || candidate.data?.config?.resourceCod || commandType,
        integrationName: candidate.data?.config?.integrationName || integration?.name || commandForType(commandType).label,
        responsePath: '$',
        schema,
        sourceType: 'api',
        commandType,
        variables: commandOutputSourceVariables({ schema, runtimeOutput })
      };
    });

  if (executionStartSource) {
    return [executionStartSource, ...ancestorSources];
  }

  return ancestorSources;
};

const apiAncestorSources = (params = {}) => commandAncestorOutputSources(params);

const CHAT_INFO_CUSTOM_ORIGIN_OPTIONS = [
  { value: 'task', label: 'Task' },
  { value: 'demand', label: 'Demanda' },
  { value: 'date', label: 'Data relativa' }
];

const makeChatInfoCustomMapping = (sources = []) => {
  const source = sources.find((item) => item.variables?.length) || sources[0] || {};
  const sourceNodeId = String(source.nodeId || '').trim();
  if (!sourceNodeId) {
    return {
      id: `chat-context-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      sourceType: 'task',
      sourceNodeId: '',
      path: '',
      useAs: '',
      arrayMatchByPath: {},
      arrayNavigationByPath: {}
    };
  }
  return {
    id: `chat-context-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    sourceType: 'api',
    sourceNodeId,
    path: String(source.variables?.[0]?.value || '').trim(),
    useAs: '',
    arrayMatchByPath: {},
    arrayNavigationByPath: {}
  };
};

const normalizeChatInfoCustomSourceType = (mapping = {}) => {
  const sourceType = String(mapping?.sourceType || mapping?.source_type || '').trim().toLowerCase();
  if (['task', 'demand', 'date', 'api'].includes(sourceType)) return sourceType;
  if (String(mapping?.sourceNodeId || mapping?.source_node_id || '').trim()) return 'api';
  return 'task';
};

const normalizeChatInfoCustomConfig = (config = {}, sources = []) => {
  const rawMappings = Array.isArray(config.infoCustomMappings)
    ? config.infoCustomMappings
    : Array.isArray(config.info_custom_mappings)
      ? config.info_custom_mappings
      : [];
  const mappings = rawMappings.map((mapping, index) => {
    const sourceType = normalizeChatInfoCustomSourceType(mapping);
    const sourceNodeId = sourceType === 'api'
      ? String(mapping?.sourceNodeId || mapping?.source_node_id || '').trim()
      : '';
    const rawPath = String(mapping?.path || mapping?.attribute || '').trim();
    const normalizedRawPath = sourceType === 'task'
      ? normalizeChatInfoCustomTaskPath(rawPath)
      : rawPath;
    const path = sourceType === 'date' && !rawPath
      ? String(DATE_TEMPLATE_VARIABLE_OPTIONS[0]?.value || 'date.now_iso').trim()
      : normalizedRawPath;
    const useAs = String(mapping?.useAs || mapping?.use_as || mapping?.name || '').trim();
    const arrayMatchByPath = normalizeConditionArrayMatches(mapping?.arrayMatchByPath || mapping?.array_match_by_path || {});
    return {
      id: String(mapping?.id || `chat-context-legacy-${index}`).trim() || `chat-context-legacy-${index}`,
      sourceType,
      sourceNodeId,
      path,
      useAs,
      arrayMatchByPath,
      arrayNavigationByPath: normalizeConditionArrayNavigations(
        mapping?.arrayNavigationByPath || mapping?.array_navigation_by_path || {},
        arrayMatchByPath
      )
    };
  }).filter((mapping) => {
    if (!mapping.sourceType) return false;
    if (mapping.sourceType !== 'api') return true;
    return Boolean(mapping.sourceNodeId);
  });
  return {
    ...config,
    infoCustomMappings: mappings.length ? mappings : [makeChatInfoCustomMapping(sources)]
  };
};

const chatInfoCustomPreviewDateSource = (now = new Date()) => ({
  now_iso: now.toISOString(),
  today_iso: now.toISOString().slice(0, 10),
  now_unix_ms: now.getTime(),
  now_unix_s: Math.floor(now.getTime() / 1000),
  now_br: now.toLocaleString('pt-BR', { hour12: false })
});

const chatInfoCustomPreviewOutputPayload = (output = null) => {
  if (!output || typeof output !== 'object') return {};
  if (Object.prototype.hasOwnProperty.call(output, 'body_json')) {
    const bodyJson = output.body_json;
    if (bodyJson !== undefined && bodyJson !== null) return bodyJson;
  }
  if (typeof output.body === 'string') {
    return parseJsonLikeValue(output.body);
  }
  if (output.body !== undefined) return output.body;
  return output;
};

const chatInfoCustomPreviewTaskPayload = (task = {}) => {
  if (isPlainObjectValue(task?.payload)) return task.payload;
  if (isPlainObjectValue(task?.task_payload)) return task.task_payload;
  return {};
};

const chatInfoCustomDemandPreviewSubset = (demand = {}) => {
  const source = isPlainObjectValue(demand) ? demand : {};
  return {
    id: source.id,
    name: source.name,
    protocol: source.protocol,
    process: source.process
  };
};

const chatInfoCustomTaskFilePreviewUrl = (value = {}) => {
  if (!isPlainObjectValue(value)) return '';
  return String(value.url || value.content_url || value.contentUrl || '').trim();
};

const chatInfoCustomTaskPayloadPreviewValue = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => chatInfoCustomTaskPayloadPreviewValue(item));
  }
  if (!isPlainObjectValue(value)) return value;

  const fileUrl = chatInfoCustomTaskFilePreviewUrl(value);
  if (fileUrl) return fileUrl;

  return Object.entries(value).reduce((acc, [key, entryValue]) => {
    acc[key] = chatInfoCustomTaskPayloadPreviewValue(entryValue);
    return acc;
  }, {});
};

const chatInfoCustomTaskPreviewSource = (task = {}) => {
  const taskObject = isPlainObjectValue(task) ? task : {};
  const payload = chatInfoCustomPreviewTaskPayload(taskObject);
  const sanitizedPayload = isPlainObjectValue(payload)
    ? chatInfoCustomTaskPayloadPreviewValue(payload)
    : {};
  const status = taskObject.status ?? payload?.status;
  if (status === undefined) return sanitizedPayload;
  return {
    ...sanitizedPayload,
    status
  };
};

const chatInfoCustomTaskPreviewHasPayload = (task = {}) => {
  const taskObject = isPlainObjectValue(task) ? task : {};
  const payload = chatInfoCustomPreviewTaskPayload(taskObject);
  return isPlainObjectValue(payload) && Object.keys(payload).length > 0;
};

const chatInfoCustomTaskOriginVariableOptions = (variables = []) => {
  return (Array.isArray(variables) ? variables : [])
    .filter((item) => !String(item?.value || '').trim().startsWith('demand.'))
    .filter((item) => !String(item?.value || '').trim().toLowerCase().endsWith('.binario'))
    .map((item) => ({
      ...item,
      label: String(item?.label || '').replace(/^Task:\s*/i, '').trim() || String(item?.value || '').trim()
    }));
};

const chatInfoCustomPreviewTaskFallbackObject = (taskVariables = []) => {
  return (Array.isArray(taskVariables) ? taskVariables : []).reduce((acc, variable) => {
    const rawPath = normalizeTaskSelectorBindingPath(variable?.value || '');
    if (!rawPath) return acc;
    if (rawPath.startsWith('demand.') || rawPath.startsWith('date.') || rawPath.startsWith('outputs.')) {
      return acc;
    }
    const effectivePath = rawPath.startsWith('task.')
      ? rawPath.slice('task.'.length)
      : rawPath.startsWith('payload.')
        ? rawPath.slice('payload.'.length)
        : rawPath;
    if (!effectivePath) return acc;
    return mergeChatInfoCustomPreviewValue(acc, effectivePath, `{{task.${effectivePath}}}`);
  }, {});
};

const chatInfoCustomMappingTargetPath = (mapping = {}) => {
  const explicitTarget = String(mapping?.useAs || mapping?.use_as || '').trim();
  if (explicitTarget) return explicitTarget;
  const rawPath = normalizeTaskSelectorBindingPath(mapping?.path || mapping?.attribute || '');
  if (!rawPath) return '';
  const parts = rawPath.split('.').map((part) => String(part || '').trim()).filter(Boolean);
  return parts[parts.length - 1] || '';
};

const chatInfoCustomMappingHasExplicitTarget = (mapping = {}) => {
  return String(mapping?.useAs || mapping?.use_as || '').trim() !== '';
};

const mergeChatInfoCustomPreviewLiteralKey = (target = {}, key = '', value) => {
  const literalKey = String(key || '').trim();
  if (!literalKey) return target;
  const next = isPlainObjectValue(target) ? JSON.parse(JSON.stringify(target)) : {};
  next[literalKey] = value;
  return next;
};

const chatInfoCustomPreviewApiFallbackObject = (sourceNodeId = '', context = {}) => {
  const normalizedSourceNodeId = String(sourceNodeId || '').trim();
  if (!normalizedSourceNodeId) return {};
  const source = (Array.isArray(context.apiSources) ? context.apiSources : [])
    .find((item) => String(item?.nodeId || '').trim() === normalizedSourceNodeId);
  const variables = Array.isArray(source?.variables) ? source.variables : [];
  return variables.reduce((acc, variable) => {
    const path = String(variable?.value || '').trim();
    if (!path) return acc;
    return mergeChatInfoCustomPreviewValue(acc, path, `{{outputs.${normalizedSourceNodeId}.${path}}}`);
  }, {});
};

const resolveChatInfoCustomPreviewTaskContextValue = (configuredPath = '', context = {}) => {
  const normalizedPath = normalizeTaskSelectorBindingPath(configuredPath);
  if (!normalizedPath) return undefined;

  const taskObject = isPlainObjectValue(context.task) ? context.task : {};
  const taskPayload = chatInfoCustomPreviewTaskPayload(taskObject);
  const demandObject = isPlainObjectValue(context.demand) ? context.demand : {};
  const dateObject = isPlainObjectValue(context.date)
    ? context.date
    : chatInfoCustomPreviewDateSource();
  const outputsObject = isPlainObjectValue(context.outputs) ? context.outputs : {};

  if (normalizedPath === 'comments' || normalizedPath === 'task.comments') {
    return taskObject.comments;
  }
  if (normalizedPath === 'last_comment' || normalizedPath === 'task.last_comment') {
    return taskObject.last_comment ?? taskObject.latest_comment;
  }

  if (normalizedPath === 'demand') return demandObject;
  if (normalizedPath.startsWith('demand.')) {
    return readPayloadPath(demandObject, normalizedPath.slice('demand.'.length));
  }

  if (normalizedPath === 'date') return dateObject;
  if (normalizedPath.startsWith('date.')) {
    return readPayloadPath({ date: dateObject }, normalizedPath);
  }

  if (normalizedPath === 'outputs') return outputsObject;
  if (normalizedPath.startsWith('outputs.')) {
    const [, sourceNodeId, ...remainingPath] = normalizedPath.split('.');
    const sourceOutput = outputsObject[String(sourceNodeId || '').trim()];
    if (!sourceOutput) return undefined;
    const sourcePayload = chatInfoCustomPreviewOutputPayload(sourceOutput);
    if (!remainingPath.length) return sourcePayload;
    return readPayloadPath(sourcePayload, remainingPath.join('.'));
  }

  if (normalizedPath === 'task') return taskObject;
  if (normalizedPath.startsWith('task.')) {
    return readPayloadPath(taskObject, normalizedPath.slice('task.'.length));
  }

  if (normalizedPath === 'payload') return taskPayload;
  if (normalizedPath.startsWith('payload.')) {
    return readPayloadPath(taskPayload, normalizedPath.slice('payload.'.length));
  }

  const payloadValue = readPayloadPath(taskPayload, normalizedPath);
  if (payloadValue !== undefined) return payloadValue;
  return readPayloadPath(taskObject, normalizedPath);
};

const mergeChatInfoCustomPreviewValue = (target = {}, configuredPath = '', value) => {
  const normalizedPath = normalizePayloadPath(normalizeTaskSelectorBindingPath(configuredPath));
  if (!normalizedPath) {
    if (isPlainObjectValue(value)) return { ...target, ...value };
    return target;
  }
  const parts = normalizedPath.split('.').map((part) => String(part || '').trim()).filter(Boolean);
  if (!parts.length) return target;

  const next = isPlainObjectValue(target) ? JSON.parse(JSON.stringify(target)) : {};
  let cursor = next;
  for (let index = 0; index < parts.length; index += 1) {
    const key = parts[index];
    if (index === parts.length - 1) {
      cursor[key] = value;
      break;
    }
    if (!isPlainObjectValue(cursor[key])) {
      cursor[key] = {};
    }
    cursor = cursor[key];
  }
  return next;
};

const resolveChatInfoCustomPreviewPathValue = (mapping = {}, context = {}) => {
  const sourceType = normalizeChatInfoCustomSourceType(mapping);
  const rawPath = String(mapping?.path || mapping?.attribute || '').trim();
  if (!rawPath) return undefined;

  if (sourceType === 'api') {
    const sourceNodeId = String(mapping?.sourceNodeId || mapping?.source_node_id || '').trim();
    if (!sourceNodeId) return undefined;
    const sourceOutput = context.outputs?.[sourceNodeId];
    if (!sourceOutput) return `{{outputs.${sourceNodeId}.${rawPath}}}`;
    return readPayloadPath(chatInfoCustomPreviewOutputPayload(sourceOutput), rawPath);
  }

  const normalizedPath = normalizeTaskSelectorBindingPath(rawPath);
  const contextualPath = sourceType === 'demand' && !normalizedPath.startsWith('demand.')
    ? `demand.${normalizedPath}`
    : sourceType === 'date' && !normalizedPath.startsWith('date.')
      ? `date.${normalizedPath}`
      : normalizedPath;
  const resolved = resolveChatInfoCustomPreviewTaskContextValue(contextualPath, context);
  if (resolved !== undefined) return resolved;
  if (sourceType === 'task') {
    return `{{task.${normalizedPath}}}`;
  }
  return resolved;
};

const resolveChatInfoCustomPreviewSourceObject = (mapping = {}, context = {}) => {
  const sourceType = normalizeChatInfoCustomSourceType(mapping);
  if (sourceType === 'task') {
    const sourceObject = chatInfoCustomTaskPreviewSource(context.task);
    if (chatInfoCustomTaskPreviewHasPayload(context.task)) return sourceObject;
    const fallbackObject = chatInfoCustomPreviewTaskFallbackObject(context.taskVariables);
    if (isPlainObjectValue(fallbackObject) && Object.keys(fallbackObject).length) return fallbackObject;
    return sourceObject;
  }
  if (sourceType === 'demand') {
    return chatInfoCustomDemandPreviewSubset(context.demand);
  }
  if (sourceType === 'date') {
    return isPlainObjectValue(context.date) ? context.date : chatInfoCustomPreviewDateSource();
  }
  if (sourceType === 'api') {
    const sourceNodeId = String(mapping?.sourceNodeId || mapping?.source_node_id || '').trim();
    if (!sourceNodeId) return {};
    const sourceOutput = context.outputs?.[sourceNodeId] || null;
    if (sourceOutput) return chatInfoCustomPreviewOutputPayload(sourceOutput);
    return chatInfoCustomPreviewApiFallbackObject(sourceNodeId, context);
  }
  return {};
};

const buildChatInfoCustomPreviewPayload = (mappings = [], context = {}) => {
  const normalizedMappings = Array.isArray(mappings) ? mappings : [];
  const previewContext = {
    task: isPlainObjectValue(context.task) ? context.task : {},
    demand: isPlainObjectValue(context.demand) ? context.demand : {},
    outputs: isPlainObjectValue(context.outputs) ? context.outputs : {},
    date: chatInfoCustomPreviewDateSource(),
    apiSources: Array.isArray(context.apiSources) ? context.apiSources : [],
    taskVariables: Array.isArray(context.taskVariables) ? context.taskVariables : []
  };
  return normalizedMappings.reduce((acc, mapping) => {
    const path = String(mapping?.path || mapping?.attribute || '').trim();
    const targetPath = chatInfoCustomMappingTargetPath(mapping);
    const hasExplicitTarget = chatInfoCustomMappingHasExplicitTarget(mapping);
    const sourceType = normalizeChatInfoCustomSourceType(mapping);
    if (!path) {
      const sourceObject = resolveChatInfoCustomPreviewSourceObject(mapping, previewContext);
      if (sourceType === 'task' && !hasExplicitTarget) {
        return mergeChatInfoCustomPreviewValue(acc, '', sourceObject);
      }
      if (hasExplicitTarget && targetPath) {
        return mergeChatInfoCustomPreviewLiteralKey(acc, targetPath, sourceObject);
      }
      return mergeChatInfoCustomPreviewValue(
        acc,
        '',
        sourceObject
      );
    }
    const resolvedValue = resolveChatInfoCustomPreviewPathValue(mapping, previewContext);
    if (resolvedValue === undefined) return acc;
    if (hasExplicitTarget && targetPath) {
      return mergeChatInfoCustomPreviewLiteralKey(acc, targetPath, resolvedValue);
    }
    return mergeChatInfoCustomPreviewValue(acc, targetPath || path, resolvedValue);
  }, {});
};

const apiVariableBindingContext = ({
  nodeId,
  nodes = [],
  edges = [],
  apis = [],
  runtimeOutputs = {},
  demand = null,
  taskExtraVariables = []
}) => {
  const allSources = commandAncestorOutputSources({ nodeId, nodes, edges, apis, runtimeOutputs });
  const startNode = nodes.find((node) => isStartNode(node)) || null;
  const executionStartSource = allSources.find((source) => String(source.nodeId) === String(startNode?.id)) || null;
  const fallbackTaskOptions = taskVariableOptions(demand, {
    extraVariables: taskExtraVariables,
    includeTaskCommentSources: true
  });
  const taskOptions = executionStartSource
    ? mergeVariableOptions(
      conditionSourceVariableOptions({
        schema: {},
        variables: executionStartSource.variables || []
      }),
      fallbackTaskOptions
    )
    : fallbackTaskOptions;

  const normalizedTaskOptions = appendTaskCommentVariableOptions(taskOptions);
  const taskOptionsWithUuid = (() => {
    const seen = new Set(normalizedTaskOptions.map((option) => String(option?.value || '').trim()));
    if (seen.has('task_uuid')) return normalizedTaskOptions;

    // Mantém no fim para não virar o default automático quando o binding ainda está vazio.
    return normalizedTaskOptions.concat({
      value: 'task_uuid',
      label: 'task_uuid (uuid)',
      displayName: 'task_uuid',
      type: 'uuid',
      group: 'configured'
    });
  })();
  return {
    sources: allSources.filter((source) => source !== executionStartSource),
    // No modo Buscar a "task" é o retorno da API (ainda não persistido), então campos
    // exclusivos de tasks salvas (ex.: data de criação) não se aplicam.
    taskOptions: executionStartSource
      ? taskOptionsWithUuid.filter((option) => String(option?.value || '') !== 'created_at')
      : taskOptionsWithUuid,
    taskSourceLabel: 'Atributos da Task'
  };
};

const makeSaveInfoMapping = (sources = []) => {
  const source = sources.find((item) => item.variables?.length) || sources[0] || {};
  const variable = source.variables?.[0] || {};
  if (!source.nodeId) {
    return {
      id: `save-info-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      sourceNodeId: '',
      sourceType: 'literal',
      integrationId: '',
      key: '',
      keyType: 'string',
      literalValue: '',
      saveAs: ''
    };
  }
  return {
    id: `save-info-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    sourceNodeId: source.nodeId || '',
    sourceType: source.sourceType || 'api',
    integrationId: source.integrationId || '',
    key: variable.value || '',
    keyType: variable.type || '',
    saveAs: ''
  };
};

const normalizeSaveInfoConfig = (config = {}, sources = [], demand = null) => {
  const statusValues = demandStatusOptions(demand).map((option) => option.value);
  const rawMappings = Array.isArray(config.mappings)
    ? config.mappings
    : Array.isArray(config.saveMappings)
      ? config.saveMappings
      : [];
  const mappings = rawMappings.length
    ? rawMappings.map((mapping) => {
      const sourceType = mappingSourceType(mapping);
      const isLiteral = sourceType === 'literal';
      const isSum = sourceType === 'sum';
      const isTask = sourceType === 'task' || String(mapping.sourceNodeId || '').trim().toLowerCase() === 'task';
      const source = isLiteral || isSum || isTask
        ? {}
        : sources.find((item) => String(item.nodeId) === String(mapping.sourceNodeId)) || sources[0] || {};
      const key = mapping.key || mapping.path || mapping.variable || '';
      const variable = source.variables?.find((item) => item.value === key) || source.variables?.[0] || {};
      const hasExplicitSaveAs = Object.prototype.hasOwnProperty.call(mapping, 'saveAs');
      const legacySaveAs = mapping.targetKey ?? mapping.targetPath ?? mapping.save_as;
      const normalizedSaveAs = hasExplicitSaveAs ? mapping.saveAs : legacySaveAs;
      return {
        ...makeSaveInfoMapping(sources),
        ...mapping,
        sourceNodeId: isLiteral || isSum || isTask ? '' : source.nodeId || mapping.sourceNodeId || '',
        sourceType: isLiteral ? 'literal' : isSum ? 'sum' : isTask ? 'task' : source.sourceType || mapping.sourceType || 'api',
        integrationId: isLiteral || isSum || isTask ? '' : source.integrationId || mapping.integrationId || '',
        key: isLiteral ? '' : key || variable.value || '',
        keyType: isSum ? 'integer' : isLiteral ? 'string' : isTask ? (mapping.keyType || '') : variable.type || mapping.keyType || '',
        ...(isLiteral && !mappingHasLiteralValue(mapping) ? { literalValue: '' } : {}),
        ...(isSum ? { sumValue: mapping.sumValue ?? mapping.sum_value ?? 1 } : {}),
        saveAs: normalizedSaveAs == null ? '' : String(normalizedSaveAs)
      };
    })
    : [];
  const nextStatus = statusValues.includes(config.nextStatus || config.taskStatus || config.statusAfter)
    ? (config.nextStatus || config.taskStatus || config.statusAfter)
    : '';

  return {
    ...commandCatalog.saveInfo.defaults,
    ...config,
    targetScope: 'Task',
    mappings,
    nextStatus
  };
};

const clearMappedKeysFromSourceNode = (nodes = [], sourceNodeId) => {
  const sourceId = String(sourceNodeId || '');
  if (!sourceId) return nodes;

  return nodes.map((node) => {
    const commandType = String(node?.data?.commandType || '').toLowerCase();
    const config = node?.data?.config && typeof node.data.config === 'object' && !Array.isArray(node.data.config)
      ? node.data.config
      : {};

    if (commandType === 'saveinfo') {
      const mappings = Array.isArray(config.mappings) ? config.mappings : [];
      let changed = false;
      const nextMappings = mappings.map((mapping) => {
        if (String(mapping?.sourceNodeId || '') !== sourceId) return mapping;
        if (!String(mapping?.key || '').trim() && !String(mapping?.keyType || '').trim()) return mapping;
        changed = true;
        return {
          ...mapping,
          key: '',
          keyType: ''
        };
      });
      if (!changed) return node;
      return {
        ...node,
        data: {
          ...node.data,
          config: {
            ...config,
            mappings: nextMappings
          }
        }
      };
    }

    if (commandType === 'api') {
      const bindings = config.variableBindings && typeof config.variableBindings === 'object' && !Array.isArray(config.variableBindings)
        ? config.variableBindings
        : {};
      let changed = false;
      const nextBindings = Object.entries(bindings).reduce((acc, [key, binding]) => {
        if (
          binding
          && binding.sourceType === 'api'
          && String(binding.sourceNodeId || '') === sourceId
          && String(binding.path || '').trim()
        ) {
          changed = true;
          acc[key] = {
            ...binding,
            path: ''
          };
          return acc;
        }
        acc[key] = binding;
        return acc;
      }, {});
      if (!changed) return node;
      return {
        ...node,
        data: {
          ...node.data,
          config: {
            ...config,
            variableBindings: nextBindings
          }
        }
      };
    }

    if (commandType === 'chat' || commandType === 'voice') {
      const rawToBinding = config.toBinding || config.to_binding || null;
      const rawParamBindingDetails = config.paramBindingDetails && typeof config.paramBindingDetails === 'object' && !Array.isArray(config.paramBindingDetails)
        ? config.paramBindingDetails
        : (config.param_binding_details && typeof config.param_binding_details === 'object' && !Array.isArray(config.param_binding_details)
          ? config.param_binding_details
          : {});
      const rawParamBindings = config.paramBindings && typeof config.paramBindings === 'object' && !Array.isArray(config.paramBindings)
        ? config.paramBindings
        : {};
      let changed = false;

      const clearBinding = (binding, fallbackPath = '') => {
        const normalized = normalizeChatVoiceBinding(binding ?? fallbackPath, [], []);
        if (normalized.sourceType === 'api' && String(normalized.sourceNodeId || '') === sourceId) {
          changed = true;
          return {
            ...normalized,
            path: ''
          };
        }
        const outputPath = parseCommandOutputBindingPath(fallbackPath);
        if (outputPath?.sourceNodeId === sourceId) {
          changed = true;
          return {
            sourceType: 'task',
            path: ''
          };
        }
        return normalized;
      };

      const nextToBinding = clearBinding(rawToBinding, config.toAttribute || config.to_attribute || '');
      const nextParamBindingDetails = Object.entries(rawParamBindingDetails).reduce((acc, [key, value]) => {
        acc[key] = clearBinding(value, rawParamBindings[key] || '');
        return acc;
      }, {});
      const nextParamBindings = Object.entries(rawParamBindings).reduce((acc, [key, value]) => {
        const nextBinding = nextParamBindingDetails[key] || clearBinding(value, value || '');
        if (nextBinding.sourceType === 'literal') {
          acc[key] = '';
          return acc;
        }
        acc[key] = String(nextBinding.path || '').trim();
        return acc;
      }, {});

      if (!changed) return node;
      return {
        ...node,
        data: {
          ...node.data,
          config: {
            ...config,
            toBinding: nextToBinding,
            toAttribute: nextToBinding.sourceType === 'literal' ? '' : String(nextToBinding.path || '').trim(),
            paramBindingDetails: nextParamBindingDetails,
            paramBindings: nextParamBindings
          }
        }
      };
    }

    if (commandType === 'condition') {
      const conditions = Array.isArray(config.conditions) ? config.conditions : [];
      let changed = false;
      const nextConditions = conditions.map((rule) => {
        if (String(rule?.sourceNodeId || '') !== sourceId) return rule;
        if (!String(rule?.variable || '').trim() && !String(rule?.variableType || '').trim()) return rule;
        changed = true;
        return {
          ...rule,
          variable: '',
          variableType: ''
        };
      });
      if (!changed) return node;
      return {
        ...node,
        data: {
          ...node.data,
          config: {
            ...config,
            conditions: nextConditions
          }
        }
      };
    }

    return node;
  });
};

const normalizeInsertDemandConfig = (config = {}, demands = [], route = null, options = {}) => {
  const routeDemandId = route?.demand_id ? String(route.demand_id) : '';
  const targetDemandId = String(config.targetDemandId || config.demandId || routeDemandId || '');
  const targetDemand = demands.find((item) => String(item.id) === targetDemandId);
  const sourceDemand = demands.find((item) => String(item.id) === String(route?.demand_id || '')) || {};
  const taskKeyOptions = insertDemandTaskKeyOptions(sourceDemand, {
    extraVariables: options.extraVariables
  });
  const payloadMode = config.payloadMode || config.payload_mode;
  const taskKey = config.taskKey == null
    ? (config.task_key == null ? commandCatalog.insertDemand.defaults.taskKey : String(config.task_key))
    : String(config.taskKey);
  const explicitTaskKeyMode = normalizeInsertDemandTaskKeyMode(config.taskKeyMode || config.task_key_mode);
  const inferredTaskKeyMode = insertDemandTaskKeySelectValue(taskKey, taskKeyOptions) === INSERT_DEMAND_TASK_KEY_CUSTOM
    ? INSERT_DEMAND_TASK_KEY_MODE_CUSTOM
    : INSERT_DEMAND_TASK_KEY_MODE_PRESET;
  const operation = normalizeInsertDemandOperation(
    config.operation || config.transferMode || config.transfer_mode
  );
  const normalized = {
    ...commandCatalog.insertDemand.defaults,
    ...config,
    targetDemandId,
    targetDemandName: targetDemand ? demandDisplayName(targetDemand) : (config.targetDemandName || ''),
    taskKey,
    taskKeyMode: explicitTaskKeyMode === INSERT_DEMAND_TASK_KEY_MODE_CUSTOM
      ? INSERT_DEMAND_TASK_KEY_MODE_CUSTOM
      : inferredTaskKeyMode,
    operation,
    upsert: operation === 'move' ? true : config.upsert !== false,
    payloadMode: payloadMode === 'api_response'
      ? 'merge_api'
      : (['template', 'merge_api'].includes(payloadMode) ? payloadMode : 'clone_task'),
    apiResponseValueAttribute: config.apiResponseValueAttribute || config.api_response_value_attribute || '',
    payloadJson: config.payloadJson || config.payload_json || commandCatalog.insertDemand.defaults.payloadJson,
    mergeSourceNodeId: config.mergeSourceNodeId || config.merge_source_node_id || '',
    status: config.status || config.nextStatus || ''
  };
  const payloadMappings = config.payloadMappings ?? config.payload_mappings;
  if (Array.isArray(payloadMappings)) {
    normalized.payloadMappings = payloadMappings;
  }
  return normalized;
};

const normalizeChatPreviewBindingLabel = (value = '') => String(value || '')
  .replace(/^(Task|Demanda):\s*/i, '')
  .replace(/\s*\([^)]*\)\s*$/, '')
  .trim();

const decodeChatPreviewEntities = (value = '') => String(value || '')
  .replace(/&nbsp;/gi, ' ')
  .replace(/&amp;/gi, '&')
  .replace(/&lt;/gi, '<')
  .replace(/&gt;/gi, '>')
  .replace(/&quot;/gi, '"')
  .replace(/&#39;/gi, "'");

const normalizeChatPreviewText = (value = '') => {
  const source = decodeChatPreviewEntities(String(value || '').trim());
  if (!source) return '';
  return source
    .replace(/<br\s*\/?>(\s*)/gi, '\n')
    .replace(/<\/p>\s*<p[^>]*>/gi, '\n\n')
    .replace(/<\/?(p|div|section|article|li|ul|ol)[^>]*>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/\r/g, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const applyChatPreviewBindings = (value = '', paramBindings = {}, paramBindingDetails = {}, bindingLabelsByPath = new Map()) => {
  const text = normalizeChatPreviewText(value);
  if (!text) return '';
  return text.replace(/\{\{\s*(\d+)\s*\}\}|\{\s*(\d+)\s*\}/g, (_, doubleIndex, singleIndex) => {
    const index = String(doubleIndex || singleIndex || '').trim();
    if (!index) return '';
    const normalizedBinding = normalizeChatVoiceBinding(
      paramBindingDetails?.[index] ?? paramBindings?.[index],
      [],
      []
    );
    if (String(normalizedBinding.sourceType || '').trim().toLowerCase() === 'literal') {
      const literalValue = Object.prototype.hasOwnProperty.call(normalizedBinding, 'literalValue')
        ? normalizedBinding.literalValue
        : normalizedBinding.literal_value;
      const textValue = String(literalValue ?? '').trim();
      return textValue || `{{${index}}}`;
    }
    const bindingPath = String(normalizedBinding.path || paramBindings?.[index] || '').trim();
    if (!bindingPath) return `{{${index}}}`;
    const label = normalizeChatPreviewBindingLabel(bindingLabelsByPath.get(bindingPath) || bindingPath);
    return label || `{{${index}}}`;
  });
};

const normalizeChatExternalUrl = (value = '') => {
  const normalized = String(value || '').trim();
  if (!normalized) return '';
  try {
    return new URL(normalized).toString().replace(/\/+$/, '');
  } catch {
    return '';
  }
};

const buildChatTemplateEditorUrl = ({ chatUrl = '', templateId = '' } = {}) => {
  const baseUrl = normalizeChatExternalUrl(chatUrl);
  const template = String(templateId || '').trim();
  if (!baseUrl || !template) return '';
  try {
    return new URL(`/templates/edit/${encodeURIComponent(template)}`, `${baseUrl}/`).toString();
  } catch {
    return '';
  }
};

const buildChatbotEditorUrl = ({ chatUrl = '', chatbotId = '' } = {}) => {
  const baseUrl = normalizeChatExternalUrl(chatUrl);
  const chatbot = String(chatbotId || '').trim();
  if (!baseUrl || !chatbot) return '';
  try {
    const url = new URL('/chatbots_management/edit_chatbot_interface', `${baseUrl}/`);
    url.searchParams.set('bot_id', chatbot);
    return url.toString();
  } catch {
    return '';
  }
};

const buildVoiceCampaignEditorUrl = ({ voiceUrl = '', templateId = '' } = {}) => {
  const baseUrl = normalizeChatExternalUrl(voiceUrl);
  const campaignId = String(templateId || '').trim();
  if (!baseUrl || !campaignId) return '';
  try {
    const url = new URL('/campaign/edit', `${baseUrl}/`);
    url.searchParams.set('campaign_id', campaignId);
    return url.toString();
  } catch {
    return '';
  }
};

const openExternalUrlInNewTab = (url = '') => {
  const targetUrl = String(url || '').trim();
  if (!targetUrl || typeof window === 'undefined' || typeof window.open !== 'function') return;
  window.open(targetUrl, '_blank', 'noopener,noreferrer');
};

const buildChatTemplatePreview = ({
  headerPreview = '',
  bodyPreview = '',
  footerPreview = '',
  paramBindings = {},
  paramBindingDetails = {},
  bindingOptions = []
} = {}) => {
  const labelsByPath = (Array.isArray(bindingOptions) ? bindingOptions : []).reduce((acc, option) => {
    const value = String(option?.value || '').trim();
    if (!value || acc.has(value)) return acc;
    acc.set(value, String(option?.label || value).trim());
    return acc;
  }, new Map());

  return {
    header: applyChatPreviewBindings(headerPreview, paramBindings, paramBindingDetails, labelsByPath),
    body: applyChatPreviewBindings(bodyPreview, paramBindings, paramBindingDetails, labelsByPath),
    footer: applyChatPreviewBindings(footerPreview, paramBindings, paramBindingDetails, labelsByPath)
  };
};

const normalizeChatConfig = (config = {}, taskOptions = [], sources = []) => {
  const defaults = commandCatalog.chat.defaults;
  const normalizedContextConfig = normalizeChatInfoCustomConfig(config, sources);
  const paramsCount = Math.max(0, Number.parseInt(config.paramsCount || config.params_count, 10) || 0);
  const sourceBindings = config.paramBindings && typeof config.paramBindings === 'object' && !Array.isArray(config.paramBindings)
    ? config.paramBindings
    : {};
  const sourceBindingDetails = config.paramBindingDetails && typeof config.paramBindingDetails === 'object' && !Array.isArray(config.paramBindingDetails)
    ? config.paramBindingDetails
    : (config.param_binding_details && typeof config.param_binding_details === 'object' && !Array.isArray(config.param_binding_details)
      ? config.param_binding_details
      : {});
  const fallbackPath = String(taskOptions?.[0]?.value || '').trim();
  const paramBindings = {};
  const paramBindingDetails = {};
  for (let index = 1; index <= paramsCount; index += 1) {
    const key = String(index);
    const normalizedBinding = normalizeChatVoiceBinding(
      sourceBindingDetails[key] ?? sourceBindings[key],
      taskOptions,
      sources
    );
    paramBindingDetails[key] = normalizedBinding;
    paramBindings[key] = normalizedBinding.sourceType === 'literal'
      ? ''
      : String(normalizedBinding.path || '').trim();
  }
  const toBinding = normalizeChatVoiceBinding(
    config.toBinding
    || config.to_binding
    || {
      sourceType: 'task',
      path: String(config.toAttribute || config.to_attribute || '').trim() || fallbackPath
    },
    taskOptions,
    sources
  );
  const attachmentBinding = normalizeChatVoiceBinding(
    config.attachmentBinding
    || config.attachment_binding
    || {
      sourceType: 'task',
      path: String(config.attachmentAttribute || config.attachment_attribute || '').trim()
    },
    taskOptions,
    sources
  );
  const normalizedAttachmentPath = normalizeCortexOcrFileBindingPath(String(attachmentBinding.path || '').trim());
  const normalizedAttachmentBinding = {
    ...attachmentBinding,
    sourceType: 'task',
    path: normalizedAttachmentPath
  };

  return {
    ...defaults,
    ...normalizedContextConfig,
    platform: String(config.platform || '').trim(),
    templateId: String(config.templateId || config.template_id || '').trim(),
    templateName: String(config.templateName || config.template_name || '').trim(),
    templateOrigin: String(config.templateOrigin || config.template_origin || '').trim(),
    templateContentTypeName: String(config.templateContentTypeName || config.template_content_type_name || config.contentTypeName || config.content_type_name || '').trim().toLowerCase(),
    chatbotId: String(config.chatbotId || config.chatbot_id || '').trim(),
    chatUrl: normalizeChatExternalUrl(config.chatUrl || config.chat_url),
    headerPreview: String(config.headerPreview || config.header_preview || '').trim(),
    preview: String(config.preview || '').trim(),
    footer: String(config.footer || '').trim(),
    paramsCount,
    paramBindings,
    paramBindingDetails,
    attachmentBinding: normalizedAttachmentBinding,
    attachmentAttribute: normalizedAttachmentBinding.sourceType === 'literal'
      ? ''
      : String(normalizedAttachmentBinding.path || '').trim(),
    toBinding,
    toAttribute: toBinding.sourceType === 'literal'
      ? ''
      : String(toBinding.path || '').trim(),
    timeoutMs: Number(config.timeoutMs || config.timeout_ms || defaults.timeoutMs)
  };
};

const normalizeVoiceConfig = (config = {}, taskOptions = [], sources = []) => {
  const defaults = commandCatalog.voice.defaults;
  const paramsCount = Math.max(0, Number.parseInt(config.paramsCount || config.params_count, 10) || 0);
  const editableParamsCount = Math.max(paramsCount - 1, 0);
  const sourceBindings = config.paramBindings && typeof config.paramBindings === 'object' && !Array.isArray(config.paramBindings)
    ? config.paramBindings
    : {};
  const sourceBindingDetails = config.paramBindingDetails && typeof config.paramBindingDetails === 'object' && !Array.isArray(config.paramBindingDetails)
    ? config.paramBindingDetails
    : (config.param_binding_details && typeof config.param_binding_details === 'object' && !Array.isArray(config.param_binding_details)
      ? config.param_binding_details
      : {});
  const fallbackPath = String(taskOptions?.[0]?.value || '').trim();
  const paramBindings = {};
  const paramBindingDetails = {};
  for (let index = 1; index <= editableParamsCount; index += 1) {
    const key = String(index);
    const normalizedBinding = normalizeChatVoiceBinding(
      sourceBindingDetails[key] ?? sourceBindings[key],
      taskOptions,
      sources
    );
    paramBindingDetails[key] = normalizedBinding;
    paramBindings[key] = normalizedBinding.sourceType === 'literal'
      ? ''
      : String(normalizedBinding.path || '').trim();
  }

  const legacyDialAtPath = String(config.dialAtAttribute || config.dial_at_attribute || '').trim();
  const sourceDialAtBinding = config.dialAtBinding && typeof config.dialAtBinding === 'object' && !Array.isArray(config.dialAtBinding)
    ? config.dialAtBinding
    : (config.dial_at_binding && typeof config.dial_at_binding === 'object' && !Array.isArray(config.dial_at_binding)
      ? config.dial_at_binding
      : null);
  const dialAtBindingSourceType = String(sourceDialAtBinding?.sourceType || sourceDialAtBinding?.source_type || '').trim().toLowerCase();
  const dialAtBinding = dialAtBindingSourceType === 'date'
    ? {
        sourceType: 'date',
        path: '',
        dateConfig: normalizeApiDateBindingConfig(sourceDialAtBinding?.dateConfig || sourceDialAtBinding?.date_config || {})
      }
    : dialAtBindingSourceType === 'task'
      ? {
          sourceType: 'task',
          path: normalizeTaskSelectorBindingPath(sourceDialAtBinding?.path || sourceDialAtBinding?.attribute || legacyDialAtPath),
          dateConfig: normalizeApiDateBindingConfig(sourceDialAtBinding?.dateConfig || sourceDialAtBinding?.date_config || {})
        }
      : legacyDialAtPath
        ? {
            sourceType: 'task',
            path: normalizeTaskSelectorBindingPath(legacyDialAtPath),
            dateConfig: defaultApiDateBindingConfig()
          }
        : {
            sourceType: '',
            path: '',
            dateConfig: defaultApiDateBindingConfig()
          };

  const toBinding = normalizeChatVoiceBinding(
    config.toBinding
    || config.to_binding
    || {
      sourceType: 'task',
      path: String(config.toAttribute || config.to_attribute || '').trim() || fallbackPath
    },
    taskOptions,
    sources
  );

  return {
    ...defaults,
    ...config,
    campaignId: String(config.campaignId || config.campaign_id || config.templateId || config.template_id || '').trim(),
    campaignName: String(config.campaignName || config.campaign_name || config.templateName || config.template_name || '').trim(),
    voiceUrl: normalizeChatExternalUrl(config.voiceUrl || config.voice_url),
    paramsCount,
    paramBindings,
    paramBindingDetails,
    toBinding,
    toAttribute: toBinding.sourceType === 'literal'
      ? ''
      : String(toBinding.path || '').trim(),
    dialAtBinding,
    dialAtAttribute: dialAtBinding.sourceType === 'task' ? String(dialAtBinding.path || '').trim() : '',
    timeoutMs: Number(config.timeoutMs || config.timeout_ms || defaults.timeoutMs)
  };
};

const conditionVariableSources = ({
  nodeId,
  nodes = [],
  edges = [],
  apis = [],
  demand = null,
  runtimeOutputs = {},
  taskExtraVariables = []
}) => {
  const apiSources = apiAncestorSources({ nodeId, nodes, edges, apis, runtimeOutputs });
  const startNode = nodes.find((candidate) => isStartNode(candidate)) || null;
  const executionStartSource = apiSources.find((source) => String(source.nodeId) === String(startNode?.id));
  const taskVariables = executionStartSource
    ? conditionSourceVariableOptions({
      schema: {},
      variables: executionStartSource.variables || []
    })
    : taskVariableOptions(demand, {
      extraVariables: taskExtraVariables,
      includeTaskCommentSources: true
    });

  return [
    {
      nodeId: 'task',
      sourceType: 'task',
      fromExecutionStart: Boolean(executionStartSource),
      title: 'Task',
      subtitle: executionStartSource
        ? 'Dados iterados do retorno da API do Início'
        : 'Atributos definidos na Demanda',
      integrationId: '',
      integrationName: '',
      schema: executionStartSource ? {} : (demand?.task_schema || {}),
      variables: appendTaskCommentVariableOptions(taskVariables)
    },
    ...apiSources.filter((source) => source !== executionStartSource)
  ].map((source) => ({
    ...source,
    variables: conditionSourceVariableOptions(source)
  }));
};

const makeConditionRule = (sources = []) => {
  const source = sources[0] || {};
  const variable = source.variables?.[0] || {};
  return {
    id: `condition-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    sourceNodeId: source.nodeId || '',
    sourceType: source.sourceType || 'api',
    integrationId: source.integrationId || '',
    variable: normalizeConditionVariablePath(variable.value || ''),
    variableType: variable.type || '',
    operator: 'equals',
    value: '',
    arrayMatchByPath: {},
    arrayNavigationByPath: {}
  };
};

const normalizeConditionRuleWithSources = (rule = {}, sources = []) => {
  const arrayMatchByPath = normalizeConditionArrayMatches(rule.arrayMatchByPath);
  return {
    ...makeConditionRule(sources),
    ...rule,
    variable: normalizeConditionVariablePath(rule.variable),
    arrayMatchByPath,
    arrayNavigationByPath: normalizeConditionArrayNavigations(rule.arrayNavigationByPath, arrayMatchByPath)
  };
};

const createDecisionPathId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `decision-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
};

const normalizeConditionDecisionMode = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return ['multiple', 'multi', 'multiplas', 'multiplas_decisoes', 'multiplas-decisoes'].includes(normalized)
    ? 'multiple'
    : 'boolean';
};

const normalizeConditionPathLogic = (value) => (String(value || '').trim().toUpperCase() === 'OR' ? 'OR' : 'AND');

const makeDefaultConditionDecisionPath = (index = 0) => ({
  id: createDecisionPathId(),
  label: `Decisão ${index + 1}`,
  color: '',
  conditionLogic: 'AND',
  conditions: []
});

const conditionDecisionColorOptions = [
  { value: 'green-500', label: 'Verde Folha', hex: '#16a34a' },
  { value: 'green-700', label: 'Verde Pinheiro', hex: '#15803d' },
  { value: 'emerald-500', label: 'Esmeralda Viva', hex: '#10b981' },
  { value: 'emerald-700', label: 'Esmeralda Profunda', hex: '#047857' },
  { value: 'teal-500', label: 'Turquesa Clara', hex: '#14b8a6' },
  { value: 'teal-700', label: 'Turquesa Oceano', hex: '#0f766e' },
  { value: 'cyan-500', label: 'Azul Piscina', hex: '#06b6d4' },
  { value: 'cyan-700', label: 'Azul Atlântico', hex: '#0e7490' },
  { value: 'sky-500', label: 'Azul Céu', hex: '#0ea5e9' },
  { value: 'sky-700', label: 'Azul Tempestade', hex: '#0369a1' },
  { value: 'blue-500', label: 'Azul Royal', hex: '#2563eb' },
  { value: 'blue-700', label: 'Azul Marinho', hex: '#1d4ed8' },
  { value: 'indigo-500', label: 'Índigo Neon', hex: '#6366f1' },
  { value: 'indigo-700', label: 'Índigo Noturno', hex: '#4338ca' },
  { value: 'violet-500', label: 'Violeta Elétrica', hex: '#8b5cf6' },
  { value: 'violet-700', label: 'Violeta Imperial', hex: '#6d28d9' },
  { value: 'purple-500', label: 'Roxo Orquídea', hex: '#a855f7' },
  { value: 'purple-700', label: 'Roxo Ametista', hex: '#7e22ce' },
  { value: 'fuchsia-500', label: 'Fúcsia Pop', hex: '#d946ef' },
  { value: 'fuchsia-700', label: 'Fúcsia Profundo', hex: '#a21caf' },
  { value: 'pink-500', label: 'Rosa Chiclete', hex: '#ec4899' },
  { value: 'pink-700', label: 'Rosa Magenta', hex: '#be185d' },
  { value: 'rose-500', label: 'Rosa Coral', hex: '#f43f5e' },
  { value: 'rose-700', label: 'Rosa Rubi', hex: '#be123c' },
  { value: 'red-500', label: 'Vermelho Vivo', hex: '#ef4444' },
  { value: 'red-700', label: 'Vermelho Granada', hex: '#b91c1c' },
  { value: 'orange-500', label: 'Laranja Solar', hex: '#f97316' },
  { value: 'orange-700', label: 'Laranja Telha', hex: '#c2410c' },
  { value: 'amber-500', label: 'Âmbar Dourado', hex: '#f59e0b' },
  { value: 'amber-700', label: 'Âmbar Bronze', hex: '#b45309' }
];

const normalizeConditionDecisionColor = (value = '', index = 0) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (conditionDecisionColorOptions.some((option) => option.value === normalized)) {
    return normalized;
  }
  return conditionDecisionColorOptions[index % conditionDecisionColorOptions.length]?.value || 'blue-500';
};

const conditionDecisionColorHex = (value = '', index = 0) => {
  const normalized = normalizeConditionDecisionColor(value, index);
  return conditionDecisionColorOptions.find((option) => option.value === normalized)?.hex || '#2563eb';
};

const conditionPathFallbackLabel = (index = 0) => `Sem nome (Decisão ${index + 1})`;

const conditionPathDisplayLabel = (path = {}, index = 0) => {
  const rawLabel = String(path?.label ?? '');
  return rawLabel.trim() ? rawLabel : conditionPathFallbackLabel(index);
};

const normalizeConditionDecisionPaths = (value = [], mode = 'boolean', sources = []) => {
  if (mode !== 'multiple') return [];
  const raw = Array.isArray(value) ? value : [];
  if (!raw.length) return [];
  const seen = new Set();
  const normalized = raw.reduce((acc, item, index) => {
    const rawId = String(item?.id || '').trim();
    const id = rawId && !seen.has(rawId) ? rawId : createDecisionPathId();
    seen.add(id);
    const label = String(item?.label ?? '');
    const rawConditions = Array.isArray(item?.conditions) ? item.conditions : [];
    const conditions = rawConditions.length
      ? rawConditions.map((rule) => normalizeConditionRuleWithSources(rule, sources))
      : [makeConditionRule(sources)];
    acc.push({
      id,
      label,
      color: normalizeConditionDecisionColor(item?.color || item?.tone, index),
      conditionLogic: normalizeConditionPathLogic(item?.conditionLogic || item?.condition_logic),
      conditions
    });
    return acc;
  }, []);
  return normalized;
};

const normalizeConditionDecisionSource = (value = {}, sources = [], paths = []) => {
  const source = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const sourceNodeId = String(source.sourceNodeId || '').trim();
  const availablePathIds = new Set((Array.isArray(paths) ? paths : []).map((path) => String(path?.id || '').trim()).filter(Boolean));
  const fallbackByConfig = String(source.fallbackPathId || source.fallback_path_id || '').trim();
  const fallbackPathId = availablePathIds.has(fallbackByConfig)
    ? fallbackByConfig
    : (Array.isArray(paths) && paths[0]?.id ? String(paths[0].id) : '');
  const selectedSource = sources.find((item) => String(item.nodeId) === sourceNodeId) || sources[0] || null;
  const selectedVariable = normalizeConditionVariablePath(source.variable || selectedSource?.variables?.[0]?.value || '');
  const variableMeta = conditionVariableMetadata(selectedSource?.variables, selectedVariable);
  const arrayPaths = conditionArrayPathsForVariable(selectedSource?.variables, selectedVariable);
  const sourceArrayMatches = normalizeConditionArrayMatches(source.arrayMatchByPath);
  const sourceArrayNavigations = normalizeConditionArrayNavigations(
    source.arrayNavigationByPath,
    sourceArrayMatches
  );
  const arrayMatchByPath = arrayPaths.reduce((acc, path) => {
    acc[path] = sourceArrayNavigations[path]?.mode || sourceArrayMatches[path] || 'any_equals';
    return acc;
  }, {});
  const arrayNavigationByPath = arrayPaths.reduce((acc, path) => {
    acc[path] = normalizeArrayNavigation(sourceArrayNavigations[path], arrayMatchByPath[path]);
    return acc;
  }, {});
  return {
    sourceNodeId: selectedSource?.nodeId || sourceNodeId || '',
    sourceType: selectedSource?.sourceType || String(source.sourceType || 'task').trim() || 'task',
    integrationId: selectedSource?.integrationId || String(source.integrationId || '').trim(),
    variable: selectedVariable,
    variableType: variableMeta?.type || String(source.variableType || '').trim(),
    fallbackPathId,
    arrayMatchByPath,
    arrayNavigationByPath
  };
};

const normalizeConditionConfig = (config = {}, sources = []) => {
  const legacyRule = config.leftPath || config.expectedValue || config.operator
    ? [{
      ...makeConditionRule(sources),
      variable: normalizeConditionVariablePath(config.leftPath),
      operator: config.operator || 'equals',
      value: config.expectedValue || ''
    }]
    : [];
  const conditions = Array.isArray(config.conditions) && config.conditions.length
    ? config.conditions
    : legacyRule;
  const mode = normalizeConditionDecisionMode(
    config.decisionMode || config.decision_mode || config.mode || (Array.isArray(config.decisionPaths) ? 'multiple' : 'boolean')
  );
  const decisionPaths = normalizeConditionDecisionPaths(
    config.decisionPaths || config.decision_paths || config.paths,
    mode,
    sources
  );
  const decisionSource = normalizeConditionDecisionSource(
    config.decisionSource || config.decision_source,
    sources,
    decisionPaths
  );
  return {
    ...commandCatalog.condition.defaults,
    ...config,
    decisionMode: mode,
    decisionPaths,
    decisionSource,
    conditionLogic: config.conditionLogic || 'AND',
    conditions: conditions.length
      ? conditions.map((rule) => normalizeConditionRuleWithSources(rule, sources))
      : [makeConditionRule(sources)],
    errorPolicy: normalizeConditionErrorPolicy(config.errorPolicy || config.missingValuePolicy)
  };
};

const ArrayNavigationEditor = ({ arrayPath, variables = [], value, canEdit, onCancel, onApply }) => {
  const [draft, setDraft] = useState(() => normalizeArrayNavigation(value));
  const [dateRuleModal, setDateRuleModal] = useState(null);
  const [dateRuleDraft, setDateRuleDraft] = useState(defaultApiDateBindingConfig());
  const fieldOptions = conditionArrayDescendantOptions(variables, arrayPath);
  const datalistId = useId();
  const openDateRuleModal = (rule = {}) => {
    setDateRuleDraft(normalizeApiDateBindingConfig(rule.dateConfig || rule.date_config || {}));
    setDateRuleModal({ id: rule.id });
  };
  const updateRule = (id, patch) => {
    setDraft((current) => ({
      ...current,
      rules: current.rules.map((rule) => {
        if (rule.id !== id) return rule;
        const next = { ...rule, ...patch };
        if (Object.prototype.hasOwnProperty.call(patch, 'path')) {
          const option = fieldOptions.find((item) => item.value === patch.path);
          next.type = option?.type || '';
          next.arrayPaths = option?.arrayPaths || [];
          if (isDateLikeSchemaType(next.type)) {
            next.dateConfig = normalizeApiDateBindingConfig(next.dateConfig || next.date_config || {});
          } else {
            next.dateConfig = undefined;
          }
        }
        if (Object.prototype.hasOwnProperty.call(patch, 'dateConfig')) {
          next.dateConfig = normalizeApiDateBindingConfig(patch.dateConfig || {});
        }
        return next;
      })
    }));
  };
  const removeRule = (id) => setDraft((current) => ({
    ...current,
    rules: current.rules.filter((rule) => rule.id !== id)
  }));
  const addRule = () => setDraft((current) => {
    const firstOption = fieldOptions[0] || null;
    return {
      ...current,
      rules: current.rules.concat({
        ...makeArrayNavigationRule(firstOption?.value || ''),
        type: firstOption?.type || '',
        arrayPaths: firstOption?.arrayPaths || []
      })
    };
  });
  const modeOptions = CONDITION_ARRAY_MATCH_OPTIONS;
  const completeRules = draft.rules.filter((rule) => (
    rule.path && (['exists', 'not_exists', 'empty', 'not_empty'].includes(rule.operator) || String(rule.value ?? '').length)
  ));
  const canApply = canEdit && (draft.mode !== 'filter_equals' || completeRules.length > 0);

  return createPortal(
    <div className="array-navigation-layer" role="presentation" onMouseDown={onCancel}>
      <section
        className="array-navigation-popover"
        role="dialog"
        aria-modal="true"
        aria-label={`Navegação na lista ${arrayPath || 'retornada'}`}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="array-navigation-head">
          <span className="material-symbols-rounded" aria-hidden="true">account_tree</span>
          <div>
            <small>Navegação na lista</small>
            <strong>{arrayPath || 'Lista retornada'}</strong>
            <p>Defina como os itens serão percorridos antes de aplicar a condição principal.</p>
          </div>
          <button className="icon-button" type="button" onClick={onCancel} aria-label="Fechar navegação da lista">
            <span className="material-symbols-rounded" aria-hidden="true">close</span>
          </button>
        </header>

        <div className="array-navigation-body">
          <div className="array-navigation-modes" role="group" aria-label="Estratégia de navegação">
            {modeOptions.map((option) => (
              <button
                className={draft.mode === option.value ? 'is-active' : ''}
                type="button"
                key={option.value}
                onClick={() => setDraft((current) => {
                  if (option.value !== 'filter_equals' || current.rules.length) {
                    return { ...current, mode: option.value };
                  }
                  const firstOption = fieldOptions[0] || null;
                  return {
                    ...current,
                    mode: option.value,
                    rules: [{
                      ...makeArrayNavigationRule(firstOption?.value || ''),
                      type: firstOption?.type || '',
                      arrayPaths: firstOption?.arrayPaths || []
                    }]
                  };
                })}
                disabled={!canEdit}
              >
                <span className="material-symbols-rounded" aria-hidden="true">{option.icon}</span>
                <strong>{option.label}</strong>
              </button>
            ))}
          </div>

          {draft.mode === 'index_equals' && (
            <label className="modal-field array-navigation-index">
              <FieldLabel
                help={{
                  title: 'Posição específica',
                  description: 'Seleciona somente um item da lista pela posição em que ele aparece.',
                  usage: 'A primeira posição é 1. Se a posição não existir, a condição será avaliada como valor ausente.'
                }}
              >Posição do item</FieldLabel>
              <input
                type="number"
                min="1"
                step="1"
                value={draft.index + 1}
                onChange={(event) => setDraft((current) => ({
                  ...current,
                  index: Math.max(0, (Number.parseInt(event.target.value, 10) || 1) - 1)
                }))}
                disabled={!canEdit}
              />
            </label>
          )}

          {draft.mode === 'filter_equals' && (
            <section className="array-navigation-filter-builder">
              <header>
                <div>
                  <small>Localizar item</small>
                  <strong>Regras aplicadas dentro de cada item</strong>
                  <p>Você pode combinar campos simples ou caminhos com novas listas internas.</p>
                </div>
                <label>
                  <span>Combinação</span>
                  <select
                    value={draft.logic}
                    onChange={(event) => setDraft((current) => ({ ...current, logic: event.target.value }))}
                    disabled={!canEdit}
                  >
                    <option value="AND">Todas (AND)</option>
                    <option value="OR">Qualquer (OR)</option>
                  </select>
                </label>
              </header>

              <datalist id={datalistId}>
                {fieldOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </datalist>

              <div className="array-navigation-rule-table">
                <div className="array-navigation-rule-row is-head">
                  <span>Campo no item</span>
                  <span>Operador</span>
                  <span>Valor</span>
                  <span></span>
                </div>
                {draft.rules.map((rule) => (
                  <div className="array-navigation-rule-row" key={rule.id}>
                    <input
                      list={datalistId}
                      value={rule.path}
                      onChange={(event) => updateRule(rule.id, { path: event.target.value })}
                      placeholder={fieldOptions[0]?.value || 'Ex: labels.id'}
                      disabled={!canEdit}
                    />
                    <select
                      value={rule.operator}
                      onChange={(event) => updateRule(rule.id, { operator: event.target.value })}
                      disabled={!canEdit}
                    >
                      {conditionOperatorOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                    {isDateLikeSchemaType(rule.type) ? (
                      <div className="api-variable-date-cell">
                        <RelativeDateTrigger
                          config={rule.dateConfig}
                          onConfigure={() => openDateRuleModal(rule)}
                          disabled={!canEdit || ['exists', 'not_exists', 'empty', 'not_empty'].includes(rule.operator)}
                        />
                      </div>
                    ) : (
                      <input
                        value={rule.value ?? ''}
                        onChange={(event) => updateRule(rule.id, { value: event.target.value })}
                        placeholder="Valor esperado"
                        disabled={!canEdit || ['exists', 'not_exists', 'empty', 'not_empty'].includes(rule.operator)}
                      />
                    )}
                    <button className="icon-button" type="button" onClick={() => removeRule(rule.id)} disabled={!canEdit} aria-label="Remover regra da lista">
                      <span className="material-symbols-rounded" aria-hidden="true">delete</span>
                    </button>
                  </div>
                ))}
              </div>
              <button className="secondary-button compact-action" type="button" onClick={addRule} disabled={!canEdit}>
                <span className="material-symbols-rounded" aria-hidden="true">add</span>
                Adicionar regra
              </button>
              {!fieldOptions.length && (
                <p className="array-navigation-manual-hint">
                  As keys internas ainda não foram observadas. Digite o caminho manualmente ou execute uma inspeção para o sistema aprendê-las.
                </p>
              )}
            </section>
          )}

          <RelativeDateModal
            open={Boolean(dateRuleModal)}
            title="Configurar data da regra"
            description="A data e hora da comparação são calculadas em tempo de execução, com base no momento de cada processamento."
            draft={dateRuleDraft}
            onDraftChange={setDateRuleDraft}
            onCancel={() => setDateRuleModal(null)}
            onApply={(nextConfig) => {
              if (dateRuleModal) {
                updateRule(dateRuleModal.id, { dateConfig: nextConfig, value: '' });
              }
              setDateRuleModal(null);
            }}
          />
        </div>

        <footer className="array-navigation-footer">
          <span>{draft.mode === 'filter_equals'
            ? `${completeRules.length} regra(s) válida(s)`
            : conditionArrayMatchLabel(draft.mode)}
          </span>
          <button className="secondary-button" type="button" onClick={onCancel}>Cancelar</button>
          <button className="primary-button" type="button" onClick={() => onApply(normalizeArrayNavigation(draft))} disabled={!canApply}>Aplicar navegação</button>
        </footer>
      </section>
    </div>,
    document.body
  );
};

const ArrayNavigationButton = ({ arrayPath, variables, value, canEdit, onChange, fallbackLabel }) => {
  const [open, setOpen] = useState(false);
  const navigation = normalizeArrayNavigation(value);
  return (
    <>
      <button
        className={`condition-array-match-button is-advanced mode-${navigation.mode}`}
        type="button"
        onClick={() => setOpen(true)}
        disabled={!canEdit}
        title={conditionArrayMatchLabel(navigation.mode)}
        aria-label={`${conditionArrayMatchLabel(navigation.mode)}: ${arrayPath || fallbackLabel}`}
      >
        <span className="material-symbols-rounded" aria-hidden="true">{conditionArrayMatchIcon(navigation.mode)}</span>
      </button>
      {open && (
        <ArrayNavigationEditor
          arrayPath={arrayPath}
          variables={variables}
          value={navigation}
          canEdit={canEdit}
          onCancel={() => setOpen(false)}
          onApply={(nextValue) => {
            onChange(nextValue);
            setOpen(false);
          }}
        />
      )}
    </>
  );
};

const ContextVariablePathSelector = ({
  variables = [],
  binding = {},
  canEdit = true,
  onChange,
  emptyLabel = 'Sem atributos conhecidos',
  emptyPathLabel = '',
  allowEmptyPath = false,
  containerClassName = 'api-variable-combo',
  controlClassName = 'api-variable-selector-control',
  arrayPathFallbackLabel = 'retorno',
  advancedArrayNavigation = false
}) => {
  const normalizedVariables = normalizeFileLikeVariableOptions(variables);
  const normalizedBinding = normalizeTaskSelectorBinding({
    ...(binding && typeof binding === 'object' && !Array.isArray(binding) ? binding : { path: String(binding || '') })
  });
  const variableLevels = conditionVariableSelectionLevels(normalizedVariables, normalizedBinding.path);
  const arrayPaths = conditionArrayPathsForVariable(normalizedVariables, normalizedBinding.path);
  const arrayMatchByPath = normalizeConditionArrayMatches(normalizedBinding.arrayMatchByPath);
  const arrayNavigationByPath = normalizeConditionArrayNavigations(
    normalizedBinding.arrayNavigationByPath,
    arrayMatchByPath
  );

  useEffect(() => {
    if (typeof onChange !== 'function') return;
    const currentPath = String(normalizedBinding.path || '').trim();
    let nextPath = currentPath;

    for (let index = 0; index < variableLevels.length; index += 1) {
      const level = variableLevels[index];
      const levelValue = String(level?.value || '').trim();
      const canStayEmpty = allowEmptyPath && index === 0;
      if (levelValue || canStayEmpty) {
        nextPath = levelValue || nextPath;
        continue;
      }
      const fallbackPath = String(level?.options?.[0]?.path || '').trim();
      if (!fallbackPath) continue;
      nextPath = fallbackPath;
      break;
    }

    if (!nextPath || nextPath === currentPath) return;
    onChange({
      ...normalizedBinding,
      path: nextPath,
      arrayMatchByPath,
      arrayNavigationByPath
    });
  }, [allowEmptyPath, arrayMatchByPath, arrayNavigationByPath, normalizedBinding, onChange, variableLevels]);

  return (
    <div className={containerClassName}>
      {!variableLevels.length && (
        <select value="" disabled>
          <option>{emptyLabel}</option>
        </select>
      )}
      {variableLevels.map((level) => {
        const arrayPath = arrayPaths.includes(level.value)
          ? level.value
          : level.parentPath === '' && arrayPaths.includes('')
            ? ''
            : null;
        const arrayMatch = arrayPath == null ? '' : arrayMatchByPath[arrayPath] || 'any_equals';
        const arrayMatchText = conditionArrayMatchLabel(arrayMatch);
        const arrayMatchSymbol = conditionArrayMatchIcon(arrayMatch);

        return (
          <div className={controlClassName} key={level.parentPath || 'root'}>
            <select
              value={level.value}
              onChange={(event) => onChange({
                ...normalizedBinding,
                path: event.target.value || level.parentPath,
                arrayMatchByPath,
                arrayNavigationByPath
              })}
              disabled={!canEdit}
              aria-label={level.label}
            >
              {!allowEmptyPath && <option value="" disabled hidden>{level.label}</option>}
              {allowEmptyPath && level.parentPath === '' && (
                <option value="">{emptyPathLabel || 'Todos os atributos'}</option>
              )}
              {level.options.map((option) => <option key={option.path} value={option.path}>{option.label}</option>)}
            </select>
            {arrayPath != null && (
              advancedArrayNavigation ? (
                <ArrayNavigationButton
                  arrayPath={arrayPath}
                  variables={normalizedVariables}
                  value={arrayNavigationByPath[arrayPath] || { mode: arrayMatch }}
                  canEdit={canEdit}
                  fallbackLabel={arrayPathFallbackLabel}
                  onChange={(navigation) => onChange({
                    ...normalizedBinding,
                    arrayMatchByPath: {
                      ...arrayMatchByPath,
                      [arrayPath]: navigation.mode
                    },
                    arrayNavigationByPath: {
                      ...arrayNavigationByPath,
                      [arrayPath]: navigation
                    }
                  })}
                />
              ) : (
                <button
                  className="condition-array-match-button"
                  type="button"
                  onClick={() => onChange({
                    ...normalizedBinding,
                    arrayMatchByPath: {
                      ...arrayMatchByPath,
                      [arrayPath]: nextConditionArrayMatch(arrayMatch)
                    },
                    arrayNavigationByPath
                  })}
                  disabled={!canEdit}
                  title={arrayMatchText}
                  aria-label={`${arrayMatchText}: ${arrayPath || arrayPathFallbackLabel}`}
                >
                  <span className="material-symbols-rounded" aria-hidden="true">{arrayMatchSymbol}</span>
                </button>
              )
            )}
          </div>
        );
      })}
    </div>
  );
};

const DATE_TEMPLATE_VARIABLE_OPTIONS = [
  { value: 'date.now_iso', label: 'Agora ISO (string)', type: 'string', group: 'configured' },
  { value: 'date.today_iso', label: 'Data de hoje ISO (string)', type: 'string', group: 'configured' },
  { value: 'date.now_unix_ms', label: 'Agora unix ms (number)', type: 'number', group: 'configured' },
  { value: 'date.now_unix_s', label: 'Agora unix s (number)', type: 'number', group: 'configured' },
  { value: 'date.now_br', label: 'Agora pt-BR (string)', type: 'string', group: 'configured' }
];

const templateCommandSourceNodeId = (sourceKey = '') => {
  const raw = String(sourceKey || '').trim();
  const match = raw.match(/^(?:api|command|cmd):(.+)$/i);
  return match?.[1] ? String(match[1]).trim() : '';
};

const normalizeTemplateCommandSourceVariables = (source = {}, normalizeUniqueVariables) => {
  const sourceNodeId = templateCommandSourceNodeId(source?.key);
  const expectedPrefix = sourceNodeId ? `outputs.${sourceNodeId}.` : '';
  const fallbackPrefix = sourceNodeId ? `${sourceNodeId}.` : '';
  const normalized = (Array.isArray(source?.variables) ? source.variables : []).map((item) => {
    const rawValue = String(item?.value || '').trim();
    if (!rawValue) return null;
    let value = rawValue;
    if (expectedPrefix && value.startsWith(expectedPrefix)) {
      value = value.slice(expectedPrefix.length);
    } else if (fallbackPrefix && value.startsWith(fallbackPrefix)) {
      value = value.slice(fallbackPrefix.length);
    } else if (value.startsWith('outputs.')) {
      value = value.replace(/^outputs\.[^.]+\.?/, '');
    }
    const normalizedValue = String(value || '').trim();
    if (!normalizedValue) return null;
    return {
      ...item,
      value: normalizedValue,
      label: String(item?.displayName || item?.label || normalizedValue).trim() || normalizedValue,
      displayName: String(item?.displayName || item?.label || normalizedValue).trim() || normalizedValue
    };
  }).filter(Boolean);
  return normalizeUniqueVariables(normalized);
};

const templateVariableSourceState = ({ suggestionSources = [], fallbackTaskVariables = [], includeDate = true } = {}) => {
  const mapped = (Array.isArray(suggestionSources) ? suggestionSources : [])
    .map((source, index) => {
      const sourceKey = String(source?.key || source?.value || `source-${index}`).trim();
      if (!sourceKey) return null;
      return {
        key: sourceKey,
        label: String(source?.label || sourceKey).trim() || sourceKey,
        variables: Array.isArray(source?.variables) ? source.variables : []
      };
    })
    .filter(Boolean);

  const normalizeUniqueVariables = (items = []) => {
    const unique = new Map();
    normalizeFileLikeVariableOptions(items).forEach((item) => {
      const itemValue = String(item?.value || '').trim();
      if (!itemValue || unique.has(itemValue)) return;
      unique.set(itemValue, item);
    });
    return [...unique.values()];
  };

  const taskSource = mapped.find((source) => source.key === 'task' || source.key === 'default') || null;
  const demandSource = mapped.find((source) => source.key === 'demand') || null;
  const commandSources = mapped.filter((source) => templateCommandSourceNodeId(source.key));
  const catalog = {
    task: {
      label: 'Task',
      variables: normalizeUniqueVariables(taskSource?.variables?.length ? taskSource.variables : fallbackTaskVariables)
    },
    demand: {
      label: 'Demanda',
      variables: normalizeUniqueVariables(demandSource?.variables || [])
    }
  };
  if (includeDate) {
    catalog.date = {
      label: 'Data relativa',
      variables: DATE_TEMPLATE_VARIABLE_OPTIONS
    };
  }
  commandSources.forEach((source) => {
    catalog[source.key] = {
      label: source.label || source.key,
      variables: normalizeTemplateCommandSourceVariables(source, normalizeUniqueVariables)
    };
  });

  const originOptions = [
    { key: 'task', label: catalog.task.label },
    { key: 'demand', label: catalog.demand.label },
    ...(includeDate ? [{ key: 'date', label: catalog.date.label }] : []),
    ...commandSources.map((source) => ({ key: source.key, label: source.label || source.key }))
  ].filter((option) => option.key !== 'demand' || catalog.demand.variables.length);

  return { catalog, originOptions };
};

const TemplateVariablePickerModal = ({
  open = false,
  canEdit = true,
  suggestionSources = [],
  fallbackTaskVariables = [],
  includeDate = true,
  title = 'Inserir variável',
  description = 'Escolha um atributo para inserir no texto como placeholder.',
  onClose,
  onConfirm
}) => {
  const sourceState = useMemo(() => templateVariableSourceState({
    suggestionSources,
    fallbackTaskVariables,
    includeDate
  }), [suggestionSources, fallbackTaskVariables, includeDate]);
  const [selectedOriginKey, setSelectedOriginKey] = useState('task');
  const [draftBinding, setDraftBinding] = useState({ path: '', arrayMatchByPath: {}, arrayNavigationByPath: {} });
  const selectedCommandSourceNodeId = templateCommandSourceNodeId(selectedOriginKey);
  const selectedOriginIsCommand = Boolean(selectedCommandSourceNodeId);
  const selectedOriginPrefix = selectedCommandSourceNodeId
    ? `outputs.${selectedCommandSourceNodeId}.`
    : '';
  const selectedVariables = useMemo(
    () => normalizeFileLikeVariableOptions(sourceState.catalog[selectedOriginKey]?.variables || []),
    [selectedOriginKey, sourceState]
  );

  useEffect(() => {
    if (!open) return;
    if (sourceState.originOptions.some((origin) => origin.key === selectedOriginKey)) return;
    setSelectedOriginKey('task');
  }, [open, selectedOriginKey, sourceState]);

  useEffect(() => {
    if (!open) return;
    const currentPath = String(normalizeTaskSelectorBinding(draftBinding).path || '').trim();
    if (currentPath && selectedVariables.some((option) => String(option?.value || '').trim() === currentPath)) return;
    setDraftBinding(normalizeTaskSelectorBinding({ path: selectedVariables[0]?.value || '' }));
  }, [open, draftBinding, selectedVariables]);

  if (!open) return null;

  return createPortal(
    <div className="flow-modal-backdrop" role="presentation" onClick={onClose}>
      <section className="flow-modal cortex-attribute-picker-modal" role="dialog" aria-modal="true" aria-label={title} onClick={(event) => event.stopPropagation()}>
        <header className="flow-modal-head">
          <div className="flow-modal-icon tone-purple">
            <span className="material-symbols-rounded" aria-hidden="true">data_object</span>
          </div>
          <div>
            <span>Variável de contexto</span>
            <h3>{title}</h3>
            <p>{description}</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Fechar modal">
            <span className="material-symbols-rounded" aria-hidden="true">close</span>
          </button>
        </header>

        <div className="flow-modal-body">
          <label className="modal-field">
            <FieldLabel>Origem</FieldLabel>
            <select value={selectedOriginKey} onChange={(event) => setSelectedOriginKey(event.target.value)}>
              {sourceState.originOptions.map((origin) => (
                <option key={origin.key} value={origin.key}>{origin.label}</option>
              ))}
            </select>
          </label>
          <section className="cortex-attribute-picker-panel">
            <span>Atributos disponíveis</span>
            <small>Valor do contexto</small>
            {selectedVariables.length ? (
              selectedOriginIsCommand ? (
                <select
                  value={String(normalizeTaskSelectorBinding(draftBinding).path || '')}
                  onChange={(event) => setDraftBinding(normalizeTaskSelectorBinding({ path: event.target.value }))}
                  disabled={!canEdit}
                >
                  {selectedVariables.map((variable) => {
                    const value = String(variable?.value || '').trim();
                    return (
                      <option key={value} value={value}>
                        {String(variable?.displayName || variable?.label || value)}
                      </option>
                    );
                  })}
                </select>
              ) : (
                <ContextVariablePathSelector
                  variables={selectedVariables}
                  binding={draftBinding}
                  canEdit={canEdit}
                  emptyLabel="Sem atributos disponíveis"
                  onChange={(nextBinding) => setDraftBinding(normalizeTaskSelectorBinding(nextBinding))}
                  arrayPathFallbackLabel="variável"
                />
              )
            ) : (
              <p className="cortex-attribute-picker-empty">Nenhum valor disponível para a origem selecionada.</p>
            )}
          </section>
        </div>

        <footer className="flow-modal-footer">
          <button className="secondary-button" type="button" onClick={onClose}>Cancelar</button>
          <button
            className="primary-button"
            type="button"
            onClick={() => {
              const normalized = normalizeTaskSelectorBinding(draftBinding);
              const path = String(normalized.path || '').trim();
              const effectivePath = selectedOriginPrefix && path && !path.startsWith(selectedOriginPrefix)
                ? `${selectedOriginPrefix}${path}`
                : path;
              onConfirm({ ...normalized, path: effectivePath });
            }}
            disabled={!String(normalizeTaskSelectorBinding(draftBinding).path || '').trim()}
          >
            Adicionar
          </button>
        </footer>
      </section>
    </div>,
    document.body
  );
};

const TemplateVariableSuggestInput = ({
  value = '',
  onChange,
  variables = [],
  suggestionSources = [],
  canEdit = true,
  placeholder = 'Texto personalizado (aceita $.atributo)',
  ariaLabel = 'Texto personalizado'
}) => {
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);

  const insertVariableToken = (path) => {
    const input = inputRef.current;
    const normalizedPath = String(path || '').trim().replace(/^\$\.?/, '');
    if (!normalizedPath) return;
    const token = `$.${normalizedPath}`;
    if (!input) {
      onChange(token);
      setOpen(false);
      return;
    }
    const currentValue = String(value ?? '');
    const start = Number.isInteger(input.selectionStart) ? input.selectionStart : currentValue.length;
    const end = Number.isInteger(input.selectionEnd) ? input.selectionEnd : start;
    const nextValue = `${currentValue.slice(0, start)}${token}${currentValue.slice(end)}`;
    onChange(nextValue);
    setOpen(false);
    requestAnimationFrame(() => {
      input.focus();
      const cursor = start + token.length;
      input.setSelectionRange(cursor, cursor);
    });
  };

  return (
    <div className="template-suggest-input">
      <input
        ref={inputRef}
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          const isSlashShortcut = (
            event.key === '/'
            || event.key === '?'
            || event.code === 'Slash'
            || event.code === 'NumpadDivide'
          );
          if (isSlashShortcut && !event.ctrlKey && !event.metaKey && !event.altKey) {
            event.preventDefault();
            setOpen(true);
            return;
          }
          if (!open) return;
          if (event.key === 'Escape') {
            event.preventDefault();
            setOpen(false);
          }
        }}
        placeholder={placeholder}
        disabled={!canEdit}
        aria-label={ariaLabel}
      />
      <TemplateVariablePickerModal
        open={open}
        canEdit={canEdit}
        suggestionSources={suggestionSources}
        fallbackTaskVariables={variables}
        includeDate
        title="Inserir variável"
        description="Escolha um atributo para inserir no texto como placeholder."
        onClose={() => setOpen(false)}
        onConfirm={(binding) => insertVariableToken(binding?.path || '')}
      />
    </div>
  );
};

const ConditionBuilder = ({
  config,
  nodeId,
  nodes,
  edges,
  apis,
  runtimeOutputs,
  demand,
  taskExtraVariables = [],
  canEdit,
  onChange
}) => {
  const sources = conditionVariableSources({
    nodeId,
    nodes,
    edges,
    apis,
    runtimeOutputs,
    demand,
    taskExtraVariables
  });
  const taskSource = sources.find((source) => String(source.nodeId) === 'task') || null;
  const hasTaskVariables = Boolean(taskSource?.variables?.length);
  const hasExecutionTaskSource = Boolean(taskSource?.fromExecutionStart);
  const current = normalizeConditionConfig(config, sources);
  const decisionMode = normalizeConditionDecisionMode(current.decisionMode);
  const [conditionDateModal, setConditionDateModal] = useState(null);
  const [conditionDateDraft, setConditionDateDraft] = useState(defaultApiDateBindingConfig());
  const updateConfig = (patch) => onChange({ ...current, ...patch });
  const openConditionDateModal = (payload = {}) => {
    setConditionDateDraft(normalizeApiDateBindingConfig(payload?.dateConfig || payload?.date_config || {}));
    setConditionDateModal(payload);
  };
  const closeConditionDateModal = () => setConditionDateModal(null);
  const patchConditionRule = (rule, patch = {}) => {
    const variableChanged = Object.prototype.hasOwnProperty.call(patch, 'sourceNodeId')
      || Object.prototype.hasOwnProperty.call(patch, 'variable');
    const nextSourceId = patch.sourceNodeId ?? rule.sourceNodeId;
    const source = sources.find((item) => String(item.nodeId) === String(nextSourceId));
    const firstVariable = source?.variables?.[0] || {};
    const nextVariable = normalizeConditionVariablePath(patch.sourceNodeId && patch.sourceNodeId !== rule.sourceNodeId
      ? firstVariable.value || ''
      : patch.variable ?? rule.variable);
    const variableMeta = conditionVariableMetadata(source?.variables, nextVariable);
    const arrayPaths = conditionArrayPathsForVariable(source?.variables, nextVariable);
    const currentArrayMatches = normalizeConditionArrayMatches(rule.arrayMatchByPath);
    const currentArrayNavigations = normalizeConditionArrayNavigations(
      patch.arrayNavigationByPath ?? rule.arrayNavigationByPath,
      patch.arrayMatchByPath ?? currentArrayMatches
    );
    const arrayMatchByPath = arrayPaths.reduce((acc, path) => {
      acc[path] = currentArrayNavigations[path]?.mode || currentArrayMatches[path] || 'any_equals';
      return acc;
    }, {});
    const arrayNavigationByPath = arrayPaths.reduce((acc, path) => {
      acc[path] = normalizeArrayNavigation(currentArrayNavigations[path], arrayMatchByPath[path]);
      return acc;
    }, {});
    const variableType = variableMeta?.type || rule.variableType || '';
    const isDateType = isDateLikeSchemaType(variableType);
    const hasExplicitDateConfig = Object.prototype.hasOwnProperty.call(patch, 'dateConfig')
      || Object.prototype.hasOwnProperty.call(patch, 'date_config');
    const currentDateConfig = hasExplicitDateConfig
      ? (patch.dateConfig || patch.date_config)
      : (rule.dateConfig || rule.date_config);
    const dateConfig = isDateType
      ? (currentDateConfig && typeof currentDateConfig === 'object' && !Array.isArray(currentDateConfig)
        ? normalizeApiDateBindingConfig(currentDateConfig)
        : (variableChanged ? defaultApiDateBindingConfig() : undefined))
      : undefined;
    return {
      ...rule,
      ...patch,
      sourceNodeId: nextSourceId || '',
      sourceType: source?.sourceType || 'api',
      integrationId: source?.integrationId || rule.integrationId || '',
      variable: nextVariable,
      variableType,
      dateConfig,
      arrayMatchByPath: patch.arrayMatchByPath
        ? normalizeConditionArrayMatches(patch.arrayMatchByPath)
        : arrayMatchByPath,
      arrayNavigationByPath,
      value: patch.sourceNodeId && patch.sourceNodeId !== rule.sourceNodeId ? '' : patch.value ?? rule.value
    };
  };
  const updateRule = (id, patch) => {
    updateConfig({
      conditions: current.conditions.map((rule) => {
        if (rule.id !== id) return rule;
        return patchConditionRule(rule, patch);
      })
    });
  };
  const addRule = () => updateConfig({ conditions: current.conditions.concat(makeConditionRule(sources)) });
  const removeRule = (id) => {
    if (current.conditions.length <= 1) return;
    updateConfig({ conditions: current.conditions.filter((rule) => rule.id !== id) });
  };
  const updateDecisionPath = (id, patch = {}) => {
    const nextPaths = (Array.isArray(current.decisionPaths) ? current.decisionPaths : [])
      .map((path) => (path.id === id ? { ...path, ...patch } : path));
    const normalizedPaths = normalizeConditionDecisionPaths(nextPaths, 'multiple', sources);
    updateConfig({ decisionPaths: normalizedPaths, decisionSource: normalizeConditionDecisionSource(current.decisionSource, sources, normalizedPaths) });
  };
  const updateDecisionPathRule = (pathId, ruleId, patch = {}) => {
    const nextPaths = (Array.isArray(current.decisionPaths) ? current.decisionPaths : []).map((path) => {
      if (path.id !== pathId) return path;
      return {
        ...path,
        conditions: (Array.isArray(path.conditions) ? path.conditions : []).map((rule) => {
          if (rule.id !== ruleId) return rule;
          return patchConditionRule(rule, patch);
        })
      };
    });
    updateConfig({ decisionPaths: normalizeConditionDecisionPaths(nextPaths, 'multiple', sources) });
  };
  const addDecisionPathRule = (pathId) => {
    const nextPaths = (Array.isArray(current.decisionPaths) ? current.decisionPaths : []).map((path) => {
      if (path.id !== pathId) return path;
      return {
        ...path,
        conditions: (Array.isArray(path.conditions) ? path.conditions : []).concat(makeConditionRule(sources))
      };
    });
    updateConfig({ decisionPaths: normalizeConditionDecisionPaths(nextPaths, 'multiple', sources) });
  };
  const removeDecisionPathRule = (pathId, ruleId) => {
    const nextPaths = (Array.isArray(current.decisionPaths) ? current.decisionPaths : []).map((path) => {
      if (path.id !== pathId) return path;
      const currentRules = Array.isArray(path.conditions) ? path.conditions : [];
      const nextRules = currentRules.filter((rule) => rule.id !== ruleId);
      return {
        ...path,
        conditions: nextRules.length ? nextRules : [makeConditionRule(sources)]
      };
    });
    updateConfig({ decisionPaths: normalizeConditionDecisionPaths(nextPaths, 'multiple', sources) });
  };
  const addDecisionPath = () => {
    const currentPaths = Array.isArray(current.decisionPaths) ? current.decisionPaths : [];
    const normalizedPaths = normalizeConditionDecisionPaths(
      currentPaths.concat({
        ...makeDefaultConditionDecisionPath(currentPaths.length),
        conditions: [makeConditionRule(sources)]
      }),
      'multiple',
      sources
    );
    updateConfig({ decisionPaths: normalizedPaths, decisionSource: normalizeConditionDecisionSource(current.decisionSource, sources, normalizedPaths) });
  };
  const removeDecisionPath = (id) => {
    const currentPaths = Array.isArray(current.decisionPaths) ? current.decisionPaths : [];
    const normalizedPaths = normalizeConditionDecisionPaths(
      currentPaths.filter((path) => path.id !== id),
      'multiple',
      sources
    );
    updateConfig({ decisionPaths: normalizedPaths, decisionSource: normalizeConditionDecisionSource(current.decisionSource, sources, normalizedPaths) });
  };

  return (
    <section className="condition-builder modal-field-wide">
      <header className="condition-builder-head">
        <div>
          <span>{decisionMode === 'multiple' ? 'Múltiplas decisões' : 'Condições inteligentes'}</span>
          {decisionMode === 'multiple' ? (
            <p>Defina as decisões e as condições de cada decisão.</p>
          ) : (
            <>
              <strong>Task + {Math.max(sources.length - 1, 0)} API(s) antecessora(s)</strong>
              <p>{hasExecutionTaskSource
                ? 'As variáveis abaixo vêm dos dados retornados pelo Início - Buscar e das chaves de API/Cortex executados antes deste comando.'
                : 'As variáveis abaixo vêm dos atributos da Task e das chaves de API/Cortex executados antes deste comando.'}
              </p>
            </>
          )}
        </div>
        {decisionMode === 'boolean' ? (
          <label>
            <FieldLabel helpKey="Combinação das regras">Combinação das regras</FieldLabel>
            <select value={current.conditionLogic || 'AND'} onChange={(event) => updateConfig({ conditionLogic: event.target.value })} disabled={!canEdit}>
              <option value="AND">Todas as regras (AND)</option>
              <option value="OR">Qualquer regra (OR)</option>
            </select>
          </label>
        ) : null}
      </header>

      <section className="modal-field modal-field-wide">
        <FieldLabel>Tipo de decisão</FieldLabel>
        <div className="condition-decision-mode-options" role="radiogroup" aria-label="Tipo de decisão">
          <label className="condition-decision-mode-option">
            <input
              type="radio"
              name="decision-mode"
              value="boolean"
              checked={decisionMode === 'boolean'}
              onChange={() => updateConfig({ decisionMode: 'boolean' })}
              disabled={!canEdit}
            />
            <span>Sucesso / Insucesso</span>
          </label>
          <label className="condition-decision-mode-option">
            <input
              type="radio"
              name="decision-mode"
              value="multiple"
              checked={decisionMode === 'multiple'}
              onChange={() => {
                const configuredPaths = normalizeConditionDecisionPaths(current.decisionPaths, 'multiple', sources);
                const ensuredPaths = configuredPaths.length
                  ? configuredPaths
                  : normalizeConditionDecisionPaths([{
                    ...makeDefaultConditionDecisionPath(0),
                    conditions: [makeConditionRule(sources)]
                  }], 'multiple', sources);
                updateConfig({
                  decisionMode: 'multiple',
                  decisionPaths: ensuredPaths,
                  decisionSource: normalizeConditionDecisionSource(current.decisionSource, sources, ensuredPaths)
                });
              }}
              disabled={!canEdit}
            />
            <span>Múltiplas decisões</span>
          </label>
        </div>
      </section>

      {!hasTaskVariables && sources.length <= 1 && (
        <div className="condition-empty-source">
          <span className="material-symbols-rounded" aria-hidden="true">info</span>
          <div>
            <strong>Nenhum atributo de Task disponível</strong>
            <p>{hasExecutionTaskSource
              ? 'Configure o Início - Buscar para retornar dados iteráveis ou conecte uma API/Cortex antes deste comando para usar variáveis de saída.'
              : 'Defina atributos na Demanda ou conecte uma API/Cortex antes deste comando para usar variáveis de saída.'}
            </p>
          </div>
        </div>
      )}

      {decisionMode === 'boolean' ? (
      <div className="condition-rule-table condition-rule-table-boolean">
        <div className="condition-rule-row is-head">
          <span>Fonte</span>
          <span>Atributo</span>
          <span>Operador</span>
          <span>Valor</span>
          <span></span>
        </div>
        {current.conditions.map((rule) => {
          const source = sources.find((item) => String(item.nodeId) === String(rule.sourceNodeId)) || sources[0] || {};
          const variables = source.variables || [];
          return (
            <div className="condition-rule-row condition-rule-row-boolean" key={rule.id}>
              <select value={rule.sourceNodeId || source.nodeId || ''} onChange={(event) => updateRule(rule.id, { sourceNodeId: event.target.value })} disabled={!canEdit || !sources.length}>
                {!sources.length && <option value="">Sem fonte</option>}
                {sources.map((item) => (
                  <option key={item.nodeId} value={item.nodeId}>{item.title}</option>
                ))}
              </select>
              <ContextVariablePathSelector
                variables={variables}
                binding={{
                  path: rule.variable || '',
                  arrayMatchByPath: rule.arrayMatchByPath || {},
                  arrayNavigationByPath: rule.arrayNavigationByPath || {}
                }}
                canEdit={canEdit}
                emptyLabel="Sem atributos conhecidos"
                containerClassName="condition-variable-selectors"
                controlClassName="condition-variable-selector-control"
                arrayPathFallbackLabel="lista retornada"
                advancedArrayNavigation
                onChange={(binding) => updateRule(rule.id, {
                  variable: binding.path,
                  arrayMatchByPath: binding.arrayMatchByPath,
                  arrayNavigationByPath: binding.arrayNavigationByPath
                })}
              />
              <select value={rule.operator || 'equals'} onChange={(event) => updateRule(rule.id, { operator: event.target.value })} disabled={!canEdit}>
                {conditionOperatorOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
              {isDateLikeSchemaType(rule.variableType) ? (
                <div className="api-variable-date-cell">
                  <RelativeDateTrigger
                    config={rule.dateConfig}
                    onConfigure={() => openConditionDateModal({ scope: 'boolean', ruleId: rule.id, dateConfig: rule.dateConfig })}
                    disabled={!canEdit || ['exists', 'not_exists', 'empty', 'not_empty'].includes(rule.operator)}
                  />
                </div>
              ) : (
                <input
                  value={rule.value ?? ''}
                  onChange={(event) => updateRule(rule.id, { value: event.target.value })}
                  disabled={!canEdit || ['exists', 'not_exists', 'empty', 'not_empty'].includes(rule.operator)}
                  placeholder={rule.variableType ? `Valor ${schemaTypeLabel(rule.variableType)}` : 'Valor esperado'}
                />
              )}
              <button className="icon-button" type="button" onClick={() => removeRule(rule.id)} disabled={!canEdit || current.conditions.length <= 1} aria-label="Remover condição">
                <span className="material-symbols-rounded" aria-hidden="true">delete</span>
              </button>
            </div>
          );
        })}
      </div>
      ) : (
        <section className="condition-paths-builder" aria-label="Editor de caminhos">
          {(current.decisionPaths || []).map((path, index) => {
            const rules = Array.isArray(path.conditions) && path.conditions.length
              ? path.conditions
              : [makeConditionRule(sources)];
            const selectedColor = normalizeConditionDecisionColor(path.color, index);
            const selectedColorHex = conditionDecisionColorHex(selectedColor, index);
            const selectedColorLabel = conditionDecisionColorOptions.find((option) => option.value === selectedColor)?.label || 'Cor';
            return (
              <section className="condition-path-card modal-field modal-field-wide" key={path.id || `path-${index}`}>
                <div className="condition-rule-row is-head condition-path-meta-head">
                  <span>Decisão</span>
                  <span>Combinação</span>
                  <span>Cor</span>
                </div>
                <div className="condition-rule-row condition-path-meta-row">
                  <input
                    value={path.label || ''}
                    onChange={(event) => updateDecisionPath(path.id, { label: event.target.value })}
                    placeholder={`Decisão ${index + 1}`}
                    disabled={!canEdit}
                  />
                  <select
                    value={normalizeConditionPathLogic(path.conditionLogic)}
                    onChange={(event) => updateDecisionPath(path.id, { conditionLogic: event.target.value })}
                    disabled={!canEdit}
                  >
                    <option value="AND">AND</option>
                    <option value="OR">OR</option>
                  </select>
                  <details className={`condition-path-color-picker${canEdit ? '' : ' is-disabled'}`}>
                    <summary
                      className="condition-path-color-trigger"
                      aria-label={`Selecionar cor da decisão ${index + 1}`}
                      onClick={(event) => {
                        if (!canEdit) {
                          event.preventDefault();
                        }
                      }}
                    >
                      <span className="condition-path-color-dot is-selected" style={{ backgroundColor: selectedColorHex }} />
                      <span className="condition-path-color-trigger-label">{selectedColorLabel}</span>
                      <span className="material-symbols-rounded" aria-hidden="true">expand_more</span>
                    </summary>
                    <div className="condition-path-color-palette" role="radiogroup" aria-label={`Cor da decisão ${index + 1}`}>
                      {conditionDecisionColorOptions.map((option) => {
                        const isSelected = selectedColor === option.value;
                        return (
                          <button
                            key={option.value}
                          type="button"
                          className={`condition-path-color-dot${isSelected ? ' is-selected' : ''}`}
                          title={option.label}
                          aria-label={option.label}
                          aria-pressed={isSelected}
                            onClick={(event) => {
                              updateDecisionPath(path.id, { color: option.value });
                              const picker = event.currentTarget.closest('.condition-path-color-picker');
                              if (picker) picker.removeAttribute('open');
                            }}
                            disabled={!canEdit}
                          >
                            <span className="condition-path-color-dot-swatch" style={{ backgroundColor: option.hex }} />
                            <span className="condition-path-color-dot-label">{option.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </details>
                  <button
                    className="secondary-button compact-action condition-path-remove-button"
                    type="button"
                    onClick={() => removeDecisionPath(path.id)}
                    disabled={!canEdit}
                    aria-label="Excluir condição"
                  >
                    Excluir condição
                    <span className="material-symbols-rounded" aria-hidden="true">delete</span>
                  </button>
                </div>

                <div className="condition-rule-table">
                <div className="condition-rule-row is-head">
                  <span>Fonte</span>
                  <span>Atributo</span>
                  <span>Operador</span>
                  <span>Valor</span>
                  <span aria-hidden="true" />
                </div>
                  {rules.map((rule) => {
                    const source = sources.find((item) => String(item.nodeId) === String(rule.sourceNodeId)) || sources[0] || {};
                    const variables = source.variables || [];
                    return (
                      <div className="condition-rule-row" key={rule.id}>
                        <select value={rule.sourceNodeId || source.nodeId || ''} onChange={(event) => updateDecisionPathRule(path.id, rule.id, { sourceNodeId: event.target.value })} disabled={!canEdit || !sources.length}>
                          {!sources.length && <option value="">Sem fonte</option>}
                          {sources.map((item) => (
                            <option key={item.nodeId} value={item.nodeId}>{item.title}</option>
                          ))}
                        </select>
                        <ContextVariablePathSelector
                          variables={variables}
                          binding={{
                            path: rule.variable || '',
                            arrayMatchByPath: rule.arrayMatchByPath || {},
                            arrayNavigationByPath: rule.arrayNavigationByPath || {}
                          }}
                          canEdit={canEdit}
                          emptyLabel="Sem atributos conhecidos"
                          containerClassName="condition-variable-selectors"
                          controlClassName="condition-variable-selector-control"
                          arrayPathFallbackLabel="lista retornada"
                          advancedArrayNavigation
                          onChange={(binding) => updateDecisionPathRule(path.id, rule.id, {
                            variable: binding.path,
                            arrayMatchByPath: binding.arrayMatchByPath,
                            arrayNavigationByPath: binding.arrayNavigationByPath
                          })}
                        />
                        <select value={rule.operator || 'equals'} onChange={(event) => updateDecisionPathRule(path.id, rule.id, { operator: event.target.value })} disabled={!canEdit}>
                          {conditionOperatorOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select>
                        {isDateLikeSchemaType(rule.variableType) ? (
                          <div className="api-variable-date-cell">
                            <RelativeDateTrigger
                              config={rule.dateConfig}
                              onConfigure={() => openConditionDateModal({ scope: 'multiple', pathId: path.id, ruleId: rule.id, dateConfig: rule.dateConfig })}
                              disabled={!canEdit || ['exists', 'not_exists', 'empty', 'not_empty'].includes(rule.operator)}
                            />
                          </div>
                        ) : (
                          <input
                            value={rule.value ?? ''}
                            onChange={(event) => updateDecisionPathRule(path.id, rule.id, { value: event.target.value })}
                            disabled={!canEdit || ['exists', 'not_exists', 'empty', 'not_empty'].includes(rule.operator)}
                            placeholder={rule.variableType ? `Valor ${schemaTypeLabel(rule.variableType)}` : 'Valor esperado'}
                          />
                        )}
                        <button className="icon-button" type="button" onClick={() => removeDecisionPathRule(path.id, rule.id)} disabled={!canEdit} aria-label="Remover condição">
                          <span className="material-symbols-rounded" aria-hidden="true">delete</span>
                        </button>
                      </div>
                    );
                  })}
                </div>

                <button className="secondary-button compact-action condition-path-add-rule-button" type="button" onClick={() => addDecisionPathRule(path.id)} disabled={!canEdit}>
                  <span className="material-symbols-rounded" aria-hidden="true">add</span>
                  Adicionar condição
                </button>
              </section>
            );
          })}
          <div className="condition-paths-footer">
            <button className="secondary-button compact-action condition-path-add-decision-button" type="button" onClick={addDecisionPath} disabled={!canEdit}>
              <span className="material-symbols-rounded" aria-hidden="true">add</span>
              Adicionar decisão
            </button>
          </div>
        </section>
      )}

      {decisionMode === 'boolean' ? (
        <div className="condition-builder-actions condition-builder-actions-boolean">
          <button className="secondary-button compact-action" type="button" onClick={addRule} disabled={!canEdit}>
            <span className="material-symbols-rounded" aria-hidden="true">add</span>
            Adicionar condição
          </button>
          <label>
            <FieldLabel>Política de erro</FieldLabel>
            <select value={normalizeConditionErrorPolicy(current.errorPolicy)} onChange={(event) => updateConfig({ errorPolicy: event.target.value })} disabled={!canEdit}>
              {conditionErrorPolicyOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </label>
        </div>
      ) : (
        <div className="condition-builder-actions">
          <span />
          <label>
            <FieldLabel>Política de erro</FieldLabel>
            <select
              key={(current.decisionPaths || []).map((path, index) => `${path.id || `fallback-path-${index}`}:${path.label || ''}`).join('|')}
              value={current.decisionSource?.fallbackPathId || (current.decisionPaths?.[0]?.id || '')}
              onChange={(event) => updateConfig({
                decisionSource: normalizeConditionDecisionSource(
                  { ...(current.decisionSource || {}), fallbackPathId: event.target.value },
                  sources,
                  current.decisionPaths
                )
              })}
              disabled={!canEdit || !(current.decisionPaths || []).length}
            >
              {(current.decisionPaths || []).map((path, index) => (
                <option key={path.id || `fallback-path-${index}`} value={path.id || ''}>
                  {conditionPathDisplayLabel(path, index)}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      <RelativeDateModal
        open={Boolean(conditionDateModal)}
        title="Configurar data da condição"
        description="A data e hora da comparação são calculadas em tempo de execução, com base no momento de cada processamento."
        draft={conditionDateDraft}
        onDraftChange={setConditionDateDraft}
        onCancel={closeConditionDateModal}
        onApply={(nextConfig) => {
          if (conditionDateModal?.scope === 'multiple' && conditionDateModal.pathId && conditionDateModal.ruleId) {
            updateDecisionPathRule(conditionDateModal.pathId, conditionDateModal.ruleId, { dateConfig: nextConfig, value: '' });
          } else if (conditionDateModal?.ruleId) {
            updateRule(conditionDateModal.ruleId, { dateConfig: nextConfig, value: '' });
          }
          closeConditionDateModal();
        }}
      />
    </section>
  );
};

const ApiIntegrationSummary = ({ integration, runtimeOutput = null, collapsible = false, defaultExpanded = true }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  useEffect(() => {
    setExpanded(defaultExpanded);
  }, [defaultExpanded, integration?.id]);

  if (!integration) {
    return (
      <div className="api-integration-empty modal-field-wide">
        <span className="material-symbols-rounded" aria-hidden="true">info</span>
        <div>
          <strong>Selecione uma Integração</strong>
          <p>O comando referencia uma Integração que já recebeu uma tentativa de teste. Uma falha pontual não impede seu uso.</p>
        </div>
      </div>
    );
  }

  const variables = mergeSourceVariableOptions({
    schema: integration.response_schema || {},
    runtimeOutput
  });
  const groups = groupedVariableOptions(variables);
  const lastAttemptFailed = integrationLastAttemptFailed(integration);
  const isExpanded = collapsible ? expanded : true;
  const collapseLabel = isExpanded ? 'Recolher detalhes da integração' : 'Expandir detalhes da integração';
  return (
    <section className="api-integration-summary modal-field-wide">
      <header>
        <div>
          <span>Integração selecionada</span>
          <strong>{integration.name}</strong>
        </div>
        <div className="api-integration-summary-actions">
          <small>{integration.request_method || 'GET'} {integration.request_path || integration.base_url}</small>
          {collapsible && (
            <button
              className="secondary-button compact-action api-integration-collapse-btn"
              type="button"
              aria-expanded={isExpanded}
              aria-label={collapseLabel}
              onClick={() => setExpanded((current) => !current)}
            >
              <span className="material-symbols-rounded" aria-hidden="true">{isExpanded ? 'expand_less' : 'expand_more'}</span>
              {isExpanded ? 'Recolher' : 'Expandir'}
            </button>
          )}
        </div>
      </header>
      {isExpanded && (
        <>
          <div className="api-integration-meta">
            <span className={lastAttemptFailed ? 'is-warning' : 'is-success'}>
              {lastAttemptFailed ? 'Último teste falhou · uso permitido' : `Status ${integration.last_status || 'disponível'}`}
            </span>
            <span>{integration.last_latency_ms ? `${integration.last_latency_ms} ms` : 'sem latencia'}</span>
            <span>{variables.length} keys conhecidas</span>
          </div>
          <div className="api-schema-preview">
            {groups.length ? groups.flatMap(([group, options]) => [
              <strong className="api-schema-group-label" key={`${group}-label`}>{variableGroupLabel(group)}</strong>,
              ...options.map((variable) => (
                <code key={variable.value}><b>{variable.value}</b><span>{variable.type}</span></code>
              ))
            ]) : (
              <p>Nenhuma key JSON aprendida nesta integração ainda.</p>
            )}
          </div>
        </>
      )}
    </section>
  );
};

const START_EXECUTION_SOURCE_OPTIONS = [
  { value: '', label: 'Selecione' },
  { value: 'date', label: 'Data relativa' }
];

const normalizeStartExecutionVariableBinding = (binding) => {
  if (!binding || typeof binding !== 'object' || Array.isArray(binding)) return null;
  if (String(binding.sourceType || '').trim().toLowerCase() !== 'date') return null;
  return {
    sourceType: 'date',
    dateConfig: normalizeApiDateBindingConfig(binding.dateConfig || binding.date_config || {})
  };
};

const startExecutionSourceValueForBinding = (binding) => (
  binding?.sourceType === 'date' ? 'date' : ''
);

const StartExecutionVariables = ({ integration, config, canEdit, onChange }) => {
  const configured = normalizeResponseSchema(config.executionVariableBindings);
  const legacyConfigured = normalizeResponseSchema(config.executionTemplateVariables);
  const variables = [...new Set([
    ...integrationTemplateVariables(integration),
    ...Object.keys(configured),
    ...Object.keys(legacyConfigured)
  ])].sort((left, right) => left.localeCompare(right, 'pt-BR'));
  const [dateBindingModal, setDateBindingModal] = useState(null);
  const [dateBindingDraft, setDateBindingDraft] = useState(defaultApiDateBindingConfig());

  if (!variables.length) return null;

  const resolveBinding = (variable) => normalizeStartExecutionVariableBinding(configured[variable]);

  const updateBindings = (variable, binding = null) => {
    const nextBindings = { ...configured };
    if (!binding) {
      delete nextBindings[variable];
    } else {
      nextBindings[variable] = binding;
    }
    onChange(nextBindings);
  };

  const openDateBindingModal = (variable, binding = {}) => {
    setDateBindingDraft(normalizeApiDateBindingConfig(binding?.dateConfig || binding?.date_config || {}));
    setDateBindingModal({ variable });
  };

  const closeDateBindingModal = () => {
    setDateBindingModal(null);
  };

  const updateSource = (variable, sourceValue) => {
    if (sourceValue !== 'date') {
      updateBindings(variable, null);
      return;
    }
    const previous = resolveBinding(variable);
    const normalizedDateConfig = normalizeApiDateBindingConfig(previous?.dateConfig || previous?.date_config || {});
    const nextBinding = {
      sourceType: 'date',
      dateConfig: normalizedDateConfig
    };
    updateBindings(variable, nextBinding);
    openDateBindingModal(variable, nextBinding);
  };

  const activeDateBinding = normalizeApiDateBindingConfig(dateBindingDraft);
  const activeDatePreviewNow = new Date();
  const activeDatePreview = resolveApiDateBindingValue(activeDateBinding, activeDatePreviewNow);

  return (
    <>
      <section className="api-variable-bindings modal-field-wide">
        <header>
          <div>
            <FieldLabel>Variáveis da request</FieldLabel>
            <strong>{variables.length} variável(is) detectada(s)</strong>
            <p>Use placeholders como <code>{'{{cliente_id}}'}</code> no body da Integração e vincule cada valor ao contexto do roteamento.</p>
          </div>
        </header>
        <div className="api-variable-table" role="table" aria-label="Variáveis da request do Início">
          <div className="api-variable-row is-head" role="row">
            <span>Variável</span>
            <span>Origem</span>
            <span>Valor do contexto</span>
          </div>
          {variables.map((variable) => {
            const binding = resolveBinding(variable);
            const sourceValue = startExecutionSourceValueForBinding(binding);
            return (
              <div className="api-variable-row" role="row" key={variable}>
                <code>{`{{${variable}}}`}</code>
                <div className="api-variable-source-cell">
                  <select value={sourceValue} onChange={(event) => updateSource(variable, event.target.value)} disabled={!canEdit}>
                    {START_EXECUTION_SOURCE_OPTIONS.map((option) => (
                      <option key={option.value || '__empty__'} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                {binding?.sourceType === 'date' ? (
                  <div className="api-variable-date-cell">
                    <RelativeDateTrigger
                      config={binding.dateConfig}
                      onConfigure={() => openDateBindingModal(variable, binding)}
                      disabled={!canEdit}
                    />
                  </div>
                ) : (
                  <span className="api-variable-static-info">Selecione "Data relativa"</span>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {dateBindingModal && (
        <div className="flow-modal-backdrop" role="presentation" onClick={closeDateBindingModal}>
          <section
            className="flow-modal api-date-binding-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Configurar variável de data do Início"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="flow-modal-head">
              <div className="flow-modal-icon tone-blue">
                <span className="material-symbols-rounded" aria-hidden="true">schedule</span>
              </div>
              <div>
                <span>Data relativa</span>
                <h3>{`Configurar {{${dateBindingModal.variable}}}`}</h3>
                <p>Defina o deslocamento em minutos, horas ou dias para calcular a data em tempo de execução.</p>
              </div>
              <button className="icon-button" type="button" onClick={closeDateBindingModal} aria-label="Fechar modal">
                <span className="material-symbols-rounded" aria-hidden="true">close</span>
              </button>
            </header>

            <div className="flow-modal-body api-date-binding-body">
              <div className="api-date-binding-grid">
                <label className="modal-field">
                  <FieldLabel>Quantidade</FieldLabel>
                  <input
                    type="number"
                    min={0}
                    max={525600}
                    step={1}
                    value={String(dateBindingDraft.amount ?? '')}
                    onChange={(event) => setDateBindingDraft((current) => ({ ...current, amount: event.target.value }))}
                  />
                </label>
                <label className="modal-field">
                  <FieldLabel>Unidade</FieldLabel>
                  <select
                    value={activeDateBinding.unit}
                    onChange={(event) => setDateBindingDraft((current) => ({ ...current, unit: event.target.value }))}
                  >
                    {API_DATE_UNIT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
                <label className="modal-field">
                  <FieldLabel>Direção</FieldLabel>
                  <select
                    value={activeDateBinding.direction}
                    onChange={(event) => setDateBindingDraft((current) => ({ ...current, direction: event.target.value }))}
                  >
                    {API_DATE_DIRECTION_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
              </div>

              <section className="api-date-binding-preview">
                <span>Exemplo com hora atual</span>
                <div>
                  <p>{`Agora: ${formatApiDatePreview(activeDatePreviewNow)} (${activeDatePreviewNow.toISOString()})`}</p>
                  <p>{`Resultado (${apiDateBindingSummary(activeDateBinding)}): ${formatApiDatePreview(activeDatePreview.date)} (${activeDatePreview.iso})`}</p>
                </div>
              </section>
            </div>

            <footer className="flow-modal-footer">
              <button className="secondary-button" type="button" onClick={closeDateBindingModal}>Cancelar</button>
              <button
                className="primary-button"
                type="button"
                onClick={() => {
                  updateBindings(dateBindingModal.variable, {
                    sourceType: 'date',
                    dateConfig: activeDateBinding
                  });
                  closeDateBindingModal();
                }}
              >
                Aplicar data
              </button>
            </footer>
          </section>
        </div>
      )}
    </>
  );
};

const ApiVariableBindings = ({
  integration,
  config,
  demand,
  nodeId,
  nodes,
  edges,
  apis,
  runtimeOutputs,
  taskExtraVariables = [],
  canEdit,
  onChange
}) => {
  const hasIntegration = Boolean(integration);
  const { sources, taskOptions, taskSourceLabel } = apiVariableBindingContext({
    nodeId,
    nodes,
    edges,
    apis,
    runtimeOutputs,
    demand,
    taskExtraVariables
  });
  const variables = hasIntegration ? integrationTemplateVariables(integration) : [];
  const current = normalizeApiVariableBindings(config, hasIntegration ? integration : null, demand, sources, taskOptions);
  const sourceOptions = bindingSourceOptions(sources, taskSourceLabel);
  const literalSuggestionSources = templateSuggestSourceGroups({
    taskLabel: taskSourceLabel,
    taskVariables: taskSelectorVariableOptions(taskOptions, {
      includeTaskMeta: true,
      includeDemandMeta: true
    }),
    includeDemand: true,
    apiSources: sources
  });
  const [dateBindingModal, setDateBindingModal] = useState(null);
  const [dateBindingDraft, setDateBindingDraft] = useState(defaultApiDateBindingConfig());

  if (!hasIntegration) return null;

  const updateBindings = (variable, binding) => {
    onChange({
      ...current,
      variableBindings: {
        ...current.variableBindings,
        [variable]: binding
      }
    });
  };

  const openDateBindingModal = (variable, binding = {}) => {
    setDateBindingDraft(normalizeApiDateBindingConfig(binding?.dateConfig || binding?.date_config || {}));
    setDateBindingModal({ variable });
  };

  const closeDateBindingModal = () => {
    setDateBindingModal(null);
  };

  const sourceValueForBinding = (binding = {}) => {
    if (binding.sourceType === 'api') return `api:${binding.sourceNodeId || ''}`;
    if (binding.sourceType === 'literal') return 'literal';
    if (binding.sourceType === 'date') return 'date';
    return 'task';
  };

  const updateSource = (variable, value) => {
    const previous = current.variableBindings?.[variable] || defaultApiVariableBinding(taskOptions, sources);
    if (value === 'task') {
      updateBindings(variable, {
        sourceType: 'task',
        path: previous.sourceType === 'task'
          ? String(previous.path || '')
          : (taskOptions[0]?.value || '')
      });
      return;
    }

    if (value === 'date') {
      const normalizedDateConfig = normalizeApiDateBindingConfig(previous.dateConfig || previous.date_config || {});
      updateBindings(variable, {
        sourceType: 'date',
        dateConfig: normalizedDateConfig
      });
      openDateBindingModal(variable, { dateConfig: normalizedDateConfig });
      return;
    }

    if (value === 'literal') {
      updateBindings(variable, {
        sourceType: 'literal',
        literalValue: Object.prototype.hasOwnProperty.call(previous || {}, 'literalValue')
          ? previous.literalValue
          : (previous.literal_value ?? '')
      });
      return;
    }

    const sourceType = 'api';
    const sourceNodeId = value.replace(/^api:/, '');
    const source = sources.find((item) => String(item.nodeId) === String(sourceNodeId));
    const firstVariable = source?.variables?.[0];
    updateBindings(variable, {
      sourceType,
      sourceNodeId,
      integrationId: source?.integrationId || '',
      path: (
        previous.sourceType === 'api' && String(previous.sourceNodeId || '') === String(sourceNodeId)
          ? String(previous.path || '')
          : (firstVariable?.value || '')
      )
    });
  };

  const activeDateBinding = normalizeApiDateBindingConfig(dateBindingDraft);
  const activeDatePreviewNow = new Date();
  const activeDatePreview = resolveApiDateBindingValue(activeDateBinding, activeDatePreviewNow);

  return (
    <>
      <section className="api-variable-bindings modal-field-wide">
        <header>
          <div>
            <FieldLabel>Variáveis da request</FieldLabel>
            <strong>{variables.length} variável(is) detectada(s)</strong>
            <p>Use placeholders como <code>{'{{cliente_id}}'}</code> na URI ou body da Integração e vincule cada valor ao contexto do roteamento.</p>
          </div>
        </header>

        {!variables.length ? (
          <div className="api-variable-empty">
            <span className="material-symbols-rounded" aria-hidden="true">data_object</span>
            <p>Esta Integração não possui variáveis no formato <code>{'{{VAR}}'}</code> na URI/body.</p>
          </div>
        ) : (
          <div className="api-variable-table" role="table" aria-label="Variáveis da request da Integração">
            <div className="api-variable-row is-head" role="row">
              <span>Variável</span>
              <span>Origem</span>
              <span>Valor do contexto</span>
            </div>
            {variables.map((variable) => {
              const binding = normalizeApiVariableBinding(current.variableBindings?.[variable], taskOptions, sources);
              const sourceValue = sourceValueForBinding(binding);
              const isDateSource = binding.sourceType === 'date';
              const selectedSource = sources.find((item) => String(item.nodeId) === String(binding.sourceNodeId));
              const variableOptions = binding.sourceType === 'api'
                ? selectedSource?.variables || []
                : taskOptions;
              return (
                <div className="api-variable-row" role="row" key={variable}>
                  <code>{`{{${variable}}}`}</code>
                  <div className="api-variable-source-cell">
                    <select value={sourceValue} onChange={(event) => updateSource(variable, event.target.value)} disabled={!canEdit}>
                      {sourceOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  {isDateSource ? (
                    <div className="api-variable-date-cell">
                      <RelativeDateTrigger
                        config={binding.dateConfig}
                        onConfigure={() => openDateBindingModal(variable, binding)}
                        disabled={!canEdit}
                      />
                    </div>
                  ) : binding.sourceType === 'literal' ? (
                    <TemplateVariableSuggestInput
                      value={Object.prototype.hasOwnProperty.call(binding || {}, 'literalValue')
                        ? (binding.literalValue ?? '')
                        : (binding.literal_value ?? '')}
                      onChange={(nextValue) => updateBindings(variable, {
                        sourceType: 'literal',
                        literalValue: nextValue
                      })}
                      variables={variableOptions}
                      suggestionSources={literalSuggestionSources}
                      canEdit={canEdit}
                      ariaLabel={`Texto personalizado da variável ${variable}`}
                    />
                  ) : (
                    <ContextVariablePathSelector
                      variables={variableOptions}
                      binding={{ path: binding.path, arrayMatchByPath: binding.arrayMatchByPath }}
                      canEdit={canEdit}
                      emptyLabel="Sem variáveis disponíveis nesta origem"
                      onChange={(nextBinding) => updateBindings(variable, {
                        ...binding,
                        path: nextBinding.path,
                        arrayMatchByPath: normalizeConditionArrayMatches(nextBinding.arrayMatchByPath)
                      })}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {dateBindingModal && (
        <div className="flow-modal-backdrop" role="presentation" onClick={closeDateBindingModal}>
          <section
            className="flow-modal api-date-binding-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Configurar variável de data"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="flow-modal-head">
              <div className="flow-modal-icon tone-blue">
                <span className="material-symbols-rounded" aria-hidden="true">schedule</span>
              </div>
              <div>
                <span>Data relativa</span>
                <h3>{`Configurar {{${dateBindingModal.variable}}}`}</h3>
                <p>Defina o deslocamento em minutos, horas ou dias para calcular a data em tempo de execução.</p>
              </div>
              <button className="icon-button" type="button" onClick={closeDateBindingModal} aria-label="Fechar modal">
                <span className="material-symbols-rounded" aria-hidden="true">close</span>
              </button>
            </header>

            <div className="flow-modal-body api-date-binding-body">
              <div className="api-date-binding-grid">
                <label className="modal-field">
                  <FieldLabel>Quantidade</FieldLabel>
                  <input
                    type="number"
                    min={0}
                    max={525600}
                    step={1}
                    value={String(dateBindingDraft.amount ?? '')}
                    onChange={(event) => setDateBindingDraft((current) => ({ ...current, amount: event.target.value }))}
                  />
                </label>
                <label className="modal-field">
                  <FieldLabel>Unidade</FieldLabel>
                  <select
                    value={activeDateBinding.unit}
                    onChange={(event) => setDateBindingDraft((current) => ({ ...current, unit: event.target.value }))}
                  >
                    {API_DATE_UNIT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
                <label className="modal-field">
                  <FieldLabel>Direção</FieldLabel>
                  <select
                    value={activeDateBinding.direction}
                    onChange={(event) => setDateBindingDraft((current) => ({ ...current, direction: event.target.value }))}
                  >
                    {API_DATE_DIRECTION_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </label>
              </div>

              <section className="api-date-binding-preview">
                <span>Exemplo com hora atual</span>
                <div>
                  <p>{`Agora: ${formatApiDatePreview(activeDatePreviewNow)} (${activeDatePreviewNow.toISOString()})`}</p>
                  <p>{`Resultado (${apiDateBindingSummary(activeDateBinding)}): ${formatApiDatePreview(activeDatePreview.date)} (${activeDatePreview.iso})`}</p>
                </div>
              </section>
            </div>

            <footer className="flow-modal-footer">
              <button className="secondary-button" type="button" onClick={closeDateBindingModal}>Cancelar</button>
              <button
                className="primary-button"
                type="button"
                onClick={() => {
                  updateBindings(dateBindingModal.variable, {
                    sourceType: 'date',
                    dateConfig: activeDateBinding
                  });
                  closeDateBindingModal();
                }}
              >
                Aplicar data
              </button>
            </footer>
          </section>
        </div>
      )}
    </>
  );
};

const SaveInfoBuilder = ({ config, demand, nodeId, nodes, edges, apis, runtimeOutputs, taskExtraVariables = [], canEdit, onChange }) => {
  const sources = apiAncestorSources({ nodeId, nodes, edges, apis, runtimeOutputs });
  const saveInfoBindingContext = apiVariableBindingContext({
    nodeId,
    nodes,
    edges,
    apis,
    runtimeOutputs,
    demand,
    taskExtraVariables
  });
  const current = normalizeSaveInfoConfig(config, sources, demand);
  const taskSourceVariables = (() => {
    const payloadVariables = appendTaskCommentVariableOptions(saveInfoBindingContext.taskOptions);
    const technicalVariables = [
      { value: 'task_key', label: 'task_key (string)', type: 'string', group: 'configured' },
      { value: 'id', label: 'id (number)', type: 'number', group: 'configured' },
      { value: 'status', label: 'status (string)', type: 'string', group: 'configured' }
    ];
    const unique = new Map();
    [...technicalVariables, ...payloadVariables].forEach((item) => {
      const value = String(item?.value || '').trim();
      if (!value || unique.has(value)) return;
      unique.set(value, item);
    });
    return [...unique.values()];
  })();
  const taskSourceOption = {
    sourceKey: SAVE_INFO_TASK_SOURCE,
    sourceNodeId: '',
    sourceType: 'task',
    integrationId: '',
    title: saveInfoBindingContext.taskSourceLabel || 'Atributo da Task',
    variables: taskSourceVariables
  };
  const literalSuggestionSources = templateSuggestSourceGroups({
    taskLabel: taskSourceOption.title,
    taskVariables: taskSourceVariables,
    includeDemand: true,
    apiSources: sources
  });
  const schemaTaskVariables = taskVariableOptions(demand, { extraVariables: taskExtraVariables });
  const saveAsAttributes = schemaTaskVariables.reduce((acc, item) => {
    const value = String(item?.value || '').trim();
    if (!value || acc.some((option) => option.value === value)) return acc;
    acc.push({ value, label: item?.label || value });
    return acc;
  }, []);
  const saveAsAttributeValues = new Set(saveAsAttributes.map((item) => item.value));
  const integerTaskVariables = schemaTaskVariables.filter((item) => isIntegerSchemaType(item.type));
  const completeMappings = current.mappings.filter(isCompleteSaveInfoMapping);
  const existingMappingCount = completeMappings.filter((item) => demandTaskSchemaOwnsPath(demand, item.saveAs)).length;
  const addedMappingCount = completeMappings.length - existingMappingCount;
  const statusOptions = [{ value: '', label: 'Não alterar status' }, ...demandStatusOptions(demand)];
  const updateConfig = (patch) => onChange(normalizeSaveInfoConfig({ ...current, ...patch }, sources, demand));
  const updateMapping = (id, patch) => {
    updateConfig({
      mappings: current.mappings.map((mapping) => {
        if (mapping.id !== id) return mapping;
        if (Object.prototype.hasOwnProperty.call(patch, 'sourceKey')) {
          if (patch.sourceKey === SAVE_INFO_LITERAL_SOURCE) {
            return {
              ...mapping,
              sourceNodeId: '',
              sourceType: 'literal',
              integrationId: '',
              key: '',
              keyType: 'string',
              literalValue: mappingHasLiteralValue(mapping) ? (mapping.literalValue ?? mapping.literal_value) : '',
              sumValue: undefined
            };
          }
          if (patch.sourceKey === SAVE_INFO_SUM_SOURCE) {
            const firstInteger = integerTaskVariables[0] || {};
            return {
              ...mapping,
              sourceNodeId: '',
              sourceType: 'sum',
              integrationId: '',
              key: firstInteger.value || '',
              keyType: firstInteger.type || 'integer',
              literalValue: undefined,
              sumValue: 1,
              saveAs: mapping.saveAs || ''
            };
          }
          if (patch.sourceKey === SAVE_INFO_TASK_SOURCE) {
            const firstTaskVariable = taskSourceVariables[0] || {};
            return {
              ...mapping,
              sourceNodeId: '',
              sourceType: 'task',
              integrationId: '',
              key: firstTaskVariable.value || '',
              keyType: firstTaskVariable.type || '',
              literalValue: undefined,
              sumValue: undefined,
              saveAs: mapping.saveAs || ''
            };
          }
          const selectedSource = sources.find((item) => String(item.nodeId) === String(patch.sourceKey)) || sources[0] || {};
          const firstVariable = selectedSource.variables?.[0] || {};
          return {
            ...mapping,
            sourceNodeId: selectedSource.nodeId || '',
            sourceType: selectedSource.sourceType || 'api',
            integrationId: selectedSource.integrationId || '',
            key: firstVariable.value || '',
            keyType: firstVariable.type || '',
            literalValue: undefined,
            sumValue: undefined,
            saveAs: mapping.saveAs || ''
          };
        }
        if (mappingSourceType(mapping) === 'task') {
          const firstTaskVariable = taskSourceVariables[0] || {};
          const nextKey = patch.key ?? mapping.key ?? firstTaskVariable.value ?? '';
          const taskVariable = taskSourceVariables.find((item) => item.value === nextKey) || firstTaskVariable;
          return {
            ...mapping,
            ...patch,
            sourceNodeId: '',
            sourceType: 'task',
            integrationId: '',
            key: nextKey,
            keyType: taskVariable?.type || mapping.keyType || '',
            saveAs: patch.saveAs ?? mapping.saveAs
          };
        }
        const source = patch.sourceNodeId
          ? sources.find((item) => String(item.nodeId) === String(patch.sourceNodeId))
          : sources.find((item) => String(item.nodeId) === String(mapping.sourceNodeId));
        const firstVariable = source?.variables?.[0] || {};
        const nextKey = patch.sourceNodeId && patch.sourceNodeId !== mapping.sourceNodeId
          ? firstVariable.value || ''
          : patch.key ?? mapping.key;
        const variable = source?.variables?.find((item) => item.value === nextKey) || firstVariable;
        return {
          ...mapping,
          ...patch,
          sourceNodeId: source?.nodeId || patch.sourceNodeId || mapping.sourceNodeId || '',
          sourceType: source?.sourceType || mapping.sourceType || 'api',
          integrationId: source?.integrationId || mapping.integrationId || '',
          key: nextKey,
          keyType: variable?.type || mapping.keyType || '',
          saveAs: patch.sourceNodeId && patch.sourceNodeId !== mapping.sourceNodeId
            ? mapping.saveAs || ''
            : patch.saveAs ?? mapping.saveAs
        };
      })
    });
  };
  const addMapping = () => updateConfig({
    mappings: current.mappings.concat({
      ...makeSaveInfoMapping(sources),
      saveAs: saveAsAttributes[0]?.value || ''
    })
  });
  const removeMapping = (id) => {
    updateConfig({ mappings: current.mappings.filter((mapping) => mapping.id !== id) });
  };
  const sourceKeyFor = (mapping) => {
    const sourceType = mappingSourceType(mapping);
    if (sourceType === 'literal') return SAVE_INFO_LITERAL_SOURCE;
    if (sourceType === 'sum') return SAVE_INFO_SUM_SOURCE;
    if (sourceType === 'task') return SAVE_INFO_TASK_SOURCE;
    return mapping.sourceNodeId || '';
  };

  return (
    <section className="save-info-builder modal-field-wide">
      <header className="save-info-head">
        <div>
          <span>Salvar informações na Task</span>
          <strong>{completeMappings.length} campo(s): {existingMappingCount} atualiza(m), {addedMappingCount} adiciona(m)</strong>
          <p>Um destino que já pertence à Demanda atualiza seu valor. Um nome novo cria uma Informação Agregada sem alterar o schema da Demanda.</p>
        </div>
        <label>
          <FieldLabel>Alterar status da Task</FieldLabel>
          <select value={current.nextStatus || ''} onChange={(event) => updateConfig({ nextStatus: event.target.value })} disabled={!canEdit}>
            {statusOptions.map((option) => <option key={option.value || 'no-change'} value={option.value}>{option.label}</option>)}
          </select>
        </label>
      </header>

      {!sources.length && (
        <div className="save-info-empty is-compact">
          <span className="material-symbols-rounded" aria-hidden="true">calculate</span>
          <div>
            <strong>Sem comandos antecessores</strong>
            <p>Texto personalizado e Somar continuam disponíveis para trabalhar diretamente com os atributos da Task.</p>
          </div>
        </div>
      )}

      <div className="save-info-table" role="table" aria-label="Mapeamento de informações salvas na Task">
        <div className="save-info-row is-head" role="row">
          <span>Salvar como</span>
          <span>Fonte / operação</span>
          <span>Valor / atributo</span>
          <span>Efeito na Task</span>
          <span></span>
        </div>
        {current.mappings.map((mapping) => {
          const sourceType = mappingSourceType(mapping);
          const source = sources.find((item) => String(item.nodeId) === String(mapping.sourceNodeId)) || {};
          const variables = source.variables || [];
          const saveAs = String(mapping.saveAs || '').trim();
          const saveAsSelectValue = saveAs && saveAsAttributeValues.has(saveAs) ? saveAs : SAVE_INFO_NEW_ATTRIBUTE;
          const isNewSaveAsAttribute = saveAsSelectValue === SAVE_INFO_NEW_ATTRIBUTE;
          return (
            <div className="save-info-row" role="row" key={mapping.id}>
              <div className="save-as-control">
                <select
                  value={saveAsSelectValue}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    updateMapping(mapping.id, {
                      saveAs: nextValue === SAVE_INFO_NEW_ATTRIBUTE
                        ? (saveAsAttributeValues.has(saveAs) ? '' : saveAs)
                        : nextValue
                    });
                  }}
                  disabled={!canEdit}
                  aria-label="Salvar como"
                >
                  <option value={SAVE_INFO_NEW_ATTRIBUTE}>Novo atributo</option>
                  {saveAsAttributes.map((attribute) => (
                    <option key={attribute.value} value={attribute.value}>{attribute.label}</option>
                  ))}
                </select>
                {isNewSaveAsAttribute && (
                  <input
                    value={mapping.saveAs || ''}
                    onChange={(event) => updateMapping(mapping.id, { saveAs: event.target.value })}
                    placeholder="Nome do novo atributo"
                    disabled={!canEdit}
                    aria-label="Nome do novo atributo"
                  />
                )}
              </div>
              <select value={sourceKeyFor(mapping)} onChange={(event) => updateMapping(mapping.id, { sourceKey: event.target.value })} disabled={!canEdit}>
                <optgroup label="Operações">
                  <option value={SAVE_INFO_LITERAL_SOURCE}>Texto personalizado</option>
                  <option value={SAVE_INFO_TASK_SOURCE}>Atributo da Task</option>
                  <option value={SAVE_INFO_SUM_SOURCE}>Somar contador da Task</option>
                </optgroup>
                {sources.length > 0 && (
                  <optgroup label="Comandos antecessores">
                    {sources.map((item) => (
                      <option key={item.nodeId} value={item.nodeId}>{item.title}</option>
                    ))}
                  </optgroup>
                )}
              </select>
              {sourceType === 'literal' ? (
                <TemplateVariableSuggestInput
                  value={mapping.literalValue ?? mapping.literal_value ?? ''}
                  onChange={(nextValue) => updateMapping(mapping.id, { literalValue: nextValue })}
                  variables={taskSourceVariables}
                  suggestionSources={literalSuggestionSources}
                  canEdit={canEdit}
                  ariaLabel="Texto personalizado do salvar informação"
                />
              ) : sourceType === 'task' ? (
                <select value={mapping.key || taskSourceVariables[0]?.value || ''} onChange={(event) => updateMapping(mapping.id, { key: event.target.value })} disabled={!canEdit || !taskSourceVariables.length}>
                  {taskSourceVariables.length
                    ? <VariableOptions variables={taskSourceVariables} preserveValue={mapping.key} />
                    : <option value="">Nenhum atributo da Task</option>}
                </select>
              ) : sourceType === 'sum' ? (
                <div className="mapping-sum-control">
                  <select value={mapping.key || ''} onChange={(event) => updateMapping(mapping.id, { key: event.target.value })} disabled={!canEdit || !integerTaskVariables.length}>
                    {integerTaskVariables.length
                      ? integerTaskVariables.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)
                      : <option value="">Nenhum atributo inteiro</option>}
                  </select>
                  <span aria-hidden="true">+</span>
                  <input
                    type="number"
                    step="1"
                    value={mapping.sumValue ?? mapping.sum_value ?? 1}
                    onChange={(event) => updateMapping(mapping.id, { sumValue: event.target.value })}
                    aria-label="Valor a somar"
                    title="Valor inteiro que será somado ao contador"
                    disabled={!canEdit}
                  />
                </div>
              ) : variables.length ? (
                <select value={mapping.key || variables[0]?.value || ''} onChange={(event) => updateMapping(mapping.id, { key: event.target.value })} disabled={!canEdit}>
                  <VariableOptions variables={variables} preserveValue={mapping.key} />
                </select>
              ) : (
                <input value="Sem keys conhecidas" disabled />
              )}
              <AttributeWriteEffect demand={demand} path={mapping.saveAs} mode="save" />
              <button className="icon-button" type="button" onClick={() => removeMapping(mapping.id)} disabled={!canEdit} aria-label="Remover mapeamento">
                <span className="material-symbols-rounded" aria-hidden="true">delete</span>
              </button>
            </div>
          );
        })}
      </div>
      <button className="secondary-button compact-action" type="button" onClick={addMapping} disabled={!canEdit}>
        <span className="material-symbols-rounded" aria-hidden="true">add</span>
        Adicionar informação
      </button>
    </section>
  );
};

const normalizedTaskAttributePath = (value = '') => String(value || '')
  .trim()
  .replace(/^\$\.?/, '')
  .replace(/^task\.payload\./, '')
  .replace(/^payload\./, '');

const demandTaskSchemaOwnsPath = (demand = {}, path = '') => {
  const normalizedPath = normalizedTaskAttributePath(path);
  if (!normalizedPath) return false;
  const schema = demand?.task_schema || demand?.args || {};
  const rootPath = normalizedPath.split(/[.[\]]/).find(Boolean) || normalizedPath;
  return Object.prototype.hasOwnProperty.call(schema, normalizedPath)
    || Object.prototype.hasOwnProperty.call(schema, rootPath);
};

const AttributeWriteEffect = ({ demand, path, mode = 'save' }) => {
  const normalizedPath = normalizedTaskAttributePath(path);
  if (!normalizedPath) {
    return (
      <span className="attribute-write-effect is-pending" title="Preencha o nome de destino para identificar o efeito da gravação.">
        <span className="material-symbols-rounded" aria-hidden="true">pending</span>
        Defina o destino
      </span>
    );
  }

  const existing = demandTaskSchemaOwnsPath(demand, normalizedPath);
  const label = existing
    ? (mode === 'insert' ? 'Preenche / atualiza' : 'Atualiza atributo')
    : (mode === 'insert' ? 'Adiciona ao payload' : 'Nova informação');
  const title = existing
    ? `“${normalizedPath}” pertence ao schema da Demanda e continuará na aba Atributos.`
    : mode === 'insert'
      ? `“${normalizedPath}” não pertence ao schema da Demanda de destino. A chave será adicionada somente ao payload.`
      : `“${normalizedPath}” não pertence ao schema da Demanda. Será salvo na aba Informações Agregadas.`;

  return (
    <span className={`attribute-write-effect ${existing ? 'is-update' : 'is-add'}`} title={title}>
      <span className="material-symbols-rounded" aria-hidden="true">{existing ? 'edit_note' : 'add_circle'}</span>
      {label}
    </span>
  );
};

const INSERT_DEMAND_PAYLOAD_TASK_SOURCE = 'task';
const INSERT_DEMAND_PAYLOAD_DEMAND_SOURCE = 'demand';
const INSERT_DEMAND_PAYLOAD_LITERAL_SOURCE = 'literal';
const INSERT_DEMAND_PAYLOAD_SUM_SOURCE = 'sum';
const insertDemandPayloadMappingIdPart = (value = '') => String(value || 'field')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-|-$/g, '') || 'field';

const flattenInsertDemandPayloadLeaves = (value, prefix = '', output = []) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const entries = Object.entries(value);
    if (!entries.length && prefix) output.push({ targetKey: prefix, value: {} });
    entries.forEach(([key, child]) => {
      flattenInsertDemandPayloadLeaves(child, prefix ? `${prefix}.${key}` : key, output);
    });
    return output;
  }
  if (prefix) output.push({ targetKey: prefix, value });
  return output;
};

const insertDemandPayloadSources = ({ demand, nodeId, nodes, edges, apis, runtimeOutputs, taskExtraVariables = [] }) => {
  const bindingContext = apiVariableBindingContext({
    nodeId,
    nodes,
    edges,
    apis,
    runtimeOutputs,
    demand,
    taskExtraVariables
  });
  const taskVariables = taskSelectorVariableOptions(bindingContext.taskOptions, {
    includeTaskMeta: true,
    includeDemandMeta: false
  });
  const demandVariables = demandTemplateVariableOptions();
  return [
    {
      key: INSERT_DEMAND_PAYLOAD_TASK_SOURCE,
      sourceType: 'task',
      sourceScope: 'task',
      title: bindingContext.taskSourceLabel,
      variables: taskVariables
    },
    {
      key: INSERT_DEMAND_PAYLOAD_DEMAND_SOURCE,
      sourceType: 'task',
      sourceScope: 'demand',
      title: 'Dados da Demanda',
      variables: demandVariables
    },
    ...bindingContext.sources.map((source) => ({
      ...source,
      key: `api:${source.nodeId}`,
      sourceType: 'api',
      sourceScope: 'api',
      title: source.title || source.integrationName || 'API antecessora'
    }))
  ];
};

const insertDemandPayloadSourceKey = (mapping = {}) => {
  const sourceType = mappingSourceType(mapping);
  if (sourceType === 'literal') return INSERT_DEMAND_PAYLOAD_LITERAL_SOURCE;
  if (sourceType === 'sum') return INSERT_DEMAND_PAYLOAD_SUM_SOURCE;
  if (sourceType === 'api') return `api:${mapping.sourceNodeId || mapping.source_node_id || ''}`;
  if (mapping.sourceScope === 'demand' || String(mapping.path || '').startsWith('demand.')) {
    return INSERT_DEMAND_PAYLOAD_DEMAND_SOURCE;
  }
  return INSERT_DEMAND_PAYLOAD_TASK_SOURCE;
};

const insertDemandPayloadLegacyMappings = (payloadJson = '', sources = []) => {
  let parsed;
  try {
    parsed = JSON.parse(String(payloadJson || '{}'));
  } catch {
    return [];
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return [];
  return flattenInsertDemandPayloadLeaves(parsed).map(({ targetKey, value }, index) => {
    const token = typeof value === 'string'
      ? value.match(/^\{\{\s*([^}]+?)\s*\}\}$/)
      : null;
    const rawPath = String(token?.[1] || '').trim();
    if (rawPath.startsWith('task.')) {
      const taskPath = rawPath.slice('task.'.length).replace(/^payload\./, '');
      return {
        id: `insert-payload-legacy-${index}-${insertDemandPayloadMappingIdPart(targetKey)}`,
        targetKey,
        sourceType: 'task',
        sourceScope: 'task',
        sourceNodeId: '',
        path: taskPath
      };
    }
    if (rawPath.startsWith('demand.')) {
      return {
        id: `insert-payload-legacy-${index}-${insertDemandPayloadMappingIdPart(targetKey)}`,
        targetKey,
        sourceType: 'task',
        sourceScope: 'demand',
        sourceNodeId: '',
        path: rawPath
      };
    }
    const apiSource = sources.find((source) => rawPath.startsWith(`outputs.${source.nodeId}.`));
    if (apiSource) {
      const sourcePrefix = `outputs.${apiSource.nodeId}.`;
      const apiPath = rawPath.slice(sourcePrefix.length).replace(/^(body_json|body)\./, '');
      return {
        id: `insert-payload-legacy-${index}-${insertDemandPayloadMappingIdPart(targetKey)}`,
        targetKey,
        sourceType: 'api',
        sourceScope: 'api',
        sourceNodeId: apiSource.nodeId,
        integrationId: apiSource.integrationId || '',
        path: apiPath
      };
    }
    return {
      id: `insert-payload-legacy-${index}-${insertDemandPayloadMappingIdPart(targetKey)}`,
      targetKey,
      sourceType: 'literal',
      sourceScope: 'literal',
      sourceNodeId: '',
      path: '__literal__',
      literalValue: value
    };
  });
};

const normalizeInsertDemandPayloadMappings = (config = {}, sources = []) => {
  const explicit = Array.isArray(config.payloadMappings ?? config.payload_mappings);
  const rawMappings = explicit
    ? (config.payloadMappings ?? config.payload_mappings)
    : insertDemandPayloadLegacyMappings(config.payloadJson || config.payload_json, sources);
  return rawMappings.map((mapping, index) => {
    const sourceKey = insertDemandPayloadSourceKey(mapping);
    const isLiteral = sourceKey === INSERT_DEMAND_PAYLOAD_LITERAL_SOURCE;
    const isSum = sourceKey === INSERT_DEMAND_PAYLOAD_SUM_SOURCE;
    const source = isLiteral || isSum ? {} : sources.find((item) => item.key === sourceKey) || sources[0] || {};
    const path = String(mapping.path || mapping.valueKey || mapping.value_key || '').trim();
    return {
      id: mapping.id || `insert-payload-${index}-${insertDemandPayloadMappingIdPart(mapping.targetKey || mapping.target_key || 'field')}`,
      targetKey: String(mapping.targetKey || mapping.target_key || mapping.name || '').trim(),
      sourceType: isLiteral ? 'literal' : isSum ? 'sum' : mapping.sourceType || mapping.source_type || source.sourceType || 'task',
      sourceScope: isLiteral ? 'literal' : isSum ? 'task' : mapping.sourceScope || mapping.source_scope || source.sourceScope || 'task',
      sourceNodeId: isLiteral || isSum ? '' : mapping.sourceNodeId || mapping.source_node_id || source.nodeId || '',
      integrationId: isLiteral || isSum ? '' : mapping.integrationId || mapping.integration_id || source.integrationId || '',
      path,
      valueType: isSum ? 'integer' : mapping.valueType || mapping.value_type || source.variables?.find((item) => item.value === path)?.type || '',
      ...(isSum ? { sumValue: mapping.sumValue ?? mapping.sum_value ?? 1 } : {}),
      ...(Object.prototype.hasOwnProperty.call(mapping, 'literalValue')
        ? { literalValue: mapping.literalValue }
        : Object.prototype.hasOwnProperty.call(mapping, 'literal_value')
          ? { literalValue: mapping.literal_value }
          : {})
    };
  });
};

const defaultInsertDemandTargetKey = (path = '') => {
  const parts = String(path || '').split('.').filter(Boolean);
  return parts[parts.length - 1] || 'novo_atributo';
};

const InsertDemandPayloadBuilder = ({ config, demand, targetDemand, nodeId, nodes, edges, apis, runtimeOutputs, taskExtraVariables = [], canEdit, onChange }) => {
  const datalistId = useId();
  const sources = insertDemandPayloadSources({ demand, nodeId, nodes, edges, apis, runtimeOutputs, taskExtraVariables });
  const mappings = normalizeInsertDemandPayloadMappings(config, sources);
  const integerTaskVariables = (sources.find((item) => item.key === INSERT_DEMAND_PAYLOAD_TASK_SOURCE)?.variables || [])
    .filter((item) => isIntegerSchemaType(item.type));
  const literalSuggestionSources = (() => {
    const taskSource = sources.find((item) => item.key === INSERT_DEMAND_PAYLOAD_TASK_SOURCE) || null;
    const demandSource = sources.find((item) => item.key === INSERT_DEMAND_PAYLOAD_DEMAND_SOURCE) || null;
    const apiSources = sources.filter((item) => item.sourceScope === 'api');
    const groups = templateSuggestSourceGroups({
      taskLabel: taskSource?.title || 'Atributos da Task',
      taskVariables: taskSource?.variables || [],
      includeDemand: false,
      apiSources
    });
    if (demandSource) {
      groups.splice(1, 0, {
        key: demandSource.key,
        label: demandSource.title || 'Dados da Demanda',
        variables: demandSource.variables || []
      });
    }
    return groups;
  })();
  const targetAttributes = taskVariableOptions(targetDemand || {}).map((option) => option.value);
  const completeMappings = mappings.filter((mapping) => (
    mapping.targetKey
    && (mapping.sourceType === 'literal' ? mappingHasLiteralValue(mapping) : mapping.path)
    && (mapping.sourceType !== 'sum' || validMappingSumValue(mapping))
    && (mapping.sourceType !== 'api' || mapping.sourceNodeId)
  ));
  const existingMappingCount = completeMappings.filter((mapping) => demandTaskSchemaOwnsPath(targetDemand, mapping.targetKey)).length;
  const addedMappingCount = completeMappings.length - existingMappingCount;
  const updateMappings = (nextMappings) => onChange({
    ...config,
    payloadMappings: nextMappings
  });
  const addMapping = () => {
    const source = sources.find((item) => item.variables?.length) || sources[0] || {};
    const variable = source.variables?.[0] || {};
    updateMappings(mappings.concat({
      id: `insert-payload-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      targetKey: defaultInsertDemandTargetKey(variable.value),
      sourceType: source.sourceType || 'task',
      sourceScope: source.sourceScope || 'task',
      sourceNodeId: source.nodeId || '',
      integrationId: source.integrationId || '',
      path: variable.value || '',
      valueType: variable.type || ''
    }));
  };
  const updateMapping = (id, patch = {}) => {
    updateMappings(mappings.map((mapping) => {
      if (mapping.id !== id) return mapping;
      if (!patch.sourceKey) return { ...mapping, ...patch };
      if (patch.sourceKey === INSERT_DEMAND_PAYLOAD_LITERAL_SOURCE) {
        return {
          ...mapping,
          sourceType: 'literal',
          sourceScope: 'literal',
          sourceNodeId: '',
          integrationId: '',
          path: '',
          valueType: 'string',
          literalValue: mappingHasLiteralValue(mapping) ? (mapping.literalValue ?? mapping.literal_value) : '',
          sumValue: undefined
        };
      }
      if (patch.sourceKey === INSERT_DEMAND_PAYLOAD_SUM_SOURCE) {
        const firstInteger = integerTaskVariables[0] || {};
        return {
          ...mapping,
          sourceType: 'sum',
          sourceScope: 'task',
          sourceNodeId: '',
          integrationId: '',
          path: firstInteger.value || '',
          valueType: firstInteger.type || 'integer',
          literalValue: undefined,
          sumValue: 1,
          targetKey: mapping.targetKey || defaultInsertDemandTargetKey(firstInteger.value)
        };
      }
      const source = sources.find((item) => item.key === patch.sourceKey) || sources[0] || {};
      const variable = source.variables?.[0] || {};
      return {
        ...mapping,
        sourceType: source.sourceType || 'task',
        sourceScope: source.sourceScope || 'task',
        sourceNodeId: source.nodeId || '',
        integrationId: source.integrationId || '',
        path: variable.value || '',
        valueType: variable.type || '',
        literalValue: undefined,
        sumValue: undefined
      };
    }));
  };
  const removeMapping = (id) => updateMappings(mappings.filter((mapping) => mapping.id !== id));

  return (
    <section className="insert-demand-payload-builder modal-field-wide">
      <header className="insert-demand-payload-head">
        <div>
          <FieldLabel>Payload template</FieldLabel>
          <strong>{completeMappings.length} campo(s): {existingMappingCount} preenche(m), {addedMappingCount} adiciona(m)</strong>
          <p>O indicador de cada linha mostra se a key pertence ao schema da Demanda de destino ou se será adicionada somente ao payload.</p>
        </div>
        <button className="secondary-button compact-action" type="button" onClick={addMapping} disabled={!canEdit}>
          <span className="material-symbols-rounded" aria-hidden="true">add</span>
          Adicionar atributo
        </button>
      </header>

      <datalist id={datalistId}>
        {targetAttributes.map((attribute) => <option key={attribute} value={attribute} />)}
      </datalist>

      {mappings.length ? (
        <div className="insert-demand-payload-table" role="table" aria-label="Payload template da Task de destino">
          <div className="insert-demand-payload-row is-head" role="row">
            <span>Nome do atributo</span>
            <span>Fonte</span>
            <span>Valor / Value key</span>
            <span>Efeito no destino</span>
            <span></span>
          </div>
          {mappings.map((mapping) => {
            const sourceKey = insertDemandPayloadSourceKey(mapping);
            const source = sources.find((item) => item.key === sourceKey) || null;
            const isLiteral = mapping.sourceType === 'literal';
            const isSum = mapping.sourceType === 'sum';
            const sourceOptions = [
              { key: INSERT_DEMAND_PAYLOAD_LITERAL_SOURCE, title: 'Texto personalizado' },
              { key: INSERT_DEMAND_PAYLOAD_SUM_SOURCE, title: 'Somar contador da Task' },
              ...sources
            ];
            const variables = source?.variables || [];
            return (
              <div className="insert-demand-payload-row" role="row" key={mapping.id}>
                <input
                  list={datalistId}
                  value={mapping.targetKey || ''}
                  onChange={(event) => updateMapping(mapping.id, { targetKey: event.target.value })}
                  placeholder="NOME_ATRIBUTO"
                  disabled={!canEdit}
                />
                <select
                  value={sourceKey}
                  onChange={(event) => updateMapping(mapping.id, { sourceKey: event.target.value })}
                  disabled={!canEdit}
                >
                  {sourceOptions.map((item) => (
                    <option key={item.key} value={item.key}>{item.title}</option>
                  ))}
                </select>
                {isLiteral ? (
                  <TemplateVariableSuggestInput
                    value={mapping.literalValue ?? mapping.literal_value ?? ''}
                    onChange={(nextValue) => updateMapping(mapping.id, { literalValue: nextValue })}
                    variables={sources.find((item) => item.key === INSERT_DEMAND_PAYLOAD_TASK_SOURCE)?.variables || []}
                    suggestionSources={literalSuggestionSources}
                    canEdit={canEdit}
                    ariaLabel="Texto personalizado do payload"
                  />
                ) : isSum ? (
                  <div className="mapping-sum-control">
                    <select
                      value={mapping.path || ''}
                      onChange={(event) => updateMapping(mapping.id, { path: event.target.value, valueType: integerTaskVariables.find((item) => item.value === event.target.value)?.type || 'integer' })}
                      disabled={!canEdit || !integerTaskVariables.length}
                    >
                      {integerTaskVariables.length
                        ? integerTaskVariables.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)
                        : <option value="">Nenhum atributo inteiro</option>}
                    </select>
                    <span aria-hidden="true">+</span>
                    <input
                      type="number"
                      step="1"
                      value={mapping.sumValue ?? mapping.sum_value ?? 1}
                      onChange={(event) => updateMapping(mapping.id, { sumValue: event.target.value })}
                      aria-label="Valor a somar"
                      title="Valor inteiro que será somado ao contador"
                      disabled={!canEdit}
                    />
                  </div>
                ) : variables.length ? (
                  <select
                    value={mapping.path || variables[0]?.value || ''}
                    onChange={(event) => {
                      const variable = variables.find((item) => item.value === event.target.value) || {};
                      updateMapping(mapping.id, { path: event.target.value, valueType: variable.type || '' });
                    }}
                    disabled={!canEdit}
                  >
                    <VariableOptions variables={variables} preserveValue={mapping.path} />
                  </select>
                ) : (
                  <select disabled><option>Sem keys conhecidas</option></select>
                )}
                <AttributeWriteEffect demand={targetDemand} path={mapping.targetKey} mode="insert" />
                <button className="icon-button" type="button" onClick={() => removeMapping(mapping.id)} disabled={!canEdit} aria-label="Remover atributo do payload">
                  <span className="material-symbols-rounded" aria-hidden="true">delete</span>
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="insert-demand-payload-empty">
          <span className="material-symbols-rounded" aria-hidden="true">data_object</span>
          <div>
            <strong>Payload ainda vazio</strong>
            <p>Adicione o primeiro atributo e vincule-o à Task, à Demanda ou a uma API antecessora.</p>
          </div>
        </div>
      )}
    </section>
  );
};

const ConfigModal = ({
  node,
  route = null,
  demands = [],
  flowNodes = [],
  flowEdges = [],
  testedApis = [],
  runtimeOutputs = {},
  inspectionTask = null,
  canEdit = true,
  onClose,
  onSave
}) => {
  const command = commandForType(node.data.commandType);
  const selectedDemand = demands.find((demand) => String(demand.id) === String(route?.demand_id || node.data.config?.demandId)) || null;
  const selectedDemandId = selectedDemand?.id || route?.demand_id || node.data.config?.demandId || '';
  const availableTestedApis = useMemo(
    () => testedApisForDemand(testedApis, selectedDemandId),
    [selectedDemandId, testedApis]
  );
  const initialConfig = { ...command.defaults, ...(node.data.config || {}) };
  const initialAutoIdentity = deriveCommandIdentity(node.data.commandType, initialConfig, {
    demandName: selectedDemand ? demandDisplayName(selectedDemand) : routeDemandName(route)
  });
  const existingCustomName = String(node.data.customName || '').trim();
  const [customNameAuto, setCustomNameAuto] = useState(
    node.data.customNameAuto === true || !existingCustomName
  );
  const [draft, setDraft] = useState({
    title: node.data.title || command.label,
    subtitle: node.data.subtitle || '',
    customName: existingCustomName || initialAutoIdentity.title,
    config: initialConfig
  });
  const selectedIntegration = availableTestedApis.find((item) => String(item.id) === String(draft.config.integrationId));
  const [observedTaskSchema, setObservedTaskSchema] = useState({});
  useEffect(() => {
    let cancelled = false;
    if (!selectedDemandId) {
      setObservedTaskSchema({});
      return () => {
        cancelled = true;
      };
    }
    api(`/api/demands/${encodeURIComponent(selectedDemandId)}/tasks/observed-schema?limit=5`)
      .then((result) => {
        if (!cancelled) setObservedTaskSchema(normalizeResponseSchema(result?.data?.schema));
      })
      .catch(() => {
        if (!cancelled) setObservedTaskSchema({});
      });
    return () => {
      cancelled = true;
    };
  }, [selectedDemandId]);
  const learnedTaskSchemaFromStart = useMemo(() => {
    const startNode = (Array.isArray(flowNodes) ? flowNodes : []).find((candidate) => isStartNode(candidate));
    return learnedTaskPayloadSchemaFromConfig(startNode?.data?.config || {});
  }, [flowNodes]);
  const taskExtraVariables = useMemo(() => {
    const observedVariables = variableOptionsFromSchema(observedTaskSchema, 'extra');
    const learnedVariables = variableOptionsFromSchema(learnedTaskSchemaFromStart, 'extra');
    const runtimeVariables = taskPayloadExtraVariableOptions(inspectionTask);
    return mergeVariableOptions(observedVariables, learnedVariables, runtimeVariables);
  }, [inspectionTask, learnedTaskSchemaFromStart, observedTaskSchema]);
  const [cortexResourceOptionsState, setCortexResourceOptionsState] = useState([]);
  const [cortexResourceOptionsLoading, setCortexResourceOptionsLoading] = useState(false);
  const [cortexResourceOptionsError, setCortexResourceOptionsError] = useState('');
  const [chatPlatforms, setChatPlatforms] = useState([]);
  const [chatPlatformsLoading, setChatPlatformsLoading] = useState(false);
  const [chatPlatformsError, setChatPlatformsError] = useState('');
  const [chatTemplates, setChatTemplates] = useState([]);
  const [chatTemplatesBaseUrl, setChatTemplatesBaseUrl] = useState('');
  const [chatTemplatesLoading, setChatTemplatesLoading] = useState(false);
  const [chatTemplatesError, setChatTemplatesError] = useState('');
  const [voiceTemplates, setVoiceTemplates] = useState([]);
  const [voiceTemplatesBaseUrl, setVoiceTemplatesBaseUrl] = useState('');
  const [voiceTemplatesLoading, setVoiceTemplatesLoading] = useState(false);
  const [voiceTemplatesError, setVoiceTemplatesError] = useState('');
  const [voiceDialAtDateModalOpen, setVoiceDialAtDateModalOpen] = useState(false);
  const [voiceDialAtDateDraft, setVoiceDialAtDateDraft] = useState(defaultApiDateBindingConfig());
  const [chatInfoCustomPreviewExpanded, setChatInfoCustomPreviewExpanded] = useState(false);
  const [modalNotice, setModalNotice] = useState('');
  const cortexTemplateTextareaRefs = useRef({});
  const cortexTemplateSelectionRef = useRef({});
  const [cortexTemplateAttributePickerOpen, setCortexTemplateAttributePickerOpen] = useState(false);
  const [cortexTemplateAttributeTarget, setCortexTemplateAttributeTarget] = useState({ key: 'message', label: 'Mensagem' });

  useEffect(() => {
    setChatInfoCustomPreviewExpanded(false);
  }, [node?.id]);

  const setConfig = (key, value) => {
    setDraft((current) => ({
      ...current,
      config: { ...current.config, [key]: value }
    }));
  };

  const setConditionConfig = (nextConfig) => {
    setDraft((current) => ({
      ...current,
      config: normalizeConditionConfig(nextConfig, conditionVariableSources({
        nodeId: node.id,
        nodes: flowNodes,
        edges: flowEdges,
        apis: testedApis,
        runtimeOutputs,
        demand: selectedDemand,
        taskExtraVariables
      }))
    }));
  };

  const setApiConfig = (nextConfig) => {
    const bindingContext = apiVariableBindingContext({
      nodeId: node.id,
      nodes: flowNodes,
      edges: flowEdges,
      apis: testedApis,
      runtimeOutputs,
      demand: selectedDemand,
      taskExtraVariables
    });
    setDraft((current) => ({
      ...current,
      config: normalizeApiVariableBindings(nextConfig, selectedIntegration, selectedDemand, bindingContext.sources, bindingContext.taskOptions)
    }));
  };

  const setSaveInfoConfig = (nextConfig) => {
    setDraft((current) => ({
      ...current,
      config: normalizeSaveInfoConfig(nextConfig, apiAncestorSources({
        nodeId: node.id,
        nodes: flowNodes,
        edges: flowEdges,
        apis: testedApis,
        runtimeOutputs
      }), selectedDemand)
    }));
  };

  const setInsertDemandConfig = (nextConfig) => {
    setDraft((current) => ({
      ...current,
      config: normalizeInsertDemandConfig(nextConfig, demands, route, {
        extraVariables: taskExtraVariables
      })
    }));
  };

  const setCortexConfig = (nextConfig) => {
    setDraft((current) => ({
      ...current,
      config: normalizeCortexConfig(stripLegacyCortexResponseTypeFields(nextConfig))
    }));
  };

  const setChatConfig = (nextConfig) => {
    setDraft((current) => ({
      ...current,
      config: normalizeChatConfig(nextConfig, chatContextSelectorVariables, chatBindingContext.sources)
    }));
  };

  const setVoiceConfig = (nextConfig) => {
    setDraft((current) => ({
      ...current,
      config: normalizeVoiceConfig(nextConfig, voiceTaskSelectorVariables, chatBindingContext.sources)
    }));
  };

  useEffect(() => {
    if (node.data.commandType !== 'cortex') return undefined;
    const normalized = normalizeCortexConfig(draft.config);
    if (!cortexResourceSupportsList(normalized.resource)) {
      setCortexResourceOptionsState([]);
      setCortexResourceOptionsLoading(false);
      setCortexResourceOptionsError('');
      return undefined;
    }

    let cancelled = false;
    setCortexResourceOptionsLoading(true);
    setCortexResourceOptionsError('');

    api(`/api/cortex/resources/${encodeURIComponent(normalized.resource)}/options`)
      .then((response) => {
        if (cancelled) return;
        const options = Array.isArray(response?.data?.options)
          ? response.data.options
              .filter((item) => {
                const deletedAt = item?.deleted_at ?? item?.deletedAt ?? item?.raw?.deleted_at ?? item?.raw?.deletedAt ?? null;
                if (deletedAt == null) return true;
                return String(deletedAt).trim() === '';
              })
              .map((item) => ({
                cod: String(item?.cod || '').trim(),
                name: String(
                  item?.name
                  || item?.nome
                  || item?.file_name
                  || item?.fileName
                  || item?.filename
                  || item?.original_file_name
                  || item?.originalFileName
                  || item?.document_name
                  || item?.documentName
                  || item?.label
                  || item?.title
                  || item?.raw?.name
                  || item?.raw?.nome
                  || item?.raw?.file_name
                  || item?.raw?.fileName
                  || item?.raw?.filename
                  || item?.raw?.original_file_name
                  || item?.raw?.originalFileName
                  || item?.raw?.document_name
                  || item?.raw?.documentName
                  || item?.raw?.label
                  || item?.raw?.title
                  || item?.raw?.resource_name
                  || item?.raw?.display_name
                  || ''
                ).trim(),
                description: String(item?.description || '').trim(),
                responseType: String(
                  item?.response_type
                  || item?.responseType
                  || item?.raw?.response_type
                  || item?.raw?.responseType
                  || ''
                ).trim().toUpperCase()
              }))
              .filter((item) => item.cod)
          : [];

        setCortexResourceOptionsState(options);
        setCortexResourceOptionsLoading(false);

        if (!options.length) {
          if (normalized.resourceCod || normalized.resourceResponseType) {
            setCortexConfig({ ...normalized, resourceCod: '', resourceResponseType: '' });
          }
          return;
        }

        const selectedOption = options.find((item) => item.cod === String(normalized.resourceCod || '').trim()) || null;
        if (!selectedOption) {
          setCortexConfig({
            ...normalized,
            resourceCod: options[0].cod,
            resourceResponseType: String(options[0].responseType || '').trim().toUpperCase()
          });
          return;
        }

        const selectedResponseType = String(selectedOption.responseType || '').trim().toUpperCase();
        if (selectedResponseType !== String(normalized.resourceResponseType || '').trim().toUpperCase()) {
          setCortexConfig({
            ...normalized,
            resourceResponseType: selectedResponseType
          });
        }
      })
      .catch((error) => {
        if (cancelled) return;
        setCortexResourceOptionsState([]);
        setCortexResourceOptionsLoading(false);
        setCortexResourceOptionsError(error.message || 'Falha ao carregar recursos Cortex.');
        if (normalized.resourceResponseType) {
          setCortexConfig({ ...normalized, resourceResponseType: '' });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [draft.config.resource, node.data.commandType]);

  useEffect(() => {
    if (node.data.commandType !== 'chat') return undefined;
    let cancelled = false;
    setChatPlatformsLoading(true);
    setChatPlatformsError('');
    api('/api/chat/platforms')
      .then((response) => {
        if (cancelled) return;
        const options = Array.isArray(response?.data?.data)
          ? response.data.data
              .map((item) => ({
                id: String(item?.id || '').trim(),
                name: String(item?.name || item?.id || '').trim()
              }))
              .filter((item) => item.id)
          : [];
        setChatPlatforms(options);
        setChatPlatformsLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        setChatPlatforms([]);
        setChatPlatformsLoading(false);
        setChatPlatformsError(error.message || 'Falha ao carregar plataformas de Chat.');
      });
    return () => {
      cancelled = true;
    };
  }, [node.data.commandType]);

  useEffect(() => {
    if (node.data.commandType !== 'chat') return undefined;
    const platform = String(draft.config.platform || '').trim();
    if (!platform) {
      setChatTemplates([]);
      setChatTemplatesBaseUrl('');
      setChatTemplatesLoading(false);
      setChatTemplatesError('');
      return undefined;
    }
    let cancelled = false;
    setChatTemplatesLoading(true);
    setChatTemplatesError('');
    api(`/api/chat/templates?platform=${encodeURIComponent(platform)}`)
      .then((response) => {
        if (cancelled) return;
        const responseChatUrl = normalizeChatExternalUrl(response?.data?.chat_url || response?.data?.chatUrl || '');
        setChatTemplatesBaseUrl(responseChatUrl);
        const items = Array.isArray(response?.data?.data)
          ? response.data.data.map((item) => ({
              id: String(item?.id || '').trim(),
              name: String(item?.name || item?.id || '').trim(),
              origin: String(item?.origin || '').trim(),
              content_type_name: String(item?.content_type_name || item?.contentTypeName || '').trim().toLowerCase(),
              chatbot_id: String(item?.chatbot_id || item?.chatbotId || '').trim(),
              chat_url: normalizeChatExternalUrl(item?.chat_url || item?.chatUrl || responseChatUrl),
              header_preview: String(item?.header_preview || '').trim(),
              preview: String(item?.preview || '').trim(),
              footer: String(item?.footer || '').trim(),
              params_count: Math.max(0, Number.parseInt(item?.params_count, 10) || 0)
            })).filter((item) => item.id)
          : [];
        setChatTemplates(items);
        setChatTemplatesLoading(false);

        const selectedTemplateId = String(draft.config.templateId || '').trim();
        if (!selectedTemplateId) return;
        const selectedTemplate = items.find((item) => item.id === selectedTemplateId);
        if (!selectedTemplate) {
          setChatConfig({
            ...draft.config,
            templateId: '',
            templateName: '',
            templateOrigin: '',
            templateContentTypeName: '',
            chatbotId: '',
            chatUrl: responseChatUrl,
            headerPreview: '',
            preview: '',
            footer: '',
            paramsCount: 0,
            paramBindings: {},
            paramBindingDetails: {}
          });
          return;
        }
        setChatConfig({
          ...draft.config,
          templateId: selectedTemplate.id,
          templateName: selectedTemplate.name,
          templateOrigin: selectedTemplate.origin,
          templateContentTypeName: selectedTemplate.content_type_name,
          chatbotId: selectedTemplate.chatbot_id,
          chatUrl: normalizeChatExternalUrl(selectedTemplate.chat_url || responseChatUrl),
          headerPreview: selectedTemplate.header_preview,
          preview: selectedTemplate.preview,
          footer: selectedTemplate.footer,
          paramsCount: selectedTemplate.params_count
        });
      })
      .catch((error) => {
        if (cancelled) return;
        setChatTemplates([]);
        setChatTemplatesBaseUrl('');
        setChatTemplatesLoading(false);
        setChatTemplatesError(error.message || 'Falha ao carregar templates de Chat.');
      });
    return () => {
      cancelled = true;
    };
  }, [draft.config.platform, node.data.commandType]);

  useEffect(() => {
    if (node.data.commandType !== 'voice') return undefined;
    let cancelled = false;
    setVoiceTemplatesLoading(true);
    setVoiceTemplatesError('');
    api('/api/voice/templates')
      .then((response) => {
        if (cancelled) return;
        const responseVoiceUrl = normalizeChatExternalUrl(response?.data?.voice_url || response?.data?.voiceUrl || '');
        setVoiceTemplatesBaseUrl(responseVoiceUrl);
        const items = Array.isArray(response?.data?.data)
          ? response.data.data.map((item) => ({
              id: String(item?.id || '').trim(),
              name: String(
                item?.name
                || item?.campaign_name
                || item?.campaign
                || item?.description
                || item?.nome
                || item?.id
                || ''
              ).trim(),
              voice_url: normalizeChatExternalUrl(item?.voice_url || item?.voiceUrl || responseVoiceUrl),
              params_count: Math.max(0, Number.parseInt(item?.params_count, 10) || 0)
            })).filter((item) => item.id)
          : [];
        setVoiceTemplates(items);
        setVoiceTemplatesLoading(false);

        const selectedCampaignId = String(draft.config.campaignId || draft.config.templateId || '').trim();
        if (!selectedCampaignId) return;
        const selectedCampaign = items.find((item) => item.id === selectedCampaignId);
        if (!selectedCampaign) {
          setVoiceConfig({
            ...draft.config,
            campaignId: '',
            campaignName: '',
            voiceUrl: responseVoiceUrl,
            paramsCount: 0,
            paramBindings: {},
            paramBindingDetails: {}
          });
          return;
        }
        setVoiceConfig({
          ...draft.config,
          campaignId: selectedCampaign.id,
          campaignName: selectedCampaign.name,
          voiceUrl: normalizeChatExternalUrl(selectedCampaign.voice_url || selectedCampaign.voiceUrl || responseVoiceUrl),
          paramsCount: selectedCampaign.params_count
        });
      })
      .catch((error) => {
        if (cancelled) return;
        setVoiceTemplates([]);
        setVoiceTemplatesBaseUrl('');
        setVoiceTemplatesLoading(false);
        setVoiceTemplatesError(error.message || 'Falha ao carregar campanhas de Voz.');
      });
    return () => {
      cancelled = true;
    };
  }, [draft.config.campaignId, draft.config.templateId, node.data.commandType]);

  const setStartConfig = (patch) => {
    setDraft((current) => {
      const config = normalizeStartConfig({
        ...current.config,
        ...patch
      }, route);
      return {
        ...current,
        title: 'Início',
        subtitle: startSubtitleForConfig(config),
        config
      };
    });
  };

  const delayConfig = normalizeDelayConfig(draft.config);
  const insertDemandConfig = normalizeInsertDemandConfig(draft.config, demands, route, {
    extraVariables: taskExtraVariables
  });
  const chatBindingContext = apiVariableBindingContext({
    nodeId: node.id,
    nodes: flowNodes,
    edges: flowEdges,
    apis: testedApis,
    runtimeOutputs,
    demand: selectedDemand,
    taskExtraVariables
  });
  const chatTaskSelectorVariables = taskSelectorVariableOptions(chatBindingContext.taskOptions, {
    includeTaskMeta: true,
    includeDemandMeta: true
  });
  const chatTaskFileSelectorVariables = cortexTaskFileSelectorOptions(taskSelectorVariableOptions(chatBindingContext.taskOptions, {
    includeTaskMeta: false,
    includeDemandMeta: false,
    fileOnly: true
  }), { resource: 'tagent' });
  const chatInfoCustomTaskVariables = chatInfoCustomTaskOriginVariableOptions(chatTaskSelectorVariables);
  const chatCommandOutputSelectorVariables = commandOutputSelectorVariableOptions(chatBindingContext.sources);
  const chatContextSelectorVariables = mergeSelectorVariableOptions(
    chatTaskSelectorVariables,
    chatCommandOutputSelectorVariables
  );
  const chatInfoCustomOriginOptions = [
    ...CHAT_INFO_CUSTOM_ORIGIN_OPTIONS,
    ...chatBindingContext.sources.map((source) => ({
      value: `api:${source.nodeId}`,
      label: source.title || source.integrationName || `Comando ${source.nodeId}`
    }))
  ];
  const chatBindingSourceOptions = chatVoiceBindingSourceOptions(
    chatBindingContext.sources,
    chatBindingContext.taskSourceLabel
  );
  const chatLiteralSuggestionSources = templateSuggestSourceGroups({
    taskLabel: chatBindingContext.taskSourceLabel,
    taskVariables: chatTaskSelectorVariables,
    includeDemand: true,
    apiSources: chatBindingContext.sources
  });
  const chatConfig = normalizeChatConfig(draft.config, chatContextSelectorVariables, chatBindingContext.sources);
  const chatTemplateSupportsAttachment = isChatMediaTemplateType(chatConfig.templateContentTypeName);
  const chatInfoCustomPreviewObject = useMemo(() => {
    const customPayload = buildChatInfoCustomPreviewPayload(
      chatConfig.infoCustomMappings,
      {
        task: inspectionTask,
        demand: selectedDemand,
        outputs: runtimeOutputs,
        apiSources: chatBindingContext.sources,
        taskVariables: chatInfoCustomTaskVariables
      }
    );
    const previewTaskKey = String(
      inspectionTask?.task_key
      || inspectionTask?.taskKey
      || inspectionTask?.payload?.task_key
      || inspectionTask?.payload?.taskKey
      || ''
    ).trim() || '{{task.task_key}}';
    const previewStatus = String(
      inspectionTask?.status
      || inspectionTask?.payload?.status
      || ''
    ).trim() || '{{task.status}}';
    return {
      ...customPayload,
      ttip_tdemand: '{{tenant.domain}}',
      task_key: previewTaskKey,
      status: previewStatus
    };
  }, [chatBindingContext.sources, chatConfig.infoCustomMappings, chatInfoCustomTaskVariables, inspectionTask, runtimeOutputs, selectedDemand]);
  const chatInfoCustomPreviewJson = useMemo(() => JSON.stringify(chatInfoCustomPreviewObject || {}, null, 2), [chatInfoCustomPreviewObject]);
  const chatTemplatePreview = buildChatTemplatePreview({
    headerPreview: chatConfig.headerPreview,
    bodyPreview: chatConfig.preview,
    footerPreview: chatConfig.footer,
    paramBindings: chatConfig.paramBindings,
    paramBindingDetails: chatConfig.paramBindingDetails,
    bindingOptions: chatContextSelectorVariables
  });
  const hasChatTemplatePreview = Boolean(chatTemplatePreview.header || chatTemplatePreview.body || chatTemplatePreview.footer);
  const chatTemplateEditorUrl = buildChatTemplateEditorUrl({
    chatUrl: chatConfig.chatUrl,
    templateId: chatConfig.templateId
  });
  const chatChatbotEditorUrl = buildChatbotEditorUrl({
    chatUrl: chatConfig.chatUrl,
    chatbotId: chatConfig.chatbotId
  });
  const chatTemplatePreviewTime = useMemo(() => new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date()), [chatTemplatePreview.header, chatTemplatePreview.body, chatTemplatePreview.footer]);
  const voiceTaskSelectorVariables = chatContextSelectorVariables;
  const voiceConfig = normalizeVoiceConfig(draft.config, voiceTaskSelectorVariables, chatBindingContext.sources);
  const voiceCampaignEditorUrl = buildVoiceCampaignEditorUrl({
    voiceUrl: voiceConfig.voiceUrl,
    templateId: voiceConfig.campaignId
  });
  const voiceDialAtBinding = voiceConfig.dialAtBinding && typeof voiceConfig.dialAtBinding === 'object' && !Array.isArray(voiceConfig.dialAtBinding)
    ? voiceConfig.dialAtBinding
    : {
        sourceType: '',
        path: '',
        dateConfig: defaultApiDateBindingConfig()
      };
  const voiceDialAtSource = voiceDialAtBinding.sourceType === 'date'
    ? 'date'
    : (voiceDialAtBinding.sourceType === 'task' ? 'task' : '');
  const voiceDialAtDateConfig = normalizeApiDateBindingConfig(voiceDialAtBinding.dateConfig || {});
  const voiceDialAtDatePreviewNow = new Date();
  const voiceDialAtDraftConfig = normalizeApiDateBindingConfig(voiceDialAtDateDraft);
  const voiceDialAtDraftPreview = resolveApiDateBindingValue(voiceDialAtDraftConfig, voiceDialAtDatePreviewNow);
  const cortexConfig = normalizeCortexConfig(draft.config);
  const cortexRequestMode = cortexRequestModeFromConfig(cortexConfig);
  const cortexBindingContext = apiVariableBindingContext({
    nodeId: node.id,
    nodes: flowNodes,
    edges: flowEdges,
    apis: testedApis,
    runtimeOutputs,
    demand: selectedDemand,
    taskExtraVariables
  });
  const cortexTaskSelectorVariables = taskSelectorVariableOptions(cortexBindingContext.taskOptions, {
    includeTaskMeta: true,
    includeDemandMeta: true
  });
  const cortexTaskFileSelectorVariables = cortexTaskFileSelectorOptions(taskSelectorVariableOptions(cortexBindingContext.taskOptions, {
    includeTaskMeta: false,
    includeDemandMeta: false,
    fileOnly: true
  }), { resource: cortexConfig.resource });
  const cortexTemplateTaskVariables = taskSelectorVariableOptions(cortexBindingContext.taskOptions, {
    includeTaskMeta: true,
    includeDemandMeta: false
  }).map((option) => {
    const rawValue = String(option?.value || '').trim();
    if (!rawValue) return null;
    const templateValue = rawValue.startsWith('demand.') || rawValue.startsWith('task.')
      ? rawValue
      : `task.${rawValue}`;
    return {
      ...option,
      value: templateValue
    };
  }).filter(Boolean);
  const cortexTemplateSuggestionSources = templateSuggestSourceGroups({
    taskLabel: 'Task',
    taskVariables: cortexTemplateTaskVariables,
    includeDemand: true,
    apiSources: cortexBindingContext.sources
  });
  const cortexPayloadFields = cortexPayloadFieldsForResource(cortexConfig.resource);
  const startConfig = normalizeStartConfig(draft.config, route);
  const startTaskVariables = taskVariableOptions(selectedDemand, {
    extraVariables: taskExtraVariables,
    includeTaskCommentSources: true
  });
  const startTaskArrayVariables = startTaskVariables.filter((item) => isArrayLikeSchemaType(item?.type));
  const startInSearchMode = isSearchStartConfig(startConfig);
  const startInTaskUpdateMode = isTaskUpdateStartConfig(startConfig);
  const routeStartConfig = normalizeStartConfig(
    (Array.isArray(flowNodes) ? flowNodes : []).find((candidate) => isStartNode(candidate))?.data?.config || {},
    route
  );
  const routeStartsFromSearchApi = isSearchStartConfig(routeStartConfig);
  const startExecutionIntegration = availableTestedApis.find((item) => (
    String(item.id) === String(startConfig.executionIntegrationId)
  ));
  const startExecutionSchema = mergeResponseSchemas(
    normalizeResponseSchema(startExecutionIntegration?.response_schema),
    normalizeResponseSchema(startConfig.executionResponseSchema),
    learnedResponseSchemaFromConfig(startConfig)
  );
  const startExecutionArrayOptions = executionStartArrayOptions(startExecutionSchema);
  const cortexResourceCodSelectOptions = [
    {
      value: '',
      label: cortexResourceOptionsLoading
        ? 'Carregando recursos Cortex...'
        : cortexResourceOptionsError
          ? 'Falha ao carregar recursos Cortex'
          : (cortexResourceOptionsState.length ? 'Selecione o recurso listado' : 'Nenhum recurso disponível')
    },
    ...cortexResourceOptionsState.map((item) => ({
      value: item.cod,
      label: item.name && item.name !== item.cod
        ? (item.description ? `${item.name} - ${item.description}` : item.name)
        : (item.description || item.name || 'Recurso sem nome')
      }))
  ];
  const chatPlatformOptions = [
    {
      value: '',
      label: chatPlatformsLoading
        ? 'Carregando plataformas...'
        : chatPlatformsError
          ? 'Falha ao carregar plataformas'
          : (chatPlatforms.length ? 'Selecione a plataforma' : 'Nenhuma plataforma disponível')
    },
    ...chatPlatforms.map((item) => ({
      value: item.id,
      label: item.name || item.id
    }))
  ];
  const chatTemplateOptions = [
    {
      value: '',
      label: !chatConfig.platform
        ? 'Selecione uma plataforma'
        : chatTemplatesLoading
          ? 'Carregando templates...'
          : chatTemplatesError
            ? 'Falha ao carregar templates'
            : (chatTemplates.length ? 'Selecione o template' : 'Nenhum template disponível')
    },
    ...chatTemplates.map((item) => ({
      value: item.id,
      label: item.name && item.origin
        ? `${item.name} - origem ${item.origin}`
        : (item.name || item.id)
    }))
  ];
  const voiceTemplateOptions = [
    {
      value: '',
      label: voiceTemplatesLoading
        ? 'Carregando campanhas...'
        : voiceTemplatesError
          ? 'Falha ao carregar campanhas'
          : (voiceTemplates.length ? 'Selecione a campanha' : 'Nenhuma campanha disponível')
    },
    ...voiceTemplates.map((item) => ({
      value: item.id,
      label: item.name || item.id
    }))
  ];
  const selectedCortexResourceOption = cortexResourceOptionsState.find((item) => (
    item.cod === String(cortexConfig.resourceCod || '').trim()
  )) || null;
  const cortexResolvedResponseType = String(
    selectedCortexResourceOption
      ? (selectedCortexResourceOption.responseType || '')
      : (cortexConfig.resourceResponseType || '')
  ).trim().toUpperCase();
  const cortexIsTagentGeneric = String(cortexConfig.resource || '').toLowerCase() === 'tagent'
    && cortexResolvedResponseType === 'GENERIC';

  const cortexTemplateValueByField = (fieldKey) => {
    if (fieldKey === 'prompt') {
      return String(cortexConfig.genericPrompt || '');
    }
    if (fieldKey === 'response_format') return String(cortexConfig.genericOutputFormat || '');
    return String(cortexConfig.payloadTemplates?.[fieldKey] || '');
  };

  const setCortexTemplateValue = (fieldKey, templateValue) => {
    if (fieldKey === 'prompt') {
      setCortexConfig({
        ...cortexConfig,
        genericPrompt: templateValue
      });
      return;
    }

    if (fieldKey === 'response_format') {
      setCortexConfig({
        ...cortexConfig,
        genericOutputFormat: templateValue
      });
      return;
    }

    setCortexConfig({
      ...cortexConfig,
      payloadTemplates: {
        ...(cortexConfig.payloadTemplates || {}),
        [fieldKey]: templateValue
      }
    });
  };

  const onCortexTemplateKeyDown = (event, fieldKey, label) => {
    const isSlashShortcut = (
      event.key === '/'
      || event.key === '?'
      || event.code === 'Slash'
      || event.code === 'NumpadDivide'
    );
    if (!isSlashShortcut || event.ctrlKey || event.metaKey || event.altKey) return;
    if (!canEdit) return;
    event.preventDefault();
    openCortexTemplateAttributePicker({ key: fieldKey, label });
  };

  const renderCortexGenericTemplateField = (fieldKey, label, helpDescription, usage, placeholder) => (
    <section className="modal-field modal-field-wide" key={`cortex-generic-${fieldKey}`}>
      <div className="cortex-template-field-head">
        <FieldLabel
          help={{
            title: label,
            description: helpDescription,
            usage
          }}
        >
          {label}
        </FieldLabel>
        <button
          className="icon-button cortex-template-variable-button"
          type="button"
          onClick={() => openCortexTemplateAttributePicker({ key: fieldKey, label })}
          disabled={!canEdit}
          title={`Inserir variavel da Task ou Demanda no campo ${label.toLowerCase()}`}
          aria-label={`Inserir variavel da Task ou Demanda no campo ${label.toLowerCase()}`}
        >
          {'{}'}
        </button>
      </div>
      <textarea
        ref={(element) => {
          if (element) {
            cortexTemplateTextareaRefs.current[fieldKey] = element;
          } else {
            delete cortexTemplateTextareaRefs.current[fieldKey];
          }
        }}
        value={cortexTemplateValueByField(fieldKey)}
        onChange={(event) => {
          setCortexTemplateValue(fieldKey, event.target.value);
          updateCortexTemplateSelection(fieldKey, event.target);
        }}
        onKeyDown={(event) => onCortexTemplateKeyDown(event, fieldKey, label)}
        onClick={(event) => updateCortexTemplateSelection(fieldKey, event.target)}
        onKeyUp={(event) => updateCortexTemplateSelection(fieldKey, event.target)}
        onSelect={(event) => updateCortexTemplateSelection(fieldKey, event.target)}
        rows={6}
        placeholder={placeholder}
        disabled={!canEdit}
      />
    </section>
  );

  const setCortexFollowUpBinding = (bindingValue) => {
    const normalized = normalizeTaskSelectorBinding(bindingValue);
    setCortexConfig({
      ...cortexConfig,
      followUpBinding: normalized,
      followUpAttribute: normalized.path
    });
  };

  const setCortexFileBinding = (bindingValue) => {
    const normalized = normalizeTaskSelectorBinding(bindingValue);
    setCortexConfig({
      ...cortexConfig,
      fileBinding: normalized,
      fileAttribute: normalized.path
    });
  };

  const setCortexPayloadBinding = (fieldKey, bindingValue) => {
    setCortexConfig({
      ...cortexConfig,
      payloadBindings: {
        ...(cortexConfig.payloadBindings || {}),
        [fieldKey]: bindingValue
      }
    });
  };
  const normalizeCortexVariableBinding = (value) => {
    const normalized = normalizeTaskSelectorBinding(value);
    const description = value && typeof value === 'object' && !Array.isArray(value)
      ? String(value.description || '').trim()
      : '';
    return {
      ...normalized,
      description
    };
  };
  const cortexVariableBindings = (
    Array.isArray(cortexConfig.payloadBindings?.variables)
      ? cortexConfig.payloadBindings.variables
      : []
  )
    .map((item) => normalizeCortexVariableBinding(item))
    .filter((item) => item.path);
  const setCortexVariableBindings = (nextValues = []) => {
    setCortexPayloadBinding('variables', (Array.isArray(nextValues) ? nextValues : [])
      .map((item) => normalizeCortexVariableBinding(item))
      .filter((item) => item.path));
  };
  const updateCortexVariableBinding = (index, nextBinding) => {
    const current = [...cortexVariableBindings];
    if (index < 0 || index >= current.length) return;
    current[index] = normalizeCortexVariableBinding({
      ...current[index],
      ...(nextBinding && typeof nextBinding === 'object' && !Array.isArray(nextBinding) ? nextBinding : {})
    });
    setCortexVariableBindings(current);
  };
  const updateCortexVariableDescription = (index, nextDescription) => {
    const current = [...cortexVariableBindings];
    if (index < 0 || index >= current.length) return;
    current[index] = normalizeCortexVariableBinding({
      ...current[index],
      description: String(nextDescription || '')
    });
    setCortexVariableBindings(current);
  };
  const addCortexVariableBinding = () => {
    const firstVariable = cortexTaskSelectorVariables[0]?.value || '';
    const nextBinding = normalizeCortexVariableBinding(firstVariable ? { path: firstVariable } : { path: '' });
    setCortexVariableBindings([...cortexVariableBindings, nextBinding]);
  };
  const removeCortexVariableBinding = (index) => {
    setCortexVariableBindings(cortexVariableBindings.filter((_, itemIndex) => itemIndex !== index));
  };
  const setCortexTagentOptions = (nextOptions) => {
    setCortexConfig({
      ...cortexConfig,
      tagentOptions: normalizeCortexTagentOptions({ tagentOptions: nextOptions })
    });
  };

  const updateCortexTagentOption = (index, nextValue) => {
    const current = Array.isArray(cortexConfig.tagentOptions) ? cortexConfig.tagentOptions : [];
    const next = current.map((item, itemIndex) => (itemIndex === index ? nextValue : item));
    setCortexTagentOptions(next);
  };

  const addCortexTagentOption = () => {
    const current = Array.isArray(cortexConfig.tagentOptions) ? cortexConfig.tagentOptions : [];
    setCortexTagentOptions(current.concat(''));
  };

  const removeCortexTagentOption = (index) => {
    const current = Array.isArray(cortexConfig.tagentOptions) ? cortexConfig.tagentOptions : [];
    setCortexTagentOptions(current.filter((_, itemIndex) => itemIndex !== index));
  };

  const setCortexOcrOptions = (nextOptions) => {
    setCortexConfig({
      ...cortexConfig,
      ocrOptions: Array.isArray(nextOptions) ? nextOptions : []
    });
  };

  const updateCortexOcrOption = (index, patch = {}) => {
    const current = Array.isArray(cortexConfig.ocrOptions) ? cortexConfig.ocrOptions : [];
    const next = current.map((item, itemIndex) => {
      if (itemIndex !== index) return item;
      return {
        ...item,
        ...patch
      };
    });
    setCortexOcrOptions(next);
  };

  const addCortexOcrOption = () => {
    const current = Array.isArray(cortexConfig.ocrOptions) ? cortexConfig.ocrOptions : [];
    setCortexOcrOptions(current.concat({ documentName: '', extractionData: '' }));
  };

  const removeCortexOcrOption = (index) => {
    const current = Array.isArray(cortexConfig.ocrOptions) ? cortexConfig.ocrOptions : [];
    setCortexOcrOptions(current.filter((_, itemIndex) => itemIndex !== index));
  };

  const updateCortexTemplateSelection = (fieldKey, target) => {
    if (!target) return;
    cortexTemplateSelectionRef.current[fieldKey] = {
      start: Number(target.selectionStart) || 0,
      end: Number(target.selectionEnd) || 0
    };
  };

  const openCortexTemplateAttributePicker = (field) => {
    const fieldKey = field?.key || 'message';
    const textarea = cortexTemplateTextareaRefs.current[fieldKey];
    updateCortexTemplateSelection(fieldKey, textarea);
    setCortexTemplateAttributeTarget({
      key: fieldKey,
      label: String(field?.label || fieldKey)
    });
    setCortexTemplateAttributePickerOpen(true);
  };

  const closeCortexTemplateAttributePicker = () => {
    setCortexTemplateAttributePickerOpen(false);
  };

  const insertCortexTemplateAttribute = (bindingValue) => {
    const attributePath = String(normalizeTaskSelectorBinding(bindingValue).path || '').trim();
    if (!attributePath) return;
    const fieldKey = String(cortexTemplateAttributeTarget?.key || 'message');
    const placeholder = `{{${attributePath}}}`;
    const currentTemplate = cortexTemplateValueByField(fieldKey);
    const selection = cortexTemplateSelectionRef.current[fieldKey] || { start: currentTemplate.length, end: currentTemplate.length };
    const start = Math.min(Math.max(selection.start || 0, 0), currentTemplate.length);
    const end = Math.min(Math.max(selection.end || start, start), currentTemplate.length);
    const nextTemplate = `${currentTemplate.slice(0, start)}${placeholder}${currentTemplate.slice(end)}`;
    setCortexTemplateValue(fieldKey, nextTemplate);
    setCortexTemplateAttributePickerOpen(false);

    window.requestAnimationFrame(() => {
      const textarea = cortexTemplateTextareaRefs.current[fieldKey];
      if (!textarea) return;
      textarea.focus();
      const nextCursor = start + placeholder.length;
      textarea.setSelectionRange(nextCursor, nextCursor);
      cortexTemplateSelectionRef.current[fieldKey] = { start: nextCursor, end: nextCursor };
    });
  };
  const routeDemandIdForInsert = route?.demand_id ? String(route.demand_id) : '';
  const insertDemandSelectOptions = [
    routeDemandIdForInsert
      ? {
          value: routeDemandIdForInsert,
          label: `${routeDemandName(route)} (demanda atual)`,
          disabled: insertDemandConfig.operation === 'move'
        }
      : { value: '', label: 'Selecione a demanda' },
    ...demands
      .filter((item) => !routeDemandIdForInsert || String(item.id) !== routeDemandIdForInsert)
      .map((item) => ({ value: String(item.id), label: demandDisplayName(item) }))
  ];
  const insertDemandApiSources = apiAncestorSources({
    nodeId: node.id,
    nodes: flowNodes,
    edges: flowEdges,
    apis: testedApis,
    runtimeOutputs
  });
  const insertDemandApiSourceOptions = [
    {
      value: '',
      label: insertDemandApiSources.length
        ? 'Selecione a API'
        : 'Nenhuma API anterior disponível',
      disabled: true
    },
    ...insertDemandApiSources.map((source) => ({
      value: source.nodeId,
      label: `${source.title} (${source.nodeId})`
    }))
  ];
  const selectedInsertDemandApiSource = insertDemandApiSources.find(
    (source) => String(source.nodeId) === String(insertDemandConfig.mergeSourceNodeId || '')
  ) || null;
  const selectedInsertDemandRuntimeOutput = selectedInsertDemandApiSource
    ? runtimeOutputs?.[String(selectedInsertDemandApiSource.nodeId)] || null
    : null;
  const selectedInsertDemandRuntimePayload = runtimeOutputPayloadDetails(selectedInsertDemandRuntimeOutput);
  const selectedInsertDemandResponsePath = selectedInsertDemandApiSource?.responsePath || '$';
  const selectedInsertDemandRuntimePayloadAtPath = selectedInsertDemandRuntimePayload.known
    ? readPayloadPath(selectedInsertDemandRuntimePayload.payload, selectedInsertDemandResponsePath)
    : null;
  const selectedInsertDemandSchemaShape = assessResponseSchemaPayloadShape(
    selectedInsertDemandApiSource?.schema || {},
    selectedInsertDemandResponsePath
  );
  const insertDemandShouldShowNonObjectAttribute = (() => {
    if (!['merge_api', 'api_response'].includes(insertDemandConfig.payloadMode)) return false;
    if (!selectedInsertDemandApiSource) return false;
    if (selectedInsertDemandSchemaShape.mayBeNonObject) return true;
    if (selectedInsertDemandRuntimePayload.known) {
      return !isPlainObjectValue(selectedInsertDemandRuntimePayloadAtPath);
    }
    return false;
  })();
  const setDelayConfig = (key, value) => {
    setDraft((current) => ({
      ...current,
      config: normalizeDelayConfig({ ...current.config, [key]: value })
    }));
  };

  const selectChatPlatform = (value) => {
    setChatConfig({
      ...chatConfig,
      platform: value,
      templateId: '',
      templateName: '',
      templateOrigin: '',
      templateContentTypeName: '',
      chatbotId: '',
      chatUrl: '',
      headerPreview: '',
      preview: '',
      footer: '',
      paramsCount: 0,
      paramBindings: {},
      paramBindingDetails: {}
    });
  };

  const selectChatTemplate = (value) => {
    const selectedTemplate = chatTemplates.find((item) => String(item.id) === String(value));
    const nextParamsCount = Math.max(0, Number.parseInt(selectedTemplate?.params_count, 10) || 0);
    const currentBindings = chatConfig.paramBindings && typeof chatConfig.paramBindings === 'object'
      ? chatConfig.paramBindings
      : {};
    const currentBindingDetails = chatConfig.paramBindingDetails && typeof chatConfig.paramBindingDetails === 'object'
      ? chatConfig.paramBindingDetails
      : {};
    const nextBindings = {};
    const nextBindingDetails = {};
    for (let index = 1; index <= nextParamsCount; index += 1) {
      const key = String(index);
      const normalized = normalizeChatVoiceBinding(
        currentBindingDetails[key] ?? currentBindings[key],
        chatContextSelectorVariables,
        chatBindingContext.sources
      );
      nextBindingDetails[key] = normalized;
      nextBindings[key] = normalized.sourceType === 'literal'
        ? ''
        : String(normalized.path || '').trim();
    }
    setChatConfig({
      ...chatConfig,
      templateId: selectedTemplate?.id || '',
      templateName: selectedTemplate?.name || '',
      templateOrigin: selectedTemplate?.origin || '',
      templateContentTypeName: String(selectedTemplate?.content_type_name || '').trim().toLowerCase(),
      chatbotId: String(selectedTemplate?.chatbot_id || selectedTemplate?.chatbotId || '').trim(),
      chatUrl: normalizeChatExternalUrl(selectedTemplate?.chat_url || selectedTemplate?.chatUrl || chatTemplatesBaseUrl),
      headerPreview: selectedTemplate?.header_preview || '',
      preview: selectedTemplate?.preview || '',
      footer: selectedTemplate?.footer || '',
      paramsCount: nextParamsCount,
      paramBindings: nextBindings,
      paramBindingDetails: nextBindingDetails
    });
  };

  const setChatToBinding = (nextBinding) => {
    const normalized = normalizeChatVoiceBinding(nextBinding, chatContextSelectorVariables, chatBindingContext.sources);
    setChatConfig({
      ...chatConfig,
      toBinding: normalized,
      toAttribute: normalized.sourceType === 'literal' ? '' : String(normalized.path || '').trim()
    });
  };

  const setChatAttachmentBinding = (nextBinding) => {
    const normalized = normalizeChatVoiceBinding(nextBinding, chatTaskFileSelectorVariables, []);
    const nextPath = normalized.sourceType === 'literal'
      ? ''
      : normalizeCortexOcrFileBindingPath(String(normalized.path || '').trim());
    setChatConfig({
      ...chatConfig,
      attachmentBinding: {
        sourceType: 'task',
        path: nextPath,
        arrayMatchByPath: normalizeConditionArrayMatches(normalized.arrayMatchByPath),
        arrayNavigationByPath: normalizeConditionArrayNavigations(normalized.arrayNavigationByPath, normalized.arrayMatchByPath)
      },
      attachmentAttribute: nextPath
    });
  };

  const setChatParamBinding = (paramIndex, nextBinding) => {
    const key = String(paramIndex);
    const normalized = normalizeChatVoiceBinding(nextBinding, chatContextSelectorVariables, chatBindingContext.sources);
    setChatConfig({
      ...chatConfig,
      paramBindings: {
        ...(chatConfig.paramBindings || {}),
        [key]: normalized.sourceType === 'literal' ? '' : String(normalized.path || '').trim()
      },
      paramBindingDetails: {
        ...(chatConfig.paramBindingDetails || {}),
        [key]: normalized
      }
    });
  };

  const addChatInfoCustomMapping = () => {
    setChatConfig({
      ...chatConfig,
      infoCustomMappings: (Array.isArray(chatConfig.infoCustomMappings) ? chatConfig.infoCustomMappings : [])
        .concat(makeChatInfoCustomMapping(chatBindingContext.sources))
    });
  };

  const removeChatInfoCustomMapping = (mappingId) => {
    const currentMappings = Array.isArray(chatConfig.infoCustomMappings) ? chatConfig.infoCustomMappings : [];
    const nextMappings = currentMappings.filter((mapping) => String(mapping?.id || '') !== String(mappingId || ''));
    setChatConfig({
      ...chatConfig,
      infoCustomMappings: nextMappings.length ? nextMappings : [makeChatInfoCustomMapping(chatBindingContext.sources)]
    });
  };

  const updateChatInfoCustomMapping = (mappingId, patch = {}) => {
    const currentMappings = Array.isArray(chatConfig.infoCustomMappings) ? chatConfig.infoCustomMappings : [];
    const nextMappings = currentMappings.map((mapping) => {
      if (String(mapping?.id || '') !== String(mappingId || '')) return mapping;
      if (Object.prototype.hasOwnProperty.call(patch, 'originValue')) {
        const originValue = String(patch.originValue || '').trim();
        if (originValue.startsWith('api:')) {
          const sourceNodeId = originValue.slice('api:'.length);
          const source = chatBindingContext.sources.find((item) => String(item.nodeId) === String(sourceNodeId));
          return {
            ...mapping,
            sourceType: 'api',
            sourceNodeId,
            path: source?.variables?.[0]?.value || '',
            useAs: String(mapping?.useAs || '').trim(),
            arrayMatchByPath: {},
            arrayNavigationByPath: {}
          };
        }
        const sourceType = ['task', 'demand', 'date'].includes(originValue) ? originValue : 'task';
        const path = sourceType === 'date'
          ? String(DATE_TEMPLATE_VARIABLE_OPTIONS[0]?.value || 'date.now_iso').trim()
          : '';
        return {
          ...mapping,
          sourceType,
          sourceNodeId: '',
          path,
          useAs: String(mapping?.useAs || '').trim(),
          arrayMatchByPath: {},
          arrayNavigationByPath: {}
        };
      }
      const nextSourceType = normalizeChatInfoCustomSourceType({ ...mapping, ...patch });
      const nextPath = Object.prototype.hasOwnProperty.call(patch, 'path')
        ? String(patch.path || '').trim()
        : String(mapping.path || '').trim();
      return {
        ...mapping,
        ...patch,
        useAs: Object.prototype.hasOwnProperty.call(patch, 'useAs')
          ? String(patch.useAs || '').trim()
          : String(mapping.useAs || '').trim(),
        path: nextSourceType === 'task'
          ? normalizeChatInfoCustomTaskPath(nextPath)
          : nextPath
      };
    });
    setChatConfig({
      ...chatConfig,
      infoCustomMappings: nextMappings
    });
  };

  const selectVoiceTemplate = (value) => {
    const selectedTemplate = voiceTemplates.find((item) => String(item.id) === String(value));
    const nextParamsCount = Math.max(0, Number.parseInt(selectedTemplate?.params_count, 10) || 0);
    const editableParamsCount = Math.max(nextParamsCount - 1, 0);
    const currentBindings = voiceConfig.paramBindings && typeof voiceConfig.paramBindings === 'object'
      ? voiceConfig.paramBindings
      : {};
    const currentBindingDetails = voiceConfig.paramBindingDetails && typeof voiceConfig.paramBindingDetails === 'object'
      ? voiceConfig.paramBindingDetails
      : {};
    const nextBindings = {};
    const nextBindingDetails = {};
    for (let index = 1; index <= editableParamsCount; index += 1) {
      const key = String(index);
      const normalized = normalizeChatVoiceBinding(
        currentBindingDetails[key] ?? currentBindings[key],
        voiceTaskSelectorVariables,
        chatBindingContext.sources
      );
      nextBindingDetails[key] = normalized;
      nextBindings[key] = normalized.sourceType === 'literal'
        ? ''
        : String(normalized.path || '').trim();
    }
    setVoiceConfig({
      ...voiceConfig,
      campaignId: selectedTemplate?.id || '',
      campaignName: selectedTemplate?.name || '',
      voiceUrl: normalizeChatExternalUrl(selectedTemplate?.voice_url || selectedTemplate?.voiceUrl || voiceTemplatesBaseUrl),
      paramsCount: nextParamsCount,
      paramBindings: nextBindings,
      paramBindingDetails: nextBindingDetails
    });
  };

  const setVoiceToBinding = (nextBinding) => {
    const normalized = normalizeChatVoiceBinding(nextBinding, voiceTaskSelectorVariables, chatBindingContext.sources);
    setVoiceConfig({
      ...voiceConfig,
      toBinding: normalized,
      toAttribute: normalized.sourceType === 'literal' ? '' : String(normalized.path || '').trim()
    });
  };

  const setVoiceParamBinding = (paramIndex, nextBinding) => {
    const key = String(paramIndex);
    const normalized = normalizeChatVoiceBinding(nextBinding, voiceTaskSelectorVariables, chatBindingContext.sources);
    setVoiceConfig({
      ...voiceConfig,
      paramBindings: {
        ...(voiceConfig.paramBindings || {}),
        [key]: normalized.sourceType === 'literal' ? '' : String(normalized.path || '').trim()
      },
      paramBindingDetails: {
        ...(voiceConfig.paramBindingDetails || {}),
        [key]: normalized
      }
    });
  };

  const closeVoiceDialAtDateModal = () => {
    setVoiceDialAtDateModalOpen(false);
  };

  const openVoiceDialAtDateModal = (dateConfig = voiceDialAtDateConfig) => {
    setVoiceDialAtDateDraft(normalizeApiDateBindingConfig(dateConfig || {}));
    setVoiceDialAtDateModalOpen(true);
  };

  const setVoiceDialAtSource = (sourceType) => {
    if (sourceType === 'task') {
      const taskPath = voiceDialAtBinding.sourceType === 'task'
        ? String(voiceDialAtBinding.path || '').trim()
        : '';
      setVoiceConfig({
        ...voiceConfig,
        dialAtBinding: {
          sourceType: 'task',
          path: taskPath,
          dateConfig: voiceDialAtDateConfig
        },
        dialAtAttribute: taskPath
      });
      return;
    }

    if (sourceType === 'date') {
      setVoiceConfig({
        ...voiceConfig,
        dialAtBinding: {
          sourceType: 'date',
          path: '',
          dateConfig: voiceDialAtDateConfig
        },
        dialAtAttribute: ''
      });
      openVoiceDialAtDateModal(voiceDialAtDateConfig);
      return;
    }

    setVoiceConfig({
      ...voiceConfig,
      dialAtBinding: {
        sourceType: '',
        path: '',
        dateConfig: voiceDialAtDateConfig
      },
      dialAtAttribute: ''
    });
  };

  const setVoiceDialAtTaskBinding = (binding = {}) => {
    const path = String(binding.path || '').trim();
    setVoiceConfig({
      ...voiceConfig,
      dialAtBinding: {
        sourceType: 'task',
        path,
        dateConfig: voiceDialAtDateConfig
      },
      dialAtAttribute: path
    });
  };

  const applyVoiceDialAtDateConfig = () => {
    const normalizedDateConfig = normalizeApiDateBindingConfig(voiceDialAtDateDraft);
    setVoiceConfig({
      ...voiceConfig,
      dialAtBinding: {
        sourceType: 'date',
        path: '',
        dateConfig: normalizedDateConfig
      },
      dialAtAttribute: ''
    });
    closeVoiceDialAtDateModal();
  };

  const selectIntegration = (value) => {
    const integration = availableTestedApis.find((item) => String(item.id) === String(value));
    const bindingContext = apiVariableBindingContext({
      nodeId: node.id,
      nodes: flowNodes,
      edges: flowEdges,
      apis: testedApis,
      runtimeOutputs,
      demand: selectedDemand,
      taskExtraVariables
    });
    setDraft((current) => {
      const integrationChanged = String(current.config.integrationId || '') !== String(value || '');
      return {
        ...current,
        title: current.title && current.title !== command.label && current.title !== 'Executar integração'
          ? current.title
          : integration?.name || command.label,
        subtitle: integration
          ? `${integration.request_method || 'GET'} ${integration.request_path || integration.base_url}`
          : 'Selecione uma Integração',
        config: {
          ...current.config,
          integrationId: value,
          integrationName: integration?.name || '',
          timeoutMs: integration?.timeout_ms || current.config.timeoutMs || 15000,
          requestVariables: integrationTemplateVariables(integration),
          learnedResponseSchema: integrationChanged ? {} : learnedResponseSchemaFromConfig(current.config),
          variableBindings: normalizeApiVariableBindings(
            current.config,
            integration,
            selectedDemand,
            bindingContext.sources,
            bindingContext.taskOptions
          ).variableBindings
        }
      };
    });
  };

  const selectStartExecutionIntegration = (value) => {
    const integration = availableTestedApis.find((item) => String(item.id) === String(value));
    const variableSet = new Set(integrationTemplateVariables(integration));
    const existingBindings = normalizeResponseSchema(draft.config.executionVariableBindings);
    const executionVariableBindings = Object.entries(existingBindings).reduce((acc, [name, binding]) => {
      if (!variableSet.has(name)) return acc;
      acc[name] = binding;
      return acc;
    }, {});
    setStartConfig({
      executionIntegrationId: value,
      executionIntegrationName: integration?.name || '',
      executionResponseSchema: normalizeResponseSchema(integration?.response_schema),
      learnedResponseSchema: {},
      executionRecordPath: '',
      executionTemplateVariables: {},
      executionVariableBindings
    });
  };

  const fields = {
    start: (
      <>
        <Field
          label="Demanda"
          value={selectedDemand ? demandDisplayName(selectedDemand) : routeDemandName(route)}
          onChange={() => {}}
          disabled
        />
        <section className="start-mode-switch modal-field-wide">
          <header>
            <FieldLabel>Tipo de início</FieldLabel>
          </header>
          <div className="start-mode-options" role="radiogroup" aria-label="Tipo de início">
            <label className="start-mode-option">
              <input
                type="radio"
                name="start-mode"
                value={START_MODE_EXECUTION}
                checked={!startInSearchMode}
                onChange={() => setStartConfig({ startMode: START_MODE_EXECUTION })}
                disabled={!canEdit}
              />
              <span>Execução</span>
            </label>
            <label className="start-mode-option">
              <input
                type="radio"
                name="start-mode"
                value={START_MODE_SEARCH}
                checked={startInSearchMode}
                onChange={() => setStartConfig({ startMode: START_MODE_SEARCH, taskArrayPath: '' })}
                disabled={!canEdit}
              />
              <span>Buscar</span>
            </label>
          </div>
        </section>
        <p className="start-processing-note modal-field-wide">
          <span className="material-symbols-rounded" aria-hidden="true">info</span>
          O fluxo trata cada registro individualmente, um a um.
        </p>
        {startInSearchMode ? (
          <>
            <Field
              label="Integração(API) *"
              value={startConfig.executionIntegrationId}
              onChange={selectStartExecutionIntegration}
              selectOptions={testedApiOptions(availableTestedApis)}
              disabled={!canEdit}
            />
            {startExecutionArrayOptions.length > 0 && (
              <Field
                label="Lista do retorno"
                value={startConfig.executionRecordPath}
                onChange={(value) => setStartConfig({ executionRecordPath: value })}
                selectOptions={[
                  { value: '', label: 'Usar retorno inteiro' },
                  ...startExecutionArrayOptions
                ]}
                disabled={!canEdit}
              />
            )}
            <div className="start-periodicity modal-field">
              <FieldLabel>Executar a cada</FieldLabel>
              <div>
                <select
                  value={String(startConfig.executionEveryMinutes || 5)}
                  onChange={(event) => setStartConfig({ executionEveryMinutes: Number(event.target.value) })}
                  disabled={!canEdit}
                >
                  {startExecutionIntervalOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </select>
                <small>minutos</small>
              </div>
            </div>
            <p className="start-execution-notice modal-field-wide">O Início apenas consulta a integração e disponibiliza cada retorno no fluxo. Use Inserir em Demanda para persistir dados.</p>
            <ApiIntegrationSummary integration={startExecutionIntegration} runtimeOutput={runtimeOutputs?.[String(node.id)]} />
            <StartExecutionVariables
              integration={startExecutionIntegration}
              config={startConfig}
              canEdit={canEdit}
              onChange={(executionVariableBindings) => setStartConfig({
                executionVariableBindings,
                executionTemplateVariables: {}
              })}
            />
          </>
        ) : (
          <>
            <Field
              label="Tipo de execução"
              value={normalizeStartExecutionType(startConfig.executionType)}
              onChange={(value) => setStartConfig({ executionType: normalizeStartExecutionType(value) })}
              selectOptions={startExecutionTypeOptions}
              disabled={!canEdit}
            />
            {startInTaskUpdateMode ? (
              <>
                <Field
                  label="Regra do gatilho"
                  value={normalizeTaskUpdateTriggerMode(startConfig.taskUpdateTriggerMode)}
                  onChange={(value) => setStartConfig({ taskUpdateTriggerMode: normalizeTaskUpdateTriggerMode(value) })}
                  selectOptions={taskUpdateTriggerModeOptions}
                  disabled={!canEdit}
                />
                <p className="start-execution-notice modal-field-wide">
                  Sempre que a task for criada ou atualizada, verifica se ela atende à busca configurada neste roteamento e, caso atenda, executa o fluxo.
                </p>
              </>
            ) : (
              <div className="start-periodicity modal-field">
                <FieldLabel>Periodicidade</FieldLabel>
                <div>
                  <select value={String(startConfig.scheduleEvery || 10)} onChange={(event) => setStartConfig({ scheduleEvery: Number(event.target.value) })} disabled={!canEdit}>
                    {scheduleNumberOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                  <select value={startConfig.scheduleUnit || 'minute'} onChange={(event) => setStartConfig({ scheduleUnit: event.target.value })} disabled={!canEdit}>
                    {scheduleUnitOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                </div>
              </div>
            )}
            <section className="modal-field modal-field-wide">
              <FieldLabel>Atributo array por item (opcional)</FieldLabel>
              <ContextVariablePathSelector
                variables={startTaskArrayVariables}
                binding={{
                  path: startConfig.taskArrayPath || '',
                  arrayMatchByPath: {},
                  arrayNavigationByPath: {}
                }}
                canEdit={canEdit}
                emptyLabel="Sem atributos de lista disponíveis"
                emptyPathLabel="Nenhum selecionado"
                allowEmptyPath
                containerClassName="condition-variable-selectors"
                controlClassName="condition-variable-selector-control"
                onChange={(binding) => setStartConfig({ taskArrayPath: binding.path || '' })}
              />
              <p className="start-execution-notice">Se informado, cada item do array será executado como um run independente.</p>
            </section>
            <StartFilterBuilder
              config={startConfig}
              demand={selectedDemand}
              taskExtraVariables={taskExtraVariables}
              canEdit={canEdit}
              onChange={setStartConfig}
            />
          </>
        )}
      </>
    ),
    api: (
      <>
        <Field label="Integração" value={draft.config.integrationId} onChange={selectIntegration} selectOptions={testedApiOptions(availableTestedApis)} />
        <Field label="Timeout (ms)" type="number" value={draft.config.timeoutMs} onChange={(value) => setConfig('timeoutMs', value)} />
        <Field label="Tentativas" type="number" value={draft.config.retryCount} onChange={(value) => setConfig('retryCount', value)} />
        <Field label="Intervalo entre tentativas" value={draft.config.retryBackoff} onChange={(value) => setConfig('retryBackoff', value)} selectOptions={retryBackoffOptions} />
        <Field label="Chave única" value={draft.config.idempotencyKey} onChange={(value) => setConfig('idempotencyKey', value)} />
        <Field label="Caminho da resposta" value={draft.config.responsePath} onChange={(value) => setConfig('responsePath', value)} />
        <ApiVariableBindings
          integration={selectedIntegration}
          config={draft.config}
          demand={selectedDemand}
          nodeId={node.id}
          nodes={flowNodes}
          edges={flowEdges}
          apis={testedApis}
          runtimeOutputs={runtimeOutputs}
          taskExtraVariables={taskExtraVariables}
          canEdit={canEdit}
          onChange={setApiConfig}
        />
        <ApiIntegrationSummary
          integration={selectedIntegration}
          runtimeOutput={runtimeOutputs?.[String(node.id)]}
          collapsible
          defaultExpanded={false}
        />
      </>
    ),
    chat: (
      <>
        <Field
          label="Plataforma"
          value={chatConfig.platform}
          onChange={selectChatPlatform}
          selectOptions={chatPlatformOptions}
        />
        <label className="modal-field">
          <div className="chat-template-field-head">
            <FieldLabel>Template</FieldLabel>
            <div className="chat-template-field-actions">
              <button
                className="secondary-button compact-action"
                type="button"
                onClick={() => openExternalUrlInNewTab(chatTemplateEditorUrl)}
                disabled={!chatTemplateEditorUrl}
                title={chatTemplateEditorUrl ? 'Abrir template na plataforma de Chat' : 'Selecione um template para habilitar'}
              >
                <span className="material-symbols-rounded" aria-hidden="true">open_in_new</span>
                Acessar Template
              </button>
              <button
                className="secondary-button compact-action"
                type="button"
                onClick={() => openExternalUrlInNewTab(chatChatbotEditorUrl)}
                disabled={!chatChatbotEditorUrl}
                title={chatChatbotEditorUrl ? 'Abrir chatbot na plataforma de Chat' : 'Template sem chatbot_id disponível'}
              >
                <span className="material-symbols-rounded" aria-hidden="true">smart_toy</span>
                Acessar Chatbot
              </button>
            </div>
          </div>
          <select
            value={chatConfig.templateId}
            onChange={(event) => selectChatTemplate(event.target.value)}
            disabled={!chatConfig.platform || chatTemplatesLoading}
          >
            {chatTemplateOptions.map((option) => {
              const normalized = typeof option === 'string' ? { value: option, label: option } : option;
              return (
                <option key={normalized.value} value={normalized.value} disabled={Boolean(normalized.disabled)}>
                  {normalized.label}
                </option>
              );
            })}
          </select>
        </label>
        <Field
          label="Timeout (ms)"
          type="number"
          value={chatConfig.timeoutMs}
          onChange={(value) => setChatConfig({ ...chatConfig, timeoutMs: value })}
        />
        <section className="api-integration-summary modal-field-wide">
          <header>
            <div>
              <span>Template selecionado</span>
              <strong>{chatConfig.templateName || 'Nenhum template selecionado'}</strong>
            </div>
            <small>{chatConfig.templateOrigin ? `Origem: ${chatConfig.templateOrigin}` : 'Origem não informada'}</small>
          </header>
          <div className="api-integration-meta">
            <span>{chatConfig.platform || 'Sem plataforma'}</span>
            <span>{`${chatConfig.paramsCount || 0} parâmetro(s)`}</span>
          </div>
          <div className="chat-template-preview">
            {hasChatTemplatePreview ? (
              <article className="chat-template-preview-device" aria-label="Preview da mensagem de chat">
                <header className="chat-template-preview-head">
                  <span className="chat-template-preview-status" aria-hidden="true" />
                  <strong>Message Preview</strong>
                </header>
                <div className="chat-template-preview-stage">
                  <div className="chat-template-preview-bubble">
                    {chatTemplatePreview.header ? <strong className="chat-template-preview-title">{chatTemplatePreview.header}</strong> : null}
                    {chatTemplatePreview.body ? <p className="chat-template-preview-body">{chatTemplatePreview.body}</p> : null}
                    {chatTemplatePreview.footer ? <small className="chat-template-preview-footer">{chatTemplatePreview.footer}</small> : null}
                    <span className="chat-template-preview-time">{chatTemplatePreviewTime}</span>
                  </div>
                </div>
              </article>
            ) : (
              <p className="chat-template-preview-empty">Sem preview informado.</p>
            )}
          </div>
        </section>
        <section className="modal-field modal-field-wide">
          <FieldLabel>Destino (to) *</FieldLabel>
          <div className="api-variable-table" role="table" aria-label="Destino do comando de chat">
            <div className="api-variable-row is-head" role="row">
              <span>Origem</span>
              <span>Valor</span>
            </div>
            {(() => {
              const toBinding = normalizeChatVoiceBinding(chatConfig.toBinding || chatConfig.toAttribute, chatContextSelectorVariables, chatBindingContext.sources);
              const sourceValue = chatVoiceBindingSourceValue(toBinding);
              const source = toBinding.sourceType === 'api'
                ? chatBindingContext.sources.find((item) => String(item.nodeId) === String(toBinding.sourceNodeId))
                : null;
              const variableOptions = toBinding.sourceType === 'api'
                ? (source?.variables || [])
                : chatContextSelectorVariables;
              return (
                <div className="api-variable-row" role="row">
                  <div className="api-variable-source-cell">
                    <select
                      value={sourceValue}
                      onChange={(event) => setChatToBinding(nextChatVoiceBindingForSource({
                        sourceValue: event.target.value,
                        previousBinding: toBinding,
                        taskOptions: chatContextSelectorVariables,
                        sources: chatBindingContext.sources
                      }))}
                      disabled={!canEdit}
                    >
                      {chatBindingSourceOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  {toBinding.sourceType === 'literal' ? (
                    <TemplateVariableSuggestInput
                      value={toBinding.literalValue ?? toBinding.literal_value ?? ''}
                      onChange={(nextValue) => setChatToBinding({ sourceType: 'literal', literalValue: nextValue })}
                      variables={chatContextSelectorVariables}
                      suggestionSources={chatLiteralSuggestionSources}
                      canEdit={canEdit}
                      ariaLabel="Texto personalizado do destino do chat"
                    />
                  ) : (
                    <ContextVariablePathSelector
                      variables={variableOptions}
                      binding={{ path: toBinding.path, arrayMatchByPath: toBinding.arrayMatchByPath }}
                      canEdit={canEdit}
                      emptyLabel="Sem atributos disponíveis"
                      onChange={(binding) => setChatToBinding({
                        ...toBinding,
                        path: binding.path || '',
                        arrayMatchByPath: normalizeConditionArrayMatches(binding.arrayMatchByPath)
                      })}
                    />
                  )}
                </div>
              );
            })()}
          </div>
        </section>
        {chatTemplateSupportsAttachment ? (
          <section className="modal-field modal-field-wide">
            <FieldLabel>Attachment</FieldLabel>
            <select
              value={normalizeCortexOcrFileBindingPath(String(chatConfig.attachmentAttribute || chatConfig.attachmentBinding?.path || '').trim())}
              onChange={(event) => setChatAttachmentBinding({
                sourceType: 'task',
                path: String(event.target.value || '').trim()
              })}
              disabled={!canEdit}
            >
              <option value="">Nenhum (usar midia padrao do template)</option>
              {(() => {
                const options = Array.isArray(chatTaskFileSelectorVariables) ? chatTaskFileSelectorVariables : [];
                const selectedPath = normalizeCortexOcrFileBindingPath(String(chatConfig.attachmentAttribute || chatConfig.attachmentBinding?.path || '').trim());
                const hasSelected = selectedPath && options.some((option) => String(option?.value || '').trim() === selectedPath);
                const merged = hasSelected
                  ? options
                  : (selectedPath ? [...options, { value: selectedPath }] : options);
                return merged.map((option) => {
                  const path = String(option?.value || '').trim();
                  if (!path) return null;
                  return (
                    <option key={`chat-attachment-${path}`} value={path}>{String(option?.label || `${path} (${schemaTypeLabel('file')})`)}</option>
                  );
                });
              })()}
            </select>
          </section>
        ) : null}
        <section className="api-integration-summary modal-field-wide">
          <header>
            <div>
              <span>Preview do envio</span>
              <strong>Context (info_custom)</strong>
            </div>
            <div className="api-integration-summary-actions">
              <button
                className="secondary-button compact-action api-integration-collapse-btn"
                type="button"
                aria-expanded={chatInfoCustomPreviewExpanded}
                aria-label={chatInfoCustomPreviewExpanded ? 'Recolher preview do contexto' : 'Expandir preview do contexto'}
                onClick={() => setChatInfoCustomPreviewExpanded((current) => !current)}
              >
                <span className="material-symbols-rounded" aria-hidden="true">{chatInfoCustomPreviewExpanded ? 'expand_less' : 'expand_more'}</span>
                {chatInfoCustomPreviewExpanded ? 'Recolher' : 'Expandir'}
              </button>
            </div>
          </header>
          {chatInfoCustomPreviewExpanded && (
            <article className="inspection-json-card">
              <header>
                <span className="material-symbols-rounded" aria-hidden="true">data_object</span>
                <strong>Context (info_custom)</strong>
              </header>
              <pre>
                <code>{chatInfoCustomPreviewJson}</code>
              </pre>
            </article>
          )}
        </section>
        {chatConfig.paramsCount > 0 ? (
          <section className="api-variable-bindings modal-field-wide">
            <header>
              <div>
                <FieldLabel>Parâmetros do template</FieldLabel>
                <strong>{`${chatConfig.paramsCount} parâmetro(s) esperado(s)`}</strong>
              </div>
            </header>
            <div className="api-variable-table" role="table" aria-label="Parâmetros do template de chat">
              <div className="api-variable-row is-head" role="row">
                <span>Parâmetro</span>
                <span>Origem</span>
                <span>Valor</span>
              </div>
              {Array.from({ length: chatConfig.paramsCount }, (_, index) => {
                const parameterIndex = index + 1;
                const binding = normalizeChatVoiceBinding(
                  chatConfig.paramBindingDetails?.[String(parameterIndex)]
                    ?? chatConfig.paramBindings?.[String(parameterIndex)],
                  chatContextSelectorVariables,
                  chatBindingContext.sources
                );
                const sourceValue = chatVoiceBindingSourceValue(binding);
                const source = binding.sourceType === 'api'
                  ? chatBindingContext.sources.find((item) => String(item.nodeId) === String(binding.sourceNodeId))
                  : null;
                const variableOptions = binding.sourceType === 'api'
                  ? (source?.variables || [])
                  : chatContextSelectorVariables;
                return (
                  <div className="api-variable-row" role="row" key={`chat-param-${parameterIndex}`}>
                    <code>{`{{${parameterIndex}}}`}</code>
                    <div className="api-variable-source-cell">
                      <select
                        value={sourceValue}
                        onChange={(event) => setChatParamBinding(parameterIndex, nextChatVoiceBindingForSource({
                          sourceValue: event.target.value,
                          previousBinding: binding,
                          taskOptions: chatContextSelectorVariables,
                          sources: chatBindingContext.sources
                        }))}
                        disabled={!canEdit}
                      >
                        {chatBindingSourceOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                    {binding.sourceType === 'literal' ? (
                      <TemplateVariableSuggestInput
                        value={binding.literalValue ?? binding.literal_value ?? ''}
                        onChange={(nextValue) => setChatParamBinding(parameterIndex, { sourceType: 'literal', literalValue: nextValue })}
                        variables={chatContextSelectorVariables}
                        suggestionSources={chatLiteralSuggestionSources}
                        canEdit={canEdit}
                        ariaLabel={`Texto personalizado do parâmetro ${parameterIndex} do chat`}
                      />
                    ) : (
                      <ContextVariablePathSelector
                        variables={variableOptions}
                        binding={{ path: binding.path, arrayMatchByPath: binding.arrayMatchByPath }}
                        canEdit={canEdit}
                        emptyLabel="Sem atributos disponíveis"
                        onChange={(nextBinding) => setChatParamBinding(parameterIndex, {
                          ...binding,
                          path: nextBinding.path || '',
                          arrayMatchByPath: normalizeConditionArrayMatches(nextBinding.arrayMatchByPath)
                        })}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ) : null}
        <section className="api-variable-bindings modal-field-wide">
          <header>
            <div>
              <FieldLabel>Contexto (info_custom)</FieldLabel>
              <strong>{`${chatConfig.infoCustomMappings?.length || 0} origem(ns) configurada(s)`}</strong>
              <p>Cada linha adiciona dados em <code>info_custom</code>. Se o atributo ficar vazio, todos os atributos da origem serão enviados.</p>
            </div>
          </header>
          <div className="api-variable-table" role="table" aria-label="Contexto customizado do Chat">
            <div className="api-variable-row api-variable-row-chat-context is-head" role="row">
              <span>Como utilizar</span>
              <span>Origem</span>
              <span>Atributo (opcional)</span>
              <span />
            </div>
            {(Array.isArray(chatConfig.infoCustomMappings) ? chatConfig.infoCustomMappings : []).map((mapping) => {
              const sourceType = String(mapping?.sourceType || '').trim().toLowerCase();
              const sourceNodeId = String(mapping?.sourceNodeId || '').trim();
              const sourceValue = sourceType === 'api' && sourceNodeId
                ? `api:${sourceNodeId}`
                : (['task', 'demand', 'date'].includes(sourceType) ? sourceType : 'task');
              const source = chatBindingContext.sources.find((item) => String(item.nodeId) === sourceNodeId);
              const variableOptions = sourceType === 'api'
                ? (source?.variables || [])
                : sourceType === 'demand'
                  ? demandTemplateVariableOptions()
                  : sourceType === 'date'
                    ? DATE_TEMPLATE_VARIABLE_OPTIONS
                    : chatInfoCustomTaskVariables;
              const hasVariables = variableOptions.length > 0;
              return (
                <div className="api-variable-row api-variable-row-chat-context" role="row" key={mapping.id}>
                  <div className="api-variable-source-cell">
                    <input
                      value={String(mapping?.useAs || '').trim()}
                      onChange={(event) => updateChatInfoCustomMapping(mapping.id, { useAs: event.target.value })}
                      disabled={!canEdit}
                      placeholder="Automático pelo atributo"
                      aria-label="Utilizar como"
                    />
                  </div>
                  <div className="api-variable-source-cell">
                    <select
                      value={sourceValue}
                      onChange={(event) => updateChatInfoCustomMapping(mapping.id, { originValue: event.target.value })}
                      disabled={!canEdit}
                    >
                      {chatInfoCustomOriginOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="api-variable-source-cell">
                    {hasVariables ? (
                      <ContextVariablePathSelector
                        variables={variableOptions}
                        binding={{
                          path: String(mapping?.path || '').trim(),
                          arrayMatchByPath: mapping?.arrayMatchByPath || {},
                          arrayNavigationByPath: mapping?.arrayNavigationByPath || {}
                        }}
                        canEdit={canEdit}
                        emptyLabel="Sem atributos disponíveis"
                        emptyPathLabel="Todos os atributos da origem"
                        allowEmptyPath={sourceType !== 'date'}
                        onChange={(binding) => updateChatInfoCustomMapping(mapping.id, {
                          path: binding.path || '',
                          arrayMatchByPath: normalizeConditionArrayMatches(binding.arrayMatchByPath),
                          arrayNavigationByPath: normalizeConditionArrayNavigations(
                            binding.arrayNavigationByPath,
                            binding.arrayMatchByPath
                          )
                        })}
                      />
                    ) : (
                      <select value="" disabled>
                        <option>Sem atributos disponíveis</option>
                      </select>
                    )}
                  </div>
                  <div className="api-variable-source-cell">
                    <button
                      className="icon-button"
                      type="button"
                      onClick={() => removeChatInfoCustomMapping(mapping.id)}
                      disabled={!canEdit}
                      aria-label="Remover origem de contexto"
                    >
                      <span className="material-symbols-rounded" aria-hidden="true">delete</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          <button className="secondary-button compact-action" type="button" onClick={addChatInfoCustomMapping} disabled={!canEdit}>
            <span className="material-symbols-rounded" aria-hidden="true">add</span>
            Adicionar contexto
          </button>
        </section>
        <section className="api-integration-summary modal-field-wide">
          <header>
            <div>
              <span>Preview do envio</span>
              <strong>Context (info_custom)</strong>
            </div>
            <div className="api-integration-summary-actions">
              <button
                className="secondary-button compact-action api-integration-collapse-btn"
                type="button"
                aria-expanded={chatInfoCustomPreviewExpanded}
                aria-label={chatInfoCustomPreviewExpanded ? 'Recolher preview do contexto' : 'Expandir preview do contexto'}
                onClick={() => setChatInfoCustomPreviewExpanded((current) => !current)}
              >
                <span className="material-symbols-rounded" aria-hidden="true">{chatInfoCustomPreviewExpanded ? 'expand_less' : 'expand_more'}</span>
                {chatInfoCustomPreviewExpanded ? 'Recolher' : 'Expandir'}
              </button>
            </div>
          </header>
          {chatInfoCustomPreviewExpanded && (
            <article className="inspection-json-card">
              <header>
                <span className="material-symbols-rounded" aria-hidden="true">data_object</span>
                <strong>Context (info_custom)</strong>
              </header>
              <pre>
                <code>{chatInfoCustomPreviewJson}</code>
              </pre>
            </article>
          )}
        </section>
      </>
    ),
    voice: (
      <>
        <label className="modal-field">
          <div className="chat-template-field-head">
            <FieldLabel>Campanha</FieldLabel>
            <div className="chat-template-field-actions">
              <button
                className="secondary-button compact-action"
                type="button"
                onClick={() => openExternalUrlInNewTab(voiceCampaignEditorUrl)}
                disabled={!voiceCampaignEditorUrl}
                title={voiceCampaignEditorUrl ? 'Abrir campanha na plataforma de Voz' : 'Selecione uma campanha para habilitar'}
              >
                <span className="material-symbols-rounded" aria-hidden="true">open_in_new</span>
                Acessar Campanha
              </button>
            </div>
          </div>
          <select
            value={voiceConfig.campaignId}
            onChange={(event) => selectVoiceTemplate(event.target.value)}
            disabled={voiceTemplatesLoading}
          >
            {voiceTemplateOptions.map((option) => {
              const normalized = typeof option === 'string' ? { value: option, label: option } : option;
              return (
                <option key={normalized.value} value={normalized.value} disabled={Boolean(normalized.disabled)}>
                  {normalized.label}
                </option>
              );
            })}
          </select>
        </label>
        <Field
          label="Timeout (ms)"
          type="number"
          value={voiceConfig.timeoutMs}
          onChange={(value) => setVoiceConfig({ ...voiceConfig, timeoutMs: value })}
        />
        <section className="api-integration-summary modal-field-wide">
          <header>
            <div>
              <span>Campanha selecionada</span>
              <strong>{voiceConfig.campaignName || 'Nenhuma campanha selecionada'}</strong>
            </div>
          </header>
          <div className="api-integration-meta">
            <span>{`${voiceConfig.paramsCount || 0} parâmetro(s)`}</span>
          </div>
        </section>
        {voiceConfig.paramsCount > 0 ? (
          <section className="auto-command-identity modal-field-wide">
            <span>Parâmetro reservado</span>
            <p>O parâmetro {'{{1}}'} é preenchido automaticamente com o <code>task_key</code> da Task.</p>
          </section>
        ) : null}
        <section className="modal-field modal-field-wide">
          <FieldLabel>Destino da ligação (to) *</FieldLabel>
          <div className="api-variable-table" role="table" aria-label="Destino do comando de voz">
            <div className="api-variable-row is-head" role="row">
              <span>Origem</span>
              <span>Valor</span>
            </div>
            {(() => {
              const toBinding = normalizeChatVoiceBinding(voiceConfig.toBinding || voiceConfig.toAttribute, voiceTaskSelectorVariables, chatBindingContext.sources);
              const sourceValue = chatVoiceBindingSourceValue(toBinding);
              const source = toBinding.sourceType === 'api'
                ? chatBindingContext.sources.find((item) => String(item.nodeId) === String(toBinding.sourceNodeId))
                : null;
              const variableOptions = toBinding.sourceType === 'api'
                ? (source?.variables || [])
                : voiceTaskSelectorVariables;
              return (
                <div className="api-variable-row" role="row">
                  <div className="api-variable-source-cell">
                    <select
                      value={sourceValue}
                      onChange={(event) => setVoiceToBinding(nextChatVoiceBindingForSource({
                        sourceValue: event.target.value,
                        previousBinding: toBinding,
                        taskOptions: voiceTaskSelectorVariables,
                        sources: chatBindingContext.sources
                      }))}
                      disabled={!canEdit}
                    >
                      {chatBindingSourceOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  {toBinding.sourceType === 'literal' ? (
                    <TemplateVariableSuggestInput
                      value={toBinding.literalValue ?? toBinding.literal_value ?? ''}
                      onChange={(nextValue) => setVoiceToBinding({ sourceType: 'literal', literalValue: nextValue })}
                      variables={voiceTaskSelectorVariables}
                      suggestionSources={chatLiteralSuggestionSources}
                      canEdit={canEdit}
                      ariaLabel="Texto personalizado do destino da voz"
                    />
                  ) : (
                    <ContextVariablePathSelector
                      variables={variableOptions}
                      binding={{ path: toBinding.path, arrayMatchByPath: toBinding.arrayMatchByPath }}
                      canEdit={canEdit}
                      emptyLabel="Sem atributos disponíveis"
                      onChange={(binding) => setVoiceToBinding({
                        ...toBinding,
                        path: binding.path || '',
                        arrayMatchByPath: normalizeConditionArrayMatches(binding.arrayMatchByPath)
                      })}
                    />
                  )}
                </div>
              );
            })()}
          </div>
          <small>Use um campo contendo telefone completo (ex.: 5511999999999).</small>
        </section>
        <section className="modal-field modal-field-wide">
          <FieldLabel>Agendar ligação - opcional</FieldLabel>
          <div className="api-variable-table" role="table" aria-label="Agendamento da ligação de voz">
            <div className="api-variable-row is-head" role="row">
              <span>Origem</span>
              <span>Valor</span>
            </div>
            <div className="api-variable-row" role="row">
              <div className="api-variable-source-cell">
                <select value={voiceDialAtSource} onChange={(event) => setVoiceDialAtSource(event.target.value)} disabled={!canEdit}>
                  {VOICE_DIAL_AT_SOURCE_OPTIONS.map((option) => (
                    <option key={option.value || '__empty__'} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              {voiceDialAtSource === 'task' ? (
                <ContextVariablePathSelector
                  variables={voiceTaskSelectorVariables}
                  binding={{ path: voiceDialAtBinding.path, arrayMatchByPath: {}, arrayNavigationByPath: {} }}
                  canEdit={canEdit}
                  emptyLabel="Sem atributos disponíveis"
                  onChange={setVoiceDialAtTaskBinding}
                />
              ) : voiceDialAtSource === 'date' ? (
                <div className="api-variable-date-cell">
                  <RelativeDateTrigger
                    config={voiceDialAtDateConfig}
                    onConfigure={() => openVoiceDialAtDateModal(voiceDialAtDateConfig)}
                    disabled={!canEdit}
                  />
                </div>
              ) : (
                <span className="api-variable-static-info">Sem agendamento</span>
              )}
            </div>
          </div>
        </section>
        {voiceConfig.paramsCount > 1 ? (
          <section className="api-variable-bindings modal-field-wide">
            <header>
              <div>
                <FieldLabel>Parâmetros da campanha</FieldLabel>
                <strong>{`${Math.max((voiceConfig.paramsCount || 0) - 1, 0)} parâmetro(s) para mapear`}</strong>
              </div>
            </header>
            <div className="api-variable-table" role="table" aria-label="Parâmetros da campanha de voz">
              <div className="api-variable-row is-head" role="row">
                <span>Parâmetro</span>
                <span>Origem</span>
                <span>Valor</span>
              </div>
              {Array.from({ length: Math.max((voiceConfig.paramsCount || 0) - 1, 0) }, (_, index) => {
                const parameterIndex = index + 1;
                const campaignParamIndex = parameterIndex + 1;
                const binding = normalizeChatVoiceBinding(
                  voiceConfig.paramBindingDetails?.[String(parameterIndex)]
                    ?? voiceConfig.paramBindings?.[String(parameterIndex)],
                  voiceTaskSelectorVariables,
                  chatBindingContext.sources
                );
                const sourceValue = chatVoiceBindingSourceValue(binding);
                const source = binding.sourceType === 'api'
                  ? chatBindingContext.sources.find((item) => String(item.nodeId) === String(binding.sourceNodeId))
                  : null;
                const variableOptions = binding.sourceType === 'api'
                  ? (source?.variables || [])
                  : voiceTaskSelectorVariables;
                return (
                  <div className="api-variable-row" role="row" key={`voice-param-${parameterIndex}`}>
                    <code>{`{{${campaignParamIndex}}}`}</code>
                    <div className="api-variable-source-cell">
                      <select
                        value={sourceValue}
                        onChange={(event) => setVoiceParamBinding(parameterIndex, nextChatVoiceBindingForSource({
                          sourceValue: event.target.value,
                          previousBinding: binding,
                          taskOptions: voiceTaskSelectorVariables,
                          sources: chatBindingContext.sources
                        }))}
                        disabled={!canEdit}
                      >
                        {chatBindingSourceOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                    {binding.sourceType === 'literal' ? (
                      <TemplateVariableSuggestInput
                        value={binding.literalValue ?? binding.literal_value ?? ''}
                        onChange={(nextValue) => setVoiceParamBinding(parameterIndex, { sourceType: 'literal', literalValue: nextValue })}
                        variables={voiceTaskSelectorVariables}
                        suggestionSources={chatLiteralSuggestionSources}
                        canEdit={canEdit}
                        ariaLabel={`Texto personalizado do parâmetro ${campaignParamIndex} da voz`}
                      />
                    ) : (
                      <ContextVariablePathSelector
                        variables={variableOptions}
                        binding={{ path: binding.path, arrayMatchByPath: binding.arrayMatchByPath }}
                        canEdit={canEdit}
                        emptyLabel="Sem atributos disponíveis"
                        onChange={(nextBinding) => setVoiceParamBinding(parameterIndex, {
                          ...binding,
                          path: nextBinding.path || '',
                          arrayMatchByPath: normalizeConditionArrayMatches(nextBinding.arrayMatchByPath)
                        })}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ) : null}
        {voiceDialAtDateModalOpen ? (
          <div className="flow-modal-backdrop" role="presentation" onClick={closeVoiceDialAtDateModal}>
            <section
              className="flow-modal api-date-binding-modal"
              role="dialog"
              aria-modal="true"
              aria-label="Configurar agendamento da ligação"
              onClick={(event) => event.stopPropagation()}
            >
              <header className="flow-modal-head">
                <div className="flow-modal-icon tone-teal">
                  <span className="material-symbols-rounded" aria-hidden="true">schedule</span>
                </div>
                <div>
                  <span>Data relativa</span>
                  <h3>Configurar agendamento da ligação</h3>
                  <p>Defina o deslocamento em minutos, horas ou dias para calcular o horário do dial.</p>
                </div>
                <button className="icon-button" type="button" onClick={closeVoiceDialAtDateModal} aria-label="Fechar modal">
                  <span className="material-symbols-rounded" aria-hidden="true">close</span>
                </button>
              </header>

              <div className="flow-modal-body api-date-binding-body">
                <div className="api-date-binding-grid">
                  <label className="modal-field">
                    <FieldLabel>Quantidade</FieldLabel>
                    <input
                      type="number"
                      min={0}
                      max={525600}
                      step={1}
                      value={String(voiceDialAtDateDraft.amount ?? '')}
                      onChange={(event) => setVoiceDialAtDateDraft((current) => ({ ...current, amount: event.target.value }))}
                    />
                  </label>
                  <label className="modal-field">
                    <FieldLabel>Unidade</FieldLabel>
                    <select
                      value={voiceDialAtDraftConfig.unit}
                      onChange={(event) => setVoiceDialAtDateDraft((current) => ({ ...current, unit: event.target.value }))}
                    >
                      {API_DATE_UNIT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="modal-field">
                    <FieldLabel>Direção</FieldLabel>
                    <select
                      value={voiceDialAtDraftConfig.direction}
                      onChange={(event) => setVoiceDialAtDateDraft((current) => ({ ...current, direction: event.target.value }))}
                    >
                      {API_DATE_DIRECTION_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                </div>

                <section className="api-date-binding-preview">
                  <span>Exemplo com hora atual</span>
                  <div>
                    <p>{`Agora: ${formatApiDatePreview(voiceDialAtDatePreviewNow)} (${voiceDialAtDatePreviewNow.toISOString()})`}</p>
                    <p>{`Resultado (${apiDateBindingSummary(voiceDialAtDraftConfig)}): ${formatApiDatePreview(voiceDialAtDraftPreview.date)} (${voiceDialAtDraftPreview.iso})`}</p>
                  </div>
                </section>
              </div>

              <footer className="flow-modal-footer">
                <button className="secondary-button" type="button" onClick={closeVoiceDialAtDateModal}>Cancelar</button>
                <button
                  className="primary-button"
                  type="button"
                  onClick={applyVoiceDialAtDateConfig}
                >
                  Aplicar data
                </button>
              </footer>
            </section>
          </div>
        ) : null}
      </>
    ),
    cortex: (
      <>
        <Field
          label="Recurso"
          value={cortexConfig.resource}
          onChange={(value) => {
            const currentTimeout = Number(cortexConfig.timeoutMs);
            const currentDefaultTimeout = cortexDefaultTimeoutMsByResource(cortexConfig.resource);
            const shouldApplyResourceDefault = Number.isFinite(currentTimeout) && currentTimeout === currentDefaultTimeout;
            const nextResourceTimeout = shouldApplyResourceDefault
              ? cortexDefaultTimeoutMsByResource(value)
              : cortexConfig.timeoutMs;
            setCortexConfig({
              ...cortexConfig,
              resource: value,
              resourceCod: '',
              resourceResponseType: '',
              timeoutMs: nextResourceTimeout
            });
          }}
          selectOptions={cortexResourceOptions}
        />
        {cortexResourceSupportsList(cortexConfig.resource) && (
          <>
            <Field
              label="Recurso listado"
              value={cortexConfig.resourceCod || ''}
              onChange={(value) => {
                const selectedOption = cortexResourceOptionsState.find((item) => item.cod === String(value || '').trim()) || null;
                setCortexConfig({
                  ...cortexConfig,
                  resourceCod: value,
                  resourceResponseType: String(selectedOption?.responseType || '').trim().toUpperCase()
                });
              }}
              selectOptions={cortexResourceCodSelectOptions}
              disabled={cortexResourceOptionsLoading}
            />
            {cortexResourceOptionsError && (
              <section className="auto-command-identity modal-field-wide">
                <span>Falha ao carregar listagem</span>
                <p>{cortexResourceOptionsError}</p>
              </section>
            )}
          </>
        )}
        <section className="modal-field modal-field-wide">
          <FieldLabel>Follow-up ID *</FieldLabel>
          <ContextVariablePathSelector
            variables={cortexTaskSelectorVariables}
            binding={cortexConfig.followUpBinding || { path: cortexConfig.followUpAttribute || '', arrayMatchByPath: {} }}
            canEdit={canEdit}
            emptyLabel="Sem atributos disponíveis"
            onChange={setCortexFollowUpBinding}
          />
        </section>
        {cortexPayloadFields.map((field) => (
          CORTEX_TEMPLATE_TEXT_FIELDS.has(field.key)
            ? (
              <Fragment key={field.key}>
                <section className="modal-field modal-field-wide">
                  <div className="cortex-template-field-head">
                    <FieldLabel
                      help={{
                        title: field.label,
                        description: `Define o conteúdo enviado em “${field.label}” para o recurso Cortex selecionado. O valor é montado no momento em que a Task passa por este comando.`,
                        usage: 'Digite um texto fixo ou use o botão {} para inserir variáveis da Task, da Demanda ou do contexto disponível no fluxo.'
                      }}
                    >
                      {`${field.label}${field.required ? ' *' : ''}`}
                    </FieldLabel>
                    <button
                      className="icon-button cortex-template-variable-button"
                      type="button"
                      onClick={() => openCortexTemplateAttributePicker(field)}
                      disabled={!canEdit}
                      title={`Inserir variavel da Task ou Demanda em ${field.label.toLowerCase()} (ex.: {{task.payload.campo}} ou {{demand.name}}) na posicao do cursor`}
                      aria-label={`Inserir variavel da Task ou Demanda em ${field.label.toLowerCase()}`}
                    >
                      {'{}'}
                    </button>
                  </div>
                  <textarea
                    ref={(element) => {
                      if (element) {
                        cortexTemplateTextareaRefs.current[field.key] = element;
                      } else {
                        delete cortexTemplateTextareaRefs.current[field.key];
                      }
                    }}
                    value={cortexTemplateValueByField(field.key)}
                    onChange={(event) => {
                      setCortexTemplateValue(field.key, event.target.value);
                      updateCortexTemplateSelection(field.key, event.target);
                    }}
                    onKeyDown={(event) => onCortexTemplateKeyDown(event, field.key, field.label)}
                    onClick={(event) => updateCortexTemplateSelection(field.key, event.target)}
                    onKeyUp={(event) => updateCortexTemplateSelection(field.key, event.target)}
                    onSelect={(event) => updateCortexTemplateSelection(field.key, event.target)}
                    rows={6}
                    placeholder={`Digite ${field.label.toLowerCase()} e use o botão para inserir variáveis da Task ou Demanda`}
                    disabled={!canEdit}
                  />
                </section>
                {field.key === 'message' && cortexIsTagentGeneric && renderCortexGenericTemplateField(
                  'prompt',
                  'Prompt',
                  'Este campo define o prompt da IA para o Agente Cortex quando o response_type e GENERIC.',
                  'Escreva as instrucoes da IA. Use o botao {} para inserir variaveis da Task ou da Demanda no texto.',
                  'Digite o prompt da IA'
                )}
                {field.key === 'message' && cortexIsTagentGeneric && renderCortexGenericTemplateField(
                  'response_format',
                  'Formato do Retorno',
                  'Este campo define como o formato do retorno e esperado em JSON quando o response_type e GENERIC.',
                  'Informe a estrutura esperada no retorno. Use o botao {} para inserir variaveis no texto quando necessario.',
                  'Descreva o formato do retorno esperado em JSON'
                )}
              </Fragment>
            )
            : (cortexConfig.resource === 'tagent' && field.key === 'options')
              ? (
                <section className="save-info-builder modal-field-wide" key={field.key}>
                  <header className="save-info-head">
                    <div>
                      <span>{field.label}</span>
                      <strong>{`${(Array.isArray(cortexConfig.tagentOptions) ? cortexConfig.tagentOptions.length : 0)} opção(ões) configurada(s)`}</strong>
                      <p>Adicione as opções string enviadas no payload do Agente IA.</p>
                    </div>
                  </header>
                    <div className="save-info-table" role="table" aria-label="Opções do Agente IA">
                      <div className="save-info-row save-info-row-2 is-head" role="row">
                        <span>Opção</span>
                        <span></span>
                      </div>
                    {(Array.isArray(cortexConfig.tagentOptions) ? cortexConfig.tagentOptions : []).map((optionItem, optionIndex) => (
                      <div className="save-info-row save-info-row-2" role="row" key={`${field.key}-${optionIndex}`}>
                        <input
                          value={optionItem || ''}
                          onChange={(event) => updateCortexTagentOption(optionIndex, event.target.value)}
                          placeholder="Ex: financeiro"
                          disabled={!canEdit}
                        />
                        <button
                          className="icon-button"
                          type="button"
                          onClick={() => removeCortexTagentOption(optionIndex)}
                          disabled={!canEdit}
                          aria-label="Remover opção"
                        >
                          <span className="material-symbols-rounded" aria-hidden="true">delete</span>
                        </button>
                      </div>
                    ))}
                  </div>
                  <button className="secondary-button compact-action" type="button" onClick={addCortexTagentOption} disabled={!canEdit}>
                    <span className="material-symbols-rounded" aria-hidden="true">add</span>
                    Adicionar opção
                  </button>
                </section>
              )
              : (cortexConfig.resource === 'ocr' && field.key === 'options')
              ? (
                <section className="save-info-builder modal-field-wide" key={field.key}>
                  <header className="save-info-head">
                    <div>
                      <span>{field.label}</span>
                      <strong>{`${(Array.isArray(cortexConfig.ocrOptions) ? cortexConfig.ocrOptions.length : 0)} opção(ões) configurada(s)`}</strong>
                      <p>Adicione o nome do documento e os dados esperados na extração OCR.</p>
                    </div>
                  </header>
                  <div className="save-info-table" role="table" aria-label="Opções OCR">
                    <div className="save-info-row save-info-row-3 is-head" role="row">
                      <span>Nome do documento</span>
                      <span>Extrair dados</span>
                      <span></span>
                    </div>
                    {(Array.isArray(cortexConfig.ocrOptions) ? cortexConfig.ocrOptions : []).map((optionItem, optionIndex) => (
                      <div className="save-info-row save-info-row-3" role="row" key={`${field.key}-${optionIndex}`}>
                        <input
                          value={optionItem?.documentName || ''}
                          onChange={(event) => updateCortexOcrOption(optionIndex, { documentName: event.target.value })}
                          placeholder="Ex: Contrato Cliente X"
                          disabled={!canEdit}
                        />
                        <input
                          value={optionItem?.extractionData || ''}
                          onChange={(event) => updateCortexOcrOption(optionIndex, { extractionData: event.target.value })}
                          placeholder="Ex: Nome e CPF"
                          disabled={!canEdit}
                        />
                        <button
                          className="icon-button"
                          type="button"
                          onClick={() => removeCortexOcrOption(optionIndex)}
                          disabled={!canEdit}
                          aria-label="Remover opção OCR"
                        >
                          <span className="material-symbols-rounded" aria-hidden="true">delete</span>
                        </button>
                      </div>
                    ))}
                  </div>
                  <button className="secondary-button compact-action" type="button" onClick={addCortexOcrOption} disabled={!canEdit}>
                    <span className="material-symbols-rounded" aria-hidden="true">add</span>
                    Adicionar opção
                  </button>
                </section>
              )
              : field.key === 'variables'
                ? (
                  <section className="save-info-builder modal-field-wide" key={field.key}>
                    <header className="save-info-head">
                      <div>
                        <span>{`${field.label}${field.required ? ' *' : ''}`}</span>
                        <strong>{`${cortexVariableBindings.length} variável(is) selecionada(s)`}</strong>
                        <p>Selecione atributos do contexto com navegação em níveis e controle de estratégia para listas.</p>
                      </div>
                    </header>
                    <div className="save-info-table" role="table" aria-label="Variáveis do Cortex">
                      <div className="save-info-row save-info-row-3 is-head" role="row">
                        <span>Valor do contexto</span>
                        <span>Descrição (opcional)</span>
                        <span></span>
                      </div>
                      {cortexVariableBindings.map((bindingItem, bindingIndex) => (
                        <div className="save-info-row save-info-row-3" role="row" key={`${field.key}-${bindingIndex}-${bindingItem.path}`}>
                          <ContextVariablePathSelector
                            variables={cortexTaskSelectorVariables}
                            binding={bindingItem}
                            canEdit={canEdit}
                            emptyLabel="Sem atributos disponíveis"
                            onChange={(value) => updateCortexVariableBinding(bindingIndex, value)}
                          />
                          <input
                            type="text"
                            value={bindingItem.description || ''}
                            onChange={(event) => updateCortexVariableDescription(bindingIndex, event.target.value)}
                            placeholder="Ex: Nome completo do cliente"
                            disabled={!canEdit}
                          />
                          <button
                            className="icon-button"
                            type="button"
                            onClick={() => removeCortexVariableBinding(bindingIndex)}
                            disabled={!canEdit}
                            aria-label="Remover variável"
                          >
                            <span className="material-symbols-rounded" aria-hidden="true">delete</span>
                          </button>
                        </div>
                      ))}
                    </div>
                    <button
                      className="secondary-button compact-action"
                      type="button"
                      onClick={addCortexVariableBinding}
                      disabled={!canEdit || !cortexTaskSelectorVariables.length}
                    >
                      <span className="material-symbols-rounded" aria-hidden="true">add</span>
                      Adicionar variável
                    </button>
                </section>
              )
            : (
              <Fragment key={field.key}>
                <section className="modal-field modal-field-wide">
                  <FieldLabel
                    help={{
                      title: field.label,
                      description: `Seleciona o valor do contexto que será enviado no campo “${field.label}” do request Cortex.`,
                      usage: 'Navegue pelos atributos disponíveis e escolha o caminho que contém o dado esperado. Em listas, configure também a estratégia de seleção.'
                    }}
                  >
                    {`${field.label}${field.required ? ' *' : ''}`}
                  </FieldLabel>
                  <ContextVariablePathSelector
                    variables={cortexTaskSelectorVariables}
                    binding={cortexConfig.payloadBindings?.[field.key] || { path: '', arrayMatchByPath: {} }}
                    canEdit={canEdit}
                    emptyLabel="Sem atributos disponíveis"
                    onChange={(value) => setCortexPayloadBinding(field.key, value)}
                  />
                </section>
              </Fragment>
            )
        ))}
        {(cortexResourceRequiresFile(cortexConfig.resource) || cortexResourceSupportsOptionalFile(cortexConfig.resource)) && (
          <section className="modal-field modal-field-wide">
            <FieldLabel>{`Arquivo da Task${cortexResourceRequiresFile(cortexConfig.resource) ? ' *' : ''}`}</FieldLabel>
            {cortexConfig.resource === 'ocr' ? (
              <select
                value={normalizeCortexOcrFileBindingPath(cortexConfig.fileBinding?.path || cortexConfig.fileAttribute || '')}
                onChange={(event) => setCortexFileBinding({ path: event.target.value })}
                disabled={!canEdit || !cortexTaskFileSelectorVariables.length}
              >
                <option value="" disabled>
                  {cortexTaskFileSelectorVariables.length ? 'Selecione o atributo file da Task' : 'Sem atributos de arquivo'}
                </option>
                {cortexTaskFileSelectorVariables.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            ) : (
              <ContextVariablePathSelector
                variables={cortexTaskFileSelectorVariables}
                binding={cortexConfig.fileBinding || { path: cortexConfig.fileAttribute || '', arrayMatchByPath: {} }}
                canEdit={canEdit}
                emptyLabel="Sem atributos de arquivo/URL"
                emptyPathLabel="Nenhum selecionado"
                allowEmptyPath={cortexResourceSupportsOptionalFile(cortexConfig.resource)}
                onChange={setCortexFileBinding}
              />
            )}
            <small>{cortexConfig.resource === 'ocr' ? 'OCR aceita apenas binario (multipart).' : 'Para atributos do tipo file: o padrão envia url; use binario para forçar multipart.'}</small>
          </section>
        )}
        <Field
          label="Timeout (ms)"
          type="number"
          value={cortexConfig.timeoutMs}
          onChange={(value) => setCortexConfig({ ...cortexConfig, timeoutMs: value })}
        />
        <Field
          label="Política de erro"
          value={cortexConfig.errorPolicy}
          onChange={(value) => setCortexConfig({ ...cortexConfig, errorPolicy: value })}
          selectOptions={[
            { value: 'Falhar execução', label: 'Falhar execução' },
            { value: 'Ignorar e continuar', label: 'Ignorar e continuar' }
          ]}
        />
        <section className="api-integration-summary modal-field-wide">
          <header>
            <div>
              <span>Guia do recurso</span>
              <strong>{cortexResourceOptions.find((item) => item.value === cortexConfig.resource)?.label || 'Cortex'}</strong>
            </div>
            <small>{`Execução ${cortexRequestMode}`}</small>
          </header>
          <p>{cortexResourceDescription[cortexConfig.resource] || 'Recurso Cortex selecionado para o roteamento.'}</p>
        </section>
      </>
    ),
    condition: (
      <>
	        <ConditionBuilder
	          config={draft.config}
	          nodeId={node.id}
	          nodes={flowNodes}
	          edges={flowEdges}
          apis={testedApis}
	          runtimeOutputs={runtimeOutputs}
	          demand={selectedDemand}
	          taskExtraVariables={taskExtraVariables}
	          canEdit={canEdit}
	          onChange={setConditionConfig}
	        />
	      </>
	    ),
    json: (
      <section className="auto-command-identity modal-field-wide">
        <span>Comando legado</span>
        <strong>Este comando foi removido</strong>
        <p>Remova este no do canvas e reconecte o fluxo com os comandos atuais.</p>
      </section>
    ),
    delay: (
      <>
        <Field label="Duração" type="number" value={delayConfig.duration} onChange={(value) => setDelayConfig('duration', value)} />
        <Field label="Unidade" value={delayConfig.unit} onChange={(value) => setDelayConfig('unit', value)} selectOptions={delayUnitOptions} />
        <Field label="Estratégia" value={delayConfig.strategy} onChange={(value) => setDelayConfig('strategy', value)} selectOptions={delayStrategyOptions} />
        {delayConfig.strategy === 'fixed' && (
          <Field label="Variação aleatória (%)" type="number" value={delayConfig.jitterPercent} onChange={(value) => setDelayConfig('jitterPercent', value)} />
        )}
        {delayConfig.strategy === 'exponential' && (
          <Field label="Espera máxima (s)" type="number" value={delayConfig.maxWait} onChange={(value) => setDelayConfig('maxWait', value)} />
        )}
      </>
    ),
    saveInfo: (
      <SaveInfoBuilder
        config={draft.config}
        demand={selectedDemand}
        nodeId={node.id}
        nodes={flowNodes}
        edges={flowEdges}
        apis={testedApis}
        runtimeOutputs={runtimeOutputs}
        taskExtraVariables={taskExtraVariables}
        canEdit={canEdit}
        onChange={setSaveInfoConfig}
      />
    ),
    insertDemand: (
      <>
        <Field
          label="Operação"
          value={insertDemandConfig.operation || 'copy'}
          onChange={(value) => setInsertDemandConfig({
            ...insertDemandConfig,
            operation: value,
            upsert: value === 'move' ? true : insertDemandConfig.upsert
          })}
          selectOptions={[
            { value: 'copy', label: 'Copiar para outra demanda' },
            {
              value: 'move',
              label: routeStartsFromSearchApi
                ? 'Mover para outra demanda (indisponível neste início)'
                : 'Mover para outra demanda',
              disabled: routeStartsFromSearchApi
            }
          ]}
        />
        <Field
          label="Demanda de destino"
          value={insertDemandConfig.targetDemandId || ''}
          onChange={(value) => setInsertDemandConfig({
            ...insertDemandConfig,
            targetDemandId: value
          })}
          selectOptions={insertDemandSelectOptions}
        />
        <Field
          label="Modo de payload"
          value={insertDemandConfig.payloadMode || 'clone_task'}
          onChange={(value) => setInsertDemandConfig({ ...insertDemandConfig, payloadMode: value })}
          selectOptions={[
            { value: 'clone_task', label: 'Manter todos os atributos da Task' },
            { value: 'merge_api', label: 'Adicionar/atualizar com retorno da API' },
            { value: 'template', label: 'Adicionar/atualizar com Payload template' }
          ]}
        />
        {['merge_api', 'api_response'].includes(insertDemandConfig.payloadMode) && (
          <>
            <Field
              label="API com atributos para adicionar/atualizar"
              value={insertDemandConfig.mergeSourceNodeId || ''}
              onChange={(value) => setInsertDemandConfig({ ...insertDemandConfig, mergeSourceNodeId: value })}
              selectOptions={insertDemandApiSourceOptions}
              disabled={!insertDemandApiSources.length}
            />
            {insertDemandShouldShowNonObjectAttribute && (
              <Field
                label="Nome do atributo"
                value={insertDemandConfig.apiResponseValueAttribute || ''}
                onChange={(value) => setInsertDemandConfig({ ...insertDemandConfig, apiResponseValueAttribute: value })}
              />
            )}
          </>
        )}
        {insertDemandConfig.payloadMode === 'template' && (
          <InsertDemandPayloadBuilder
            config={insertDemandConfig}
            demand={selectedDemand || {}}
            targetDemand={demands.find((item) => String(item.id) === String(insertDemandConfig.targetDemandId || '')) || {}}
            nodeId={node.id}
            nodes={flowNodes}
            edges={flowEdges}
            apis={testedApis}
            runtimeOutputs={runtimeOutputs}
            taskExtraVariables={taskExtraVariables}
            canEdit={canEdit}
            onChange={setInsertDemandConfig}
          />
        )}
        <Field
          label="Status da task criada"
          value={insertDemandConfig.status || ''}
          onChange={(value) => setInsertDemandConfig({ ...insertDemandConfig, status: value })}
          selectOptions={[
            { value: '', label: 'Status inicial da demanda' },
            ...demandStatusOptions(
              demands.find((item) => String(item.id) === String(insertDemandConfig.targetDemandId || route?.demand_id || '')) ||
              selectedDemand ||
              {}
            )
          ]}
        />
        <section className={`insert-demand-operation-note modal-field-wide is-${insertDemandConfig.operation || 'copy'}`}>
          <span className="material-symbols-rounded" aria-hidden="true">
            {insertDemandConfig.operation === 'move' ? 'drive_file_move' : 'content_copy'}
          </span>
          <div>
            <strong>{insertDemandConfig.operation === 'move' ? 'Movimentação segura' : 'Cópia preservada'}</strong>
            <p>
              {insertDemandConfig.operation === 'move'
                ? 'A mesma task_key é preservada e a Task sai da demanda de origem somente após o sucesso no destino.'
                : 'Uma nova Task é criada com task_key automática e a Task original continua na demanda atual.'}
            </p>
          </div>
        </section>
      </>
    )
  };
  const conditionSources = conditionVariableSources({
    nodeId: node.id,
    nodes: flowNodes,
    edges: flowEdges,
    apis: testedApis,
    runtimeOutputs,
    demand: selectedDemand,
    taskExtraVariables
  });
  const contractIntegrationForNode = (candidate) => {
    const candidateConfig = candidate?.data?.config || {};
    const integrationId = candidate?.data?.commandType === 'start'
      ? candidateConfig.executionIntegrationId || candidateConfig.execution_integration_id
      : candidateConfig.integrationId || candidateConfig.integration_id;
    return testedApis.find((item) => String(item.id) === String(integrationId || '')) || null;
  };
  const currentContractIntegration = node.data.commandType === 'start'
    ? startExecutionIntegration
    : selectedIntegration;
  const learnedInputContract = hasCurrentInspectionContract(draft.config)
    ? learnedInputSchemaFromConfig(draft.config)
    : {};
  const learnedOutputContract = hasCurrentInspectionContract(draft.config)
    ? learnedResponseSchemaFromConfig(draft.config)
    : {};
  const ancestorIdsForContract = upstreamNodeIds(node.id, flowEdges);
  const commandContractKeyLabels = flowNodes.reduce((acc, candidate) => {
    const customName = String(candidate?.data?.customName || '').trim();
    if (customName) acc[`outputs.${candidate.id}`] = customName;
    return acc;
  }, {});
  const ancestorContracts = flowNodes
    .filter((candidate) => ancestorIdsForContract.has(String(candidate.id)))
    .filter((candidate) => !isStartNode(candidate) || isSearchStartConfig(candidate.data?.config || {}))
    .map((candidate) => schemaWithPrefix(knownCommandOutputSchema({
      commandType: candidate.data?.commandType,
      config: candidate.data?.config || {},
      demand: selectedDemand || {},
      integration: contractIntegrationForNode(candidate),
      runtimeOutput: runtimeOutputs?.[String(candidate.id)] || null
    }), `outputs.${candidate.id}`));
  const commandInputContractRaw = mergeResponseSchemas(
    taskContextContractSchema(selectedDemand || {}, taskExtraVariables),
    commandRequestContractSchema({
      commandType: node.data.commandType,
      config: draft.config,
      integration: currentContractIntegration
    }),
    ...ancestorContracts,
    learnedInputContract
  );
  const commandInputContract = normalizeFileInputContractSchema(commandInputContractRaw, selectedDemand || {});
  const commandOutputContract = knownCommandOutputSchema({
    commandType: node.data.commandType,
    config: draft.config,
    demand: selectedDemand || {},
    integration: currentContractIntegration,
    runtimeOutput: runtimeOutputs?.[String(node.id)] || null
  });
  const effectiveConfig = node.data.commandType === 'condition'
    ? normalizeConditionConfig(draft.config, conditionSources)
    : node.data.commandType === 'api'
      ? normalizeApiVariableBindings(draft.config, selectedIntegration, selectedDemand, conditionSources)
      : node.data.commandType === 'start'
        ? normalizeStartConfig(draft.config, route)
      : node.data.commandType === 'cortex'
        ? normalizeCortexConfig(draft.config)
      : node.data.commandType === 'delay'
        ? normalizeDelayConfig(draft.config)
        : node.data.commandType === 'saveInfo'
          ? normalizeSaveInfoConfig(draft.config, apiAncestorSources({
            nodeId: node.id,
            nodes: flowNodes,
            edges: flowEdges,
            apis: testedApis,
            runtimeOutputs
          }), selectedDemand)
          : node.data.commandType === 'insertDemand'
            ? normalizeInsertDemandConfig(draft.config, demands, route, {
              extraVariables: taskExtraVariables
            })
          : draft.config;
  const autoIdentity = deriveCommandIdentity(node.data.commandType, effectiveConfig, {
    demandName: selectedDemand ? demandDisplayName(selectedDemand) : routeDemandName(route)
  });

  useEffect(() => {
    if (!customNameAuto || !autoIdentity.title) return;
    setDraft((current) => (
      current.customName === autoIdentity.title
        ? current
        : { ...current, customName: autoIdentity.title }
    ));
  }, [autoIdentity.title, customNameAuto]);

  const saveConfig = () => {
    if (!canEdit) return;

    const requiredFieldsValidation = validateCommandRequiredFields({
      commandType: node.data.commandType,
      config: effectiveConfig
    }) || (
      node.data.commandType === 'insertDemand'
      && effectiveConfig.operation === 'move'
      && routeStartsFromSearchApi
        ? 'Mover não está disponível quando o fluxo começa buscando registros por API, pois não existe uma Task persistida na demanda atual.'
        : ''
    ) || (
      node.data.commandType === 'insertDemand'
      && effectiveConfig.operation === 'move'
      && String(effectiveConfig.targetDemandId || '') === String(route?.demand_id || '')
        ? 'Para mover, selecione uma Demanda de destino diferente da demanda atual.'
        : ''
    );

    if (requiredFieldsValidation) {
      setModalNotice(requiredFieldsValidation);
      return;
    }

    setModalNotice('');
    onSave(node.id, {
      ...node.data,
      title: String(draft.customName || '').trim() || autoIdentity.title,
      subtitle: autoIdentity.subtitle,
      customName: String(draft.customName || '').trim(),
      customNameAuto,
      config: effectiveConfig
    });
  };

  return (
    <>
      <div className="flow-modal-backdrop" role="presentation" onClick={onClose}>
        <section className="flow-modal command-config-modal" role="dialog" aria-modal="true" aria-label={`Configuração ${command.label}`} onClick={(event) => event.stopPropagation()}>
        <header className="flow-modal-head">
          <div className={`flow-modal-icon tone-${command.tone}`}>
            <span className="material-symbols-rounded" aria-hidden="true">{command.icon}</span>
          </div>
          <div>
            <span>{command.label}</span>
            <h3>Configuração extrema do comando</h3>
            <p>{command.description}</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Fechar modal">
            <span className="material-symbols-rounded" aria-hidden="true">close</span>
          </button>
        </header>

        <div className="flow-modal-body">
          <CommandContractPanel
            side="input"
            title="Contexto disponível"
            subtitle="Keys que este comando pode consultar na Task, Demanda, requests e comandos antecessores."
            schema={commandInputContract}
            learnedCount={Object.keys(learnedInputContract).length}
            keyLabels={commandContractKeyLabels}
          />

          <form className="modal-config-grid">
            <section className="auto-command-identity modal-field-wide">
              <span>Identificação automática</span>
              <strong>{autoIdentity.title}</strong>
              <p>{autoIdentity.subtitle}</p>
            </section>
            <section className="modal-field modal-field-wide">
              <FieldLabel>Nome do comando (opcional)</FieldLabel>
              <input
                value={draft.customName || ''}
                onChange={(event) => {
                  setCustomNameAuto(false);
                  setDraft((current) => ({ ...current, customName: event.target.value }));
                }}
                placeholder={autoIdentity.title}
                maxLength={120}
                disabled={!canEdit}
              />
            </section>
            {fields[node.data.commandType] || fields.api}
          </form>

          <CommandContractPanel
            side="output"
            title="Contrato produzido"
            subtitle="Keys do body efetivamente disponibilizadas aos próximos comandos pelas inspeções reais."
            schema={commandOutputContract}
            learnedCount={Object.keys(learnedOutputContract).length}
          />
        </div>

          <footer className="flow-modal-footer">
            <span className={`notice inline-notice ${modalNotice ? 'error' : ''}`}>{modalNotice}</span>
            <button className="secondary-button" type="button" onClick={onClose}>Cancelar</button>
            <button
              className="primary-button"
              type="button"
              disabled={!canEdit}
              onClick={saveConfig}
            >
              {canEdit ? 'Salvar configuração' : 'Somente visualização'}
            </button>
          </footer>
        </section>
      </div>

      <TemplateVariablePickerModal
        open={cortexTemplateAttributePickerOpen}
        canEdit={canEdit}
        suggestionSources={cortexTemplateSuggestionSources}
        fallbackTaskVariables={cortexTemplateTaskVariables}
        includeDate
        title={`Adicionar variável em ${cortexTemplateAttributeTarget?.label || 'texto'}`}
        description="Selecione o atributo para inserir no texto como template."
        onClose={closeCortexTemplateAttributePicker}
        onConfirm={insertCortexTemplateAttribute}
      />
    </>
  );
};

const RouteListModal = ({ routes, demands, selectedRouteId, routesLoaded, canEdit, onClose, onSelect, onDuplicate, onDelete }) => {
  const groups = groupRoutesByDemand(routes, demands);
  const [collapsedFolders, setCollapsedFolders] = useState(() => new Set());
  const [routeContextMenu, setRouteContextMenu] = useState(null);

  const toggleFolder = (folderId) => {
    setCollapsedFolders((current) => {
      const next = new Set(current);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  };

  const closeRouteContextMenu = useCallback(() => {
    setRouteContextMenu(null);
  }, []);

  const openRouteContextMenu = useCallback((event, route) => {
    if (!route?.id) return;
    event.preventDefault();
    event.stopPropagation();
    const gap = 8;
    const menuWidth = 188;
    const menuHeight = canEdit ? 104 : 96;
    const viewportW = window.innerWidth || document.documentElement.clientWidth || 0;
    const viewportH = window.innerHeight || document.documentElement.clientHeight || 0;
    const left = Math.max(gap, Math.min(Number(event.clientX) || 0, viewportW - menuWidth - gap));
    const top = Math.max(gap, Math.min(Number(event.clientY) || 0, viewportH - menuHeight - gap));
    setRouteContextMenu({
      routeId: String(route.id),
      left,
      top
    });
  }, [canEdit]);

  const contextRoute = useMemo(() => {
    if (!routeContextMenu?.routeId) return null;
    return routes.find((route) => String(route.id) === String(routeContextMenu.routeId)) || null;
  }, [routeContextMenu, routes]);

  useEffect(() => {
    if (!routeContextMenu) return undefined;

    const handlePointerDown = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('.route-card-context-menu')) return;
      closeRouteContextMenu();
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') closeRouteContextMenu();
    };

    window.addEventListener('resize', closeRouteContextMenu);
    window.addEventListener('scroll', closeRouteContextMenu, true);
    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('resize', closeRouteContextMenu);
      window.removeEventListener('scroll', closeRouteContextMenu, true);
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [closeRouteContextMenu, routeContextMenu]);

  useEffect(() => {
    if (!routeContextMenu?.routeId) return;
    const stillExists = routes.some((route) => String(route.id) === String(routeContextMenu.routeId));
    if (!stillExists) {
      setRouteContextMenu(null);
    }
  }, [routeContextMenu, routes]);

  return (
  <div className="flow-modal-backdrop" role="presentation" onClick={onClose}>
    <section className="flow-modal route-picker-modal" role="dialog" aria-modal="true" aria-label="Selecionar roteamento" onClick={(event) => event.stopPropagation()}>
      <header className="flow-modal-head">
        <div className="flow-modal-icon tone-purple">
          <span className="material-symbols-rounded" aria-hidden="true">alt_route</span>
        </div>
        <div>
          <span>Roteamentos autonomos</span>
          <h3>Selecionar roteamento</h3>
          <p>Escolha qual desenho de processamento deve ser renderizado no canvas.</p>
        </div>
        <button className="icon-button" type="button" onClick={onClose} aria-label="Fechar modal">
          <span className="material-symbols-rounded" aria-hidden="true">close</span>
        </button>
      </header>
      <div className="flow-modal-body route-picker-body">
        {!routesLoaded ? (
          <div className="route-empty-state">
            <span className="material-symbols-rounded" aria-hidden="true">hourglass_top</span>
            <strong>Carregando roteamentos</strong>
            <p>Aguarde enquanto buscamos os roteamentos deste ambiente.</p>
          </div>
        ) : groups.length ? groups.map((group) => {
          const folderId = String(group.id || 'general');
          const collapsed = collapsedFolders.has(folderId);
          return (
          <section className="route-demand-folder" key={group.id}>
            <button
              className={`route-folder-row ${collapsed ? 'is-collapsed' : ''}`}
              type="button"
              onClick={() => {
                closeRouteContextMenu();
                toggleFolder(folderId);
              }}
              aria-expanded={collapsed ? 'false' : 'true'}
              title={group.subtitle}
            >
              <span className="route-folder-chevron material-symbols-rounded" aria-hidden="true">
                {collapsed ? 'chevron_right' : 'expand_more'}
              </span>
              <span className="route-folder-icon material-symbols-rounded" aria-hidden="true">folder</span>
              <strong>{group.name}</strong>
              <small>{group.routes.length}</small>
            </button>
            <div
              className={`route-demand-folder-list ${collapsed ? 'is-collapsed' : ''}`}
              onContextMenu={(event) => {
                const target = event.target instanceof Element ? event.target : null;
                if (target?.closest('.route-picker-item')) return;
                const fallbackRoute = group.routes.find((route) => String(route.id) === String(selectedRouteId)) || group.routes[0];
                if (fallbackRoute) openRouteContextMenu(event, fallbackRoute);
              }}
            >
              {group.routes.length ? group.routes.map((route) => (
                <button
                  key={route.id}
                  type="button"
                  className={`route-picker-item ${String(route.id) === String(selectedRouteId) ? 'is-active' : ''}`}
                  onClick={() => {
                    closeRouteContextMenu();
                    onSelect(route);
                  }}
                  onContextMenu={(event) => openRouteContextMenu(event, route)}
                >
                  <span className="material-symbols-rounded" aria-hidden="true">schema</span>
                  <div>
                    <strong>{route.name}</strong>
                    <p>{route.trigger_rule || 'manual'} - {route.target_queue || 'fila-padrao'}</p>
                  </div>
                  <span className={`route-status-pill ${route.active === true ? 'is-active' : ''}`}>
                    {route.active === true ? 'Ativo' : 'Inativo'}
                  </span>
                  <small>{Array.isArray(route.flow_nodes) && route.flow_nodes.length ? `${route.flow_nodes.length} comandos` : 'draft base'}</small>
                </button>
              )) : (
                <div className="route-demand-empty">Nenhum roteamento nesta demanda.</div>
              )}
            </div>
          </section>
          );
        }) : (
          <div className="route-empty-state">
            <span className="material-symbols-rounded" aria-hidden="true">route</span>
            <strong>Nenhum roteamento cadastrado</strong>
            <p>Crie o primeiro roteamento para iniciar o desenho do flow.</p>
          </div>
        )}
        {routeContextMenu && (
          <section
            className="route-card-context-menu"
            role="menu"
            aria-label="Acoes do roteamento"
            style={{ left: `${routeContextMenu.left}px`, top: `${routeContextMenu.top}px` }}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              role="menuitem"
              disabled={!canEdit || !contextRoute}
              onClick={() => {
                if (!contextRoute) return;
                closeRouteContextMenu();
                onDuplicate?.(contextRoute);
              }}
            >
              <span className="material-symbols-rounded" aria-hidden="true">content_copy</span>
              Duplicar
            </button>
            <button
              type="button"
              role="menuitem"
              className="is-danger"
              disabled={!canEdit || !contextRoute}
              onClick={() => {
                if (!contextRoute) return;
                closeRouteContextMenu();
                onDelete?.(contextRoute);
              }}
            >
              <span className="material-symbols-rounded" aria-hidden="true">delete</span>
              Apagar
            </button>
          </section>
        )}
      </div>
      <footer className="flow-modal-footer">
        <button className="secondary-button" type="button" onClick={onClose}>Cancelar</button>
      </footer>
    </section>
  </div>
  );
};

const RouteCreateModal = ({ mode = 'create', value, demandId, demands, notice, creating, onChange, onDemandChange, onClose, onSubmit }) => {
  const isDuplicate = mode === 'duplicate';
  return (
  <div className="flow-modal-backdrop" role="presentation" onClick={onClose}>
    <section className="flow-modal route-create-modal" role="dialog" aria-modal="true" aria-label={isDuplicate ? 'Duplicar roteamento' : 'Novo roteamento'} onClick={(event) => event.stopPropagation()}>
      <header className="flow-modal-head">
        <div className="flow-modal-icon tone-purple">
          <span className="material-symbols-rounded" aria-hidden="true">{isDuplicate ? 'content_copy' : 'add'}</span>
        </div>
        <div>
          <span>Roteamento autônomo</span>
          <h3>{isDuplicate ? 'Duplicar roteamento' : 'Novo roteamento'}</h3>
          <p>{isDuplicate ? 'Escolha o novo nome e a demanda que recebera a copia deste flow.' : 'Informe um nome claro para identificar o flow na listagem de roteamentos.'}</p>
        </div>
        <button className="icon-button" type="button" onClick={onClose} aria-label="Fechar modal">
          <span className="material-symbols-rounded" aria-hidden="true">close</span>
        </button>
      </header>
      <form className="flow-modal-body route-create-body" onSubmit={onSubmit}>
        <label className="modal-field">
          <FieldLabel>Demanda</FieldLabel>
          <select value={demandId} onChange={(event) => onDemandChange(event.target.value)} required>
            <option value="">Selecione a demanda</option>
            {demands.map((demand) => (
              <option key={demand.id} value={demand.id}>{demandDisplayName(demand)}</option>
            ))}
          </select>
        </label>
        <label className="modal-field">
          <FieldLabel>Nome do roteamento</FieldLabel>
          <input value={value} onChange={(event) => onChange(event.target.value)} placeholder={isDuplicate ? 'Copia do roteamento atual' : 'Roteamento de contratos enterprise'} autoFocus />
        </label>
      </form>
      <footer className="flow-modal-footer">
        <span className={`notice inline-notice ${notice ? 'error' : ''}`}>{notice}</span>
        <button className="secondary-button" type="button" onClick={onClose}>Cancelar</button>
        <button className="primary-button" type="button" disabled={creating} onClick={onSubmit}>{creating ? (isDuplicate ? 'Duplicando...' : 'Criando...') : (isDuplicate ? 'Duplicar' : 'Criar roteamento')}</button>
      </footer>
    </section>
  </div>
  );
};

const createInspectionSeed = (route = null) => ({
  context_version: INSPECTION_CONTEXT_VERSION,
  route: route ? {
    id: route.id,
    name: route.name,
    demand_id: route.demand_id,
    demand_name: route.demand_name,
    demand_protocol: route.demand_protocol
  } : null,
  task: {
    id: null,
    task_key: '',
    status: '',
    payload: {}
  },
  linked_task: null,
  outputs: {},
  steps: []
});

const clearLearnedSchemasFromFlowNodes = (flowNodes = []) => {
  let changed = false;
  const nextNodes = (Array.isArray(flowNodes) ? flowNodes : []).map((node) => {
    const currentConfig = node?.data?.config && typeof node.data.config === 'object' && !Array.isArray(node.data.config)
      ? node.data.config
      : {};
    const currentResponseSchema = learnedResponseSchemaFromConfig(currentConfig);
    const currentInputSchema = learnedInputSchemaFromConfig(currentConfig);
    const currentTaskPayloadSchema = learnedTaskPayloadSchemaFromConfig(currentConfig);
    if (
      !Object.keys(currentResponseSchema).length
      && !Object.keys(currentInputSchema).length
      && !Object.keys(currentTaskPayloadSchema).length
    ) return node;

    changed = true;
    const nextConfig = {
      ...currentConfig,
      learnedInputSchema: {},
      learnedResponseSchema: {},
      learnedTaskPayloadSchema: {}
    };
    return {
      ...node,
      data: {
        ...(node?.data || {}),
        config: nextConfig,
        ...(isStartNode(node) ? { subtitle: startSubtitleForConfig(nextConfig) } : {})
      }
    };
  });
  return changed ? nextNodes : flowNodes;
};

const applyLearnedSchemasFromInspectionPayload = (flowNodes = [], payload = {}) => {
  const inputSchema = schemaFromInspectionInput(payload?.input || null);
  const taskSchema = schemaFromTaskPayload(payload?.context?.task || null);
  const outputSchema = schemaFromRuntimeOutput(payload?.output, { includeDeclaredSchema: false });
  const targetNodeId = String(payload?.node_id || '').trim();
  const shouldLearnInputSchema = targetNodeId && Object.keys(inputSchema).length > 0;
  const shouldLearnTaskSchema = Object.keys(taskSchema).length > 0;
  const shouldLearnOutputSchema = targetNodeId && Object.keys(outputSchema).length > 0;
  const shouldUpgradeContract = Boolean(targetNodeId);
  if (!shouldLearnInputSchema && !shouldLearnTaskSchema && !shouldLearnOutputSchema && !shouldUpgradeContract) {
    return { nodes: flowNodes, changed: false };
  }

  let changed = false;
  const nextNodes = (Array.isArray(flowNodes) ? flowNodes : []).map((node) => {
    const isTargetNode = shouldUpgradeContract && String(node?.id) === targetNodeId;
    const isStart = isStartNode(node);
    if (!isTargetNode && !isStart) return node;

    const currentConfig = node?.data?.config && typeof node.data.config === 'object' && !Array.isArray(node.data.config)
      ? node.data.config
      : {};
    let nextConfig = currentConfig;
    let nodeChanged = false;

    if (isTargetNode) {
      const currentContractVersion = Number(currentConfig.inspectionContractVersion || 0);
      const replacesLegacyContract = currentContractVersion < INSPECTION_CONTRACT_VERSION;
      const currentInputSchema = replacesLegacyContract ? {} : learnedInputSchemaFromConfig(currentConfig);
      const mergedInputSchema = mergeResponseSchemas(currentInputSchema, inputSchema);
      if (replacesLegacyContract || !schemasEqual(currentInputSchema, mergedInputSchema)) {
        nextConfig = {
          ...nextConfig,
          learnedInputSchema: mergedInputSchema
        };
        nodeChanged = true;
      }

      const currentResponseSchema = replacesLegacyContract ? {} : learnedResponseSchemaFromConfig(currentConfig);
      const mergedResponseSchema = mergeResponseSchemas(currentResponseSchema, outputSchema);
      if (replacesLegacyContract || !schemasEqual(currentResponseSchema, mergedResponseSchema)) {
        nextConfig = {
          ...nextConfig,
          learnedResponseSchema: mergedResponseSchema,
          inspectionContractVersion: INSPECTION_CONTRACT_VERSION
        };
        nodeChanged = true;
      }
    }

    if (isStart && shouldLearnTaskSchema) {
      const currentTaskSchema = learnedTaskPayloadSchemaFromConfig(nextConfig);
      const mergedTaskSchema = mergeResponseSchemas(currentTaskSchema, taskSchema);
      if (!schemasEqual(currentTaskSchema, mergedTaskSchema)) {
        nextConfig = {
          ...nextConfig,
          learnedTaskPayloadSchema: mergedTaskSchema
        };
        nodeChanged = true;
      }
    }

    if (!nodeChanged) return node;

    changed = true;
    return {
      ...node,
      data: {
        ...(node?.data || {}),
        config: nextConfig,
        ...(isStart ? { subtitle: startSubtitleForConfig(nextConfig) } : {})
      }
    };
  });

  return {
    nodes: changed ? nextNodes : flowNodes,
    changed
  };
};

const INSPECTION_FILE_BASE64_PREVIEW_LIMIT = 50;

const cleanText = (value) => String(value || '').trim();

const inspectionFileString = (...values) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
};

const inspectionFileBase64Preview = (value) => {
  if (typeof value !== 'string' || !value.trim()) return null;
  const normalized = value.trim();
  if (normalized.length <= INSPECTION_FILE_BASE64_PREVIEW_LIMIT) return normalized;
  return `${normalized.slice(0, INSPECTION_FILE_BASE64_PREVIEW_LIMIT)}...`;
};

const inspectionStorageDescriptor = (value) => {
  const storage = value?.storage;
  if (!storage || typeof storage !== 'object' || Array.isArray(storage)) return '';
  return inspectionFileString(
    storage.relative_path,
    storage.path,
    storage.key,
    storage.s3_key
  ) || '';
};

const normalizeInspectionTaskFileField = (value = '') => normalizeTaskSelectorBindingPath(
  String(value || '')
    .replace(/^task\.payload\./, '')
    .replace(/^task\./, '')
    .replace(/^payload\./, '')
);

const inspectionFileFieldTargets = (taskFileFields = []) => {
  const targets = new Set();
  (Array.isArray(taskFileFields) ? taskFileFields : []).forEach((field) => {
    const normalized = normalizeInspectionTaskFileField(field);
    if (!normalized) return;
    targets.add(normalized);
    targets.add(`task.${normalized}`);
    targets.add(`task.payload.${normalized}`);
    targets.add(`payload.${normalized}`);
  });
  return targets;
};

const inspectionPathKeyFromSegments = (segments = []) => (Array.isArray(segments) ? segments : [])
  .reduce((parts, segment) => {
    const normalized = cleanText(segment);
    if (!normalized || /^\d+$/.test(normalized)) return parts;
    parts.push(normalized);
    return parts;
  }, [])
  .join('.');

const inspectionFilePresentation = (file) => {
  const source = file && typeof file === 'object' && !Array.isArray(file)
    ? file
    : {};
  const scalar = typeof file === 'string' ? cleanText(file) : '';
  const storageDescriptor = inspectionStorageDescriptor(source);
  const scalarUrl = /^https?:\/\//i.test(scalar) ? scalar : '';
  const scalarBase64 = /^data:/i.test(scalar) ? scalar : '';

  return {
    name: inspectionFileString(source?.name, source?.file_name, source?.filename, source?.original_file_name),
    url: inspectionFileString(source?.url, source?.file_url, source?.download_url, scalarUrl)
      || (storageDescriptor || source?.storage_path ? '[runtime-url]' : null),
    base64: inspectionFileBase64Preview(inspectionFileString(source?.base64, source?.content_base64, source?.file_base64, source?.data, scalarBase64))
      || (storageDescriptor || source?.storage_path ? '[runtime-base64]' : null),
    binario: '[binary]'
  };
};

const sanitizeInspectionJsonValue = (value, pathSegments = [], options = {}) => {
  const taskFileFieldTargets = options?.taskFileFieldTargets instanceof Set
    ? options.taskFileFieldTargets
    : inspectionFileFieldTargets(options?.taskFileFields || []);
  const currentPath = inspectionPathKeyFromSegments(pathSegments);
  if (currentPath && taskFileFieldTargets.has(currentPath)) {
    return inspectionFilePresentation(value);
  }

  const nextOptions = options?.taskFileFieldTargets instanceof Set
    ? options
    : { ...options, taskFileFieldTargets };
  if (Array.isArray(value)) {
    return value.map((item, index) => sanitizeInspectionJsonValue(item, pathSegments.concat(String(index)), nextOptions));
  }
  if (!value || typeof value !== 'object') return value;

  return Object.entries(value).reduce((acc, [key, item]) => {
    acc[key] = sanitizeInspectionJsonValue(item, pathSegments.concat(String(key)), nextOptions);
    return acc;
  }, {});
};

const prettyInspectJson = (value, options = {}) => JSON.stringify(
  sanitizeInspectionJsonValue(value ?? {}, [], options),
  null,
  2
);

const parseInspectJsonValue = (value) => {
  if (value == null) return null;
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const copyPlainText = async (value) => {
  const text = String(value ?? '');
  if (!text) return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', 'true');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    textarea.remove();
    return true;
  }
};

const inspectionTextMatches = (text, query) => {
  const source = String(text || '');
  const needle = String(query || '').trim();
  if (!source || !needle) return [];
  const normalizedSource = source.toLocaleLowerCase('pt-BR');
  const normalizedNeedle = needle.toLocaleLowerCase('pt-BR');
  const matches = [];
  let startIndex = 0;
  while (startIndex <= source.length - normalizedNeedle.length) {
    const foundIndex = normalizedSource.indexOf(normalizedNeedle, startIndex);
    if (foundIndex === -1) break;
    matches.push({
      start: foundIndex,
      end: foundIndex + normalizedNeedle.length
    });
    startIndex = foundIndex + normalizedNeedle.length;
  }
  return matches;
};

const inspectionHighlightSegments = (text, matches = []) => {
  if (!matches.length) return [{ text: String(text || ''), isMatch: false, matchIndex: -1 }];
  const source = String(text || '');
  const segments = [];
  let cursor = 0;
  matches.forEach((match, index) => {
    if (match.start > cursor) {
      segments.push({ text: source.slice(cursor, match.start), isMatch: false, matchIndex: -1 });
    }
    segments.push({ text: source.slice(match.start, match.end), isMatch: true, matchIndex: index });
    cursor = match.end;
  });
  if (cursor < source.length) {
    segments.push({ text: source.slice(cursor), isMatch: false, matchIndex: -1 });
  }
  return segments;
};

const getInspectionDrawerBounds = () => {
  const availableHeight = window.innerHeight || 720;
  return {
    min: 180,
    max: Math.max(260, Math.min(620, availableHeight - 220))
  };
};

const clampInspectionDrawerHeight = (height) => {
  const { min, max } = getInspectionDrawerBounds();
  return Math.round(Math.min(max, Math.max(min, Number(height) || 300)));
};

const createInspectionRunId = () => (
  `inspect-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
);

const seedInspectionNodeStates = (flowNodes = []) => flowNodes.reduce((acc, node) => {
  acc[String(node.id)] = 'waiting';
  return acc;
}, {});

const markIgnoredInspectionNodeStates = (states = {}, flowNodes = []) => {
  const next = { ...(states || {}) };
  flowNodes.forEach((node) => {
    const id = String(node.id);
    if (!next[id] || next[id] === 'waiting' || next[id] === 'executing') {
      next[id] = 'ignored';
    }
  });
  return next;
};

const inspectionStepFromEvent = (payload, fallbackNode = null, options = {}) => {
  const nodeId = payload.node_id || fallbackNode?.id || null;
  const commandType = payload.command_type || fallbackNode?.data?.commandType || 'api';
  const title = String(fallbackNode?.data?.customName || '').trim()
    || fallbackNode?.data?.title
    || commandForType(commandType).label;
  const inputPayload = payload.input ?? payload.task ?? {};
  const outputPayload = payload.output ?? payload.outputs ?? {
    event: payload.event,
    error_message: payload.error_message || null,
    status: payload.execution_status || null
  };

  return {
    nodeId,
    title,
    commandType,
    inputPayload,
    outputPayload,
    input: prettyInspectJson(inputPayload, {
      taskFileFields: Array.isArray(options?.taskFileFields) ? options.taskFileFields : []
    }),
    output: prettyInspectJson(outputPayload),
    startedAt: payload.started_at || null,
    finishedAt: payload.finished_at || null,
    durationMs: Number(payload.duration_ms) || null,
    status: payload.status || payload.execution_status || null,
    errorMessage: payload.error_message || null,
    errorStack: payload.error_stack || null,
    at: new Date(payload.ts || Date.now()).toLocaleTimeString('pt-BR')
  };
};

const inspectionStepFromSnapshot = (step, fallbackNode = null, options = {}) => inspectionStepFromEvent({
  node_id: step?.node_id,
  command_type: step?.command_type,
  input: step?.input ?? {},
  output: step?.output ?? step?.runtime_output ?? {},
  started_at: step?.started_at || null,
  finished_at: step?.finished_at || step?.at || null,
  duration_ms: step?.duration_ms ?? null,
  status: step?.status || null,
  error_message: step?.error_message || null,
  error_stack: step?.error_stack || null,
  ts: step?.finished_at || step?.at || step?.started_at || null
}, fallbackNode, options);

const AutomationBuilder = () => {
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [nodeContextMenu, setNodeContextMenu] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [routesLoaded, setRoutesLoaded] = useState(false);
  const [demands, setDemands] = useState([]);
  const [testedApis, setTestedApis] = useState([]);
  const [selectedRouteId, setSelectedRouteId] = useState(() => readStoredSelectedRouteId());
  const [routeCreateOpen, setRouteCreateOpen] = useState(false);
  const [routeDuplicateOpen, setRouteDuplicateOpen] = useState(false);
  const [commandsModalOpen, setCommandsModalOpen] = useState(false);
  const [compactPaletteMode, setCompactPaletteMode] = useState(() => isCompactPaletteViewport());
  const [newRouteName, setNewRouteName] = useState('');
  const [newRouteDemandId, setNewRouteDemandId] = useState('');
  const [duplicateRouteName, setDuplicateRouteName] = useState('');
  const [duplicateRouteDemandId, setDuplicateRouteDemandId] = useState('');
  const [duplicateSourceRouteId, setDuplicateSourceRouteId] = useState('');
  const [routeNotice, setRouteNotice] = useState('');
  const [duplicateRouteNotice, setDuplicateRouteNotice] = useState('');
  const [creatingRoute, setCreatingRoute] = useState(false);
  const [duplicatingRoute, setDuplicatingRoute] = useState(false);
  const [savingFlow, setSavingFlow] = useState(false);
  const [validation, setValidation] = useState('Flow pronto para validação.');
  const [canEdit, setCanEdit] = useState(window.TDEMAND_CAN_EDIT !== false);
  const [hasCortex, setHasCortex] = useState(() => normalizeBooleanFlag(window.TDEMAND_HAS_CORTEX));
  const [hasChatIntegration, setHasChatIntegration] = useState(() => normalizeBooleanFlag(window.TDEMAND_HAS_CHAT_INTEGRATION, true));
  const [hasVoiceIntegration, setHasVoiceIntegration] = useState(() => normalizeBooleanFlag(window.TDEMAND_HAS_VOICE_INTEGRATION, true));
  const [inspectionMode, setInspectionMode] = useState(false);
  const [inspectionViewMode, setInspectionViewMode] = useState('json');
  const [inspectionContext, setInspectionContext] = useState(createInspectionSeed(null));
  const [inspectionStep, setInspectionStep] = useState(null);
  const [inspectionRunning, setInspectionRunning] = useState(false);
  const [inspectionNodeStates, setInspectionNodeStates] = useState({});
  const [inspectionEvents, setInspectionEvents] = useState([]);
  const [inspectionArrayItemIndex, setInspectionArrayItemIndex] = useState('');
  const [inspectionPayloadCopyNotice, setInspectionPayloadCopyNotice] = useState('');
  const [inspectionOutputCopyNotice, setInspectionOutputCopyNotice] = useState('');
  const [inspectionPayloadQuery, setInspectionPayloadQuery] = useState('');
  const [inspectionPayloadMatchIndex, setInspectionPayloadMatchIndex] = useState(-1);
  const [inspectionOutputQuery, setInspectionOutputQuery] = useState('');
  const [inspectionOutputMatchIndex, setInspectionOutputMatchIndex] = useState(-1);
  const [connectionCommandMenu, setConnectionCommandMenu] = useState(null);
  const [connectionDeleteContext, setConnectionDeleteContext] = useState(null);
  const [inspectionDrawerHeight, setInspectionDrawerHeight] = useState(() => (
    clampInspectionDrawerHeight(localStorage.getItem('t-demand-route-inspection-height') || 300)
  ));
  const stageRef = useRef(null);
  const drawflowRef = useRef(null);
  const editorRef = useRef(null);
  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);
  const inspectionModeRef = useRef(inspectionMode);
  const inspectionContextRef = useRef(inspectionContext);
  const inspectionStepRef = useRef(inspectionStep);
  const inspectionNodeStatesRef = useRef(inspectionNodeStates);
  const drawIdByNodeIdRef = useRef(new Map());
  const nodeIdByDrawIdRef = useRef(new Map());
  const pointerDragRef = useRef(null);
  const pointerNodeClickRef = useRef(null);
  const pendingConnectionRef = useRef(null);
  const inspectionRunRef = useRef(null);
  const inspectionSessionRef = useRef(null);
  const hydratedRouteIdRef = useRef(null);
  const lastDragAtRef = useRef(0);
  const hydratingRouteRef = useRef(false);
  const syncingFromDrawflowRef = useRef(false);
  const flowDirtyRef = useRef(false);
  const suppressDirtyUntilRef = useRef(0);
  const saveTimerRef = useRef(null);
  const drawflowHotkeysActiveRef = useRef(false);
  const inspectionPayloadCodeRef = useRef(null);
  const inspectionOutputCodeRef = useRef(null);
  const [, setViewportTick] = useState(0);

  const selectedRoute = useMemo(() => (
    routes.find((route) => String(route.id) === String(selectedRouteId)) || null
  ), [routes, selectedRouteId]);
  const selectedRouteDemand = useMemo(() => (
    demands.find((demand) => String(demand.id) === String(selectedRoute?.demand_id || '')) || null
  ), [demands, selectedRoute?.demand_id]);
  const inspectionTaskFileFields = useMemo(() => (
    demandTaskFileFields(selectedRouteDemand)
  ), [selectedRouteDemand]);
  const canEditSelectedRoute = canEdit && Boolean(selectedRoute);
  const availableCommandEntries = useMemo(() => Object.entries(commandCatalog)
    .filter(([commandType]) => commandType !== 'start'), []);
  const canUseCommand = useCallback((commandType, { notify = true } = {}) => {
    if (commandType === 'cortex' && !hasCortex) {
      if (notify) {
        setValidation('Cortex não contratado. Contrate o benefício em Configurações > Planos para usar este comando.');
      }
      return false;
    }
    if (commandType === 'chat' && !hasChatIntegration) {
      if (notify) {
        setValidation('Chat não configurado. Configure em Configurações > Integrações.');
      }
      return false;
    }
    if (commandType === 'voice' && !hasVoiceIntegration) {
      if (notify) {
        setValidation('Voz não configurada. Configure em Configurações > Integrações.');
      }
      return false;
    }
    return true;
  }, [hasChatIntegration, hasCortex, hasVoiceIntegration]);

  inspectionModeRef.current = inspectionMode;
  inspectionContextRef.current = inspectionContext;
  inspectionStepRef.current = inspectionStep;
  inspectionNodeStatesRef.current = inspectionNodeStates;

  const commitInspectionContext = useCallback((valueOrUpdater) => {
    const current = inspectionContextRef.current || createInspectionSeed(selectedRoute);
    const resolved = typeof valueOrUpdater === 'function'
      ? valueOrUpdater(current)
      : valueOrUpdater;
    const next = {
      ...(resolved || createInspectionSeed(selectedRoute)),
      context_version: INSPECTION_CONTEXT_VERSION
    };
    inspectionContextRef.current = next;
    setInspectionContext(next);
    return next;
  }, [selectedRoute]);

  const markDirty = useCallback(() => {
    if (!canEdit) return;
    if (!hydratingRouteRef.current && Date.now() > suppressDirtyUntilRef.current) {
      flowDirtyRef.current = true;
    }
  }, [canEdit]);

  const applyDrawflowViewport = useCallback(({ zoom, canvasX, canvasY }) => {
    const editor = editorRef.current;
    if (!editor?.precanvas) return;
    const nextZoom = clampDrawflowZoom(zoom);
    if (nextZoom == null) return;

    editor.zoom = nextZoom;
    editor.zoom_last_value = nextZoom;
    editor.canvas_x = Math.round((Number(canvasX) || 0) * 100) / 100;
    editor.canvas_y = Math.round((Number(canvasY) || 0) * 100) / 100;
    editor.precanvas.style.transform = `translate(${editor.canvas_x}px, ${editor.canvas_y}px) scale(${nextZoom})`;
    editor.dispatch?.('zoom', nextZoom);
  }, []);

  const zoomDrawflowAtPoint = useCallback((targetZoom, clientX = null, clientY = null) => {
    const editor = editorRef.current;
    const canvas = drawflowRef.current;
    if (!editor?.precanvas || !canvas) return;

    const nextZoom = clampDrawflowZoom(targetZoom);
    const currentZoom = clampDrawflowZoom(editor.zoom) || 1;
    if (nextZoom == null || Math.abs(nextZoom - currentZoom) < 0.0001) return;

    const rect = canvas.getBoundingClientRect();
    const anchorX = Number.isFinite(Number(clientX))
      ? Number(clientX) - rect.left
      : rect.width / 2;
    const anchorY = Number.isFinite(Number(clientY))
      ? Number(clientY) - rect.top
      : Math.max(76, rect.height / 2);
    const worldX = (anchorX - Number(editor.canvas_x || 0)) / currentZoom;
    const worldY = (anchorY - Number(editor.canvas_y || 0)) / currentZoom;

    applyDrawflowViewport({
      zoom: nextZoom,
      canvasX: anchorX - worldX * nextZoom,
      canvasY: anchorY - worldY * nextZoom
    });
  }, [applyDrawflowViewport]);

  const zoomDrawflowBy = useCallback((direction, clientX = null, clientY = null) => {
    const currentZoom = clampDrawflowZoom(editorRef.current?.zoom) || 1;
    zoomDrawflowAtPoint(currentZoom + Number(direction || 0) * 0.1, clientX, clientY);
  }, [zoomDrawflowAtPoint]);

  const centerDrawflow = useCallback((options = {}) => {
    const editor = editorRef.current;
    const canvas = drawflowRef.current;
    const currentNodes = nodesRef.current;
    if (!editor?.precanvas || !canvas || !currentNodes.length) return;

    const preserveZoom = options?.preserveZoom === true;
    const preferredZoom = clampDrawflowZoom(options?.preferredZoom);
    const comfortable = options?.comfortable === true;

    const rect = canvas.getBoundingClientRect();
    if (rect.width < 24 || rect.height < 24) return;
    const nodeWidth = 260;
    const nodeHeight = 132;
    const minX = Math.min(...currentNodes.map((node) => node.position?.x || 0));
    const minY = Math.min(...currentNodes.map((node) => node.position?.y || 0));
    const maxX = Math.max(...currentNodes.map((node) => (node.position?.x || 0) + nodeWidth));
    const maxY = Math.max(...currentNodes.map((node) => (node.position?.y || 0) + nodeHeight));
    const flowWidth = Math.max(1, maxX - minX);
    const flowHeight = Math.max(1, maxY - minY);
    const horizontalPadding = Math.min(64, Math.max(28, rect.width * 0.055));
    const topPadding = rect.height < 520 ? 66 : 82;
    const bottomPadding = rect.height < 520 ? 34 : 52;
    const usableWidth = Math.max(1, rect.width - horizontalPadding * 2);
    const usableHeight = Math.max(1, rect.height - topPadding - bottomPadding);
    const naturalFit = Math.min(usableWidth / flowWidth, usableHeight / flowHeight);
    const comfortableMinimum = rect.width >= 900 ? 0.58 : 0.48;
    const fittedZoom = Math.max(
      comfortable ? comfortableMinimum : 0.35,
      Math.min(currentNodes.length === 1 ? 1.1 : 1.05, naturalFit)
    );
    const zoom = preserveZoom
      ? (preferredZoom ?? Math.max(0.35, Math.min(1.6, Number(editor.zoom || 1) || 1)))
      : fittedZoom;

    applyDrawflowViewport({
      zoom,
      canvasX: horizontalPadding + (usableWidth - flowWidth * zoom) / 2 - minX * zoom,
      canvasY: topPadding + (usableHeight - flowHeight * zoom) / 2 - minY * zoom
    });
  }, [applyDrawflowViewport]);

  const renderDrawflow = useCallback((flowNodes, flowEdges, shouldCenter = false) => {
    const editor = editorRef.current;
    if (!editor) return;

    hydratingRouteRef.current = true;
    drawIdByNodeIdRef.current = new Map();
    nodeIdByDrawIdRef.current = new Map();
    editor.clearModuleSelected();
    editor.nodeId = 1;

    flowNodes.forEach((node) => {
      const inputsCount = isStartNode(node) ? 0 : 1;
      const outputsCount = nodeOutputCount(node);
      const currentInspectionContext = inspectionContextRef.current || {};
      const currentInspectionStep = inspectionStepRef.current;
      const currentInspectionNodeStates = inspectionNodeStatesRef.current || {};
      const drawId = editor.addNode(
        'command',
        inputsCount,
        outputsCount,
        node.position?.x || 0,
        node.position?.y || 0,
        nodeClassName(node),
        { rfId: node.id },
        renderCommandNodeHtml(node, {
          inspectionMode: inspectionModeRef.current,
          inspectedNodeId: currentInspectionStep?.nodeId,
          executedNodeIds: new Set((currentInspectionContext.steps || []).map((step) => String(step.node_id))),
          nodeStates: currentInspectionNodeStates
        })
      );
      drawIdByNodeIdRef.current.set(String(node.id), String(drawId));
      nodeIdByDrawIdRef.current.set(String(drawId), String(node.id));
      syncConditionNodeOutputs(editor, drawId, node);
    });

    flowEdges.forEach((edge) => {
      const outputId = drawIdByNodeIdRef.current.get(String(edge.source));
      const inputId = drawIdByNodeIdRef.current.get(String(edge.target));
      if (!outputId || !inputId) return;
      const sourceNode = flowNodes.find((node) => String(node.id) === String(edge.source));
      const outputClass = edgeDrawflowOutputClass(edge, sourceNode || {});
      try {
        editor.addConnection(outputId, inputId, outputClass, 'input_1');
      } catch {
        // Drawflow rejects duplicated or invalid links; saved state remains the source of truth.
      }
    });

    window.requestAnimationFrame(() => {
      hydratingRouteRef.current = false;
      if (shouldCenter) {
        const centerOptions = typeof shouldCenter === 'object' ? shouldCenter : {};
        centerDrawflow(centerOptions);
      }
    });
  }, [centerDrawflow]);

  const refreshDrawflowNodeHtml = useCallback(() => {
    const editor = editorRef.current;
    if (!editor?.precanvas) return;

    const currentInspectionContext = inspectionContextRef.current || {};
    const currentInspectionStep = inspectionStepRef.current;
    const executedNodeIds = new Set((currentInspectionContext.steps || []).map((step) => String(step.node_id)));
    const currentInspectionNodeStates = inspectionNodeStatesRef.current || {};

    nodesRef.current.forEach((node) => {
      const drawId = drawIdByNodeIdRef.current.get(String(node.id));
      if (!drawId) return;
      const content = editor.precanvas.querySelector(`#node-${drawId} .drawflow_content_node`);
      if (!content) return;
      content.innerHTML = renderCommandNodeHtml(node, {
        inspectionMode: inspectionModeRef.current,
        inspectedNodeId: currentInspectionStep?.nodeId,
        executedNodeIds,
        nodeStates: currentInspectionNodeStates
      });
      try {
        syncConditionNodeOutputs(editor, drawId, node);
        editor.updateConnectionNodes(`node-${drawId}`);
      } catch {
        // Drawflow may reject updates during a transient redraw; the next render will correct it.
      }
    });
  }, []);

  const onDragStart = (event, commandType) => {
    if (!canEdit) {
      event.preventDefault();
      return;
    }
    if (!canUseCommand(commandType)) {
      event.preventDefault();
      return;
    }
    pointerDragRef.current = null;
    lastDragAtRef.current = Date.now();
    event.dataTransfer.setData('application/t-demand-command', commandType);
    event.dataTransfer.effectAllowed = 'move';
  };

  const clientPointToDrawflowPosition = useCallback((clientX, clientY) => {
    const editor = editorRef.current;
    const canvas = drawflowRef.current || stageRef.current;
    const rect = canvas?.getBoundingClientRect();
    if (!rect) return { x: 180, y: 120 };

    const zoom = editor?.zoom || 1;
    const canvasX = editor?.canvas_x || 0;
    const canvasY = editor?.canvas_y || 0;

    return {
      x: Math.round((clientX - rect.left - canvasX) / zoom),
      y: Math.round((clientY - rect.top - canvasY) / zoom)
    };
  }, []);

  const addCommandAtClientPoint = useCallback((commandType, clientX, clientY) => {
    if (!canEditSelectedRoute || !canUseCommand(commandType)) return;
    const position = clientPointToDrawflowPosition(clientX, clientY);
    markDirty();
    setNodes((current) => current.concat(makeNode(commandType, position, current.length + 1)));
  }, [canEditSelectedRoute, canUseCommand, clientPointToDrawflowPosition, markDirty]);

  const closeConnectionCommandMenu = useCallback(() => {
    setConnectionCommandMenu(null);
  }, []);

  const openConnectionCommandMenu = useCallback(({ sourceNodeId, sourceHandle, clientX, clientY }) => {
    if (!canEditSelectedRoute) return;
    setConnectionCommandMenu({
      sourceNodeId: String(sourceNodeId || ''),
      sourceHandle: String(sourceHandle || 'output_1'),
      clientX: Number(clientX) || 0,
      clientY: Number(clientY) || 0
    });
  }, [canEditSelectedRoute]);

  const createCommandFromConnectionMenu = useCallback((commandType) => {
    if (!canEditSelectedRoute || !connectionCommandMenu?.sourceNodeId) {
      closeConnectionCommandMenu();
      return;
    }
    if (!canUseCommand(commandType)) {
      closeConnectionCommandMenu();
      return;
    }

    const sourceNode = nodesRef.current.find((node) => String(node.id) === String(connectionCommandMenu.sourceNodeId));
    if (!sourceNode) {
      closeConnectionCommandMenu();
      return;
    }

    const sourceHandle = sourceNode.data?.commandType === 'condition'
      ? conditionOutputHandleByClass(sourceNode, connectionCommandMenu.sourceHandle || 'output_1')
      : 'output_1';
    const position = clientPointToDrawflowPosition(connectionCommandMenu.clientX, connectionCommandMenu.clientY);
    const newNode = makeNode(commandType, position, nodesRef.current.length + 1);
    const edgeLabel = edgeLabelForOutput(sourceNode, sourceHandle, '');

    markDirty();
    setNodes((current) => current.concat(newNode));
    setEdges((current) => current.concat(makeEdge(sourceNode.id, newNode.id, edgeLabel, sourceHandle)));
    closeConnectionCommandMenu();
  }, [canEditSelectedRoute, canUseCommand, clientPointToDrawflowPosition, closeConnectionCommandMenu, connectionCommandMenu, markDirty]);

  const onDrop = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    if (inspectionMode) return;
    const commandType = event.dataTransfer.getData('application/t-demand-command');
    if (!commandType) return;
    pointerDragRef.current = null;
    lastDragAtRef.current = Date.now();
    addCommandAtClientPoint(commandType, event.clientX, event.clientY);
  }, [addCommandAtClientPoint, inspectionMode]);

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onPalettePointerDown = (event, commandType) => {
    if (inspectionMode) return;
    if (!canEditSelectedRoute) return;
    if (!canUseCommand(commandType)) return;
    pointerDragRef.current = {
      commandType,
      moved: false,
      startX: event.clientX,
      startY: event.clientY
    };
  };

  const saveNode = (id, nextData) => {
    if (!canEdit) return;
    markDirty();
    setNodes((current) => {
      const previousNode = current.find((node) => node.id === id);
      const previousResource = String(previousNode?.data?.config?.resource || '').toLowerCase();
      const nextResource = String(nextData?.config?.resource || '').toLowerCase();
      const resourceChanged = previousNode?.data?.commandType === 'cortex' && previousResource !== nextResource;

      const updatedNodes = current.map((node) => (
        node.id === id ? { ...node, data: nextData } : node
      ));

      if (!resourceChanged) return updatedNodes;
      return clearMappedKeysFromSourceNode(updatedNodes, id);
    });
    const conditionEdgeResult = reconcileConditionEdgesForNode({
      nodeId: id,
      nodeData: nextData,
      edges: edgesRef.current
    });
    setEdges(conditionEdgeResult.edges);
    setSelectedNode(null);
    setNodeContextMenu(null);
    setValidation(
      conditionEdgeResult.removedCount > 0
        ? `Configuração salva. ${conditionEdgeResult.removedCount} conexão(ões) removida(s) por caminho inexistente.`
        : `Configuração salva em ${new Date().toLocaleTimeString('pt-BR')}.`
    );
  };

  const closeNodeContextMenu = useCallback(() => {
    setNodeContextMenu(null);
  }, []);

  const onNodeContextMenu = useCallback((event, nodeId) => {
    event.preventDefault();
    event.stopPropagation();
    const node = nodesRef.current.find((item) => item.id === nodeId);
    if (isStartNode(node)) {
      setNodeContextMenu(null);
      return;
    }

    const stageRect = stageRef.current?.getBoundingClientRect();
    const rawX = stageRect ? event.clientX - stageRect.left : event.clientX;
    const rawY = stageRect ? event.clientY - stageRect.top : event.clientY;
    const maxX = stageRect ? Math.max(12, stageRect.width - 58) : rawX;
    const maxY = stageRect ? Math.max(54, stageRect.height - 28) : rawY;

    setSelectedNode(null);
    setNodeContextMenu({
      nodeId,
      x: Math.min(maxX, Math.max(12, rawX)),
      y: Math.min(maxY, Math.max(54, rawY))
    });
  }, []);

  const deleteNode = useCallback((nodeId) => {
    if (!canEdit) {
      setNodeContextMenu(null);
      return;
    }
    const node = nodes.find((item) => item.id === nodeId);
    if (!node) {
      setNodeContextMenu(null);
      return;
    }
    if (isStartNode(node)) {
      setNodeContextMenu(null);
      setValidation('O comando Início é obrigatório e não  pode ser removido.');
      return;
    }

    markDirty();
    const editor = editorRef.current;
    const drawId = drawIdByNodeIdRef.current.get(String(nodeId));
    setNodes((current) => current.filter((item) => item.id !== nodeId));
    setEdges((current) => current.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
    if (editor && drawId && editor.drawflow?.drawflow?.Home?.data?.[drawId]) {
      try {
        hydratingRouteRef.current = true;
        editor.removeNodeId(`node-${drawId}`);
      } catch {
        // The React state update below also redraws the canvas, so stale DOM is short lived.
      } finally {
        hydratingRouteRef.current = false;
      }
    }
    setSelectedNode((current) => (current?.id === nodeId ? null : current));
    setNodeContextMenu(null);
    setValidation(`Comando "${node.data?.title || node.id}" removido do roteamento.`);
  }, [canEdit, markDirty, nodes]);

  const requestConnectionDelete = useCallback((event) => {
    const connection = event.target?.closest?.('.connection');
    if (!connection || !canEditSelectedRoute) return false;

    const classes = Array.from(connection.classList || []);
    const outputClass = classes.find((className) => className.startsWith('node_out_node-'));
    const inputClass = classes.find((className) => className.startsWith('node_in_node-'));
    if (!outputClass || !inputClass) return false;

    const source = nodeIdByDrawIdRef.current.get(outputClass.slice('node_out_node-'.length));
    const targetNode = nodeIdByDrawIdRef.current.get(inputClass.slice('node_in_node-'.length));
    if (!source || !targetNode) return false;

    const sourceNode = nodesRef.current.find((node) => String(node.id) === String(source));
    const sourceHandle = sourceNode?.data?.commandType === 'condition'
      ? conditionOutputHandleByClass(
        sourceNode,
        classes.find((className) => /^output_\d+$/.test(className)) || 'output_1'
      )
      : 'output_1';
    const edge = edgesRef.current.find((item) => (
      String(item.source) === String(source)
      && String(item.target) === String(targetNode)
      && edgeOutputHandle(item, sourceNode || {}) === sourceHandle
    ));
    if (!edge) return false;

    setNodeContextMenu(null);
    closeConnectionCommandMenu();
    const stageRect = stageRef.current?.getBoundingClientRect();
    const rawX = stageRect ? event.clientX - stageRect.left : event.clientX;
    const rawY = stageRect ? event.clientY - stageRect.top : event.clientY;
    const maxX = stageRect ? Math.max(12, stageRect.width - 58) : rawX;
    const maxY = stageRect ? Math.max(54, stageRect.height - 28) : rawY;

    setConnectionDeleteContext({
      source,
      target: targetNode,
      sourceHandle,
      sourceLabel: sourceNode?.data?.title || source,
      targetLabel: nodesRef.current.find((node) => String(node.id) === String(targetNode))?.data?.title || targetNode,
      x: Math.min(maxX, Math.max(12, rawX)),
      y: Math.min(maxY, Math.max(54, rawY))
    });
    return true;
  }, [canEditSelectedRoute, closeConnectionCommandMenu]);

  const confirmConnectionDelete = useCallback(() => {
    if (!connectionDeleteContext || !canEditSelectedRoute) {
      setConnectionDeleteContext(null);
      return;
    }

    const sourceNode = nodesRef.current.find((node) => String(node.id) === String(connectionDeleteContext.source));
    markDirty();
    const editor = editorRef.current;
    const outputId = drawIdByNodeIdRef.current.get(String(connectionDeleteContext.source));
    const inputId = drawIdByNodeIdRef.current.get(String(connectionDeleteContext.target));

    if (editor && outputId && inputId) {
      try {
        const drawflowOutputClass = sourceNode?.data?.commandType === 'condition'
          ? conditionOutputClassByHandle(sourceNode, connectionDeleteContext.sourceHandle)
          : 'output_1';
        editor.removeSingleConnection(
          outputId,
          inputId,
          drawflowOutputClass,
          'input_1'
        );
      } catch {
        // React state remains the source of truth and redraws without this connection.
      }
    }

    setEdges((current) => current.filter((edge) => !(
      String(edge.source) === String(connectionDeleteContext.source)
      && String(edge.target) === String(connectionDeleteContext.target)
      && edgeOutputHandle(edge, sourceNode || {}) === connectionDeleteContext.sourceHandle
    )));
    setConnectionDeleteContext(null);
    setValidation(`Ligação entre "${connectionDeleteContext.sourceLabel}" e "${connectionDeleteContext.targetLabel}" removida.`);
  }, [canEditSelectedRoute, connectionDeleteContext, markDirty]);

  const learnSchemasFromInspectionPayload = useCallback((payload = {}) => {
    const learned = applyLearnedSchemasFromInspectionPayload(nodesRef.current, payload);
    if (!learned.changed) return;
    markDirty();
    nodesRef.current = learned.nodes;
    setNodes(learned.nodes);
  }, [markDirty]);

  const runInspectionNode = useCallback(async (nodeId) => {
    if (!selectedRoute?.id) return;
    if (inspectionRunRef.current) {
      setValidation('Já existe uma inspeção em execução. Aguarde finalizar para iniciar outra.');
      return;
    }

    const node = nodesRef.current.find((item) => String(item.id) === String(nodeId));
    if (!node) return;
    if (inspectionNodeStatesRef.current?.[String(node.id)] === 'blocked') {
      setValidation('Este comando está fora do ramo escolhido pela última Condição. Continue pelo caminho indicado.');
      return;
    }

    const storedContext = inspectionContextRef.current || createInspectionSeed(selectedRoute);
    const invalidatedNodeIds = downstreamNodeIdsIncludingSelf(node.id, edgesRef.current);
    const previousContext = cloneFlow(storedContext);
    previousContext.outputs = Object.fromEntries(
      Object.entries(previousContext.outputs || {}).filter(([outputNodeId]) => !invalidatedNodeIds.has(String(outputNodeId)))
    );
    previousContext.steps = (Array.isArray(previousContext.steps) ? previousContext.steps : [])
      .filter((step) => !invalidatedNodeIds.has(String(step?.node_id)));
    const ancestorNodeIds = upstreamNodeIds(node.id, edgesRef.current);
    const rollbackStep = [...previousContext.steps]
      .reverse()
      .find((step) => (
        String(step?.status || '').toLowerCase() === 'success'
        && step?.context?.task
        && ancestorNodeIds.has(String(step?.node_id))
      ));
    if (rollbackStep?.context?.task) {
      previousContext.task = cloneFlow(rollbackStep.context.task);
      previousContext.linked_task = cloneFlow(rollbackStep.context.linked_task || null);
    } else {
      const cleanSeed = createInspectionSeed(selectedRoute);
      previousContext.task = cleanSeed.task;
      previousContext.linked_task = null;
    }
    const hasCachedOutputs = previousContext?.outputs && Object.keys(previousContext.outputs).length > 0;
    const hasCachedTask = Boolean(
      Number(previousContext?.task?.id || 0) > 0
      || String(previousContext?.task?.task_key || '').trim()
      || Object.keys(previousContext?.task?.payload || {}).length
    );
    const hasSuccessfulSteps = Array.isArray(previousContext?.steps)
      && previousContext.steps.some((step) => String(step?.status || '').toLowerCase() === 'success');
    const selectedArrayItemIndexText = String(inspectionArrayItemIndex || '').trim();
    let selectedArrayItemIndex = null;
    if (selectedArrayItemIndexText) {
      if (!/^\d+$/.test(selectedArrayItemIndexText)) {
        setValidation('Item do array inválido. Informe um índice inteiro maior ou igual a zero.');
        return;
      }
      selectedArrayItemIndex = Number.parseInt(selectedArrayItemIndexText, 10);
    }
    const shouldReuseContext = selectedArrayItemIndex === null
      && Boolean(hasCachedOutputs || hasCachedTask || hasSuccessfulSteps);
    const taskIdFromCache = Number(previousContext?.task?.id || 0);

    const inspectionSession = inspectionSessionRef.current;
    if (
      !inspectionSession?.inspectionId
      || !Number.isInteger(Number(inspectionSession?.executionId))
      || Number(inspectionSession.executionId) <= 0
    ) {
      setValidation('A sessão de inspeção não está ativa. Saia e entre novamente no modo Inspeção.');
      return;
    }
    const inspectionId = inspectionSession.inspectionId;
    const streamUrl = `/api/autonomous-routing/inspection/${encodeURIComponent(inspectionId)}/events?route_id=${encodeURIComponent(selectedRoute.id)}`;
    const stream = new window.EventSource(streamUrl);
    let terminalFallbackTimeout = null;
    const runHandle = {
      inspectionId,
      stream,
      closed: false,
      close: () => {
        if (runHandle.closed) return;
        runHandle.closed = true;
        if (terminalFallbackTimeout) {
          window.clearTimeout(terminalFallbackTimeout);
          terminalFallbackTimeout = null;
        }
        try {
          stream.close();
        } catch {
          // noop
        }
        if (inspectionRunRef.current === runHandle) {
          inspectionRunRef.current = null;
          setInspectionRunning(false);
        }
      }
    };
    let streamReady = false;
    let readyTimeout = null;
    const setInspectionFailureStep = (message, payload = {}, options = {}) => {
      const fallbackNode = nodesRef.current.find((item) => String(item.id) === String(payload.node_id || node.id)) || node;
      const fallbackStatus = payload.status || payload.execution_status || 'error';
      const failureMessage = String(message || payload.error_message || 'Inspeção finalizou com erro.').trim() || 'Inspeção finalizou com erro.';
      const nextPayload = {
        ...payload,
        event: payload.event || 'failed',
        node_id: payload.node_id || fallbackNode?.id || node.id,
        command_type: payload.command_type || fallbackNode?.data?.commandType || node.data?.commandType || 'api',
        status: fallbackStatus,
        execution_status: payload.execution_status || fallbackStatus,
        error_message: payload.error_message || failureMessage,
        input: payload.input || {
          inspection_id: inspectionId,
          node_id: payload.node_id || fallbackNode?.id || node.id
        },
        output: payload.output ?? {
          status: fallbackStatus,
          message: failureMessage
        }
      };

      setInspectionStep((current) => {
        const currentStatus = String(current?.status || '').toLowerCase();
        if (!options.force && currentStatus && currentStatus !== 'pending' && currentStatus !== 'connecting') {
          return current;
        }
        return inspectionStepFromEvent(nextPayload, fallbackNode, {
          taskFileFields: inspectionTaskFileFields
        });
      });
    };
    const markStreamReady = () => {
      if (streamReady || runHandle.closed) return;
      streamReady = true;
      if (readyTimeout) {
        window.clearTimeout(readyTimeout);
        readyTimeout = null;
      }
    };

    stream.onopen = () => {
      markStreamReady();
    };

    inspectionRunRef.current = runHandle;
    setInspectionRunning(true);
    setInspectionEvents([]);
    setInspectionNodeStates((current) => {
      const base = shouldReuseContext
        ? { ...(current || seedInspectionNodeStates(nodesRef.current)) }
        : seedInspectionNodeStates(nodesRef.current);
      invalidatedNodeIds.forEach((invalidatedNodeId) => {
        base[String(invalidatedNodeId)] = 'waiting';
      });
      base[String(node.id)] = 'executing';
      return base;
    });
    commitInspectionContext(shouldReuseContext
      ? cloneFlow(previousContext)
      : createInspectionSeed(selectedRoute));
    setInspectionStep({
      nodeId: node.id,
      title: node.data?.title || commandForType(node.data?.commandType).label,
      commandType: node.data?.commandType || 'api',
      input: prettyInspectJson({ status: 'connecting', inspection_id: inspectionId }),
      output: prettyInspectJson({ status: 'pending', message: 'Aguardando conexão com stream de inspeção...' }),
      at: new Date().toLocaleTimeString('pt-BR')
    });
    setValidation(`Iniciando inspeção real para "${node.data?.title || node.id}"...`);

    const updateFromEvent = (eventName, payload = {}) => {
      setInspectionEvents((current) => current.concat({
        event: eventName,
        ts: payload.ts || new Date().toISOString(),
        node_id: payload.node_id || null,
        status: payload.status || null,
        duration_ms: payload.duration_ms || null,
        error_message: payload.error_message || null
      }));

      if (eventName === 'node_started' || eventName === 'node_finished' || eventName === 'node_error') {
        const latestNode = nodesRef.current.find((item) => String(item.id) === String(payload.node_id)) || node;
        const step = inspectionStepFromEvent({ ...payload, event: eventName }, latestNode, {
          taskFileFields: inspectionTaskFileFields
        });
        setInspectionStep(step);
      }

      if (eventName === 'node_started' && payload.node_id) {
        setInspectionNodeStates((current) => ({
          ...(current || {}),
          [String(payload.node_id)]: 'executing'
        }));
      }

      if (eventName === 'started') {
        commitInspectionContext((current) => ({
          ...(current || createInspectionSeed(selectedRoute)),
          task: {
            ...(current?.task || createInspectionSeed(selectedRoute).task),
            id: payload.task_id || null,
            task_key: payload.task_key || current?.task?.task_key || ''
          },
          outputs: shouldReuseContext
            ? (current?.outputs || {})
            : {},
          steps: shouldReuseContext
            ? (Array.isArray(current?.steps) ? current.steps : [])
            : []
        }));
      }

      if (eventName === 'node_finished' || eventName === 'node_error') {
        if (payload.node_id) {
          setInspectionNodeStates((current) => ({
            ...(current || {}),
            [String(payload.node_id)]: eventName === 'node_error' ? 'error' : 'success'
          }));
        }
        commitInspectionContext((current) => {
          const next = cloneFlow(current || createInspectionSeed(selectedRoute));
          const completedNodeId = String(payload.node_id || '');
          const invalidatedAfterCompletion = completedNodeId
            ? downstreamNodeIdsIncludingSelf(completedNodeId, edgesRef.current)
            : new Set();
          next.outputs = Object.fromEntries(
            Object.entries(next.outputs || {}).filter(([outputNodeId]) => (
              !invalidatedAfterCompletion.has(String(outputNodeId))
              || String(outputNodeId) === completedNodeId
            ))
          );
          if (payload.context?.task) {
            next.task = payload.context.task;
          }
          if (Object.prototype.hasOwnProperty.call(payload.context || {}, 'linked_task')) {
            next.linked_task = payload.context.linked_task || null;
          }
          if (payload.output !== undefined && payload.node_id) {
            next.outputs = {
              ...(next.outputs || {}),
              [payload.node_id]: payload.runtime_output ?? payload.output
            };
          }
          const nextStep = {
            node_id: payload.node_id,
            command_type: payload.command_type,
            at: payload.finished_at || payload.ts || new Date().toISOString(),
            started_at: payload.started_at || null,
            finished_at: payload.finished_at || payload.ts || new Date().toISOString(),
            duration_ms: Number(payload.duration_ms) || null,
            status: eventName === 'node_error' ? 'error' : 'success',
            run_id: inspectionId,
            input: cloneFlow(payload.input ?? {}),
            output: cloneFlow(payload.output ?? {}),
            runtime_output: cloneFlow(payload.runtime_output ?? payload.output ?? {}),
            error_message: payload.error_message || null,
            error_stack: payload.error_stack || null,
            context: payload.context ? cloneFlow(payload.context) : null
          };
          const steps = (Array.isArray(next.steps) ? next.steps : [])
            .filter((item) => (
              !invalidatedAfterCompletion.has(String(item?.node_id))
              || String(item?.node_id) === completedNodeId
            ));
          const existingIndex = steps.findIndex((item) => String(item.node_id) === String(nextStep.node_id));
          if (existingIndex >= 0) {
            steps[existingIndex] = nextStep;
            next.steps = steps;
          } else {
            next.steps = steps.concat(nextStep);
          }
          return next;
        });
        const completedNode = nodesRef.current.find((item) => String(item.id) === String(payload.node_id));
        const conditionOutput = payload.runtime_output ?? payload.output;
        if (
          eventName === 'node_finished'
          && completedNode?.data?.commandType === 'condition'
          && conditionOutput
        ) {
          const branch = inspectionConditionBranches(
            completedNode,
            conditionOutput,
            edgesRef.current
          );
          setInspectionNodeStates((current) => {
            const next = { ...(current || {}) };
            branch.selectedNodeIds.forEach((selectedNodeId) => {
              if (!['success', 'error', 'executing'].includes(next[selectedNodeId])) {
                next[selectedNodeId] = 'waiting';
              }
            });
            branch.blockedNodeIds.forEach((blockedNodeId) => {
              next[blockedNodeId] = 'blocked';
            });
            return next;
          });
          setValidation(
            normalizeConditionDecisionMode(completedNode?.data?.config?.decisionMode) === 'multiple'
              ? `Decisão selecionada: ${conditionOutput?.selected_label || conditionOutput?.selectedLabel || branch.expectedOutput}.`
              : (conditionOutput.passed
                ? 'Condição verdadeira. Continue pelo ramo de Sucesso.'
                : 'Condição falsa. Continue pelo ramo de Insucesso.')
          );
        }
        learnSchemasFromInspectionPayload(payload);
      }

      if (eventName === 'finished') {
        setInspectionNodeStates((current) => ({ ...(current || {}) }));
        if (node.data?.commandType !== 'condition') {
          setValidation(`Inspeção concluída em "${node.data?.title || node.id}".`);
        }
        runHandle.close();
      }
      if (eventName === 'failed') {
        if (!payload.node_id) {
          setInspectionFailureStep(payload.error_message || 'Inspeção finalizou com erro.', payload);
          setInspectionNodeStates((current) => ({
            ...(current || {}),
            [String(node.id)]: 'error'
          }));
        }
        setInspectionNodeStates((current) => markIgnoredInspectionNodeStates(current, nodesRef.current));
        setValidation(payload.error_message || 'Inspeção finalizou com erro.');
        runHandle.close();
      }
    };

    const bindEvent = (eventName) => {
      stream.addEventListener(eventName, (event) => {
        let payload = {};
        try {
          payload = JSON.parse(event.data || '{}');
        } catch {
          payload = {};
        }
        if (eventName === 'ready') {
          markStreamReady();
          return;
        }
        updateFromEvent(eventName, payload);
      });
    };

    ['ready', 'started', 'node_started', 'node_finished', 'node_error', 'finished', 'failed'].forEach(bindEvent);

    stream.onerror = () => {
      if (runHandle.closed) return;
      const message = 'Falha de conexão no stream da inspeção.';
      setInspectionFailureStep(message, { event: 'failed' });
      setValidation(message);
      runHandle.close();
    };

    readyTimeout = window.setTimeout(() => {
      if (streamReady || runHandle.closed) return;
      const message = 'Timeout ao conectar no stream da inspeção.';
      setInspectionFailureStep(message, { event: 'failed' });
      setValidation(message);
      runHandle.close();
    }, 8000);

    try {
      while (!streamReady) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => window.setTimeout(resolve, 50));
        if (runHandle.closed) return;
      }
      const runPayload = {
        inspection_id: inspectionId,
        execution_id: Number(inspectionSession.executionId),
        session_mode: true,
        node_id: node.id,
        nodeId: node.id,
        execution_mode: 'direct'
      };
      if (selectedArrayItemIndex !== null) {
        runPayload.array_item_index = selectedArrayItemIndex;
      }
      if (shouldReuseContext) {
        runPayload.reuse_context = true;
        runPayload.inspection_context = {
          context_version: INSPECTION_CONTEXT_VERSION,
          task: previousContext?.task || null,
          linked_task: previousContext?.linked_task || null,
          outputs: previousContext?.outputs || {},
          steps: Array.isArray(previousContext?.steps) ? previousContext.steps : []
        };
        if (Number.isInteger(taskIdFromCache) && taskIdFromCache > 0) {
          runPayload.task_id = taskIdFromCache;
        }
      }

      const runResult = await api(`/api/autonomous-routing/${selectedRoute.id}/inspection/run`, {
        method: 'POST',
        body: JSON.stringify(runPayload)
      });
      if (runHandle.closed) return;
      const executionStatus = String(runResult?.data?.execution_status || '').toLowerCase();
      if (executionStatus === 'error' || executionStatus === 'failed') {
        const message = runResult?.data?.error_message || 'A inspeção terminou com erro no servidor.';
        setInspectionFailureStep(message, {
          event: 'failed',
          execution_status: executionStatus,
          node_id: node.id
        }, { force: true });
        setInspectionNodeStates((current) => ({
          ...(current || {}),
          [String(node.id)]: 'error'
        }));
        setValidation(message);
        runHandle.close();
        return;
      }

      setValidation(`Finalizando inspeção em "${node.data?.title || node.id}"...`);
      terminalFallbackTimeout = window.setTimeout(() => {
        if (runHandle.closed || inspectionRunRef.current !== runHandle) return;
        setInspectionNodeStates((current) => ({
          ...(current || {}),
          [String(node.id)]: 'success'
        }));
        setValidation(`Inspeção concluída em "${node.data?.title || node.id}".`);
        runHandle.close();
      }, 2000);
    } catch (error) {
      const message = error.message || 'Falha ao iniciar inspeção.';
      setInspectionFailureStep(message, { event: 'failed' }, { force: true });
      setInspectionNodeStates((current) => ({
        ...(current || {}),
        [String(node.id)]: 'error'
      }));
      setValidation(message);
      runHandle.close();
    } finally {
      if (readyTimeout) window.clearTimeout(readyTimeout);
    }
  }, [
    commitInspectionContext,
    inspectionArrayItemIndex,
    inspectionTaskFileFields,
    learnSchemasFromInspectionPayload,
    selectedRoute
  ]);

  const createInspectionSession = useCallback(async () => {
    if (!selectedRoute?.id) {
      throw new Error('Selecione um roteamento antes de iniciar a inspeção.');
    }
    const inspectionId = createInspectionRunId();
    const response = await api(`/api/autonomous-routing/${selectedRoute.id}/inspection/session`, {
      method: 'POST',
      body: JSON.stringify({ inspection_id: inspectionId })
    });
    const executionId = Number(response?.data?.execution_id || 0);
    if (!Number.isInteger(executionId) || executionId <= 0) {
      throw new Error('O servidor não criou a execução da inspeção.');
    }
    const session = {
      inspectionId: response?.data?.inspection_id || inspectionId,
      executionId,
      routeId: selectedRoute.id
    };
    inspectionSessionRef.current = session;
    return session;
  }, [selectedRoute]);

  const finishInspectionSession = useCallback(async (reason = 'user_exit') => {
    const session = inspectionSessionRef.current;
    inspectionSessionRef.current = null;
    if (!session?.inspectionId || !session?.executionId || !session?.routeId) return null;
    try {
      const response = await api(
        `/api/autonomous-routing/${session.routeId}/inspection/session/${encodeURIComponent(session.inspectionId)}`,
        {
          method: 'DELETE',
          body: JSON.stringify({ execution_id: session.executionId, reason })
        }
      );
      window.dispatchEvent(new CustomEvent('tdemand:routing-executions-refresh', {
        detail: { routeId: session.routeId, executionId: session.executionId }
      }));
      return response?.data || null;
    } catch (error) {
      setValidation(error.message || 'Não foi possível encerrar a execução da inspeção.');
      return null;
    }
  }, []);

  const closeInspectionMode = useCallback(async () => {
    if (inspectionRunRef.current) {
      inspectionRunRef.current.close();
    }
    closeConnectionCommandMenu();
    setInspectionMode(false);
    setInspectionStep(null);
    setInspectionRunning(false);
    setInspectionEvents([]);
    setInspectionArrayItemIndex('');
    setInspectionNodeStates({});
    commitInspectionContext(createInspectionSeed(selectedRoute));
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => centerDrawflow({ preserveZoom: true }));
    });
    setValidation('Modo Inspeção encerrado.');
    await finishInspectionSession('user_exit');
  }, [centerDrawflow, closeConnectionCommandMenu, commitInspectionContext, finishInspectionSession, selectedRoute]);

  const resetLearnedVariables = useCallback(async (message = 'Variáveis obtidas redefinidas.') => {
    if (inspectionRunRef.current) {
      inspectionRunRef.current.close();
    }
    await finishInspectionSession('restart');
    const nextNodes = clearLearnedSchemasFromFlowNodes(nodesRef.current);
    if (nextNodes !== nodesRef.current) {
      markDirty();
      nodesRef.current = nextNodes;
      setNodes(nextNodes);
    }
    commitInspectionContext(createInspectionSeed(selectedRoute));
    setInspectionStep(null);
    setInspectionRunning(false);
    setInspectionEvents([]);
    setInspectionArrayItemIndex('');
    setInspectionNodeStates(seedInspectionNodeStates(nodesRef.current));
    window.dispatchEvent(new CustomEvent('tdemand:tested-apis-refresh'));
    try {
      const session = await createInspectionSession();
      setValidation(`${message} Nova execução ${session.executionId} iniciada.`);
    } catch (error) {
      setInspectionMode(false);
      setInspectionNodeStates({});
      setValidation(error.message || 'Não foi possível reiniciar a inspeção.');
    }
  }, [commitInspectionContext, createInspectionSession, finishInspectionSession, markDirty, selectedRoute]);

  const openInspectionMode = useCallback(async () => {
    if (inspectionRunRef.current) {
      inspectionRunRef.current.close();
    }
    if (selectedRoute?.active === true) {
      setValidation('Modo Inspeção disponível apenas para roteamentos inativos. Desative o roteamento antes de testar.');
      return;
    }
    setSelectedNode(null);
    setNodeContextMenu(null);
    closeConnectionCommandMenu();
    setConnectionDeleteContext(null);
    commitInspectionContext(createInspectionSeed(selectedRoute));
      setInspectionStep(null);
      setInspectionRunning(false);
      setInspectionEvents([]);
      setInspectionArrayItemIndex('');
      setInspectionNodeStates(seedInspectionNodeStates(nodesRef.current));
    setValidation('Criando uma nova execução para a inspeção...');
    try {
      const session = await createInspectionSession();
      setInspectionMode(true);
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => centerDrawflow({ preserveZoom: true }));
      });
      setValidation(`Modo Inspeção ativo na execução ${session.executionId}. Avance pelo Play de cada comando.`);
    } catch (error) {
      inspectionSessionRef.current = null;
      setInspectionMode(false);
      setInspectionNodeStates({});
      setValidation(error.message || 'Não foi possível iniciar a inspeção.');
    }
  }, [centerDrawflow, closeConnectionCommandMenu, commitInspectionContext, createInspectionSession, selectedRoute]);

  const toggleInspectionMode = useCallback(async () => {
    if (inspectionMode) {
      await closeInspectionMode();
      return;
    }
    await openInspectionMode();
  }, [closeInspectionMode, inspectionMode, openInspectionMode]);

  const resizeInspectionDrawer = useCallback((height, persist = false) => {
    const nextHeight = clampInspectionDrawerHeight(height);
    setInspectionDrawerHeight(nextHeight);
    if (persist) {
      localStorage.setItem('t-demand-route-inspection-height', String(nextHeight));
    }
  }, []);

  const onInspectionDrawerResizeStart = useCallback((event) => {
    if (event.button !== undefined && event.button !== 0) return;
    const startY = event.clientY;
    const startHeight = inspectionDrawerHeight;
    let nextHeight = startHeight;

    const resize = (moveEvent) => {
      nextHeight = startHeight - (moveEvent.clientY - startY);
      resizeInspectionDrawer(nextHeight);
    };

    const stop = () => {
      document.body.classList.remove('route-inspection-resizing');
      window.removeEventListener('pointermove', resize);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
      resizeInspectionDrawer(nextHeight, true);
    };

    document.body.classList.add('route-inspection-resizing');
    event.currentTarget.setPointerCapture?.(event.pointerId);
    window.addEventListener('pointermove', resize);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
    event.preventDefault();
  }, [inspectionDrawerHeight, resizeInspectionDrawer]);

  const applyRoute = useCallback((route) => {
    if (inspectionRunRef.current) {
      inspectionRunRef.current.close();
    }
    if (inspectionSessionRef.current) {
      finishInspectionSession('route_change');
      setInspectionMode(false);
      setInspectionNodeStates({});
    }
    hydratingRouteRef.current = true;
    flowDirtyRef.current = false;
    suppressDirtyUntilRef.current = Date.now() + 1600;
    const nextNodes = routeNodes(route);
    const nextEdges = routeEdges(route);
    const nextRouteId = route?.id ?? null;
    hydratedRouteIdRef.current = nextRouteId == null ? null : String(nextRouteId);
    setSelectedRouteId(nextRouteId);
    persistSelectedRouteId(nextRouteId);
    setSelectedNode(null);
    setNodeContextMenu(null);
    closeConnectionCommandMenu();
    nodesRef.current = nextNodes;
    edgesRef.current = nextEdges;
    setNodes(nextNodes);
    setEdges(nextEdges);
    commitInspectionContext(createInspectionSeed(route));
    setInspectionStep(null);
    renderDrawflow(nextNodes, nextEdges, { comfortable: true });
    setValidation(route
      ? `Roteamento "${route.name}" carregado.`
      : 'Nenhum roteamento selecionado.');
    window.requestAnimationFrame(() => {
      hydratingRouteRef.current = false;
    });
  }, [closeConnectionCommandMenu, commitInspectionContext, finishInspectionSession, renderDrawflow]);

  const createRoute = async (event) => {
    event?.preventDefault?.();
    if (!canEdit) {
      setRouteNotice('Sua role permite apenas visualização.');
      return;
    }
    const name = newRouteName.trim();
    if (!name) {
      setRouteNotice('Informe o nome do roteamento.');
      return;
    }
    if (!newRouteDemandId) {
      setRouteNotice('Selecione a demanda do roteamento.');
      return;
    }

    setCreatingRoute(true);
    setRouteNotice('');
    try {
      const demand = demands.find((item) => String(item.id) === String(newRouteDemandId));
      const routeSeed = {
        demand_id: newRouteDemandId,
        demand_name: demand?.name,
        demand_protocol: demand?.protocol,
        demand_process: demand?.process,
        flow_nodes: initialNodes,
        flow_edges: initialEdges
      };
      const initialFlow = normalizeFlowForRoute(routeSeed);
      const result = await api('/api/autonomous-routing', {
        method: 'POST',
        body: JSON.stringify({
          name,
          demand_id: newRouteDemandId,
          flow_nodes: initialFlow.nodes,
          flow_edges: initialFlow.edges
        })
      });
      setRoutes((current) => [result.data, ...current]);
      setRouteCreateOpen(false);
      setNewRouteName('');
      setNewRouteDemandId('');
      applyRoute(result.data);
      window.dispatchEvent(new CustomEvent('tdemand:routing-created', {
        detail: { route: result.data }
      }));
    } catch (error) {
      setRouteNotice(error.message);
    } finally {
      setCreatingRoute(false);
    }
  };

  const openDuplicateRouteModal = useCallback((route = null) => {
    const sourceRoute = route?.id ? route : selectedRoute;
    if (!canEdit) {
      setDuplicateRouteNotice('Sua role permite apenas visualização.');
      return;
    }
    if (!sourceRoute) {
      setValidation('Selecione um roteamento antes de duplicar.');
      return;
    }
    if (!demands.length) {
      setValidation('Crie uma demanda antes de duplicar um roteamento.');
      return;
    }

    setDuplicateRouteNotice('');
    setDuplicateSourceRouteId(String(sourceRoute.id));
    setDuplicateRouteName(`${sourceRoute.name || 'Roteamento'} - copia`);
    setDuplicateRouteDemandId(sourceRoute?.demand_id ? String(sourceRoute.demand_id) : String(demands[0]?.id || ''));
    setRouteDuplicateOpen(true);
  }, [canEdit, demands, selectedRoute]);

  const duplicateRoute = async (event) => {
    event?.preventDefault?.();
    if (!canEdit) {
      setDuplicateRouteNotice('Sua role permite apenas visualização.');
      return;
    }
    const sourceRouteId = String(duplicateSourceRouteId || selectedRouteId || '');
    const sourceRoute = routes.find((item) => String(item.id) === sourceRouteId) || selectedRoute;
    if (!sourceRoute?.id) {
      setDuplicateRouteNotice('Selecione um roteamento para duplicar.');
      return;
    }

    const name = duplicateRouteName.trim();
    if (!name) {
      setDuplicateRouteNotice('Informe o nome do novo roteamento.');
      return;
    }
    if (!duplicateRouteDemandId) {
      setDuplicateRouteNotice('Selecione a demanda do novo roteamento.');
      return;
    }

    setDuplicatingRoute(true);
    setDuplicateRouteNotice('');
    try {
      const demand = demands.find((item) => String(item.id) === String(duplicateRouteDemandId));
      const sourceFlowNodes = String(sourceRoute.id) === String(selectedRouteId)
        ? nodesRef.current
        : routeNodes(sourceRoute);
      const sourceFlowEdges = String(sourceRoute.id) === String(selectedRouteId)
        ? edgesRef.current
        : routeEdges(sourceRoute);
      const routeSeed = {
        ...sourceRoute,
        demand_id: duplicateRouteDemandId,
        demand_name: demand?.name,
        demand_protocol: demand?.protocol,
        demand_process: demand?.process,
        flow_nodes: sourceFlowNodes,
        flow_edges: sourceFlowEdges
      };
      const duplicatedFlow = normalizeFlowForRoute(routeSeed);
      const result = await api(`/api/autonomous-routing/${encodeURIComponent(sourceRoute.id)}/duplicate`, {
        method: 'POST',
        body: JSON.stringify({
          name,
          demand_id: duplicateRouteDemandId,
          flow_nodes: duplicatedFlow.nodes,
          flow_edges: duplicatedFlow.edges
        })
      });

      setRoutes((current) => [result.data, ...current]);
      setRouteDuplicateOpen(false);
      setDuplicateSourceRouteId('');
      setDuplicateRouteName('');
      setDuplicateRouteDemandId('');
      applyRoute(result.data);
    } catch (error) {
      setDuplicateRouteNotice(error.message);
    } finally {
      setDuplicatingRoute(false);
    }
  };

  const applyIncomingRoutes = useCallback((nextRoutes = []) => {
    if (inspectionModeRef.current && !nextRoutes.length && nodesRef.current.length) {
      return;
    }
    setRoutesLoaded(true);
    setRoutes(nextRoutes);
    if (!nextRoutes.length) {
      applyRoute(null);
      return;
    }
    const selectedStillExists = selectedRouteId
      ? nextRoutes.some((route) => String(route.id) === String(selectedRouteId))
      : false;
    if (!selectedRouteId && nextRoutes.length) {
      applyRoute(nextRoutes[0]);
      return;
    }
    if (selectedRouteId && !selectedStillExists) {
      applyRoute(nextRoutes[0] || null);
      return;
    }
    if (selectedRouteId && selectedStillExists) {
      const selectedFromPayload = nextRoutes.find((route) => String(route.id) === String(selectedRouteId)) || null;
      const alreadyHydrated = String(hydratedRouteIdRef.current || '') === String(selectedRouteId);
      if (!alreadyHydrated && selectedFromPayload) {
        applyRoute(selectedFromPayload);
      }
    }
  }, [applyRoute, selectedRouteId]);

  const openRouteCreateModal = useCallback(() => {
    if (!canEdit) {
      setRouteNotice('Sua role permite apenas visualização.');
      return;
    }
    setRouteNotice(demands.length ? '' : 'Crie uma demanda antes de criar um roteamento.');
    setNewRouteName('');
    setNewRouteDemandId(selectedRoute?.demand_id
      ? String(selectedRoute.demand_id)
      : String(demands[0]?.id || ''));
    setRouteCreateOpen(true);
  }, [canEdit, demands, selectedRoute]);

  useEffect(() => {
    const hydrateRoutesFromSnapshot = () => {
      const snapshot = window.TDEMAND_ROUTING_SNAPSHOT;
      if (!snapshot || !Array.isArray(snapshot.routes)) return;
      applyIncomingRoutes(snapshot.routes);
    };

    const receiveRoutes = (event) => {
      applyIncomingRoutes(event.detail?.routes || []);
    };
    const receivePermissions = (event) => {
      setCanEdit(event.detail?.canEdit !== false);
      setHasCortex(normalizeBooleanFlag(event.detail?.hasCortex));
      if (typeof event.detail?.hasChatIntegration !== 'undefined') {
        setHasChatIntegration(normalizeBooleanFlag(event.detail?.hasChatIntegration));
      }
      if (typeof event.detail?.hasVoiceIntegration !== 'undefined') {
        setHasVoiceIntegration(normalizeBooleanFlag(event.detail?.hasVoiceIntegration));
      }
    };

    const handleDuplicateRouteRequest = () => openDuplicateRouteModal();
    const handleCreateRouteRequest = () => openRouteCreateModal();
    const handleOpenRouteRequest = (event) => {
      const requestedRoute = event.detail?.route;
      if (!requestedRoute?.id) return;
      const route = routes.find((item) => String(item.id) === String(requestedRoute.id)) || requestedRoute;
      applyRoute(route);
    };
    const handleResetVariablesRequest = (event) => {
      const targetRouteId = String(event?.detail?.routeId || '').trim();
      if (targetRouteId && String(selectedRouteId || '').trim() !== targetRouteId) {
        return;
      }
      resetLearnedVariables('Variáveis obtidas redefinidas.');
    };

    window.addEventListener('tdemand:duplicate-route', handleDuplicateRouteRequest);
    window.addEventListener('tdemand:routing-create-request', handleCreateRouteRequest);
    window.addEventListener('tdemand:routing-open-route', handleOpenRouteRequest);
    window.addEventListener('tdemand:inspection-toggle', toggleInspectionMode);
    window.addEventListener('tdemand:routing-reset-variables', handleResetVariablesRequest);
    window.addEventListener('tdemand:routing-data', receiveRoutes);
    window.addEventListener('tdemand:permissions', receivePermissions);
    hydrateRoutesFromSnapshot();
    window.dispatchEvent(new CustomEvent('tdemand:routing-sync-request'));
    setHasCortex(normalizeBooleanFlag(window.TDEMAND_HAS_CORTEX));
    setHasChatIntegration(normalizeBooleanFlag(window.TDEMAND_HAS_CHAT_INTEGRATION, true));
    setHasVoiceIntegration(normalizeBooleanFlag(window.TDEMAND_HAS_VOICE_INTEGRATION, true));
    return () => {
      window.removeEventListener('tdemand:duplicate-route', handleDuplicateRouteRequest);
      window.removeEventListener('tdemand:routing-create-request', handleCreateRouteRequest);
      window.removeEventListener('tdemand:routing-open-route', handleOpenRouteRequest);
      window.removeEventListener('tdemand:inspection-toggle', toggleInspectionMode);
      window.removeEventListener('tdemand:routing-reset-variables', handleResetVariablesRequest);
      window.removeEventListener('tdemand:routing-data', receiveRoutes);
      window.removeEventListener('tdemand:permissions', receivePermissions);
    };
  }, [applyIncomingRoutes, applyRoute, openDuplicateRouteModal, openRouteCreateModal, resetLearnedVariables, routes, selectedRouteId, toggleInspectionMode]);

  useEffect(() => {
    let mounted = true;
    const loadIntegrationStatus = async () => {
      try {
        const [chatResult, voiceResult] = await Promise.all([
          api('/api/chat/integration/status'),
          api('/api/voice/integration/status')
        ]);
        if (!mounted) return;
        const nextChat = normalizeBooleanFlag(chatResult?.data?.configured, false);
        const nextVoice = normalizeBooleanFlag(voiceResult?.data?.configured, false);
        setHasChatIntegration(nextChat);
        setHasVoiceIntegration(nextVoice);
        window.TDEMAND_HAS_CHAT_INTEGRATION = nextChat;
        window.TDEMAND_HAS_VOICE_INTEGRATION = nextVoice;
      } catch {
        // Keep current flags when status endpoints are temporarily unavailable.
      }
    };
    loadIntegrationStatus();
    window.addEventListener('tdemand:integration-config-updated', loadIntegrationStatus);
    return () => {
      mounted = false;
      window.removeEventListener('tdemand:integration-config-updated', loadIntegrationStatus);
    };
  }, []);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('tdemand:routing-selected', {
      detail: { route: selectedRoute }
    }));
  }, [selectedRoute]);

  useEffect(() => {
    if (inspectionMode && selectedRoute?.active === true) {
      closeInspectionMode().then(() => {
        setValidation('Modo Inspeção encerrado porque o roteamento foi ativado.');
      });
    }
  }, [closeInspectionMode, inspectionMode, selectedRoute?.active]);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('tdemand:inspection-state', {
      detail: { active: inspectionMode }
    }));
  }, [inspectionMode]);

  useEffect(() => {
    let mounted = true;
    const loadSupportData = async () => {
      try {
        const [apisResult, demandsResult] = await Promise.all([
          api('/api/tdemand-apis/tested'),
          api('/api/demands')
        ]);
        if (mounted) {
          setTestedApis(apisResult.data || []);
          setDemands(demandsResult.data || []);
        }
      } catch (error) {
        if (mounted) {
          setValidation(error.message);
        }
      }
    };

    loadSupportData();
    window.addEventListener('tdemand:tested-apis-refresh', loadSupportData);
    return () => {
      mounted = false;
      window.removeEventListener('tdemand:tested-apis-refresh', loadSupportData);
    };
  }, []);

  useEffect(() => {
    nodesRef.current = nodes;
    edgesRef.current = edges;

    if (!editorRef.current) return;
    if (syncingFromDrawflowRef.current) {
      syncingFromDrawflowRef.current = false;
      return;
    }

    renderDrawflow(nodes, edges);
  }, [edges, nodes, renderDrawflow]);

  useEffect(() => {
    if (!editorRef.current) return;
    refreshDrawflowNodeHtml();
  }, [inspectionContext.steps?.length, inspectionMode, inspectionNodeStates, inspectionStep?.nodeId, refreshDrawflowNodeHtml]);

  useEffect(() => {
    if (!drawflowRef.current || editorRef.current) return undefined;

    const editor = new Drawflow(drawflowRef.current);
    editor.zoom_max = 1.6;
    editor.zoom_min = 0.35;
    editor.zoom_value = 0.1;
    editor.reroute = false;
    editor.force_first_input = true;
    editor.start();
    if (drawflowRef.current?.classList?.contains('parent-drawflow')) {
      drawflowRef.current.classList.remove('drawflow-canvas');
      drawflowRef.current.classList.add('drawflow-canvas');
    }
    editor.editor_mode = canEdit ? 'edit' : 'view';
    editorRef.current = editor;

    const handleNodeMoved = (drawId) => {
      if (hydratingRouteRef.current || inspectionModeRef.current) return;
      const rfId = nodeIdByDrawIdRef.current.get(String(drawId));
      const drawNode = editor.drawflow?.drawflow?.Home?.data?.[drawId];
      if (!rfId || !drawNode) return;
      markDirty();
      syncingFromDrawflowRef.current = true;
      setNodes((current) => current.map((node) => (
        node.id === rfId
          ? { ...node, position: { x: Math.round(drawNode.pos_x), y: Math.round(drawNode.pos_y) } }
          : node
      )));
    };

    const handleConnectionCreated = ({ output_id: outputId, input_id: inputId, output_class: outputClass = 'output_1' }) => {
      if (hydratingRouteRef.current || inspectionModeRef.current) return;
      const source = nodeIdByDrawIdRef.current.get(String(outputId));
      const target = nodeIdByDrawIdRef.current.get(String(inputId));
      if (!source || !target) return;

      if (pendingConnectionRef.current && String(pendingConnectionRef.current.sourceNodeId) === String(source)) {
        pendingConnectionRef.current.created = true;
      }
      closeConnectionCommandMenu();

      const sourceNode = nodesRef.current.find((node) => String(node.id) === String(source));
      const sourceHandle = sourceNode?.data?.commandType === 'condition'
        ? conditionOutputHandleByClass(sourceNode, outputClass)
        : 'output_1';
      const label = edgeLabelForOutput(sourceNode || {}, sourceHandle, '');
      markDirty();
      syncingFromDrawflowRef.current = true;
      setEdges((current) => (
        current.some((edge) => edge.source === source && edge.target === target && edgeOutputHandle(edge, sourceNode || {}) === sourceHandle)
          ? current
          : current.concat(makeEdge(source, target, label, sourceHandle))
      ));
    };

    const handleConnectionRemoved = ({ output_id: outputId, input_id: inputId, output_class: outputClass = 'output_1' }) => {
      if (hydratingRouteRef.current || inspectionModeRef.current) return;
      const source = nodeIdByDrawIdRef.current.get(String(outputId));
      const target = nodeIdByDrawIdRef.current.get(String(inputId));
      if (!source || !target) return;
      const sourceNode = nodesRef.current.find((node) => String(node.id) === String(source));
      const sourceHandle = sourceNode?.data?.commandType === 'condition'
        ? conditionOutputHandleByClass(sourceNode, outputClass)
        : 'output_1';
      markDirty();
      syncingFromDrawflowRef.current = true;
      setEdges((current) => current.filter((edge) => !(
        edge.source === source
        && edge.target === target
        && edgeOutputHandle(edge, sourceNode || {}) === sourceHandle
      )));
    };

    const handleNodeRemoved = (drawId) => {
      if (hydratingRouteRef.current || inspectionModeRef.current) return;
      const rfId = nodeIdByDrawIdRef.current.get(String(drawId));
      if (!rfId) return;
      const node = nodesRef.current.find((item) => item.id === rfId);
      if (isStartNode(node)) {
        hydratingRouteRef.current = true;
        renderDrawflow(nodesRef.current, edgesRef.current);
        window.requestAnimationFrame(() => {
          hydratingRouteRef.current = false;
        });
        setValidation('O comando Início é obrigatório e não  pode ser removido.');
        return;
      }
      markDirty();
      syncingFromDrawflowRef.current = true;
      setNodes((current) => current.filter((node) => node.id !== rfId));
      setEdges((current) => current.filter((edge) => edge.source !== rfId && edge.target !== rfId));
      nodeIdByDrawIdRef.current.delete(String(drawId));
      drawIdByNodeIdRef.current.delete(String(rfId));
    };

    editor.on('nodeMoved', handleNodeMoved);
    editor.on('connectionCreated', handleConnectionCreated);
    editor.on('connectionRemoved', handleConnectionRemoved);
    editor.on('nodeRemoved', handleNodeRemoved);
    editor.on('zoom', () => {
      setViewportTick((value) => value + 1);
    });
    editor.on('translate', () => setViewportTick((value) => value + 1));

    renderDrawflow(nodesRef.current, edgesRef.current, { comfortable: true });
    return () => {
      editorRef.current = null;
      editor.clear();
    };
  }, [canEdit, closeConnectionCommandMenu, markDirty, renderDrawflow]);

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.editor_mode = canEdit && !inspectionMode ? 'edit' : 'view';
    }
    if (!canEdit || inspectionMode) {
      setNodeContextMenu(null);
      setConnectionDeleteContext(null);
    }
  }, [canEdit, inspectionMode]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia(COMPACT_PALETTE_MEDIA_QUERY);

    const syncCompactMode = () => {
      setCompactPaletteMode(media.matches);
      if (!media.matches) {
        setCommandsModalOpen(false);
      }
    };

    syncCompactMode();
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', syncCompactMode);
      return () => media.removeEventListener('change', syncCompactMode);
    }
    media.addListener(syncCompactMode);
    return () => media.removeListener(syncCompactMode);
  }, []);

  useEffect(() => {
    if (!connectionCommandMenu) return undefined;

    const handlePointerDown = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('.route-connection-command-menu')) return;
      closeConnectionCommandMenu();
    };

    const handleEscape = (event) => {
      if (event.key !== 'Escape') return;
      closeConnectionCommandMenu();
    };

    window.addEventListener('resize', closeConnectionCommandMenu);
    window.addEventListener('scroll', closeConnectionCommandMenu, true);
    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('resize', closeConnectionCommandMenu);
      window.removeEventListener('scroll', closeConnectionCommandMenu, true);
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [closeConnectionCommandMenu, connectionCommandMenu]);

  useEffect(() => {
    const canvas = drawflowRef.current;
    canvas?.querySelectorAll('.connection.is-context-target').forEach((connection) => {
      connection.classList.remove('is-context-target');
    });
    if (!connectionDeleteContext) return undefined;

    const outputId = drawIdByNodeIdRef.current.get(String(connectionDeleteContext.source));
    const inputId = drawIdByNodeIdRef.current.get(String(connectionDeleteContext.target));
    const sourceNode = nodesRef.current.find((node) => String(node.id) === String(connectionDeleteContext.source));
    const outputClass = sourceNode?.data?.commandType === 'condition'
      ? conditionOutputClassByHandle(sourceNode, connectionDeleteContext.sourceHandle)
      : 'output_1';
    const selector = outputId && inputId
      ? `.connection.node_out_node-${CSS.escape(String(outputId))}.node_in_node-${CSS.escape(String(inputId))}.${CSS.escape(outputClass)}`
      : '';
    const connection = selector ? canvas?.querySelector(selector) : null;
    connection?.classList.add('is-context-target');

    const dismiss = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('.connection-delete-context-button')) return;
      setConnectionDeleteContext(null);
    };
    const dismissOnEscape = (event) => {
      if (event.key === 'Escape') setConnectionDeleteContext(null);
    };

    document.addEventListener('pointerdown', dismiss, true);
    document.addEventListener('keydown', dismissOnEscape);
    window.addEventListener('resize', dismiss);
    window.addEventListener('scroll', dismiss, true);
    return () => {
      connection?.classList.remove('is-context-target');
      document.removeEventListener('pointerdown', dismiss, true);
      document.removeEventListener('keydown', dismissOnEscape);
      window.removeEventListener('resize', dismiss);
      window.removeEventListener('scroll', dismiss, true);
    };
  }, [connectionDeleteContext]);

  useEffect(() => {
    const canvas = drawflowRef.current;
    const stage = stageRef.current;
    if (!canvas) return undefined;

    let timer = 0;
    let frame = 0;

    const redrawForViewport = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        if (frame) window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(() => {
          const editor = editorRef.current;
          if (!editor) return;
          const rect = canvas.getBoundingClientRect();
          if (rect.width < 24 || rect.height < 24) return;
          renderDrawflow(
            nodesRef.current,
            edgesRef.current,
            !inspectionModeRef.current ? { preserveZoom: true } : false
          );
        });
      }, 90);
    };

    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => redrawForViewport())
      : null;

    observer?.observe(canvas);
    if (stage && stage !== canvas) observer?.observe(stage);

    window.addEventListener('resize', redrawForViewport);
    window.addEventListener('orientationchange', redrawForViewport);

    return () => {
      window.removeEventListener('resize', redrawForViewport);
      window.removeEventListener('orientationchange', redrawForViewport);
      if (observer) observer.disconnect();
      window.clearTimeout(timer);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [renderDrawflow]);

  useEffect(() => {
    const container = drawflowRef.current;
    if (!container) return undefined;

    const handlePointerDown = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        drawflowHotkeysActiveRef.current = false;
        return;
      }
      drawflowHotkeysActiveRef.current = container.contains(target);
    };

    const handleWindowBlur = () => {
      drawflowHotkeysActiveRef.current = false;
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    window.addEventListener('blur', handleWindowBlur);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, []);

  useEffect(() => {
    const isEditableTarget = (target) => (
      target instanceof Element
      && Boolean(target.closest('input, textarea, select, [contenteditable="true"], [role="textbox"]'))
    );

    const onKeyDown = (event) => {
      if (!drawflowHotkeysActiveRef.current) return;
      if (!selectedRouteId || !editorRef.current) return;
      if (!(event.ctrlKey || event.metaKey) || event.altKey) return;
      if (isEditableTarget(event.target)) return;

      const key = String(event.key || '');
      const isZoomIn = event.code === 'NumpadAdd' || key === '+' || key === '=';
      const isZoomOut = event.code === 'NumpadSubtract' || key === '-' || key === '_';
      if (!isZoomIn && !isZoomOut) return;

      event.preventDefault();
      event.stopPropagation();
      zoomDrawflowBy(isZoomIn ? 1 : -1);
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [selectedRouteId, zoomDrawflowBy]);

  useEffect(() => {
    const container = drawflowRef.current;
    if (!container) return undefined;

    const handleWheelZoom = (event) => {
      if (!(event.ctrlKey || event.metaKey) || !selectedRouteId || !editorRef.current) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const unitMultiplier = event.deltaMode === 1
        ? 16
        : event.deltaMode === 2
          ? Math.max(320, container.clientHeight)
          : 1;
      const normalizedDelta = Math.max(-120, Math.min(120, Number(event.deltaY || 0) * unitMultiplier));
      if (Math.abs(normalizedDelta) < 0.01) return;

      const currentZoom = clampDrawflowZoom(editorRef.current.zoom) || 1;
      const nextZoom = currentZoom * Math.exp(-normalizedDelta * 0.0017);
      zoomDrawflowAtPoint(nextZoom, event.clientX, event.clientY);
    };

    container.addEventListener('wheel', handleWheelZoom, { capture: true, passive: false });
    return () => container.removeEventListener('wheel', handleWheelZoom, true);
  }, [selectedRouteId, zoomDrawflowAtPoint]);

  useEffect(() => {
    const container = drawflowRef.current;
    if (!container) return undefined;

    const readNodeId = (target) => {
      const nodeElement = target?.closest?.('.drawflow-node');
      if (!nodeElement?.id?.startsWith('node-')) return null;
      const drawId = nodeElement.id.slice(5);
      return nodeIdByDrawIdRef.current.get(String(drawId)) || null;
    };

    const handlePointerDown = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const nodeId = readNodeId(target);
      if (!nodeId) {
        closeNodeContextMenu();
        setConnectionDeleteContext(null);
        pointerNodeClickRef.current = null;
        pendingConnectionRef.current = null;
        return;
      }

      setConnectionDeleteContext(null);

      const outputHandle = target?.closest?.('.output');
      if (outputHandle && event.button === 0 && canEditSelectedRoute && !inspectionMode) {
        pendingConnectionRef.current = {
          sourceNodeId: nodeId,
          sourceHandle: readOutputHandleClass(outputHandle),
          startX: event.clientX,
          startY: event.clientY,
          created: false
        };
        closeConnectionCommandMenu();
      } else {
        pendingConnectionRef.current = null;
      }

      pointerNodeClickRef.current = {
        nodeId,
        button: event.button,
        x: event.clientX,
        y: event.clientY
      };
    };

    const handlePointerUp = (event) => {
      const pendingConnection = pendingConnectionRef.current;
      pendingConnectionRef.current = null;

      if (pendingConnection && !pendingConnection.created && canEditSelectedRoute && !inspectionMode) {
        const target = event.target instanceof Element ? event.target : null;
        const movedEnough = Math.hypot(
          Number(event.clientX) - Number(pendingConnection.startX || 0),
          Number(event.clientY) - Number(pendingConnection.startY || 0)
        ) > 6;
        const droppedOnNode = Boolean(target?.closest?.('.drawflow-node'));
        const droppedOnInput = Boolean(target?.closest?.('.input'));
        const droppedOnUi = Boolean(target?.closest?.('.drawflow-controls, .drawflow-minimap, .route-connection-command-menu'));

        if (movedEnough && !droppedOnNode && !droppedOnInput && !droppedOnUi) {
          openConnectionCommandMenu({
            sourceNodeId: pendingConnection.sourceNodeId,
            sourceHandle: pendingConnection.sourceHandle,
            clientX: event.clientX,
            clientY: event.clientY
          });
          pointerNodeClickRef.current = null;
          return;
        }
      }

      const playButton = event.target?.closest?.('[data-inspect-node-id]');
      if (playButton) {
        pointerNodeClickRef.current = null;
        return;
      }
      const click = pointerNodeClickRef.current;
      pointerNodeClickRef.current = null;
      if (!click || click.button !== 0) return;
      const nodeId = readNodeId(event.target);
      const moved = Math.hypot(event.clientX - click.x, event.clientY - click.y) > 6;
      if (moved || nodeId !== click.nodeId) return;
      const node = nodesRef.current.find((item) => item.id === click.nodeId);
      if (inspectionMode) {
        const savedStep = [...(inspectionContextRef.current?.steps || [])]
          .reverse()
          .find((step) => String(step?.node_id) === String(click.nodeId));
        if (!savedStep || !node) {
          setValidation('Este comando ainda não foi executado nesta inspeção. Use o Play para executá-lo.');
          return;
        }
        setInspectionStep(inspectionStepFromSnapshot(savedStep, node, {
          taskFileFields: inspectionTaskFileFields
        }));
        setValidation(`Consultando a execução salva de "${node.data?.title || node.id}". Use o Play somente se quiser reexecutar.`);
        return;
      }
      if (node) {
        closeNodeContextMenu();
        setSelectedNode(node);
      }
    };

    const handleContextMenu = (event) => {
      if (inspectionMode) return;
      if (!canEdit) return;
      if (requestConnectionDelete(event)) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      const nodeId = readNodeId(event.target);
      if (!nodeId) return;
      onNodeContextMenu(event, nodeId);
    };

    const handleClick = (event) => {
      const playButton = event.target?.closest?.('[data-inspect-node-id]');
      if (!playButton) return;
      event.preventDefault();
      event.stopPropagation();
      if (!inspectionMode) return;
      runInspectionNode(playButton.dataset.inspectNodeId);
    };

    const handleDoubleClick = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      if (target.closest('.drawflow-node, .connection, .main-path, .point, .drawflow-controls, .drawflow-minimap, .route-connection-command-menu')) return;
      if (!target.closest('.drawflow-canvas')) return;
      event.preventDefault();
      zoomDrawflowBy(1, event.clientX, event.clientY);
    };

    container.addEventListener('pointerdown', handlePointerDown, true);
    container.addEventListener('pointerup', handlePointerUp, true);
    container.addEventListener('contextmenu', handleContextMenu, true);
    container.addEventListener('click', handleClick, true);
    container.addEventListener('dblclick', handleDoubleClick, true);
    return () => {
      container.removeEventListener('pointerdown', handlePointerDown, true);
      container.removeEventListener('pointerup', handlePointerUp, true);
      container.removeEventListener('contextmenu', handleContextMenu, true);
      container.removeEventListener('click', handleClick, true);
      container.removeEventListener('dblclick', handleDoubleClick, true);
    };
  }, [
    canEdit,
    canEditSelectedRoute,
    closeConnectionCommandMenu,
    closeNodeContextMenu,
    inspectionMode,
    onNodeContextMenu,
    openConnectionCommandMenu,
    requestConnectionDelete,
    inspectionTaskFileFields,
    runInspectionNode,
    zoomDrawflowBy
  ]);

  useEffect(() => {
    if (!canEdit || !selectedRouteId || !flowDirtyRef.current || Date.now() < suppressDirtyUntilRef.current) return undefined;

    const flowNodes = cloneFlow(nodes);
    const flowEdges = cloneFlow(edges);
    setRoutes((current) => current.map((route) => (
      String(route.id) === String(selectedRouteId)
        ? { ...route, flow_nodes: flowNodes, flow_edges: flowEdges }
        : route
    )));

    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      setSavingFlow(true);
      try {
        const result = await api(`/api/autonomous-routing/${selectedRouteId}/flow`, {
          method: 'PUT',
          body: JSON.stringify({
            flow_nodes: flowNodes,
            flow_edges: flowEdges
          })
        });
        setRoutes((current) => current.map((route) => (
          String(route.id) === String(selectedRouteId) ? { ...route, ...result.data } : route
        )));
        flowDirtyRef.current = false;
      } catch (error) {
        setValidation(error.message);
      } finally {
        setSavingFlow(false);
      }
    }, 800);

    return () => clearTimeout(saveTimerRef.current);
  }, [canEdit, edges, nodes, selectedRouteId]);

  useEffect(() => {
    const onPointerMove = (event) => {
      const drag = pointerDragRef.current;
      if (!drag) return;
      const distance = Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY);
      if (distance > 10) {
        drag.moved = true;
      }
    };

    const onPointerUp = (event) => {
      const drag = pointerDragRef.current;
      pointerDragRef.current = null;
      if (Date.now() - lastDragAtRef.current < 350) return;
      if (!drag?.moved || !stageRef.current) return;
      const rect = stageRef.current.getBoundingClientRect();
      const insideStage = (
        event.clientX >= rect.left
        && event.clientX <= rect.right
        && event.clientY >= rect.top
        && event.clientY <= rect.bottom
      );
      if (!insideStage) return;
      lastDragAtRef.current = Date.now();
      addCommandAtClientPoint(drag.commandType, event.clientX, event.clientY);
    };

    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
    return () => {
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
    };
  }, [addCommandAtClientPoint]);

  const startConfig = useMemo(() => normalizeStartConfig(
    nodes.find(isStartNode)?.data?.config || commandCatalog.start.defaults,
    selectedRoute || {}
  ), [nodes, selectedRoute]);

  const periodicityLabel = useMemo(() => {
    if (isSearchStartConfig(startConfig)) {
      const every = normalizeStartExecutionInterval(startConfig.executionEveryMinutes, 5);
      return `busca API a cada ${every} minuto${every === 1 ? '' : 's'}`;
    }
    if (isTaskUpdateStartConfig(startConfig)) return 'a cada atualização da task';
    const every = Number(startConfig.scheduleEvery || 10);
    const unit = startConfig.scheduleUnit === 'hour' ? 'hora' : 'minuto';
    return `a cada ${every} ${unit}${every === 1 ? '' : 's'}`;
  }, [startConfig.executionEveryMinutes, startConfig.executionType, startConfig.scheduleEvery, startConfig.scheduleUnit, startConfig.startMode]);

  const stats = useMemo(() => [
    ['Periodicidade', periodicityLabel],
    ['Comandos', nodes.length],
    ['Conexões', edges.length],
    ['APIs testadas', testedApis.length],
    ['Inspeção', inspectionMode ? `${inspectionContext.steps?.length || 0} passo(s)` : 'off'],
    ['Save', savingFlow ? 'salvando' : 'ok']
  ], [edges.length, inspectionContext.steps?.length, inspectionMode, nodes.length, periodicityLabel, savingFlow, testedApis.length]);

  const minimapNodes = useMemo(() => {
    const xValues = nodes.map((item) => item.position?.x || 0);
    const yValues = nodes.map((item) => item.position?.y || 0);
    const minX = Math.min(...xValues, 0);
    const minY = Math.min(...yValues, 0);
    const maxX = Math.max(...xValues.map((value) => value + 260), 260);
    const maxY = Math.max(...yValues.map((value) => value + 132), 132);
    const scaleX = 156 / Math.max(1, maxX - minX);
    const scaleY = 88 / Math.max(1, maxY - minY);
    const scale = Math.min(scaleX, scaleY);

    return nodes.map((node) => {
      const command = commandForType(node.data?.commandType);
      return {
        id: node.id,
        tone: command.tone,
        x: 12 + ((node.position?.x || 0) - minX) * scale,
        y: 12 + ((node.position?.y || 0) - minY) * scale,
        width: Math.max(18, 236 * scale),
        height: Math.max(10, 86 * scale)
      };
    });
  }, [nodes]);

  const inspectionPayload = inspectionStep?.input || prettyInspectJson({
    status: 'aguardando_play',
    message: 'Clique em Play no footer de um comando para inspecionar o payload.',
    context: inspectionContext
  });
  const inspectionOutput = inspectionStep?.output || prettyInspectJson({
    status: 'idle',
    message: 'A resposta do comando selecionado aparecerá aqui.'
  });
  const inspectionPreviewInputSchema = useMemo(() => {
    const source = inspectionStep?.inputPayload ?? parseInspectJsonValue(inspectionPayload);
    return schemaFromInspectionInput(source || {});
  }, [inspectionPayload, inspectionStep]);
  const inspectionPreviewOutputSchema = useMemo(() => {
    const source = inspectionStep?.outputPayload ?? parseInspectJsonValue(inspectionOutput);
    return schemaFromRuntimeOutput(source || {}, { includeDeclaredSchema: false });
  }, [inspectionOutput, inspectionStep]);
  const inspectionPreviewInputNodes = useMemo(() => (
    buildCommandSchemaTree(inspectionPreviewInputSchema)
  ), [inspectionPreviewInputSchema]);
  const inspectionPreviewOutputNodes = useMemo(() => (
    buildCommandSchemaTree(inspectionPreviewOutputSchema)
  ), [inspectionPreviewOutputSchema]);
  const inspectionPayloadMatches = useMemo(() => (
    inspectionTextMatches(inspectionPayload, inspectionPayloadQuery)
  ), [inspectionPayload, inspectionPayloadQuery]);
  const inspectionPayloadSegments = useMemo(() => (
    inspectionHighlightSegments(inspectionPayload, inspectionPayloadMatches)
  ), [inspectionPayload, inspectionPayloadMatches]);
  const inspectionOutputMatches = useMemo(() => (
    inspectionTextMatches(inspectionOutput, inspectionOutputQuery)
  ), [inspectionOutput, inspectionOutputQuery]);
  const inspectionOutputSegments = useMemo(() => (
    inspectionHighlightSegments(inspectionOutput, inspectionOutputMatches)
  ), [inspectionOutput, inspectionOutputMatches]);

  useEffect(() => {
    if (!inspectionPayloadCopyNotice) return undefined;
    const timer = window.setTimeout(() => setInspectionPayloadCopyNotice(''), 1600);
    return () => window.clearTimeout(timer);
  }, [inspectionPayloadCopyNotice]);

  useEffect(() => {
    if (!inspectionOutputCopyNotice) return undefined;
    const timer = window.setTimeout(() => setInspectionOutputCopyNotice(''), 1600);
    return () => window.clearTimeout(timer);
  }, [inspectionOutputCopyNotice]);

  useEffect(() => {
    if (!inspectionPayloadQuery.trim() || !inspectionPayloadMatches.length) {
      setInspectionPayloadMatchIndex(-1);
      return;
    }
    setInspectionPayloadMatchIndex((current) => {
      if (current < 0) return 0;
      if (current >= inspectionPayloadMatches.length) return inspectionPayloadMatches.length - 1;
      return current;
    });
  }, [inspectionPayloadMatches.length, inspectionPayloadQuery]);

  useEffect(() => {
    if (!inspectionOutputQuery.trim() || !inspectionOutputMatches.length) {
      setInspectionOutputMatchIndex(-1);
      return;
    }
    setInspectionOutputMatchIndex((current) => {
      if (current < 0) return 0;
      if (current >= inspectionOutputMatches.length) return inspectionOutputMatches.length - 1;
      return current;
    });
  }, [inspectionOutputMatches.length, inspectionOutputQuery]);

  useEffect(() => {
    setInspectionPayloadQuery('');
    setInspectionPayloadMatchIndex(-1);
    setInspectionOutputQuery('');
    setInspectionOutputMatchIndex(-1);
    setInspectionPayloadCopyNotice('');
    setInspectionOutputCopyNotice('');
  }, [inspectionStep]);

  useEffect(() => {
    if (inspectionPayloadMatchIndex < 0) return;
    const codeElement = inspectionPayloadCodeRef.current;
    if (!codeElement) return;
    const activeMatch = codeElement.querySelector(`mark[data-match-index="${inspectionPayloadMatchIndex}"]`);
    if (!activeMatch) return;
    activeMatch.scrollIntoView({ block: 'center', inline: 'nearest' });
  }, [inspectionPayload, inspectionPayloadMatchIndex, inspectionPayloadQuery]);

  useEffect(() => {
    if (inspectionOutputMatchIndex < 0) return;
    const codeElement = inspectionOutputCodeRef.current;
    if (!codeElement) return;
    const activeMatch = codeElement.querySelector(`mark[data-match-index="${inspectionOutputMatchIndex}"]`);
    if (!activeMatch) return;
    activeMatch.scrollIntoView({ block: 'center', inline: 'nearest' });
  }, [inspectionOutputMatchIndex, inspectionOutputQuery, inspectionOutput]);

  const copyInspectionPayload = useCallback(async () => {
    const copied = await copyPlainText(inspectionPayload);
    setInspectionPayloadCopyNotice(copied ? 'Copiado!' : 'Falha ao copiar payload.');
  }, [inspectionPayload]);

  const copyInspectionOutput = useCallback(async () => {
    const copied = await copyPlainText(inspectionOutput);
    setInspectionOutputCopyNotice(copied ? 'Copiado!' : 'Falha ao copiar resposta.');
  }, [inspectionOutput]);

  const goToInspectionPayloadMatch = useCallback((direction = 1) => {
    if (!inspectionPayloadMatches.length) {
      setInspectionPayloadMatchIndex(-1);
      return;
    }
    setInspectionPayloadMatchIndex((current) => {
      if (current < 0) return 0;
      const length = inspectionPayloadMatches.length;
      const delta = direction >= 0 ? 1 : -1;
      return (current + delta + length) % length;
    });
  }, [inspectionPayloadMatches.length]);

  const goToInspectionOutputMatch = useCallback((direction = 1) => {
    if (!inspectionOutputMatches.length) {
      setInspectionOutputMatchIndex(-1);
      return;
    }
    setInspectionOutputMatchIndex((current) => {
      if (current < 0) return 0;
      const length = inspectionOutputMatches.length;
      const delta = direction >= 0 ? 1 : -1;
      return (current + delta + length) % length;
    });
  }, [inspectionOutputMatches.length]);

  const renderPaletteCommands = () => availableCommandEntries
    .map(([key, command]) => {
      const commandAvailable = canUseCommand(key, { notify: false });
      const commandEnabled = canEditSelectedRoute && commandAvailable;
      const unavailableTitle = key === 'chat' || key === 'voice'
        ? `${command.label} indisponível. Configure em Configurações > Integrações.`
        : key === 'cortex'
          ? `${command.label} indisponível. Contrate em Configurações > Planos.`
          : `${command.label} indisponível.`;
      return (
        <button
          key={key}
          type="button"
          className={`palette-command tone-${command.tone}${commandEnabled ? '' : ' is-disabled'}`}
          draggable={commandEnabled}
          disabled={!commandEnabled}
          aria-disabled={!commandEnabled}
          style={commandEnabled ? undefined : { opacity: 0.45, filter: 'grayscale(0.55)' }}
          onDragStart={(event) => onDragStart(event, key)}
          onPointerDown={(event) => onPalettePointerDown(event, key)}
          title={commandEnabled
            ? `Arraste ${command.label} para o canvas`
            : unavailableTitle}
        >
          <span className="material-symbols-rounded" aria-hidden="true">{command.icon}</span>
          <div>
            <strong>{command.label}</strong>
          </div>
        </button>
      );
    });

  return (
    <div
      className={`automation-builder-shell ${inspectionMode ? 'is-inspection-mode' : ''}`}
      style={inspectionMode ? { '--inspection-drawer-height': `${inspectionDrawerHeight}px` } : undefined}
    >
      <aside className="command-palette" aria-hidden={inspectionMode ? 'true' : undefined}>
        {compactPaletteMode ? (
          <button
            className="route-commands-button palette-commands-launcher"
            type="button"
            onClick={() => setCommandsModalOpen(true)}
            aria-label="Abrir comandos"
          >
            <span className="material-symbols-rounded" aria-hidden="true">widgets</span>
            <span>Comandos</span>
          </button>
        ) : (
          <>
            <div className="palette-command-title">
              <span className="material-symbols-rounded" aria-hidden="true">widgets</span>
              <div>
                <strong>Comandos</strong>
                <p>Arraste para o canvas</p>
              </div>
            </div>
            <div className="palette-list">
              {renderPaletteCommands()}
            </div>
          </>
        )}
      </aside>

      <section className={`flow-stage ${inspectionMode ? 'is-inspection-mode' : ''}`} ref={stageRef} onDrop={onDrop} onDragOver={onDragOver}>
        <div className="flow-stage-status">
          <span className="material-symbols-rounded" aria-hidden="true">schema</span>
          <strong>{validation}</strong>
          {stats.map(([label, value]) => (
            <small key={label}>{label}: {value}</small>
          ))}
        </div>
        <div className="drawflow-canvas" ref={drawflowRef} aria-label="Canvas de roteamento autônomo" />
        {!selectedRoute && !routesLoaded && (
          <section className="drawflow-empty-state" aria-live="polite">
            <span className="material-symbols-rounded" aria-hidden="true">hourglass_top</span>
            <strong>Carregando roteamentos</strong>
            <p>Aguarde enquanto sincronizamos os roteamentos para exibir no canvas.</p>
          </section>
        )}

        {!selectedRoute && routesLoaded && (
          <section className="drawflow-empty-state" aria-live="polite">
            <span className="material-symbols-rounded" aria-hidden="true">alt_route</span>
            <strong>Nenhum roteamento selecionado</strong>
            <p>Volte à listagem e escolha o fluxo que deseja editar.</p>
            <div>
              <button className="primary-button" type="button" onClick={() => {
                window.dispatchEvent(new CustomEvent('tdemand:routing-back-request'));
              }}>
                <span className="material-symbols-rounded" aria-hidden="true">arrow_back</span>
                Ver roteamentos
              </button>
            </div>
          </section>
        )}

        <div className={`drawflow-controls ${!selectedRoute ? 'is-disabled' : ''}`} aria-label="Controles do canvas">
          <button type="button" onClick={() => zoomDrawflowBy(1)} disabled={!selectedRoute} aria-label="Aproximar">
            <span className="material-symbols-rounded" aria-hidden="true">add</span>
          </button>
          <button type="button" onClick={() => zoomDrawflowBy(-1)} disabled={!selectedRoute} aria-label="Afastar">
            <span className="material-symbols-rounded" aria-hidden="true">remove</span>
          </button>
          <button type="button" onClick={() => centerDrawflow({ comfortable: false })} disabled={!selectedRoute} aria-label="Centralizar e enquadrar todo o flow">
            <span className="material-symbols-rounded" aria-hidden="true">center_focus_strong</span>
          </button>
        </div>

        <div className="drawflow-minimap" aria-hidden="true">
          <svg viewBox="0 0 180 112" role="presentation">
            {minimapNodes.map((node) => (
              <rect
                key={node.id}
                className={`minimap-node tone-${node.tone}`}
                x={node.x}
                y={node.y}
                width={node.width}
                height={node.height}
                rx="4"
              />
            ))}
          </svg>
        </div>

        {connectionCommandMenu && (
          <section
            className="route-connection-command-menu"
            role="menu"
            aria-label="Selecionar comando"
            style={{
              left: `${connectionCommandMenu.clientX}px`,
              top: `${connectionCommandMenu.clientY}px`
            }}
            onClick={(event) => event.stopPropagation()}
          >
            {availableCommandEntries.map(([commandType, command]) => {
              const commandEnabled = canEditSelectedRoute && canUseCommand(commandType, { notify: false });
              return (
                <button
                  key={commandType}
                  type="button"
                  role="menuitem"
                  className={commandEnabled ? '' : 'is-disabled'}
                  disabled={!commandEnabled}
                  aria-disabled={!commandEnabled}
                  style={commandEnabled ? undefined : { opacity: 0.45, filter: 'grayscale(0.55)' }}
                  onClick={() => createCommandFromConnectionMenu(commandType)}
                  title={commandEnabled ? `Adicionar comando ${command.label}` : `${command.label} indisponível`}
                >
                  <span className="material-symbols-rounded" aria-hidden="true">{command.icon}</span>
                  {command.label}
                </button>
              );
            })}
          </section>
        )}

        {nodeContextMenu && (
          <button
            className="node-delete-context-button"
            type="button"
            style={{ left: nodeContextMenu.x, top: nodeContextMenu.y }}
            onClick={() => deleteNode(nodeContextMenu.nodeId)}
            title="Excluir comando"
            aria-label="Excluir comando"
          >
            <span className="material-symbols-rounded" aria-hidden="true">delete</span>
          </button>
        )}
        {connectionDeleteContext && (
          <button
            className="node-delete-context-button connection-delete-context-button"
            type="button"
            style={{ left: connectionDeleteContext.x, top: connectionDeleteContext.y }}
            onClick={confirmConnectionDelete}
            title="Excluir ligação"
            aria-label="Excluir ligação"
          >
            <span className="material-symbols-rounded" aria-hidden="true">delete</span>
          </button>
        )}
      </section>

      {inspectionMode && (
        <section className="inspection-drawer" aria-label="Painel de inspecao do roteamento">
          <div
            className="inspection-drawer-resizer"
            role="separator"
            aria-orientation="horizontal"
            aria-valuemin={getInspectionDrawerBounds().min}
            aria-valuemax={getInspectionDrawerBounds().max}
            aria-valuenow={inspectionDrawerHeight}
            title="Arraste para redimensionar o painel de inspecao"
            onPointerDown={onInspectionDrawerResizeStart}
            onDoubleClick={() => resizeInspectionDrawer(300, true)}
          />
          <header className="inspection-drawer-head">
            <div className="inspection-drawer-title">
              <span className="material-symbols-rounded" aria-hidden="true">troubleshoot</span>
              <div>
                <strong>{inspectionStep?.title || 'Modo Inspeção pronto'}</strong>
                <p>{inspectionStep ? `${inspectionRunning ? 'Executando' : 'Executado'} às ${inspectionStep.at} | ${inspectionStep.commandType}` : 'Execute um comando por vez para acompanhar payload e resposta.'}</p>
              </div>
            </div>
            <div className="inspection-drawer-meta">
              <div className="inspection-view-mode" role="group" aria-label="Modos de visualização da inspeção">
                <button
                  type="button"
                  className={inspectionViewMode === 'json' ? 'active' : ''}
                  aria-pressed={inspectionViewMode === 'json' ? 'true' : 'false'}
                  onClick={() => setInspectionViewMode('json')}
                >
                  JSON
                </button>
                <button
                  type="button"
                  className={inspectionViewMode === 'preview' ? 'active' : ''}
                  aria-pressed={inspectionViewMode === 'preview' ? 'true' : 'false'}
                  onClick={() => setInspectionViewMode('preview')}
                >
                  Preview
                </button>
              </div>
              <span>{inspectionContext.steps?.length || 0} passo(s)</span>
              {inspectionRunning && <span>em execução</span>}
              <label className="inspection-array-item-field" title="Opcional: processa apenas o item informado do array configurado no Início.">
                <small>Item do array</small>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={inspectionArrayItemIndex}
                  onChange={(event) => setInspectionArrayItemIndex(event.target.value.replace(/[^\d]/g, ''))}
                  placeholder="auto"
                  disabled={inspectionRunning}
                />
              </label>
              <button className="secondary-button" type="button" onClick={() => {
                resetLearnedVariables('Inspeção reiniciada. Clique em Play para executar novamente.');
              }}>
                <span className="material-symbols-rounded" aria-hidden="true">restart_alt</span>
                Reiniciar
              </button>
              <button className="secondary-button" type="button" onClick={closeInspectionMode}>
                <span className="material-symbols-rounded" aria-hidden="true">close</span>
                Sair
              </button>
            </div>
          </header>
          {inspectionViewMode === 'preview' ? (
            <div className="inspection-drawer-grid">
              <article className="inspection-json-card inspection-structured-card">
                <header>
                  <span className="material-symbols-rounded" aria-hidden="true">input</span>
                  <strong>Payload</strong>
                </header>
                <div className="task-detail-structured-content command-contract-content inspection-structured-content">
                  {inspectionPreviewInputNodes.length ? (
                    <div className="task-detail-structured-tree">
                      {inspectionPreviewInputNodes.map((schemaNode) => <CommandSchemaNode key={schemaNode.path} node={schemaNode} />)}
                    </div>
                  ) : (
                    <div className="command-contract-empty">
                      <span className="material-symbols-rounded" aria-hidden="true">manage_search</span>
                      <strong>Nenhuma key no payload</strong>
                      <small>Execute um comando para gerar a estrutura do payload.</small>
                    </div>
                  )}
                </div>
              </article>
              <article className="inspection-json-card inspection-structured-card">
                <header>
                  <span className="material-symbols-rounded" aria-hidden="true">output</span>
                  <strong>Resposta obtida</strong>
                </header>
                <div className="task-detail-structured-content command-contract-content inspection-structured-content">
                  {inspectionPreviewOutputNodes.length ? (
                    <div className="task-detail-structured-tree">
                      {inspectionPreviewOutputNodes.map((schemaNode) => <CommandSchemaNode key={schemaNode.path} node={schemaNode} />)}
                    </div>
                  ) : (
                    <div className="command-contract-empty">
                      <span className="material-symbols-rounded" aria-hidden="true">manage_search</span>
                      <strong>Nenhuma key na resposta</strong>
                      <small>Execute um comando para gerar a estrutura da resposta.</small>
                    </div>
                  )}
                </div>
              </article>
            </div>
          ) : (
            <div className="inspection-drawer-grid">
              <article className="inspection-json-card">
                <header className="inspection-json-card-head-search">
                  <span className="material-symbols-rounded" aria-hidden="true">input</span>
                  <strong>Payload</strong>
                  <div className="inspection-json-card-head-tools">
                    <div className="inspection-json-card-actions">
                      <button
                        type="button"
                        className="secondary-button is-copy-icon"
                        onClick={() => void copyInspectionPayload()}
                        aria-label="Copiar payload do modo inspeção"
                        title={inspectionPayloadCopyNotice || 'Copiar payload'}
                      >
                        <span className="material-symbols-rounded" aria-hidden="true">content_copy</span>
                      </button>
                    </div>
                    <div className="inspection-json-search" role="search" aria-label="Buscar no payload">
                      <input
                        type="search"
                        value={inspectionPayloadQuery}
                        onChange={(event) => setInspectionPayloadQuery(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key !== 'Enter') return;
                          event.preventDefault();
                          goToInspectionPayloadMatch(event.shiftKey ? -1 : 1);
                        }}
                        aria-label="Buscar texto no payload"
                        placeholder="Buscar no payload..."
                      />
                      <button
                        type="button"
                        className="icon-button"
                        onClick={() => goToInspectionPayloadMatch(-1)}
                        disabled={!inspectionPayloadMatches.length}
                        aria-label="Buscar ocorrência anterior no payload"
                      >
                        <span className="material-symbols-rounded" aria-hidden="true">keyboard_arrow_up</span>
                      </button>
                      <small>{inspectionPayloadMatches.length ? `${inspectionPayloadMatchIndex + 1}/${inspectionPayloadMatches.length}` : '0/0'}</small>
                      <button
                        type="button"
                        className="icon-button"
                        onClick={() => goToInspectionPayloadMatch(1)}
                        disabled={!inspectionPayloadMatches.length}
                        aria-label="Buscar próxima ocorrência no payload"
                      >
                        <span className="material-symbols-rounded" aria-hidden="true">keyboard_arrow_down</span>
                      </button>
                    </div>
                  </div>
                </header>
                <pre>
                  <code ref={inspectionPayloadCodeRef} className="inspection-searchable-code">
                    {inspectionPayloadSegments.map((segment, index) => {
                      if (!segment.isMatch) {
                        return <Fragment key={`payload-text-${index}`}>{segment.text}</Fragment>;
                      }
                      const isActive = segment.matchIndex === inspectionPayloadMatchIndex;
                      return (
                        <mark
                          key={`payload-match-${segment.matchIndex}-${index}`}
                          className={isActive ? 'is-active' : ''}
                          data-match-index={segment.matchIndex}
                        >
                          {segment.text}
                        </mark>
                      );
                    })}
                  </code>
                </pre>
              </article>
              <article className="inspection-json-card">
                <header className="inspection-json-card-head-search">
                  <span className="material-symbols-rounded" aria-hidden="true">output</span>
                  <strong>Resposta obtida</strong>
                  <div className="inspection-json-card-head-tools">
                    <div className="inspection-json-card-actions">
                      <button
                        type="button"
                        className="secondary-button is-copy-icon"
                        onClick={() => void copyInspectionOutput()}
                        aria-label="Copiar resposta obtida do modo inspeção"
                        title={inspectionOutputCopyNotice || 'Copiar resposta'}
                      >
                        <span className="material-symbols-rounded" aria-hidden="true">content_copy</span>
                      </button>
                    </div>
                    <div className="inspection-json-search" role="search" aria-label="Buscar na resposta obtida">
                      <input
                        type="search"
                        value={inspectionOutputQuery}
                        onChange={(event) => setInspectionOutputQuery(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key !== 'Enter') return;
                          event.preventDefault();
                          goToInspectionOutputMatch(event.shiftKey ? -1 : 1);
                        }}
                        aria-label="Buscar texto na resposta"
                        placeholder="Buscar na resposta..."
                      />
                      <button
                        type="button"
                        className="icon-button"
                        onClick={() => goToInspectionOutputMatch(-1)}
                        disabled={!inspectionOutputMatches.length}
                        aria-label="Buscar ocorrência anterior"
                      >
                        <span className="material-symbols-rounded" aria-hidden="true">keyboard_arrow_up</span>
                      </button>
                      <small>{inspectionOutputMatches.length ? `${inspectionOutputMatchIndex + 1}/${inspectionOutputMatches.length}` : '0/0'}</small>
                      <button
                        type="button"
                        className="icon-button"
                        onClick={() => goToInspectionOutputMatch(1)}
                        disabled={!inspectionOutputMatches.length}
                        aria-label="Buscar próxima ocorrência"
                      >
                        <span className="material-symbols-rounded" aria-hidden="true">keyboard_arrow_down</span>
                      </button>
                    </div>
                  </div>
                </header>
                <pre>
                  <code ref={inspectionOutputCodeRef} className="inspection-searchable-code">
                    {inspectionOutputSegments.map((segment, index) => {
                      if (!segment.isMatch) {
                        return <Fragment key={`text-${index}`}>{segment.text}</Fragment>;
                      }
                      const isActive = segment.matchIndex === inspectionOutputMatchIndex;
                      return (
                        <mark
                          key={`match-${segment.matchIndex}-${index}`}
                          className={isActive ? 'is-active' : ''}
                          data-match-index={segment.matchIndex}
                        >
                          {segment.text}
                        </mark>
                      );
                    })}
                  </code>
                </pre>
              </article>
            </div>
          )}
        </section>
      )}

      {commandsModalOpen && compactPaletteMode && (
        <div className="flow-modal-backdrop" role="presentation" onClick={() => setCommandsModalOpen(false)}>
          <section
            className="flow-modal command-palette-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Comandos do roteamento"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="flow-modal-head">
              <div className="flow-modal-icon tone-purple">
                <span className="material-symbols-rounded" aria-hidden="true">widgets</span>
              </div>
              <div>
                <span>Biblioteca</span>
                <h3>Comandos</h3>
                <p>Arraste um comando para a posição desejada no canvas.</p>
              </div>
              <button className="icon-button" type="button" onClick={() => setCommandsModalOpen(false)} aria-label="Fechar modal">
                <span className="material-symbols-rounded" aria-hidden="true">close</span>
              </button>
            </header>
            <div className="flow-modal-body command-palette-modal-body">
              <div className="palette-command-title">
                <span className="material-symbols-rounded" aria-hidden="true">widgets</span>
                <div>
                  <strong>Comandos</strong>
                  <p>Arraste para o canvas</p>
                </div>
              </div>
              <div className="palette-list">
                {renderPaletteCommands()}
              </div>
            </div>
            <footer className="flow-modal-footer">
              <button className="secondary-button" type="button" onClick={() => setCommandsModalOpen(false)}>Fechar</button>
            </footer>
          </section>
        </div>
      )}

      {selectedNode && (
        <ConfigModal
          node={selectedNode}
          route={selectedRoute}
          demands={demands}
          flowNodes={nodes}
          flowEdges={edges}
          testedApis={testedApis}
          runtimeOutputs={inspectionContext?.outputs || {}}
          inspectionTask={inspectionContext?.task || null}
          canEdit={canEdit}
          onClose={() => setSelectedNode(null)}
          onSave={saveNode}
        />
      )}

      {routeCreateOpen && typeof document !== 'undefined' && createPortal(
        <RouteCreateModal
          value={newRouteName}
          demandId={newRouteDemandId}
          demands={demands}
          notice={routeNotice}
          creating={creatingRoute}
          onChange={setNewRouteName}
          onDemandChange={setNewRouteDemandId}
          onClose={() => setRouteCreateOpen(false)}
          onSubmit={createRoute}
        />,
        document.body
      )}

      {routeDuplicateOpen && (
        <RouteCreateModal
          mode="duplicate"
          value={duplicateRouteName}
          demandId={duplicateRouteDemandId}
          demands={demands}
          notice={duplicateRouteNotice}
          creating={duplicatingRoute}
          onChange={setDuplicateRouteName}
          onDemandChange={setDuplicateRouteDemandId}
          onClose={() => {
            setRouteDuplicateOpen(false);
            setDuplicateSourceRouteId('');
          }}
          onSubmit={duplicateRoute}
        />
      )}
    </div>
  );
};

const rootElement = document.getElementById('automationFlowRoot');

if (rootElement) {
  createRoot(rootElement).render(<AutomationBuilder />);
}
